import React, { useState, useEffect, useCallback, useRef } from "react";

const API_BASE = "https://socipulses.com";

const DEFAULT_TEMPLATES = [
  { id:1, label:"Quick Response", text:"Hi! We service your area and can usually get someone out same day. Call us at (586) 486-5766 or visit hvacanytime.com — we'd love to help!" },
  { id:2, label:"Furnace / No Heat", text:"Hi! Sounds like you need a hand with your furnace. We specialize in heating repairs and offer emergency response. Call (586) 486-5766 or visit hvacanytime.com for a free estimate!" },
  { id:3, label:"AC / Cooling", text:"Hi! Sorry to hear about your AC issue. We offer same-day AC service across Metro Detroit. Call (586) 486-5766 or visit hvacanytime.com — we'll get you cooled off fast!" },
  { id:4, label:"General HVAC", text:"Hi! We're a local HVAC company serving Metro Detroit — licensed, insured, and family-owned. Call (586) 486-5766 for a free estimate or visit hvacanytime.com!" },
  { id:5, label:"Boiler / Water Heater", text:"Hi! We handle boiler and water heater repairs throughout Metro Detroit. $90 diagnostic call — we'll figure out exactly what's going on. Call (586) 486-5766 anytime!" },
];

const DEFAULT_INBOX_PAGES = [];

const MOCK_INBOX = [
  { id:1, pageId:"demo", senderName:"Mike Johnson", senderId:"111", text:"Hi, do you guys service Sterling Heights? My furnace stopped working last night.", time:new Date(Date.now()-14*60000).toISOString(), read:false, replied:false },
  { id:2, pageId:"demo", senderName:"Sarah K.", senderId:"222", text:"What's your pricing for an AC tune-up? I have a 3-ton unit.", time:new Date(Date.now()-45*60000).toISOString(), read:false, replied:false },
  { id:3, pageId:"demo", senderName:"Tony Rossi", senderId:"333", text:"Do you do commercial HVAC? We have a restaurant in Warren.", time:new Date(Date.now()-2*3600000).toISOString(), read:true, replied:true },
];

function useApi() {
  const req = useCallback(async (method, path, body=null) => {
    const headers = { "Content-Type":"application/json" };
    const token = localStorage.getItem("sl_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}${path}`, { method, headers, body:body?JSON.stringify(body):null });
    if (res.status===401) { localStorage.removeItem("sl_token"); window.location.reload(); return null; }
    if (!res.ok) { const e = await res.json().catch(()=>({detail:"Unknown error"})); throw new Error(e.detail||"Failed"); }
    if (res.status===204) return null;
    return res.json();
  }, []);
  return { get:p=>req("GET",p), post:(p,b)=>req("POST",p,b), patch:(p,b)=>req("PATCH",p,b), put:(p,b)=>req("PUT",p,b), del:p=>req("DELETE",p) };
}

function timeAgo(iso) {
  const d=(Date.now()-new Date(iso))/1000;
  if(d<60) return `${Math.floor(d)}s ago`;
  if(d<3600) return `${Math.floor(d/60)}m ago`;
  if(d<86400) return `${Math.floor(d/3600)}h ago`;
  return `${Math.floor(d/86400)}d ago`;
}
function scoreColor(s){ return s>=40?"#ff4d4d":s>=25?"#f59e0b":"#22c55e"; }
function scoreLabel(s){ return s>=40?"URGENT":s>=25?"HIGH":"MATCH"; }
function platformBadge(platform) {
  if (platform==="reddit")     return { bg:"#ff450020", color:"#ff6314", border:"1px solid #ff450030", label:"REDDIT",      icon:"🟠" };
  if (platform==="nextdoor") return { bg:"#7c3aed20", color:"#a78bfa", border:"1px solid #7c3aed30", label:"NEXTDOOR",  icon:"🟣" };
  return { bg:"#1877F220", color:"#1877F2", border:"1px solid #1877F230", label:"FACEBOOK", icon:"🔵" };
}

function Spinner({ size=16 }) {
  return <span style={{ display:"inline-block", width:size, height:size, border:"2px solid #2a2a3e", borderTopColor:"#6366f1", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />;
}

function Toast({ toasts }) {
  return (
    <div style={{ position:"fixed", bottom:24, right:24, zIndex:9999, display:"flex", flexDirection:"column", gap:8 }}>
      {toasts.map(t=>(
        <div key={t.id} style={{ padding:"10px 18px", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", animation:"slideIn 0.2s ease",
          background:t.type==="error"?"#2a0a0a":t.type==="success"?"#0a2a1a":"#0f0f1a",
          border:`1px solid ${t.type==="error"?"#ff4d4d40":t.type==="success"?"#22c55e40":"#2a2a3e"}`,
          color:t.type==="error"?"#ff4d4d":t.type==="success"?"#22c55e":"#ccc",
          boxShadow:"0 4px 20px rgba(0,0,0,0.5)" }}>
          {t.message}
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts,setToasts] = useState([]);
  const add = (message,type="info") => {
    const id=Date.now(); setToasts(p=>[...p,{id,message,type}]);
    setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),3500);
  };
  return { toasts, success:m=>add(m,"success"), error:m=>add(m,"error"), info:m=>add(m,"info") };
}

function Sidebar({ view, setView, unreadCount, inboxUnread, fbConnected, userEmail, onSignOut, isAdmin, api }) {
  const nav = [
    { id:"feed",      icon:"⚡", label:"Live Feed",       badge:unreadCount },
    { id:"pipeline",  icon:"🎯", label:"Lead Pipeline",   badge:0 },
    { id:"analytics", icon:"📊", label:"Analytics",       badge:0 },
    { id:"inbox",     icon:"💬", label:"Page Inbox",      badge:inboxUnread },
    { id:"templates", icon:"📋", label:"Reply Templates", badge:0 },
    { id:"myzone",    icon:"📍", label:"My Zone",         badge:0 },
    { id:"onboarding-setup", icon:"🚀", label:"Zone Setup", badge:0 },
    { id:"pages",     icon:"📡", label:"Monitored Pages", badge:0 },
    { id:"pricing",   icon:"💰", label:"Pricing",         badge:0 },
    { id:"settings",  icon:"⚙️", label:"Settings",        badge:0 },
    { id:"billing",   icon:"💳", label:"Billing",          badge:0 },
    { id:"status",    icon:"📡", label:"System Status",    badge:0 },
    { id:"referral",  icon:"🤝", label:"Refer & Earn",     badge:0 },
    ...(isAdmin ? [
      { id:"admin",   icon:"🛡️", label:"Admin Panel",     badge:0, admin:true },
      { id:"coupons", icon:"🏷️", label:"Coupons",         badge:0, admin:true },
      { id:"comms",    icon:"📣", label:"Announce",         badge:0, admin:true },
      { id:"refunds",  icon:"💸", label:"Refunds",          badge:0, admin:true },
      { id:"security", icon:"🔐", label:"Security",         badge:0, admin:true },
      { id:"cms",      icon:"✏️",  label:"Site Editor",      badge:0, admin:true },
      { id:"plans",    icon:"📦", label:"Plan Manager",     badge:0, admin:true },
      { id:"kb",       icon:"📚", label:"Knowledge Base",   badge:0, admin:true },
      { id:"guides",   icon:"📗", label:"Admin Guides",      badge:0, admin:true },
      { id:"advanced", icon:"📈", label:"Advanced Stats",    badge:0, admin:true },
    ] : []),
  ];
  return (
    <aside className="sl-sidebar" style={{ width:224, minHeight:"100vh", background:"#0a0a0f", borderRight:"1px solid #1e1e2e", display:"flex", flexDirection:"column", padding:"0 0 24px", transition:"width 0.2s", flexShrink:0 }}>
      <div style={{ padding:"28px 24px 20px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:34, height:34, borderRadius:8, background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:700, color:"#fff" }}>S</div>
            <div>
              <div style={{ color:"#fff", fontFamily:"'DM Mono',monospace", fontSize:13, fontWeight:600, letterSpacing:1 }}>SOCIALISTEN</div>
              <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:2 }}>MONITOR</div>
            </div>
          </div>
          <NotificationCenter api={api}/>
        </div>
      </div>
      <div style={{ margin:"0 16px 8px", padding:"8px 12px", borderRadius:8, background:fbConnected?"#0d2e1a":"#1e1020", border:`1px solid ${fbConnected?"#22c55e30":"#a855f730"}`, display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ width:7, height:7, borderRadius:"50%", background:fbConnected?"#22c55e":"#a855f7", boxShadow:`0 0 6px ${fbConnected?"#22c55e":"#a855f7"}` }}/>
        <span style={{ color:fbConnected?"#22c55e":"#a855f7", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{fbConnected?"FB Connected":"FB Disconnected"}</span>
      </div>
      <div style={{ margin:"0 16px 8px", padding:"8px 12px", borderRadius:8, background:"#0a1208", border:"1px solid #22c55e20", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ width:7, height:7, borderRadius:"50%", background:"#22c55e", boxShadow:"0 0 6px #22c55e" }}/>
        <span style={{ color:"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace" }}>Reddit Active</span>
      </div>
      <div style={{ margin:"0 16px 16px", padding:"8px 12px", borderRadius:8, background:"#0f0a1f", border:"1px solid #7c3aed20", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ width:7, height:7, borderRadius:"50%", background:"#a78bfa", boxShadow:"0 0 6px #a78bfa" }}/>
        <span style={{ color:"#a78bfa", fontSize:11, fontFamily:"'DM Mono',monospace" }}>Nextdoor Active</span>
      </div>
      <nav style={{ flex:1, padding:"0 12px" }}>
        {nav.map(item=>(
          <button key={item.id} onClick={()=>setView(item.id)} style={{
            width:"100%", display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8, border:"none", cursor:"pointer",
            background:view===item.id?"#1e1e2e":"transparent", color:view===item.id?"#fff":"#4a4a6a",
            fontSize:13, fontFamily:"'DM Mono',monospace", marginBottom:2, transition:"all 0.15s",
            borderLeft:view===item.id?"2px solid #6366f1":"2px solid transparent",
          }}>
            <span style={{ fontSize:15 }}>{item.icon}</span>
            <span style={{ flex:1, textAlign:"left" }}>{item.label}</span>
            {item.badge>0&&<span style={{ background:"#6366f1", color:"#fff", borderRadius:10, padding:"1px 7px", fontSize:10 }}>{item.badge}</span>}
          </button>
        ))}
      </nav>
      <div style={{ padding:"0 16px" }}>
        <div style={{ borderTop:"1px solid #1e1e2e", paddingTop:16 }}>
          <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", marginBottom:8, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{userEmail}</div>
          <button onClick={onSignOut} style={{ width:"100%", padding:"8px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#3a3a5a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Sign Out</button>
        </div>
      </div>
    </aside>
  );
}

function StatCard({ label, value, sub, color="#6366f1", loading }) {
  return (
    <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", flex:1 }}>
      <div style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{label}</div>
      <div style={{ color, fontSize:32, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{loading?<Spinner size={24}/>:value}</div>
      {sub&&<div style={{ color:"#4a4a6a", fontSize:11, marginTop:6 }}>{sub}</div>}
    </div>
  );
}

function MatchCard({ match, onRead, pages, templates }) {
  const [expanded,setExpanded] = useState(false);
  const [showTemplates,setShowTemplates] = useState(false);
  const [copied,setCopied] = useState(null);
  const pageName = match.page_name||pages.find(p=>p.id===match.page_id)?.name||"Unknown";
  const pb = platformBadge(match.platform||"facebook");

  const copyTemplate = (tpl) => {
    navigator.clipboard.writeText(tpl.text);
    setCopied(tpl.id); setTimeout(()=>setCopied(null),2000);
  };

  return (
    <div style={{ background:match.read?"#0c0c18":"#0f0f1a", border:`1px solid ${match.read?"#1a1a2a":"#2a2a3e"}`,
      borderLeft:`3px solid ${match.read?"#2a2a3e":scoreColor(match.score)}`,
      borderRadius:10, marginBottom:10, overflow:"hidden", opacity:match.read?0.7:1, transition:"all 0.2s" }}>
      <div style={{ padding:"16px 18px", cursor:"pointer" }} onClick={()=>{ setExpanded(!expanded); setShowTemplates(false); if(!match.read) onRead(match.id); }}>
        <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
          <div style={{ padding:"3px 8px", borderRadius:4, fontSize:9, fontWeight:700, fontFamily:"'DM Mono',monospace", letterSpacing:1, background:`${scoreColor(match.score)}20`, color:scoreColor(match.score), whiteSpace:"nowrap", marginTop:2 }}>
            {scoreLabel(match.score)} {match.score}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
              <span style={{ padding:"2px 7px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:pb.bg, color:pb.color, border:pb.border }}>{pb.icon} {pb.label}</span>
              <span style={{ color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{pageName}</span>
              <span style={{ color:"#2a2a3e" }}>•</span>
              <span style={{ color:"#3a3a5a", fontSize:11 }}>{timeAgo(match.found_at)}</span>
              {!match.read&&<span style={{ width:6, height:6, borderRadius:"50%", background:"#6366f1", display:"inline-block" }}/>}
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:8 }}>
              {(match.matched_terms||[]).map(t=><span key={t} style={{ background:"#1e1e3e", color:"#a5b4fc", fontSize:10, padding:"2px 8px", borderRadius:4, fontFamily:"'DM Mono',monospace" }}>{t}</span>)}
            </div>
            <p style={{ color:"#8888aa", fontSize:13, lineHeight:1.6, margin:0, display:expanded?"block":"-webkit-box", WebkitLineClamp:expanded?"unset":2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
              {match.post_text}
            </p>
          </div>
        </div>
      </div>
      {expanded&&(
        <div style={{ borderTop:"1px solid #1a1a2a", padding:"12px 18px", background:"#080810" }}>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {match.post_url&&match.post_url!=="#"&&(
              <a href={match.post_url} target="_blank" rel="noreferrer" style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6 }}>
                {match.platform==="reddit"?"🟠 View on Reddit":match.platform==="nextdoor"?"🟣 View on Nextdoor":"🔵 Open on Facebook"}
              </a>
            )}
            <button onClick={e=>{e.stopPropagation();setShowTemplates(!showTemplates);}} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${showTemplates?"#6366f1":"#2a2a3e"}`, background:showTemplates?"#6366f120":"transparent", color:showTemplates?"#a5b4fc":"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              📋 {showTemplates?"Hide Templates":"Reply Templates"}
            </button>
            {match.status && match.status !== "new" && (
              <span style={{ padding:"4px 10px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:STATUS_CONFIG[match.status]?.bg||"#1e1e2e", color:STATUS_CONFIG[match.status]?.color||"#ccc", border:`1px solid ${STATUS_CONFIG[match.status]?.color||"#2a2a3e"}30` }}>
                {STATUS_CONFIG[match.status]?.label||match.status}
              </span>
            )}
          </div>
          {showTemplates&&(
            <div style={{ marginTop:12 }}>
              <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>CLICK TO COPY — THEN PASTE ON {(match.platform==="reddit"?"REDDIT":"FACEBOOK").toUpperCase()}</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {templates.length===0?(
                  <div style={{ color:"#3a3a5a", fontSize:12, fontStyle:"italic" }}>No templates yet — add some in the Reply Templates tab</div>
                ):templates.map(tpl=>(
                  <div key={tpl.id} onClick={()=>copyTemplate(tpl)} style={{ padding:"10px 14px", borderRadius:8, border:"1px solid #2a2a3e", background:copied===tpl.id?"#0a2a1a":"#0f0f1a", cursor:"pointer", transition:"all 0.15s" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                      <span style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{tpl.label}</span>
                      <span style={{ color:copied===tpl.id?"#22c55e":"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{copied===tpl.id?"✓ Copied!":"Click to copy"}</span>
                    </div>
                    <p style={{ color:"#6a6a8a", fontSize:12, margin:0, lineHeight:1.5, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{tpl.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FeedView({ api, pages, stats, statsLoading, toast, templates, userPlan }) {
  const [matches,setMatches] = useState([]);
  const [loading,setLoading] = useState(true);
  const [filter,setFilter] = useState("all");
  const [selectedPage,setSelectedPage] = useState("all");
  const [scanning,setScanning] = useState({});
  const [offset,setOffset] = useState(0);
  const [total,setTotal] = useState(0);
  const LIMIT=20;

  const fetchMatches = useCallback(async(reset=false)=>{
    setLoading(true);
    try {
      const params=new URLSearchParams({limit:LIMIT,offset:reset?0:offset});
      if(filter==="unread") params.append("unread_only","true");
      if(selectedPage!=="all") params.append("page_id",selectedPage);
      const data=await api.get(`/matches?${params}`);
      if(!data) return;
      setTotal(data.total);
      if(reset){setMatches(data.matches);setOffset(LIMIT);}
      else{setMatches(p=>[...p,...data.matches]);setOffset(o=>o+LIMIT);}
    } catch(e){toast.error("Failed to load matches");}
    finally{setLoading(false);}
  },[filter,selectedPage,offset]);

  useEffect(()=>{fetchMatches(true);},[filter,selectedPage]);

  useEffect(()=>{
    const token=localStorage.getItem("sl_token");
    const ws=new WebSocket(`wss://socipulses.com/ws/matches?token=${token}`);
    ws.onmessage=(e)=>{try{const m=JSON.parse(e.data);setMatches(p=>[m,...p]);toast.info(`⚡ New: "${(m.matched_terms||[]).join(", ")}"`);}catch(_){}};
    ws.onerror=()=>{};
    return ()=>ws.close();
  },[]);

  const markRead=async(id)=>{setMatches(p=>p.map(m=>m.id===id?{...m,read:true}:m));try{await api.patch(`/matches/${id}/read`);}catch(_){}};
  const markAllRead=async()=>{setMatches(p=>p.map(m=>({...m,read:true})));try{await api.post("/matches/mark-all-read");toast.success("All marked as read");}catch(e){toast.error(e.message);}};
  const scanPage=async(pageId,name)=>{setScanning(s=>({...s,[pageId]:true}));try{await api.post(`/pages/${pageId}/scan`);toast.success(`Scan queued for "${name}"`);}catch(e){toast.error(e.message);}finally{setTimeout(()=>setScanning(s=>({...s,[pageId]:false})),4000);}};

  const fbPages=pages.filter(p=>p.active&&p.platform==="facebook");
  const redditPages=pages.filter(p=>p.active&&p.platform==="reddit");
  const clPages=pages.filter(p=>p.active&&p.platform==="nextdoor");

  return (
    <div>

      <PlanUsageMeter api={api} userPlan={userPlan} onUpgrade={()=>window.dispatchEvent(new CustomEvent("sl-navigate",{detail:"pricing"}))}/>      <div style={{ display:"flex", gap:12, marginBottom:24 }}>
        <StatCard label="TOTAL MATCHES" value={stats?.total_matches??"—"} sub="all time" loading={statsLoading}/>
        <StatCard label="UNREAD" value={stats?.unread_matches??"—"} sub="need attention" color="#f59e0b" loading={statsLoading}/>
        <StatCard label="TODAY" value={stats?.today_matches??"—"} sub="new matches" color="#22c55e" loading={statsLoading}/>
        <StatCard label="ACTIVE PAGES" value={stats?.total_pages??"—"} sub="monitoring" color="#8b5cf6" loading={statsLoading}/>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        {["all","unread"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{ padding:"6px 14px", borderRadius:6, border:"1px solid", cursor:"pointer", borderColor:filter===f?"#6366f1":"#1e1e2e", background:filter===f?"#6366f120":"transparent", color:filter===f?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:1 }}>{f}</button>
        ))}
        <select value={selectedPage} onChange={e=>setSelectedPage(e.target.value)} style={{ padding:"6px 12px", borderRadius:6, border:"1px solid #1e1e2e", background:"#0f0f1a", color:"#8888aa", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
          <option value="all">All Platforms</option>
          {fbPages.length>0&&<optgroup label="Facebook">{fbPages.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
          {redditPages.length>0&&<optgroup label="Reddit">{redditPages.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
          {clPages.length>0&&<optgroup label="Nextdoor">{clPages.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
        </select>
        <div style={{ flex:1 }}/>
        <MatchExportButton api={api} toast={toast} filters={{days:30}}/>
        {(stats?.unread_matches>0)&&<button onClick={markAllRead} style={{ padding:"6px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Mark all read</button>}
        {pages.filter(p=>p.active).slice(0,3).map(p=>{
          const pb=platformBadge(p.platform);
          return <button key={p.id} onClick={()=>scanPage(p.id,p.name)} disabled={scanning[p.id]} style={{ padding:"7px 14px", borderRadius:6, border:"none", background:scanning[p.id]?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:scanning[p.id]?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:5 }}>
            {scanning[p.id]?<><Spinner size={10}/> Scanning…</>:<>{pb.icon} {p.name.split(" ")[0]}</>}
          </button>;
        })}
      </div>
      {loading&&matches.length===0?<div style={{ display:"flex", justifyContent:"center", padding:60 }}><Spinner size={32}/></div>
      :matches.length===0?<div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace", fontSize:13 }}>No matches yet — add pages and run a scan</div>
      :<>
        {matches.map(m=><MatchCard key={m.id} match={m} onRead={markRead} pages={pages} templates={templates}/>)}
        {matches.length<total&&<button onClick={()=>fetchMatches(false)} disabled={loading} style={{ width:"100%", padding:"12px", borderRadius:8, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", marginTop:8, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>{loading?<Spinner/>:`Load more (${total-matches.length} remaining)`}</button>}
      </>}
    </div>
  );
}

function TemplatesView({ templates, setTemplates, toast }) {
  const [showAdd,setShowAdd] = useState(false);
  const [editId,setEditId] = useState(null);
  const [form,setForm] = useState({label:"",text:""});
  const [copied,setCopied] = useState(null);
  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS={color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};

  const save=()=>{
    if(!form.label||!form.text){toast.error("Label and text required");return;}
    if(editId){setTemplates(p=>p.map(t=>t.id===editId?{...t,...form}:t));toast.success("Updated");}
    else{setTemplates(p=>[...p,{id:Date.now(),...form}]);toast.success("Added");}
    setShowAdd(false);setEditId(null);
  };
  const copy=(t)=>{navigator.clipboard.writeText(t.text);setCopied(t.id);setTimeout(()=>setCopied(null),2000);toast.success("Copied!");};
  const del=(id)=>{setTemplates(p=>p.filter(t=>t.id!==id));toast.success("Deleted");};

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <h2 style={{ color:"#fff", margin:"0 0 4px", fontSize:16, fontFamily:"'DM Mono',monospace" }}>Reply Templates</h2>
          <p style={{ color:"#4a4a6a", margin:0, fontSize:12 }}>Pre-written responses. Click any template on a match card to copy, then paste wherever you're replying.</p>
        </div>
        <button onClick={()=>{setForm({label:"",text:""});setEditId(null);setShowAdd(true);}} style={{ padding:"8px 16px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", flexShrink:0 }}>+ Add Template</button>
      </div>
      {showAdd&&(
        <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:12, padding:20, marginBottom:20 }}>
          <h3 style={{ color:"#a5b4fc", margin:"0 0 16px", fontSize:13, fontFamily:"'DM Mono',monospace" }}>{editId?"Edit Template":"New Template"}</h3>
          <div style={{ display:"grid", gap:12 }}>
            <div><label style={lS}>TEMPLATE NAME</label><input style={iS} placeholder="e.g. Furnace Repair, AC Issue, Reddit Response" value={form.label} onChange={e=>setForm({...form,label:e.target.value})}/></div>
            <div><label style={lS}>REPLY TEXT</label><textarea style={{...iS,minHeight:100,resize:"vertical",lineHeight:1.6}} placeholder="Type your reply..." value={form.text} onChange={e=>setForm({...form,text:e.target.value})}/></div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:16 }}>
            <button onClick={save} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Save</button>
            <button onClick={()=>setShowAdd(false)} style={{ padding:"9px 20px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{ display:"grid", gap:10 }}>
        {templates.map(tpl=>(
          <div key={tpl.id} style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px" }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ color:"#a5b4fc", fontSize:13, fontWeight:600, fontFamily:"'DM Mono',monospace", marginBottom:8 }}>{tpl.label}</div>
                <p style={{ color:"#6a6a8a", fontSize:13, lineHeight:1.6, margin:0 }}>{tpl.text}</p>
                <div style={{ color:"#3a3a5a", fontSize:10, marginTop:8 }}>{tpl.text.length} characters</div>
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                <button onClick={()=>copy(tpl)} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${copied===tpl.id?"#22c55e30":"#2a2a3e"}`, background:copied===tpl.id?"#0a2a1a":"transparent", color:copied===tpl.id?"#22c55e":"#ccc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{copied===tpl.id?"✓ Copied!":"📋 Copy"}</button>
                <button onClick={()=>{setForm({label:tpl.label,text:tpl.text});setEditId(tpl.id);setShowAdd(true);}} style={{ padding:"7px 12px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Edit</button>
                <button onClick={()=>del(tpl.id)} style={{ padding:"7px 12px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Del</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// FIX 3 — WELCOME MODAL (first login / post-checkout)
// ════════════════════════════════════════════════════════════════════════════
function WelcomeModal({ onClose, onNavigate, isPostCheckout, planName }) {
  const [step, setStep] = useState(0);

  const STEPS = isPostCheckout ? [
    {
      icon: "🎉",
      title: `You're on ${planName || "a paid plan"}!`,
      body: "Your account is fully upgraded. Let's get your first lead monitor set up in about 2 minutes.",
      action: "Let's go →",
    },
    {
      icon: "📡",
      title: "Add a page to monitor",
      body: "Paste a Facebook group URL, subreddit,  category. Local homeowner groups and neighborhood Facebook groups have the most HVAC lead activity.",
      action: "Add a page →",
      nav: "pages",
    },
    {
      icon: "🔑",
      title: "Set your keywords",
      body: 'Add terms like "furnace broken", "no heat", "HVAC repair", "AC not working". The more specific the better — broad keywords get noise, specific ones get real leads.',
      action: "Set keywords →",
      nav: "pages",
    },
    {
      icon: "🔔",
      title: "Turn on SMS alerts",
      body: "Speed wins jobs. Responding within minutes of a post is the #1 factor in closing leads. Add your phone number to get a text the second a match is found.",
      action: "Set up alerts →",
      nav: "settings",
    },
  ] : [
    {
      icon: "👋",
      title: "Welcome to Socialisten",
      body: "You're set up to catch contractors leads in real time from Facebook groups, Reddit, and Nextdoor — before anyone else even sees them.",
      action: "Show me around →",
    },
    {
      icon: "📡",
      title: "Step 1: Add a page",
      body: 'Go to Monitored Pages and paste any Facebook group URL — try your city\'s "homeowners" group or a local neighborhood group. That\'s where people post "my furnace broke".',
      action: "Add a page →",
      nav: "pages",
    },
    {
      icon: "🔑",
      title: "Step 2: Set keywords",
      body: 'Keywords trigger matches. Start with: furnace, boiler, AC not working, no heat, HVAC repair. You\'ll refine these as you see what comes in.',
      action: "Next →",
    },
    {
      icon: "🔔",
      title: "Step 3: Set up alerts",
      body: "The faster you respond, the more jobs you close. Add your phone number to get a text the moment someone posts a match. Email is too slow.",
      action: "Go to settings →",
      nav: "settings",
    },
    {
      icon: "🚀",
      title: "You're ready",
      body: "Scans run automatically every 15–30 minutes. You'll start seeing leads appear in your feed. Respond fast — you're competing with every other contractor who might see the same post.",
      action: "Open my feed →",
      nav: "feed",
    },
  ];

  const current = STEPS[step];
  const isLast  = step === STEPS.length - 1;

  const advance = () => {
    if (isLast || current.nav) {
      if (current.nav) onNavigate(current.nav);
      onClose();
    } else {
      setStep(s => s + 1);
    }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"linear-gradient(135deg,#0f0f2a,#0a0a1a)", border:"1px solid #6366f140", borderRadius:16, padding:"36px 40px", maxWidth:480, width:"100%", textAlign:"center", boxShadow:"0 24px 80px rgba(0,0,0,0.8)" }}>
        {/* Progress dots */}
        <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:28 }}>
          {STEPS.map((_,i) => (
            <div key={i} style={{ width:i===step?20:6, height:6, borderRadius:3, background:i===step?"#6366f1":i<step?"#6366f160":"#2a2a3e", transition:"all 0.3s" }}/>
          ))}
        </div>

        <div style={{ fontSize:52, marginBottom:16 }}>{current.icon}</div>
        <div style={{ color:"#fff", fontSize:20, fontWeight:700, marginBottom:12, lineHeight:1.3 }}>{current.title}</div>
        <div style={{ color:"#8888aa", fontSize:14, lineHeight:1.8, marginBottom:32 }}>{current.body}</div>

        <button onClick={advance} style={{ width:"100%", padding:"14px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:12 }}>
          {current.action}
        </button>

        <button onClick={onClose} style={{ background:"transparent", border:"none", color:"#3a3a5a", fontSize:12, cursor:"pointer" }}>
          Skip intro
        </button>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FIX 4 — DUNNING ADMIN UI
// ════════════════════════════════════════════════════════════════════════════
function DunningManager({ api, toast }) {
  const [pastDue, setPastDue]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    api.get("/admin/dunning/past-due")
      .then(d => setPastDue(d || []))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const sendDunning = async () => {
    if (!window.confirm(`Send payment failure emails to ${selected.length || pastDue.length} users?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/dunning/send", { user_ids: selected.length ? selected : null });
      toast.success(`Dunning emails sent to ${r.sent || pastDue.length} users`);
      setSelected([]);
    } catch(e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s,id]);
  const allSelected  = pastDue.length > 0 && selected.length === pastDue.length;

  const cardS = {background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"20px 24px",marginBottom:16};

  return (
    <div>
      <div style={{...cardS, borderColor:"#ff4d4d20", background:"#1a0808", marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{color:"#ff4d4d",fontSize:14,fontWeight:600,marginBottom:4}}>
              {loading ? "Loading..." : `${pastDue.length} past-due account${pastDue.length!==1?"s":""}`}
            </div>
            <div style={{color:"#8a4a4a",fontSize:12}}>
              These users have failed payments. Send dunning emails to prompt them to update billing.
            </div>
          </div>
          <button onClick={sendDunning} disabled={sending||pastDue.length===0} style={{padding:"10px 20px",borderRadius:7,border:"none",background:(sending||pastDue.length===0)?"#2a2a3e":"#ff4d4d",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:(sending||pastDue.length===0)?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
            {sending?<><Spinner size={11}/>Sending…</>:`📧 Send to ${selected.length||pastDue.length} user${(selected.length||pastDue.length)!==1?"s":""}`}
          </button>
        </div>
      </div>

      {loading ? <div style={{display:"flex",justifyContent:"center",padding:40}}><Spinner size={28}/></div>
      : pastDue.length===0 ? (
        <div style={{textAlign:"center",padding:"60px 0",color:"#3a3a5a",fontFamily:"'DM Mono',monospace"}}>
          <div style={{fontSize:40,marginBottom:12}}>✅</div>
          No past-due accounts — all payments current
        </div>
      ) : (
        <div style={{...cardS}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <input type="checkbox" checked={allSelected} onChange={()=>setSelected(allSelected?[]:pastDue.map(u=>u.id))} style={{cursor:"pointer"}}/>
            <span style={{color:"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
              {selected.length > 0 ? `${selected.length} selected` : "Select all"}
            </span>
          </div>
          {pastDue.map(u => (
            <div key={u.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid #111"}}>
              <input type="checkbox" checked={selected.includes(u.id)} onChange={()=>toggleSelect(u.id)} style={{cursor:"pointer"}}/>
              <div style={{flex:1}}>
                <div style={{color:"#fff",fontSize:12,fontWeight:600}}>{u.email}</div>
                <div style={{color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace"}}>
                  {u.plan} plan · Past due {u.days_past_due||"?"}d · Last charge failed: {u.failure_reason||"unknown"}
                </div>
              </div>
              <div style={{color:"#ff4d4d",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
                ${u.amount_due||"?"} due
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FIX 5 — STATUS PAGE INCIDENT CREATION
// Extends the existing StatusPage component with admin incident management
// ════════════════════════════════════════════════════════════════════════════
function IncidentManager({ api, toast, isAdmin }) {
  const [incidents,  setIncidents]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [form, setForm] = useState({
    title: "", message: "", severity: "minor",
    affected_components: [],
  });

  const COMPONENTS = ["API","Scraper","Alerts","Billing","Dashboard","Facebook","Reddit","Nextdoor"];
  const SEVERITIES = {
    minor:    {color:"#22c55e", label:"Minor"},
    major:    {color:"#f59e0b", label:"Major"},
    critical: {color:"#ff4d4d", label:"Critical"},
  };

  useEffect(() => {
    api.get("/status")
      .then(d => setIncidents(d?.incidents || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const create = async () => {
    if (!form.title || !form.message) { toast.error("Title and message required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/status/incident", form);
      setIncidents(p => [r, ...p]);
      setForm({ title:"", message:"", severity:"minor", affected_components:[] });
      setShowForm(false);
      toast.success("Incident posted — status page updated");
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const resolve = async (id) => {
    try {
      await api.del(`/admin/status/incident/${id}`);
      setIncidents(p => p.filter(i => i.id !== id));
      toast.success("Incident resolved");
    } catch(e) { toast.error(e.message); }
  };

  const toggleComponent = (c) => setForm(f => ({
    ...f,
    affected_components: f.affected_components.includes(c)
      ? f.affected_components.filter(x=>x!==c)
      : [...f.affected_components, c]
  }));

  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS={color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};
  const cardS={background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"20px 24px",marginBottom:16};

  if (!isAdmin) return null;

  return (
    <div style={{marginTop:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
        <div style={{color:"#fff",fontSize:13,fontWeight:600}}>
          🚨 Incident Management <span style={{color:"#4a4a6a",fontSize:11,fontWeight:400}}>({incidents.length} active)</span>
        </div>
        <button onClick={()=>setShowForm(p=>!p)} style={{padding:"7px 16px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#ff4d4d,#dc2626)",color:"#fff",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>
          {showForm?"Cancel":"+ Report Incident"}
        </button>
      </div>

      {showForm && (
        <div style={{...cardS,borderColor:"#ff4d4d30",background:"#1a0808",marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div><label style={lS}>TITLE</label><input style={iS} placeholder="e.g. Facebook scraping degraded" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}/></div>
            <div><label style={lS}>SEVERITY</label>
              <select style={iS} value={form.severity} onChange={e=>setForm(f=>({...f,severity:e.target.value}))}>
                {Object.entries(SEVERITIES).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div style={{gridColumn:"1/-1"}}><label style={lS}>MESSAGE (shown to users)</label><textarea style={{...iS,minHeight:70,resize:"vertical"}} placeholder="Describe what's affected and what you're doing about it..." value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))}/></div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={lS}>AFFECTED COMPONENTS</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {COMPONENTS.map(c=>(
                <div key={c} onClick={()=>toggleComponent(c)} style={{padding:"4px 10px",borderRadius:5,border:`1px solid ${form.affected_components.includes(c)?"#ff4d4d":"#2a2a3e"}`,background:form.affected_components.includes(c)?"#ff4d4d15":"transparent",color:form.affected_components.includes(c)?"#ff4d4d":"#4a4a6a",fontSize:11,cursor:"pointer"}}>
                  {c}
                </div>
              ))}
            </div>
          </div>
          <button onClick={create} disabled={saving} style={{padding:"9px 20px",borderRadius:6,border:"none",background:saving?"#2a2a3e":"#ff4d4d",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:saving?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
            {saving?<><Spinner size={11}/>Posting…</>:"🚨 Post Incident"}
          </button>
        </div>
      )}

      {incidents.length>0 && incidents.map(inc=>{
        const sev = SEVERITIES[inc.severity]||SEVERITIES.minor;
        return (
          <div key={inc.id} style={{...cardS,borderColor:`${sev.color}30`,background:`${sev.color}08`,marginBottom:8}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <span style={{color:sev.color,fontSize:11,fontFamily:"'DM Mono',monospace",fontWeight:600}}>{sev.label.toUpperCase()}</span>
                  <span style={{color:"#fff",fontSize:13,fontWeight:600}}>{inc.title}</span>
                </div>
                <div style={{color:"#8888aa",fontSize:12,marginBottom:4}}>{inc.message}</div>
                {inc.affected_components?.length>0 && (
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    {inc.affected_components.map(c=>(
                      <span key={c} style={{padding:"1px 6px",borderRadius:3,background:`${sev.color}20`,color:sev.color,fontSize:10,fontFamily:"'DM Mono',monospace"}}>{c}</span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={()=>resolve(inc.id)} style={{padding:"5px 12px",borderRadius:5,border:"1px solid #22c55e30",background:"#22c55e10",color:"#22c55e",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer",flexShrink:0}}>
                ✓ Resolve
              </button>
            </div>
          </div>
        );
      })}
      {!loading&&incidents.length===0&&(
        <div style={{color:"#3a3a5a",fontSize:12,textAlign:"center",padding:"16px 0"}}>No active incidents — all systems operational</div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FIX 6 — GEO FILTER AUTOCOMPLETE
// Drop-in replacement for the plain text geo filter input
// ════════════════════════════════════════════════════════════════════════════
function GeoAutocomplete({ api, value, onChange, placeholder }) {
  const [query,       setQuery]       = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open,        setOpen]        = useState(false);
  const [loading,     setLoading]     = useState(false);
  const debounceRef = useState(null);

  const search = (q) => {
    setQuery(q);
    onChange(q);
    if (debounceRef[0]) clearTimeout(debounceRef[0]);
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef[0] = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/geo/suggestions?q=${encodeURIComponent(q)}`);
        setSuggestions(r || []);
        setOpen(true);
      } catch(e) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 280);
  };

  const select = (s) => {
    setQuery(s.label || s);
    onChange(s.label || s);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div style={{position:"relative"}}>
      <div style={{position:"relative"}}>
        <input
          value={query}
          onChange={e=>search(e.target.value)}
          onFocus={()=>suggestions.length>0&&setOpen(true)}
          onBlur={()=>setTimeout(()=>setOpen(false),150)}
          placeholder={placeholder||"City, zip code, or region..."}
          style={{width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 34px 9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"}}
        />
        {loading && <div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)"}}><Spinner size={12}/></div>}
      </div>
      {open && suggestions.length>0 && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#0f0f1a",border:"1px solid #2a2a3e",borderRadius:8,zIndex:200,boxShadow:"0 8px 24px rgba(0,0,0,0.5)",marginTop:2,maxHeight:200,overflowY:"auto"}}>
          {suggestions.map((s,i)=>(
            <div key={i} onMouseDown={()=>select(s)} style={{padding:"9px 12px",cursor:"pointer",borderBottom:"1px solid #111",color:"#ccc",fontSize:12}}>
              <span style={{fontFamily:"'DM Mono',monospace"}}>{s.label||s}</span>
              {s.state&&<span style={{color:"#4a4a6a",fontSize:11,marginLeft:8}}>{s.state}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FIX 7 — REAL FACEBOOK PAGE INBOX (replaces mock data)
// Uses the Facebook Graph API via backend proxy
// ════════════════════════════════════════════════════════════════════════════
function InboxView({ templates, toast, api }) {
  const [pages,    setPages]    = useState([]);
  const [threads,  setThreads]  = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply,    setReply]    = useState("");
  const [sending,  setSending]  = useState(false);
  const [loadingT, setLoadingT] = useState(false);
  const [loadingM, setLoadingM] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [pageForm, setPageForm] = useState({name:"", pageId:"", accessToken:""});
  const [savingPage, setSavingPage] = useState(false);
  const [configured, setConfigured] = useState(false);

  // Load connected pages from backend
  const loadPages = () => {
    api.get("/inbox/pages").then(d => {
      setPages(d || []);
      setConfigured((d||[]).length > 0);
    }).catch(() => {
      // Fallback — try localStorage for legacy setup
      try {
        const stored = JSON.parse(localStorage.getItem("sl_inbox_pages") || "[]");
        setPages(stored);
        setConfigured(stored.length > 0);
      } catch {}
    });
  };
  useEffect(loadPages, []);

  const loadThreads = async (pageId, accessToken) => {
    setLoadingT(true);
    setThreads([]); setSelected(null); setMessages([]);
    try {
      const r = await api.get(`/inbox/threads?page_id=${pageId}&access_token=${encodeURIComponent(accessToken)}`);
      setThreads(r || []);
    } catch(e) {
      toast.error("Could not load messages: " + e.message);
    } finally { setLoadingT(false); }
  };

  const [activePage, setActivePage] = useState(null);
  const selectPage = (page) => {
    setActivePage(page);
    loadThreads(page.pageId, page.accessToken);
  };

  const openThread = async (thread) => {
    setSelected(thread); setLoadingM(true); setMessages([]);
    try {
      const r = await api.get(`/inbox/messages?thread_id=${thread.id}&access_token=${encodeURIComponent(activePage?.accessToken||"")}`);
      setMessages(r || []);
    } catch(e) { toast.error(e.message); }
    finally { setLoadingM(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/inbox/reply", {
        recipient_id: selected.senderId,
        message:      reply,
        page_id:      activePage?.pageId,
        access_token: activePage?.accessToken,
      });
      setMessages(p => [...p, { from:"me", text:reply, time:new Date().toISOString() }]);
      setReply("");
      toast.success("Reply sent");
    } catch(e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const savePage = async () => {
    if (!pageForm.name||!pageForm.pageId||!pageForm.accessToken) { toast.error("All fields required"); return; }
    setSavingPage(true);
    try {
      // Try to save to backend, fall back to localStorage
      try {
        await api.post("/inbox/pages", pageForm);
      } catch {
        const stored = JSON.parse(localStorage.getItem("sl_inbox_pages")||"[]");
        localStorage.setItem("sl_inbox_pages", JSON.stringify([...stored, {...pageForm, id:Date.now()}]));
      }
      setPageForm({name:"",pageId:"",accessToken:""});
      setShowConnect(false);
      loadPages();
      toast.success("Page connected");
    } catch(e) { toast.error(e.message); }
    finally { setSavingPage(false); }
  };

  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS={color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};
  const unread = threads.filter(t=>!t.read).length;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <h2 style={{color:"#fff",margin:"0 0 4px",fontSize:16,fontFamily:"'DM Mono',monospace"}}>Page Inbox</h2>
          <p style={{color:"#4a4a6a",margin:0,fontSize:12}}>Real-time messages sent to your Facebook pages via Graph API. {unread>0&&<span style={{color:"#ff4d4d"}}>({unread} unread)</span>}</p>
        </div>
        <button onClick={()=>setShowConnect(p=>!p)} style={{padding:"8px 16px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>
          {showConnect?"Cancel":"+ Connect Page"}
        </button>
      </div>

      {/* Connect page form */}
      {showConnect && (
        <div style={{background:"#0f0f1a",border:"1px solid #1877F230",borderRadius:12,padding:20,marginBottom:20}}>
          <div style={{color:"#1877F2",fontSize:13,fontWeight:600,marginBottom:12}}>Connect a Facebook Page</div>
          <div style={{color:"#4a4a6a",fontSize:11,marginBottom:14,lineHeight:1.8}}>
            You need a Page Access Token from your Facebook Developer app. Go to <strong style={{color:"#ccc"}}>developers.facebook.com → Your App → Messenger → Access Tokens</strong> and add your page.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div><label style={lS}>PAGE NAME</label><input style={iS} placeholder="Peak Home Services" value={pageForm.name} onChange={e=>setPageForm(f=>({...f,name:e.target.value}))}/></div>
            <div><label style={lS}>PAGE ID</label><input style={iS} placeholder="123456789012345" value={pageForm.pageId} onChange={e=>setPageForm(f=>({...f,pageId:e.target.value}))}/></div>
            <div style={{gridColumn:"1/-1"}}><label style={lS}>PAGE ACCESS TOKEN</label><input type="password" style={iS} placeholder="EAAxxxxxxxx..." value={pageForm.accessToken} onChange={e=>setPageForm(f=>({...f,accessToken:e.target.value}))}/></div>
          </div>
          <button onClick={savePage} disabled={savingPage} style={{padding:"9px 20px",borderRadius:6,border:"none",background:savingPage?"#2a2a3e":"#1877F2",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:savingPage?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
            {savingPage?<><Spinner size={11}/>Connecting…</>:"Connect Page"}
          </button>
        </div>
      )}

      {/* No pages state */}
      {!configured && !showConnect && (
        <div style={{textAlign:"center",padding:"60px 0"}}>
          <div style={{fontSize:48,marginBottom:16}}>💬</div>
          <div style={{color:"#fff",fontSize:15,fontWeight:600,marginBottom:8}}>Connect your Facebook page</div>
          <div style={{color:"#4a4a6a",fontSize:13,marginBottom:20}}>Respond to messages sent to your business page without leaving Socialisten.</div>
          <button onClick={()=>setShowConnect(true)} style={{padding:"11px 24px",borderRadius:8,border:"none",background:"#1877F2",color:"#fff",fontSize:13,cursor:"pointer"}}>Connect a Page</button>
        </div>
      )}

      {/* Main inbox layout */}
      {configured && (
        <div style={{display:"grid",gridTemplateColumns:"200px 1fr",gap:0,border:"1px solid #1e1e2e",borderRadius:12,overflow:"hidden",minHeight:480}}>
          {/* Page selector + thread list */}
          <div style={{background:"#0a0a0f",borderRight:"1px solid #1e1e2e"}}>
            {pages.map(p=>(
              <div key={p.id||p.pageId} onClick={()=>selectPage(p)} style={{padding:"12px 14px",borderBottom:"1px solid #111",cursor:"pointer",background:activePage?.pageId===p.pageId?"#1a1a2e":"transparent",borderLeft:activePage?.pageId===p.pageId?"3px solid #1877F2":"3px solid transparent"}}>
                <div style={{color:"#fff",fontSize:12,fontWeight:600}}>{p.name}</div>
                <div style={{color:"#3a3a5a",fontSize:10,fontFamily:"'DM Mono',monospace",marginTop:2}}>ID: {p.pageId}</div>
              </div>
            ))}
            <div style={{borderTop:"1px solid #111",maxHeight:300,overflowY:"auto"}}>
              {loadingT ? <div style={{display:"flex",justifyContent:"center",padding:20}}><Spinner size={16}/></div>
              : !activePage ? <div style={{color:"#3a3a5a",fontSize:11,padding:"16px 12px"}}>Select a page</div>
              : threads.length===0 ? <div style={{color:"#3a3a5a",fontSize:11,padding:"16px 12px"}}>No messages yet</div>
              : threads.map(t=>(
                <div key={t.id} onClick={()=>openThread(t)} style={{padding:"10px 12px",borderBottom:"1px solid #0a0a12",cursor:"pointer",background:selected?.id===t.id?"#1a1a2e":"transparent"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                    {!t.read&&<div style={{width:6,height:6,borderRadius:"50%",background:"#1877F2",flexShrink:0}}/>}
                    <span style={{color:"#ccc",fontSize:11,fontWeight:t.read?400:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.senderName||"Visitor"}</span>
                  </div>
                  <div style={{color:"#3a3a5a",fontSize:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.preview||"No preview"}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Message thread */}
          <div style={{background:"#0f0f1a",display:"flex",flexDirection:"column"}}>
            {!selected ? (
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"#3a3a5a",fontSize:12}}>
                {activePage ? "Select a conversation" : "Select a page first"}
              </div>
            ) : (
              <>
                <div style={{padding:"12px 16px",borderBottom:"1px solid #1e1e2e"}}>
                  <div style={{color:"#fff",fontSize:13,fontWeight:600}}>{selected.senderName}</div>
                  <div style={{color:"#4a4a6a",fontSize:11,marginTop:2}}>{selected.senderId}</div>
                </div>
                <div style={{flex:1,overflowY:"auto",padding:"14px 16px",display:"flex",flexDirection:"column",gap:10,maxHeight:300}}>
                  {loadingM ? <div style={{display:"flex",justifyContent:"center",padding:20}}><Spinner size={16}/></div>
                  : messages.map((m,i)=>{
                    const isMe = m.from==="me";
                    return (
                      <div key={i} style={{display:"flex",justifyContent:isMe?"flex-end":"flex-start"}}>
                        <div style={{maxWidth:"75%",padding:"8px 12px",borderRadius:isMe?"12px 12px 2px 12px":"12px 12px 12px 2px",background:isMe?"#1877F2":"#1e1e2e"}}>
                          <div style={{color:"#fff",fontSize:12,lineHeight:1.6}}>{m.text||m.content||m.message}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Reply templates */}
                {templates?.length > 0 && (
                  <div style={{padding:"8px 12px",borderTop:"1px solid #111",display:"flex",gap:6,overflowX:"auto"}}>
                    {templates.slice(0,4).map(t=>(
                      <button key={t.id} onClick={()=>setReply(t.text)} style={{padding:"4px 10px",borderRadius:5,border:"1px solid #2a2a3e",background:"transparent",color:"#a5b4fc",fontSize:10,fontFamily:"'DM Mono',monospace",cursor:"pointer",whiteSpace:"nowrap"}}>{t.label}</button>
                    ))}
                  </div>
                )}
                <div style={{padding:"10px 14px",borderTop:"1px solid #1e1e2e",display:"flex",gap:8}}>
                  <textarea value={reply} onChange={e=>setReply(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&(e.metaKey||e.ctrlKey))sendReply();}} placeholder="Type a reply... (⌘+Enter to send)" style={{flex:1,background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:8,padding:"8px 12px",color:"#ccc",fontSize:12,outline:"none",resize:"none",minHeight:56,fontFamily:"inherit"}}/>
                  <button onClick={sendReply} disabled={!reply.trim()||sending} style={{padding:"0 16px",borderRadius:8,border:"none",background:(!reply.trim()||sending)?"#2a2a3e":"#1877F2",color:"#fff",fontSize:11,cursor:(!reply.trim()||sending)?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:4,alignSelf:"flex-end",height:38}}>
                    {sending?<Spinner size={11}/>:"Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PagesView({ api, pages, setPages, toast }) {
  const [showAdd,setShowAdd] = useState(false);
  const [saving,setSaving] = useState(false);
  const [editId,setEditId] = useState(null);
  const [form,setForm] = useState({name:"",url:"",platform:"facebook",keywords:"",negative_keywords:"",check_interval:30,geo_filter:""});
  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS={color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};

  const openAdd=()=>{setForm({name:"",url:"",platform:"facebook",keywords:"",negative_keywords:"",check_interval:30,geo_filter:""});setEditId(null);setShowAdd(true);};
  const openEdit=(p)=>{setForm({...p,keywords:(p.keywords||[]).join(", "),negative_keywords:(p.negative_keywords||[]).join(", "),geo_filter:p.geo_filter||""});setEditId(p.id);setShowAdd(true);};
  const save=async()=>{
    if(!form.name||!form.url){toast.error("Name and URL required");return;}
    setSaving(true);
    const payload={...form,keywords:form.keywords.split(",").map(k=>k.trim()).filter(Boolean),negative_keywords:form.negative_keywords.split(",").map(k=>k.trim()).filter(Boolean),check_interval:parseInt(form.check_interval),geo_filter:form.geo_filter||null};
    try{
      if(editId){const u=await api.patch(`/pages/${editId}`,payload);setPages(p=>p.map(x=>x.id===editId?u:x));toast.success("Updated");}
      else{const c=await api.post("/pages",payload);setPages(p=>[...p,c]);toast.success("Added");}
      setShowAdd(false);
    }catch(e){toast.error(e.message);}finally{setSaving(false);}
  };
  const toggleActive=async(page)=>{try{const u=await api.patch(`/pages/${page.id}`,{active:!page.active});setPages(p=>p.map(x=>x.id===page.id?u:x));toast.success(u.active?"Resumed":"Paused");}catch(e){toast.error(e.message);}};
  const del=async(id)=>{if(!window.confirm("Delete this page and all its matches?"))return;try{await api.del(`/pages/${id}`);setPages(p=>p.filter(x=>x.id!==id));toast.success("Deleted");}catch(e){toast.error(e.message);}};

  const redditTip      = form.platform==="reddit";
  const nextdoorTip  = form.platform==="nextdoor";

  // Nextdoor preset URLs for common cities
  const CL_PRESETS = [
    { label:"Detroit — Services Wanted",  url:"https://detroit.nextdoor.org/search/zip" },
    { label:"Detroit — Housing",          url:"https://detroit.nextdoor.org/search/hhh" },
    { label:"Detroit — Community",        url:"https://detroit.nextdoor.org/search/bbb" },
    { label:"Detroit — For Sale (General)",url:"https://detroit.nextdoor.org/search/sss" },
    { label:"Ann Arbor — Services Wanted",url:"https://annarbor.nextdoor.org/search/zip" },
    { label:"Flint — Services Wanted",    url:"https://flint.nextdoor.org/search/zip" },
    { label:"Lansing — Services Wanted",  url:"https://lansing.nextdoor.org/search/zip" },
  ];

  return (
    <div>
      <UsageLimitBar api={api} onUpgrade={()=>{ /* navigate to pricing */ }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h2 style={{ color:"#fff", margin:0, fontSize:16, fontFamily:"'DM Mono',monospace" }}>Monitored Pages <span style={{ color:"#3a3a5a", fontSize:13 }}>({pages.length})</span></h2>
        <button onClick={openAdd} style={{ padding:"8px 16px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>+ Add Page</button>
      </div>

      {showAdd&&(
        <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:12, padding:20, marginBottom:20 }}>
          <h3 style={{ color:"#a5b4fc", margin:"0 0 16px", fontSize:13, fontFamily:"'DM Mono',monospace" }}>{editId?"Edit Page":"Add New Page"}</h3>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div><label style={lS}>PAGE NAME</label><input style={iS} placeholder="Detroit Homeowners" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
            <div><label style={lS}>PLATFORM</label>
              <select style={iS} value={form.platform} onChange={e=>setForm({...form,platform:e.target.value,url:""})}>
                <option value="facebook">🔵 Facebook</option>
                <option value="reddit">🟠 Reddit</option>
                <option value="nextdoor">🟣 Nextdoor</option>
                <option value="nextdoor">🟢 Nextdoor</option>
              </select>
            </div>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={lS}>{redditTip?"SUBREDDIT URL":nextdoorTip?"NEXTDOOR SEARCH URL":"PAGE / GROUP URL"}</label>
              <input style={iS} placeholder={redditTip?"https://reddit.com/r/Detroit":nextdoorTip?"https://detroit.nextdoor.org/search/zip":"https://facebook.com/groups/..."} value={form.url} onChange={e=>setForm({...form,url:e.target.value})}/>
              {redditTip&&<div style={{ color:"#ff6314", fontSize:10, marginTop:5 }}>Example: https://reddit.com/r/Detroit — No login required</div>}
              {nextdoorTip&&(
                <div style={{ marginTop:8 }}>
                  <div style={{ color:"#a78bfa", fontSize:10, fontFamily:"'DM Mono',monospace", marginBottom:6 }}>QUICK PRESETS — click to fill:</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                    {CL_PRESETS.map(p=>(
                      <button key={p.url} type="button" onClick={()=>setForm(f=>({...f,url:p.url}))} style={{ padding:"3px 10px", borderRadius:4, border:`1px solid ${form.url===p.url?"#a78bfa":"#2a2a3e"}`, background:form.url===p.url?"#7c3aed20":"transparent", color:form.url===p.url?"#a78bfa":"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{p.label}</button>
                    ))}
                  </div>
                  <div style={{ color:"#4a4a6a", fontSize:10, marginTop:6, lineHeight:1.6 }}>
                    Keywords you enter above will be used to search within the selected category. "Services Wanted" is the best category for finding people looking to hire.
                  </div>
                </div>
              )}
            </div>
            <div style={{ gridColumn:"1/-1" }}><label style={lS}>KEYWORDS (comma separated)</label><input style={iS} placeholder="HVAC, furnace, AC not working, no heat" value={form.keywords} onChange={e=>setForm({...form,keywords:e.target.value})}/></div>
            <div style={{ gridColumn:"1/-1" }}><label style={lS}>NEGATIVE KEYWORDS</label><input style={iS} placeholder="hiring, for sale, job opening" value={form.negative_keywords} onChange={e=>setForm({...form,negative_keywords:e.target.value})}/></div>
            <div><label style={lS}>GEO FILTER — SERVICE AREA (optional)</label><GeoAutocomplete api={api} value={form.geo_filter||""} onChange={v=>setForm(f=>({...f,geo_filter:v}))} placeholder="e.g. Detroit, MI or 48301"/><div style={{color:"#3a3a5a",fontSize:10,marginTop:4}}>Only match posts that mention this city or zip. Leave blank to match everywhere.</div></div>
            <div><label style={lS}>CHECK INTERVAL</label>
              <select style={iS} value={form.check_interval} onChange={e=>setForm({...form,check_interval:e.target.value})}>
                {[15,30,60,120].map(v=><option key={v} value={v}>Every {v} min</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:16 }}>
            <button onClick={save} disabled={saving} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:8 }}>{saving?<><Spinner size={12}/>Saving…</>:"Save Page"}</button>
            <button onClick={()=>setShowAdd(false)} style={{ padding:"9px 20px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {pages.length===0?<div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace", fontSize:13 }}>No pages yet — click "Add Page" to start monitoring</div>
      :pages.map(page=>{
        const pb=platformBadge(page.platform);
        return (
          <div key={page.id} style={{ background:"#0f0f1a", border:`1px solid ${page.active?"#1e1e2e":"#150f0f"}`, borderRadius:10, padding:"16px 18px", marginBottom:10, opacity:page.active?1:0.5 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ color:"#fff", fontSize:14, fontWeight:600 }}>{page.name}</span>
                  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:pb.bg, color:pb.color, border:pb.border }}>{pb.icon} {pb.label}</span>
                  {!page.active&&<span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:"#ff4d4d20", color:"#ff4d4d" }}>PAUSED</span>}
                </div>
                <div style={{ color:"#3a3a5a", fontSize:11, marginBottom:8 }}>{page.url}</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:6 }}>
                  {(page.keywords||[]).map(k=><span key={k} style={{ background:"#1e1e3e", color:"#a5b4fc", fontSize:10, padding:"2px 8px", borderRadius:4, fontFamily:"'DM Mono',monospace" }}>{k}</span>)}
                </div>
                <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>Every {page.check_interval}min · Last checked: {page.last_checked?timeAgo(page.last_checked):"never"}</div>
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                <button onClick={()=>openEdit(page)} style={{ padding:"6px 12px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Edit</button>
                <button onClick={()=>toggleActive(page)} style={{ padding:"6px 12px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:page.active?"#f59e0b":"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{page.active?"Pause":"Resume"}</button>
                <button onClick={()=>del(page.id)} style={{ padding:"6px 12px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Del</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SettingsView({ api, fbConnected, setFbConnected, toast }) {
  const [fbEmail,setFbEmail] = useState("");
  const [fbPassword,setFbPassword] = useState("");
  const [connecting,setConnecting] = useState(false);
  const [connMsg,setConnMsg] = useState("");
  const [alerts,setAlerts] = useState({ email_enabled:true, sms_enabled:false, call_enabled:false, whatsapp_enabled:false, email:"", phone:"", min_score:20, call_min_score:50 });
  const [loadingAlerts,setLoadingAlerts] = useState(true);
  const [savingAlerts,setSavingAlerts] = useState(false);
  const [checkingSession,setCheckingSession] = useState(true);
  useEffect(()=>{
    api.get("/settings/alerts").then(d=>{if(d)setAlerts(p=>({...p,...d}));}).catch(()=>{}).finally(()=>setLoadingAlerts(false));
    api.get("/settings/facebook/status").then(d=>{if(d)setFbConnected(d.connected);}).catch(()=>{}).finally(()=>setCheckingSession(false));
  },[]);
  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS={color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};
  const Toggle=({on,toggle})=>(<div onClick={toggle} style={{ width:40,height:22,borderRadius:11,cursor:"pointer",transition:"all 0.2s",background:on?"#6366f1":"#2a2a3e",position:"relative",flexShrink:0 }}><div style={{ position:"absolute",top:3,left:on?21:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/></div>);
  const connectFB=async()=>{if(!fbEmail||!fbPassword){setConnMsg("Please enter email and password.");return;}setConnecting(true);setConnMsg("");try{await api.post("/settings/facebook/connect",{email:fbEmail,password:fbPassword});setFbConnected(true);setConnMsg("✅ Login initiated.");setFbEmail("");setFbPassword("");}catch(e){setConnMsg("❌ "+e.message);}finally{setConnecting(false);}};
  const disconnectFB=async()=>{try{await api.post("/settings/facebook/disconnect");setFbConnected(false);toast.success("Disconnected");}catch(e){toast.error(e.message);}};
  const saveAlerts=async()=>{setSavingAlerts(true);try{await api.put("/settings/alerts",alerts);toast.success("Saved");}catch(e){toast.error(e.message);}finally{setSavingAlerts(false);}};
  const hasTwilio=alerts.sms_enabled||alerts.call_enabled||alerts.whatsapp_enabled;

  return (
    <div style={{ maxWidth:620 }}>
      {/* Facebook */}
      <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:24, marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
          <div style={{ width:32,height:32,borderRadius:8,background:"#1877F2",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:16 }}>f</div>
          <div><div style={{ color:"#fff",fontSize:14,fontWeight:600 }}>Facebook Session</div><div style={{ color:"#4a4a6a",fontSize:11 }}>For scraping public Facebook groups and pages</div></div>
          <div style={{ marginLeft:"auto",display:"flex",alignItems:"center",gap:6 }}>
            {checkingSession?<Spinner size={12}/>:<><div style={{ width:8,height:8,borderRadius:"50%",background:fbConnected?"#22c55e":"#3a3a5a",boxShadow:fbConnected?"0 0 8px #22c55e":"none" }}/><span style={{ color:fbConnected?"#22c55e":"#3a3a5a",fontSize:11,fontFamily:"'DM Mono',monospace" }}>{fbConnected?"CONNECTED":"DISCONNECTED"}</span></>}
          </div>
        </div>
        {!fbConnected?(<>
          <div style={{ background:"#1a1020",border:"1px solid #a855f720",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:11,color:"#a855f7",lineHeight:1.6 }}>⚠️ Use a dedicated Facebook account. Password only used to create a local session file.</div>
          <div style={{ display:"grid",gap:12,marginBottom:14 }}>
            <div><label style={lS}>FACEBOOK EMAIL</label><input type="email" style={iS} placeholder="monitor@email.com" value={fbEmail} onChange={e=>setFbEmail(e.target.value)}/></div>
            <div><label style={lS}>PASSWORD</label><input type="password" style={iS} placeholder="••••••••" value={fbPassword} onChange={e=>setFbPassword(e.target.value)}/></div>
          </div>
          <button onClick={connectFB} disabled={connecting} style={{ padding:"10px 20px",width:"100%",borderRadius:6,border:"none",background:connecting?"#2a2a3e":"#1877F2",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:connecting?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8 }}>{connecting?<><Spinner size={14}/>Opening browser…</>:"Connect Facebook Session"}</button>
        </>):(<div>
          <div style={{ background:"#0d2e1a",border:"1px solid #22c55e30",borderRadius:8,padding:"12px 16px",marginBottom:14 }}><div style={{ color:"#22c55e",fontSize:13,fontWeight:600,marginBottom:4 }}>✓ Session Active</div><div style={{ color:"#4a6a5a",fontSize:11 }}>All crawls run as a logged-in user. Sessions last 2–4 weeks.</div></div>
          <button onClick={disconnectFB} style={{ padding:"8px 16px",borderRadius:6,border:"1px solid #ff4d4d30",background:"transparent",color:"#ff4d4d",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer" }}>Disconnect &amp; Clear Session</button>
        </div>)}
        {connMsg&&<div style={{ marginTop:12,fontSize:11,color:connMsg.startsWith("✅")?"#22c55e":connMsg.startsWith("❌")?"#ff4d4d":"#f59e0b" }}>{connMsg}</div>}
      </div>

      {/* Reddit info */}
      <div style={{ background:"#0f0f1a", border:"1px solid #ff450020", borderRadius:12, padding:20, marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:32,height:32,borderRadius:8,background:"#ff4500",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18 }}>🟠</div>
          <div><div style={{ color:"#fff",fontSize:14,fontWeight:600 }}>Reddit</div><div style={{ color:"#4a4a6a",fontSize:11 }}>Uses Reddit's official public JSON API — zero setup</div></div>
          <div style={{ marginLeft:"auto",display:"flex",alignItems:"center",gap:6 }}><div style={{ width:8,height:8,borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 8px #22c55e" }}/><span style={{ color:"#22c55e",fontSize:11,fontFamily:"'DM Mono',monospace" }}>ALWAYS ACTIVE</span></div>
        </div>
        <div style={{ marginTop:14,padding:"12px 14px",background:"#0a1208",border:"1px solid #22c55e20",borderRadius:8,fontSize:11,color:"#4a6a4a",lineHeight:1.8 }}>
          ✓ No API key or account required<br/>
          ✓ Add any public subreddit in Monitored Pages<br/>
          ✓ Example URL: https://reddit.com/r/Detroit<br/>
          ✓ Good subreddits: r/homeowners, r/Detroit, r/AskHomeImprovement, r/HVAC
        </div>
      </div>

      {/* Nextdoor info */}
      <div style={{ background:"#0f0f1a", border:"1px solid #7c3aed20", borderRadius:12, padding:20, marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:32,height:32,borderRadius:8,background:"#7c3aed",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18 }}>🟣</div>
          <div><div style={{ color:"#fff",fontSize:14,fontWeight:600 }}>Nextdoor</div><div style={{ color:"#4a4a6a",fontSize:11 }}>Uses Nextdoor's free public RSS feeds — zero setup</div></div>
          <div style={{ marginLeft:"auto",display:"flex",alignItems:"center",gap:6 }}><div style={{ width:8,height:8,borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 8px #22c55e" }}/><span style={{ color:"#22c55e",fontSize:11,fontFamily:"'DM Mono',monospace" }}>ALWAYS ACTIVE</span></div>
        </div>
        <div style={{ marginTop:14,padding:"12px 14px",background:"#0f0a1f",border:"1px solid #7c3aed20",borderRadius:8,fontSize:11,color:"#6a5a8a",lineHeight:1.8 }}>
          ✓ No login, no API key, no account needed<br/>
          ✓ Best category: "Services Wanted" — people actively looking to hire<br/>
          ✓ Add in Monitored Pages → select Nextdoor → use quick presets<br/>
          ✓ Keywords are automatically injected into the RSS search query<br/>
          ✓ Covers Detroit, Ann Arbor, Flint, Lansing and any other city
        </div>
      </div>

      {/* Nextdoor */}
      <NextdoorSession api={api} toast={toast}/>
      <PushNotificationSettings api={api} toast={toast}/>

      {/* Alerts */}
      <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:24 }}>
        <h3 style={{ color:"#fff", margin:"0 0 8px", fontSize:14, fontFamily:"'DM Mono',monospace" }}>Alert Settings</h3>
        <div style={{ background:"#0a0a1a", border:"1px solid #1e1e2e", borderRadius:8, padding:"10px 14px", marginBottom:18, fontSize:11, color:"#3a3a5a", lineHeight:1.9 }}>
          Alerts fire in priority order — first available channel wins:<br/>
          <span style={{ color:"#22c55e" }}>① Browser push</span> (free, desktop + Android) &nbsp;→&nbsp;
          <span style={{ color:"#0ea5e9" }}>② Telegram</span> (free, all phones) &nbsp;→&nbsp;
          <span style={{ color:"#4a4a6a" }}>③ Twilio SMS</span> (optional) &nbsp;→&nbsp;
          <span style={{ color:"#4a4a6a" }}>④ Email</span> (always on)
        </div>
        <div style={{ color:"#3a3a5a", fontSize:11, marginBottom:16, lineHeight:1.7 }}>
          Alert priority: <span style={{ color:"#6366f1" }}>Browser Push</span> → <span style={{ color:"#22a5e3" }}>Telegram</span> → <span style={{ color:"#22c55e" }}>Email</span> → <span style={{ color:"#f59e0b" }}>SMS (Twilio, optional)</span>.
          Push and Telegram are free — no Twilio account needed.
        </div>
        <PushNotificationSettings api={api} toast={toast}/>
        <TelegramConnect api={api} toast={toast}/>
        <TelegramConnect api={api} toast={toast}/>
        {loadingAlerts?<div style={{ display:"flex",justifyContent:"center",padding:20 }}><Spinner/></div>:<>

          {/* Email */}
          <div style={{ background:"#0a0a0f",borderRadius:10,padding:"14px 16px",marginBottom:12,border:"1px solid #1a1a2e" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:alerts.email_enabled?12:0 }}>
              <div style={{ display:"flex",alignItems:"center",gap:10 }}><span style={{ fontSize:18 }}>📧</span><div><div style={{ color:"#ccc",fontSize:13,fontWeight:600 }}>Email Alerts</div><div style={{ color:"#4a4a6a",fontSize:11 }}>via SendGrid — free 100/day</div></div></div>
              <Toggle on={alerts.email_enabled} toggle={()=>setAlerts(a=>({...a,email_enabled:!a.email_enabled}))}/>
            </div>
            {alerts.email_enabled&&<div style={{ paddingTop:12,borderTop:"1px solid #1e1e2e" }}><label style={lS}>SEND TO THIS EMAIL</label><input style={iS} type="email" placeholder="you@domain.com" value={alerts.email||""} onChange={e=>setAlerts(a=>({...a,email:e.target.value}))}/></div>}
          </div>

          {/* Phone number — shared field */}
          {hasTwilio&&<div style={{ marginBottom:12 }}><label style={lS}>YOUR PHONE NUMBER FOR ALL TWILIO ALERTS</label><input style={iS} type="tel" placeholder="+15864865766" value={alerts.phone||""} onChange={e=>setAlerts(a=>({...a,phone:e.target.value}))}/><div style={{ color:"#3a3a5a",fontSize:10,marginTop:4 }}>Format: +1 then 10 digits. Example: +15864865766</div></div>}

          {/* SMS */}
          <div style={{ background:"#0a0a0f",borderRadius:10,padding:"14px 16px",marginBottom:12,border:"1px solid #1a1a2e" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
              <div style={{ display:"flex",alignItems:"center",gap:10 }}><span style={{ fontSize:18 }}>💬</span><div><div style={{ color:"#ccc",fontSize:13,fontWeight:600 }}>SMS Alerts</div><div style={{ color:"#4a4a6a",fontSize:11 }}>Text message via Twilio ~$0.008/msg</div></div></div>
              <Toggle on={alerts.sms_enabled} toggle={()=>setAlerts(a=>({...a,sms_enabled:!a.sms_enabled}))}/>
            </div>
          </div>

          {/* WhatsApp */}
          <div style={{ background:"#0a0a0f",borderRadius:10,padding:"14px 16px",marginBottom:20,border:"1px solid #1a1a2e" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:alerts.whatsapp_enabled?12:0 }}>
              <div style={{ display:"flex",alignItems:"center",gap:10 }}><span style={{ fontSize:18 }}>📱</span><div><div style={{ color:"#ccc",fontSize:13,fontWeight:600 }}>WhatsApp Alerts</div><div style={{ color:"#4a4a6a",fontSize:11 }}>via Twilio WhatsApp</div></div></div>
              <Toggle on={alerts.whatsapp_enabled} toggle={()=>setAlerts(a=>({...a,whatsapp_enabled:!a.whatsapp_enabled}))}/>
            </div>
            {alerts.whatsapp_enabled&&<div style={{ paddingTop:12,borderTop:"1px solid #1e1e2e",fontSize:11,color:"#f59e0b",lineHeight:1.6 }}>⚠️ Requires Twilio WhatsApp sandbox approval. Go to twilio.com/console/sms/whatsapp/sandbox and follow the 5-minute setup.</div>}
          </div>

          {/* Score slider */}
          <div style={{ marginBottom:24 }}>
            <label style={lS}>MINIMUM SCORE TO TRIGGER SMS / WHATSAPP / CALL</label>
            <div style={{ display:"flex",alignItems:"center",gap:12 }}>
              <input type="range" min="10" max="50" step="5" value={alerts.min_score} onChange={e=>setAlerts(a=>({...a,min_score:parseInt(e.target.value)}))} style={{ flex:1,accentColor:"#6366f1" }}/>
              <ThresholdSuggestion
                api={api}
                api={api}
                currentThreshold={alerts.min_score}
                onApply={async (v)=>{ setAlerts(a=>({...a,min_score:v})); }}
              />
              <span style={{ color:"#a5b4fc",fontFamily:"'DM Mono',monospace",fontSize:16,minWidth:30 }}>{alerts.min_score}</span>
            </div>
            <div style={{ color:"#3a3a5a",fontSize:11,marginTop:4 }}>{alerts.min_score<=15?"All matches":alerts.min_score<=25?"Medium confidence+":alerts.min_score<=35?"High confidence+":"Urgent only"}</div>
          </div>

          {/* Twilio checklist */}
          {hasTwilio&&<div style={{ background:"#0a100f",border:"1px solid #22c55e20",borderRadius:8,padding:"12px 14px",marginBottom:20,fontSize:11,color:"#4a6a5a",lineHeight:1.8 }}>
            <div style={{ color:"#22c55e",fontWeight:600,marginBottom:4 }}>Twilio Setup (takes 5 minutes)</div>
            1. Sign up free at <span style={{ color:"#6366f1" }}>twilio.com</span> — includes $15 credit<br/>
            2. Buy a phone number from the Twilio console (~$1/mo)<br/>
            3. Add to your .env: TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER<br/>
            4. Restart: <code style={{ color:"#6366f1" }}>docker-compose restart api</code>
          </div>}

          <button onClick={saveAlerts} disabled={savingAlerts} style={{ padding:"10px 24px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:savingAlerts?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:8 }}>{savingAlerts?<><Spinner size={12}/>Saving…</>:"Save Settings"}</button>
        </>}
      </div>
      <WeeklyEmailSettings api={api} toast={toast}/>
      <ChangePasswordForm api={api} toast={toast}/>
      <WebhookSettings api={api} toast={toast}/>
      <DangerZoneSettings api={api} toast={toast}/>
    </div>
  );
}

// ── Analytics View ────────────────────────────────────────────────────────────
function AnalyticsView({ api, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics?days=${days}`)
      .then(d => setData(d))
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [days]);

  const PLATFORM_COLORS = { facebook:"#1877F2", reddit:"#ff4500", };
  const PLATFORM_ICONS  = { facebook:"🔵", reddit:"🟠", };

  const maxTs   = data ? Math.max(...(data.timeseries.map(d=>d.count)||[1]), 1) : 1;
  const maxPage = data ? Math.max(...(data.by_page.map(d=>d.count)||[1]), 1) : 1;
  const maxKw   = data ? Math.max(...(data.top_keywords.map(d=>d.count)||[1]), 1) : 1;
  const totalMatches = data ? (data.score_distribution||[]).reduce((a,b)=>a+b.count,0) : 0;

  const iS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px" };

  return (
    <div>
      {/* Header controls */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <p style={{ color:"#4a4a6a", margin:0, fontSize:12 }}>Keyword match trends and performance across all your monitored sources.</p>
        <div style={{ display:"flex", gap:6 }}>
          {[7,14,30,60].map(d=>(
            <button key={d} onClick={()=>setDays(d)} style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${days===d?"#6366f1":"#1e1e2e"}`, background:days===d?"#6366f120":"transparent", color:days===d?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display:"flex", justifyContent:"center", padding:80 }}><Spinner size={36}/></div>
      ) : !data || data.timeseries.length === 0 ? (
        <div style={{ textAlign:"center", padding:"80px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
          <div style={{ fontSize:48, marginBottom:16 }}>📊</div>
          No data yet — add pages and run some scans to see analytics
        </div>
      ) : (
        <>
          {/* Score distribution cards */}
          <div className="sl-grid-3" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
            {(data.score_distribution||[]).map(s=>{
              const color = s.label.includes("Urgent")?"#ff4d4d":s.label.includes("High")?"#f59e0b":"#22c55e";
              const pct = totalMatches > 0 ? Math.round((s.count/totalMatches)*100) : 0;
              return (
                <div key={s.label} style={{ ...iS, borderLeft:`3px solid ${color}` }}>
                  <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{s.label.toUpperCase()}</div>
                  <div style={{ color, fontSize:36, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{s.count}</div>
                  <div style={{ color:"#3a3a5a", fontSize:11, marginTop:6 }}>{pct}% of total matches</div>
                </div>
              );
            })}
          </div>

          {/* Match volume chart */}
          <div style={{ ...iS, marginBottom:20 }}>
            <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:4 }}>Match Volume — Last {days} Days</div>
            <div style={{ color:"#4a4a6a", fontSize:11, marginBottom:16 }}>Number of keyword matches found each day</div>
            <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:140, paddingBottom:24, position:"relative" }}>
              {/* Y axis lines */}
              {[0,25,50,75,100].map(pct=>(
                <div key={pct} style={{ position:"absolute", left:0, right:0, bottom:`${pct+17}%`, borderBottom:"1px dashed #1e1e2e", display:"flex", alignItems:"center" }}>
                  <span style={{ color:"#3a3a5a", fontSize:9, fontFamily:"'DM Mono',monospace", minWidth:24 }}>{Math.round(maxTs*pct/100)}</span>
                </div>
              ))}
              <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:"100%", width:"100%", paddingLeft:28 }}>
                {data.timeseries.length > 0 ? data.timeseries.map((d,i)=>{
                  const h = Math.max(4, (d.count/maxTs)*100);
                  return (
                    <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2, height:"100%", justifyContent:"flex-end" }}>
                      <div title={`${d.date}: ${d.count} matches`} style={{ width:"100%", height:`${h}%`, background:"linear-gradient(180deg,#6366f1,#4f46e5)", borderRadius:"3px 3px 0 0", minHeight:4, cursor:"pointer", transition:"opacity 0.2s" }}/>
                      {data.timeseries.length <= 14 && <div style={{ color:"#3a3a5a", fontSize:8, fontFamily:"'DM Mono',monospace", transform:"rotate(-45deg)", whiteSpace:"nowrap" }}>{d.date.slice(5)}</div>}
                    </div>
                  );
                }) : <div style={{ color:"#3a3a5a", fontSize:12, margin:"auto" }}>No matches in this period</div>}
              </div>
            </div>
          </div>

          <div className="sl-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }}>
            {/* By platform */}
            <div style={{ ...iS }}>
              <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:4 }}>Matches by Platform</div>
              <div style={{ color:"#4a4a6a", fontSize:11, marginBottom:16 }}>Which source produces the most leads</div>
              {(data.by_platform||[]).filter(p=>p.count>0).length === 0 ? (
                <div style={{ color:"#3a3a5a", fontSize:12, padding:"20px 0" }}>No data yet</div>
              ) : (data.by_platform||[]).filter(p=>p.count>0).map(p=>{
                const total = (data.by_platform||[]).reduce((a,b)=>a+b.count,0)||1;
                const pct = Math.round((p.count/total)*100);
                const color = PLATFORM_COLORS[p.platform]||"#6366f1";
                const icon  = PLATFORM_ICONS[p.platform]||"🔵";
                return (
                  <div key={p.platform} style={{ marginBottom:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ color:"#ccc", fontSize:12 }}>{icon} {p.platform.charAt(0).toUpperCase()+p.platform.slice(1)}</span>
                      <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{p.count} ({pct}%)</span>
                    </div>
                    <div style={{ height:6, background:"#1e1e2e", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:3, transition:"width 0.5s" }}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Top keywords */}
            <div style={{ ...iS }}>
              <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:4 }}>Top Keywords</div>
              <div style={{ color:"#4a4a6a", fontSize:11, marginBottom:16 }}>Which keywords trigger the most matches</div>
              {(data.top_keywords||[]).length === 0 ? (
                <div style={{ color:"#3a3a5a", fontSize:12, padding:"20px 0" }}>No data yet</div>
              ) : (data.top_keywords||[]).map((k,i)=>{
                const pct = Math.round((k.count/maxKw)*100);
                return (
                  <div key={k.keyword} style={{ marginBottom:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                      <span style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace" }}>#{i+1} {k.keyword}</span>
                      <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{k.count}</span>
                    </div>
                    <div style={{ height:5, background:"#1e1e2e", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:"linear-gradient(90deg,#6366f1,#8b5cf6)", borderRadius:3 }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By page */}
          <div style={{ ...iS }}>
            <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:4 }}>Matches by Page</div>
            <div style={{ color:"#4a4a6a", fontSize:11, marginBottom:16 }}>Which monitored pages produce the most keyword matches</div>
            {(data.by_page||[]).length === 0 ? (
              <div style={{ color:"#3a3a5a", fontSize:12 }}>No data yet</div>
            ) : (data.by_page||[]).map(p=>{
              const pct = Math.round((p.count/maxPage)*100);
              return (
                <div key={p.name} style={{ marginBottom:12 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ color:"#ccc", fontSize:12 }}>{p.name}</span>
                    <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{p.count} matches</span>
                  </div>
                  <div style={{ height:8, background:"#1e1e2e", borderRadius:4, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, background:"linear-gradient(90deg,#22c55e,#16a34a)", borderRadius:4, transition:"width 0.5s" }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Admin View ────────────────────────────────────────────────────────────────
function AdminView({ api, toast, userEmail }) {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [activity, setActivity] = useState([]);
  const [adminTs, setAdminTs] = useState([]);
  const [adminUserTs, setAdminUserTs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createForm, setCreateForm] = useState({ email:"", password:"", plan:"starter" });
  const [saving, setSaving] = useState(false);

  const PLANS = ["free","starter","pro"];
  const PLAN_COLORS = { free:"#4a4a6a", starter:"#22c55e", pro:"#6366f1" };
  const PLAN_LIMITS = { free:2, starter:10, pro:50 };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, u, a, ts, uts] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users?limit=50"),
        api.get("/admin/activity"),
        api.get("/admin/matches/timeseries?days=30"),
        api.get("/admin/users/timeseries?days=30"),
      ]);
      setStats(s);
      setUsers(u.users||[]);
      setUsersTotal(u.total||0);
      setActivity(a||[]);
      setAdminTs(ts||[]);
      setAdminUserTs(uts||[]);
    } catch(e) {
      toast.error("Admin access denied or API offline");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const searchUsers = async (q) => {
    setSearch(q);
    try {
      const u = await api.get(`/admin/users?limit=50&search=${encodeURIComponent(q)}`);
      setUsers(u.users||[]); setUsersTotal(u.total||0);
    } catch(e) {}
  };

  const updateUser = async (id, data) => {
    setSaving(true);
    try {
      const updated = await api.patch(`/admin/users/${id}`, data);
      setUsers(prev => prev.map(u => u.id===id ? {...u,...updated} : u));
      if (selectedUser?.id===id) setSelectedUser(p => ({...p,...updated}));
      toast.success("User updated");
      // The backend writes to audit log automatically via verify_admin
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Permanently delete this user and all their data?")) return;
    try {
      await api.del(`/admin/users/${id}`);
      setUsers(prev => prev.filter(u => u.id!==id));
      setSelectedUser(null);
      toast.success("User deleted");
    } catch(e) { toast.error(e.message); }
  };

  const createUser = async () => {
    if (!createForm.email || !createForm.password) { toast.error("Email and password required"); return; }
    setSaving(true);
    try {
      const u = await api.post("/admin/users", createForm);
      toast.success(`Account created for ${u.email}`);
      setShowCreateUser(false);
      setCreateForm({ email:"", password:"", plan:"starter" });
      loadAll();
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const openUserDetail = async (u) => {
    try {
      const detail = await api.get(`/admin/users/${u.id}`);
      setSelectedUser(detail);
      setTab("user-detail");
    } catch(e) { toast.error(e.message); }
  };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };
  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px" };

  const maxAdminTs = Math.max(...(adminTs.map(d=>d.count)||[1]), 1);

  if (loading) return <div style={{ display:"flex", justifyContent:"center", padding:80 }}><Spinner size={36}/></div>;

  // ── Overview tab ────────────────────────────────────────────────────────────
  if (tab === "overview") return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:24 }}>
        {[{id:"overview",label:"Overview"},{id:"users",label:`Users (${usersTotal})`},{id:"activity",label:"Activity"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${tab===t.id?"#6366f1":"#1e1e2e"}`, background:tab===t.id?"#6366f120":"transparent", color:tab===t.id?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{t.label}</button>
        ))}
        <div style={{ flex:1 }}/>
        <button onClick={()=>setShowCreateUser(!showCreateUser)} style={{ padding:"7px 16px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>+ Create Account</button>
      </div>

      {showCreateUser && (
        <div style={{ ...cardS, marginBottom:20, borderColor:"#2a2a3e" }}>
          <div style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Create New Account</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
            <div><label style={lS}>EMAIL</label><input style={iS} type="email" placeholder="customer@email.com" value={createForm.email} onChange={e=>setCreateForm(f=>({...f,email:e.target.value}))}/></div>
            <div><label style={lS}>PASSWORD</label><input style={iS} type="password" placeholder="Min 8 chars" value={createForm.password} onChange={e=>setCreateForm(f=>({...f,password:e.target.value}))}/></div>
            <div><label style={lS}>PLAN</label>
              <select style={iS} value={createForm.plan} onChange={e=>setCreateForm(f=>({...f,plan:e.target.value}))}>
                {PLANS.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={createUser} disabled={saving} style={{ padding:"8px 16px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>{saving?<><Spinner size={12}/>Creating…</>:"Create Account"}</button>
            <button onClick={()=>setShowCreateUser(false)} style={{ padding:"8px 16px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        {[
          { label:"TOTAL USERS",    value:stats?.users?.total??0,          sub:`${stats?.users?.new_this_week??0} new this week`,    color:"#6366f1" },
          { label:"ACTIVE PAGES",   value:stats?.pages?.active??0,         sub:`${stats?.pages?.total??0} total pages`,              color:"#22c55e" },
          { label:"MATCHES TODAY",  value:stats?.matches?.today??0,        sub:`${stats?.matches?.this_week??0} this week`,           color:"#f59e0b" },
          { label:"TOTAL MATCHES",  value:stats?.matches?.total??0,        sub:`${stats?.matches?.this_month??0} this month`,        color:"#8b5cf6" },
        ].map(s=>(
          <div key={s.label} style={{ ...cardS }}>
            <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{s.label}</div>
            <div style={{ color:s.color, fontSize:32, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{s.value}</div>
            <div style={{ color:"#3a3a5a", fontSize:11, marginTop:6 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }}>
        {/* Match chart */}
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Match Volume — Last 30 Days</div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:100 }}>
            {adminTs.length===0?<div style={{ color:"#3a3a5a", fontSize:12, margin:"auto" }}>No data yet</div>:adminTs.map((d,i)=>{
              const h = Math.max(4,(d.count/maxAdminTs)*100);
              return <div key={i} title={`${d.date}: ${d.count}`} style={{ flex:1, height:`${h}%`, background:"linear-gradient(180deg,#6366f1,#4f46e5)", borderRadius:"2px 2px 0 0", minHeight:4, cursor:"pointer" }}/>;
            })}
          </div>
        </div>

        {/* Plan breakdown */}
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Users by Plan</div>
          {Object.entries(stats?.users?.by_plan||{}).map(([plan, count])=>{
            const total = Object.values(stats?.users?.by_plan||{}).reduce((a,b)=>a+b,0)||1;
            const pct = Math.round((count/total)*100);
            const color = PLAN_COLORS[plan]||"#6366f1";
            return (
              <div key={plan} style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ color:"#ccc", fontSize:12, textTransform:"capitalize" }}>{plan}</span>
                  <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{count} users ({pct}%)</span>
                </div>
                <div style={{ height:6, background:"#1e1e2e", borderRadius:3 }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:3 }}/>
                </div>
              </div>
            );
          })}
          {Object.keys(stats?.users?.by_plan||{}).length===0 && <div style={{ color:"#3a3a5a", fontSize:12 }}>No users yet</div>}
        </div>
      </div>

      {/* Platform breakdown */}
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Pages by Platform (All Users)</div>
        <div style={{ display:"flex", gap:16 }}>
          {Object.entries(stats?.pages?.by_platform||{}).map(([p,c])=>{
            const ICONS = {facebook:"🔵",reddit:"🟠",};
            return <div key={p} style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 16px", background:"#0a0a0f", borderRadius:8, border:"1px solid #1e1e2e" }}>
              <span style={{ fontSize:20 }}>{ICONS[p]||"📡"}</span>
              <div><div style={{ color:"#ccc", fontSize:13, textTransform:"capitalize" }}>{p}</div><div style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{c} pages</div></div>
            </div>;
          })}
          {Object.keys(stats?.pages?.by_platform||{}).length===0 && <div style={{ color:"#3a3a5a", fontSize:12 }}>No pages yet</div>}
        </div>
      </div>
    </div>
  );

  // ── Users tab ───────────────────────────────────────────────────────────────
  if (tab === "users") return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:20 }}>
        {[{id:"overview",label:"Overview"},{id:"users",label:`Users (${usersTotal})`},{id:"activity",label:"Activity"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${tab===t.id?"#6366f1":"#1e1e2e"}`, background:tab===t.id?"#6366f120":"transparent", color:tab===t.id?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{t.label}</button>
        ))}
        <div style={{ flex:1 }}/>
        <button onClick={()=>setShowCreateUser(!showCreateUser)} style={{ padding:"7px 16px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>+ Create Account</button>
      </div>

      {showCreateUser && (
        <div style={{ ...cardS, marginBottom:20, borderColor:"#2a2a3e" }}>
          <div style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Create New Account</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
            <div><label style={lS}>EMAIL</label><input style={iS} type="email" placeholder="customer@email.com" value={createForm.email} onChange={e=>setCreateForm(f=>({...f,email:e.target.value}))}/></div>
            <div><label style={lS}>PASSWORD</label><input style={iS} type="password" placeholder="Min 8 chars" value={createForm.password} onChange={e=>setCreateForm(f=>({...f,password:e.target.value}))}/></div>
            <div><label style={lS}>PLAN</label>
              <select style={iS} value={createForm.plan} onChange={e=>setCreateForm(f=>({...f,plan:e.target.value}))}>
                {PLANS.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={createUser} disabled={saving} style={{ padding:"8px 16px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>{saving?<><Spinner size={12}/>Creating…</>:"Create Account"}</button>
            <button onClick={()=>setShowCreateUser(false)} style={{ padding:"8px 16px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom:16 }}>
        <input style={{...iS, maxWidth:320}} placeholder="Search by email..." value={search} onChange={e=>searchUsers(e.target.value)}/>
      </div>

      {users.length===0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>No users found</div>
      ) : users.map(u=>(
        <div key={u.id} style={{ ...cardS, marginBottom:10, cursor:"pointer" }} onClick={()=>openUserDetail(u)}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:14, fontWeight:700, flexShrink:0 }}>{u.email[0].toUpperCase()}</div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                <span style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{u.email}</span>
                {u.email===userEmail && <span style={{ padding:"1px 7px", borderRadius:4, background:"#6366f120", border:"1px solid #6366f130", color:"#a5b4fc", fontSize:9, fontFamily:"'DM Mono',monospace" }}>YOU</span>}
                <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${PLAN_COLORS[u.plan]}20`, color:PLAN_COLORS[u.plan]||"#6366f1", border:`1px solid ${PLAN_COLORS[u.plan]}30` }}>{u.plan.toUpperCase()}</span>
                {!u.is_active && <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:"#ff4d4d20", color:"#ff4d4d" }}>SUSPENDED</span>}
              </div>
              <div style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                {u.page_count} pages · {u.match_count} matches · joined {u.created_at?new Date(u.created_at).toLocaleDateString():"?"}
              </div>
            </div>
            <div style={{ display:"flex", gap:6 }} onClick={e=>e.stopPropagation()}>
              <select value={u.plan} onChange={e=>updateUser(u.id,{plan:e.target.value})} style={{ padding:"5px 10px", borderRadius:6, border:"1px solid #2a2a3e", background:"#0a0a0f", color:"#ccc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                {PLANS.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
              <button onClick={()=>updateUser(u.id,{is_active:!u.is_active})} style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:u.is_active?"#f59e0b":"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{u.is_active?"Suspend":"Restore"}</button>
              <button onClick={()=>deleteUser(u.id)} style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Delete</button>
              <button onClick={async ()=>{
                if(!window.confirm(`Log in as ${u.email} for troubleshooting? Your session will be replaced for 30 minutes.`)) return;
                try {
                  const res = await api.post(`/admin/users/${u.id}/impersonate`);
                  const adminToken = localStorage.getItem("sl_token");
                  const adminEmail = localStorage.getItem("sl_email");
                  localStorage.setItem("sl_admin_token", adminToken);
                  localStorage.setItem("sl_admin_email", adminEmail);
                  localStorage.setItem("sl_token", res.access_token);
                  localStorage.setItem("sl_email", res.target_email);
                  localStorage.setItem("sl_impersonating", "1");
                  toast.success(`Logged in as ${res.target_email} — 30 min session`);
                  window.location.reload();
                } catch(e) { toast.error(e.message); }
              }} style={{ padding:"5px 12px", borderRadius:6, border:"1px solid #8b5cf630", background:"#8b5cf610", color:"#a78bfa", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                👤 Login as
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // ── Activity tab ────────────────────────────────────────────────────────────
  if (tab === "activity") return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:20 }}>
        {[{id:"overview",label:"Overview"},{id:"users",label:`Users (${usersTotal})`},{id:"activity",label:"Activity"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${tab===t.id?"#6366f1":"#1e1e2e"}`, background:tab===t.id?"#6366f120":"transparent", color:tab===t.id?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{t.label}</button>
        ))}
      </div>
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Recent Matches — All Users</div>
        {activity.length===0 ? (
          <div style={{ color:"#3a3a5a", fontSize:12, padding:"20px 0" }}>No activity yet</div>
        ) : activity.map(a=>{
          const ICONS = {facebook:"🔵",reddit:"🟠",};
          const sc = a.score>=40?"#ff4d4d":a.score>=25?"#f59e0b":"#22c55e";
          return (
            <div key={a.match_id} style={{ padding:"12px 0", borderBottom:"1px solid #1a1a2a", display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ padding:"3px 8px", borderRadius:4, fontSize:9, fontWeight:700, fontFamily:"'DM Mono',monospace", background:`${sc}20`, color:sc, whiteSpace:"nowrap" }}>{a.score}</div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                  <span style={{ fontSize:12 }}>{ICONS[a.platform]||"📡"}</span>
                  <span style={{ color:"#ccc", fontSize:12 }}>{a.page_name}</span>
                  <span style={{ color:"#3a3a5a", fontSize:10 }}>→</span>
                  <span style={{ color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{a.user_email}</span>
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                  {(a.matched_terms||[]).map(t=><span key={t} style={{ background:"#1e1e3e", color:"#a5b4fc", fontSize:9, padding:"1px 6px", borderRadius:3, fontFamily:"'DM Mono',monospace" }}>{t}</span>)}
                </div>
              </div>
              <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap" }}>{timeAgo(a.found_at)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── User detail tab ─────────────────────────────────────────────────────────
  if (tab === "user-detail" && selectedUser) return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <button onClick={()=>setTab("users")} style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>← Back to Users</button>
        <h2 style={{ color:"#fff", margin:0, fontSize:16, fontFamily:"'DM Mono',monospace" }}>{selectedUser.email}</h2>
        <span style={{ padding:"3px 10px", borderRadius:6, fontSize:11, fontFamily:"'DM Mono',monospace", background:`${PLAN_COLORS[selectedUser.plan]||"#6366f1"}20`, color:PLAN_COLORS[selectedUser.plan]||"#6366f1", border:`1px solid ${PLAN_COLORS[selectedUser.plan]||"#6366f1"}30`, textTransform:"capitalize" }}>{selectedUser.plan}</span>
        {!selectedUser.is_active && <span style={{ padding:"3px 10px", borderRadius:6, fontSize:11, background:"#ff4d4d20", color:"#ff4d4d" }}>SUSPENDED</span>}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }}>
        {/* Plan management */}
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Plan Management</div>
          <div style={{ display:"grid", gap:12 }}>
            <div>
              <label style={lS}>PLAN</label>
              <select style={iS} value={selectedUser.plan} onChange={e=>updateUser(selectedUser.id,{plan:e.target.value})}>
                {PLANS.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)} — up to {PLAN_LIMITS[p]===999?"unlimited":PLAN_LIMITS[p]} pages</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>CUSTOM PAGE LIMIT</label>
              <input style={iS} type="number" min={1} max={999} value={selectedUser.pages_limit} onChange={e=>updateUser(selectedUser.id,{pages_limit:parseInt(e.target.value)})}/>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>updateUser(selectedUser.id,{is_active:!selectedUser.is_active})} style={{ flex:1, padding:"9px", borderRadius:6, border:`1px solid ${selectedUser.is_active?"#ff4d4d30":"#22c55e30"}`, background:"transparent", color:selectedUser.is_active?"#ff4d4d":"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{selectedUser.is_active?"Suspend Account":"Restore Account"}</button>
              <button onClick={()=>deleteUser(selectedUser.id)} style={{ padding:"9px 14px", borderRadius:6, border:"1px solid #ff4d4d30", background:"#ff4d4d10", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Delete</button>
            </div>
          </div>
        </div>

        {/* Account info */}
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Account Info</div>
          {[
            ["User ID",       `#${selectedUser.id}`],
            ["Email",         selectedUser.email],
            ["Plan",          selectedUser.plan],
            ["Pages Limit",   selectedUser.pages_limit===999?"Unlimited":selectedUser.pages_limit],
            ["Pages Used",    selectedUser.pages?.length||0],
            ["Total Matches", selectedUser.recent_matches?.length>0?"10+ (recent shown)":"0"],
            ["Joined",        selectedUser.created_at?new Date(selectedUser.created_at).toLocaleDateString():"?"],
          ].map(([k,v])=>(
            <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #1a1a2a" }}>
              <span style={{ color:"#4a4a6a", fontSize:12 }}>{k}</span>
              <span style={{ color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* User's pages */}
      <div style={{ ...cardS, marginBottom:20 }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Monitored Pages ({selectedUser.pages?.length||0})</div>
        {!selectedUser.pages?.length ? <div style={{ color:"#3a3a5a", fontSize:12 }}>No pages yet</div>
        : selectedUser.pages.map(p=>{
          const ICONS={facebook:"🔵",reddit:"🟠",};
          return (
            <div key={p.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:"1px solid #1a1a2a" }}>
              <span>{ICONS[p.platform]||"📡"}</span>
              <div style={{ flex:1 }}>
                <div style={{ color:"#ccc", fontSize:12, fontWeight:600 }}>{p.name}</div>
                <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{p.keyword_count} keywords · {p.active?"Active":"Paused"}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent matches */}
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Recent Matches</div>
        {!selectedUser.recent_matches?.length ? <div style={{ color:"#3a3a5a", fontSize:12 }}>No matches yet</div>
        : selectedUser.recent_matches.map(m=>{
          const sc=m.score>=40?"#ff4d4d":m.score>=25?"#f59e0b":"#22c55e";
          return (
            <div key={m.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #1a1a2a" }}>
              <div style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontWeight:700, background:`${sc}20`, color:sc, fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap" }}>{m.score}</div>
              <div style={{ color:"#ccc", fontSize:11, flex:1, textTransform:"capitalize" }}>{m.platform}</div>
              <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{timeAgo(m.found_at)}</div>
            </div>
          );
        })}
      </div>

      {/* Internal notes */}
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>📝 Internal Notes</div>
        <UserNotes api={api} toast={toast} userId={selectedUser.id} userEmail={selectedUser.email}/>
      </div>
      {/* Call log for this user */}
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>📞 Call History</div>
        <UserCallHistory api={api} toast={toast} userId={selectedUser.id}/>
      </div>
    </div>
  );

  return null;
}
// ── Pricing Page ──────────────────────────────────────────────────────────────
function PricingView({ api, toast, onSelectPlan }) {
  const [couponCode, setCouponCode] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const PLANS = [
    {
      id: "starter",
      name: "Starter",
      price: 59,
      color: "#22c55e",
      badge: null,
      pages: 15,
      keywords: 25,
      platforms: ["Facebook", "Reddit", "Nextdoor"],
      alerts: ["Email"],
      interval: "Every 30 min",
      features: ["15 monitored pages", "25 keywords per page", "Email alerts", "Reply templates", "Page inbox monitor"],
    },
    {
      id: "pro",
      name: "Pro",
      price: 89,
      color: "#6366f1",
      badge: "MOST POPULAR",
      pages: 30,
      keywords: "Unlimited",
      platforms: ["Facebook", "Reddit", "Nextdoor"],
      alerts: ["Email", "SMS", "WhatsApp"],
      interval: "Every 15 min",
      features: ["30 monitored pages", "Unlimited keywords", "Email + SMS + WhatsApp alerts", "Voice call for urgent matches", "Reply templates", "Page inbox monitor", "Analytics dashboard"],
    },
    ];

  const checkCoupon = async (plan) => {
    if (!couponCode.trim()) return;
    setCheckingCoupon(true); setCouponResult(null);
    try {
      const result = await api.get(`/coupons/validate/${encodeURIComponent(couponCode.trim())}?plan=${plan||""}`);
      setCouponResult({ ...result, valid: true });
    } catch(e) {
      setCouponResult({ valid: false, message: e.message });
    } finally { setCheckingCoupon(false); }
  };

  const redeemCoupon = async (planId) => {
    if (!couponResult?.valid) return;
    setRedeeming(true);
    try {
      const result = await api.post("/coupons/redeem", { code: couponCode.trim(), plan: planId });
      toast.success(result.message);
      setCouponResult(null); setCouponCode("");
      setSelectedPlan(null);
    } catch(e) {
      toast.error(e.message);
    } finally { setRedeeming(false); }
  };

  const discountedPrice = (price) => {
    if (!couponResult?.valid) return null;
    if (couponResult.discount_type === "percent") {
      return Math.round(price * (1 - couponResult.discount_value / 100));
    }
    return null; // months_free — shown differently
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ textAlign:"center", marginBottom:48 }}>
        <div style={{ color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", letterSpacing:3, marginBottom:12 }}>SIMPLE PRICING</div>
        <h1 style={{ color:"#fff", fontSize:36, fontWeight:700, margin:"0 0 12px", letterSpacing:-1 }}>Choose Your Plan</h1>
        <p style={{ color:"#4a4a6a", fontSize:14, margin:0 }}>Monitor Facebook, Reddit, and Nextdoor for keyword leads. Cancel anytime.</p>
      </div>

      {/* Coupon input */}
      <div style={{ display:"flex", justifyContent:"center", marginBottom:40 }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"10px 16px" }}>
          <span style={{ fontSize:16 }}>🏷️</span>
          <input
            placeholder="Have a coupon code?"
            value={couponCode}
            onChange={e=>{ setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }}
            onKeyDown={e=>e.key==="Enter"&&checkCoupon("")}
            style={{ background:"transparent", border:"none", outline:"none", color:"#ccc", fontSize:13, fontFamily:"'DM Mono',monospace", width:180 }}
          />
          <button onClick={()=>checkCoupon("")} disabled={!couponCode.trim()||checkingCoupon} style={{ padding:"6px 14px", borderRadius:6, border:"none", background:couponCode.trim()?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#2a2a3e", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:couponCode.trim()?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:6 }}>
            {checkingCoupon?<><Spinner size={10}/>Checking…</>:"Apply"}
          </button>
        </div>
      </div>

      {/* Coupon result banner */}
      {couponResult && (
        <div style={{ marginBottom:24, padding:"12px 20px", borderRadius:10, textAlign:"center", background:couponResult.valid?"#0a2a1a":"#2a0a0a", border:`1px solid ${couponResult.valid?"#22c55e30":"#ff4d4d30"}`, color:couponResult.valid?"#22c55e":"#ff4d4d", fontSize:13, fontFamily:"'DM Mono',monospace" }}>
          {couponResult.valid ? `✓ Coupon valid — ${couponResult.message}${couponResult.valid_for_plan?" ("+couponResult.valid_for_plan.toUpperCase()+" plan only)":""}` : `✗ ${couponResult.message}`}
        </div>
      )}

      {/* Plan cards */}
      <div className="sl-grid-3" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20, marginBottom:48 }}>
        {PLANS.map(plan => {
          const discounted = discountedPrice(plan.price);
          const isFree = couponResult?.valid && couponResult.discount_type==="months_free";
          return (
            <div key={plan.id} style={{
              background:"#0f0f1a", border:`2px solid ${selectedPlan===plan.id?plan.color:"#1e1e2e"}`,
              borderRadius:16, padding:28, position:"relative", transition:"all 0.2s",
              boxShadow:selectedPlan===plan.id?`0 0 24px ${plan.color}30`:"none",
            }}>
              {/* Badge */}
              {plan.badge && (
                <div style={{ position:"absolute", top:-12, left:"50%", transform:"translateX(-50%)", padding:"3px 12px", borderRadius:20, background:plan.color, color:"#fff", fontSize:9, fontFamily:"'DM Mono',monospace", fontWeight:700, letterSpacing:1, whiteSpace:"nowrap" }}>
                  {plan.badge}
                </div>
              )}

              {/* Plan header */}
              <div style={{ marginBottom:20 }}>
                <div style={{ color:plan.color, fontSize:11, fontFamily:"'DM Mono',monospace", letterSpacing:2, marginBottom:6 }}>{plan.name.toUpperCase()}</div>
                <div style={{ display:"flex", alignItems:"flex-end", gap:6 }}>
                  {discounted !== null ? (
                    <>
                      <span style={{ color:"#3a3a5a", fontSize:24, textDecoration:"line-through" }}>${plan.price}</span>
                      <span style={{ color:"#fff", fontSize:40, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>${discounted}</span>
                      <span style={{ color:"#4a4a6a", fontSize:13, paddingBottom:6 }}>/mo</span>
                    </>
                  ) : isFree ? (
                    <>
                      <span style={{ color:"#fff", fontSize:40, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>${plan.price}</span>
                      <span style={{ color:"#4a4a6a", fontSize:13, paddingBottom:6 }}>/mo</span>
                      <span style={{ color:"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace", paddingBottom:6 }}>+{couponResult.discount_value} free</span>
                    </>
                  ) : (
                    <>
                      <span style={{ color:"#fff", fontSize:40, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>${plan.price}</span>
                      <span style={{ color:"#4a4a6a", fontSize:13, paddingBottom:6 }}>/mo</span>
                    </>
                  )}
                </div>
                {discounted !== null && (
                  <div style={{ color:"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace", marginTop:4 }}>
                    Save ${plan.price - discounted}/mo with coupon
                  </div>
                )}
              </div>

              {/* Quick stats */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:20 }}>
                {[
                  ["Pages", plan.pages],
                  ["Keywords", plan.keywords],
                  ["Scan", plan.interval],
                  ["Alerts", plan.alerts.join(", ")],
                ].map(([k,v])=>(
                  <div key={k} style={{ background:"#0a0a0f", borderRadius:8, padding:"8px 10px" }}>
                    <div style={{ color:"#4a4a6a", fontSize:9, fontFamily:"'DM Mono',monospace", letterSpacing:1 }}>{k}</div>
                    <div style={{ color:"#ccc", fontSize:11, fontWeight:600, marginTop:2 }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Features */}
              <div style={{ marginBottom:24 }}>
                {plan.features.map(f=>(
                  <div key={f} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", borderBottom:"1px solid #111120" }}>
                    <span style={{ color:plan.color, fontSize:12 }}>✓</span>
                    <span style={{ color:"#8888aa", fontSize:12 }}>{f}</span>
                  </div>
                ))}
              </div>

              {/* CTA button */}
              <button
                onClick={() => { setSelectedPlan(plan.id); if (couponResult?.valid) redeemCoupon(plan.id); else onSelectPlan(plan); }}
                style={{
                  width:"100%", padding:"12px", borderRadius:8, border:"none",
                  background:selectedPlan===plan.id?plan.color:`${plan.color}20`,
                  color:selectedPlan===plan.id?"#fff":plan.color,
                  fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer",
                  fontWeight:600, transition:"all 0.2s",
                }}>
                {couponResult?.valid ? `Apply Coupon & Get ${plan.name}` : `Get ${plan.name}`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Compare table */}
      <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:16, padding:28, marginBottom:40 }}>
        <h3 style={{ color:"#fff", margin:"0 0 20px", fontSize:15, fontFamily:"'DM Mono',monospace" }}>Full Comparison</h3>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr>
                <td style={{ padding:"10px 14px", color:"#4a4a6a", fontFamily:"'DM Mono',monospace", fontSize:11, borderBottom:"1px solid #1e1e2e" }}>FEATURE</td>
                {PLANS.map(p=><th key={p.id} style={{ padding:"10px 14px", color:p.color, fontFamily:"'DM Mono',monospace", fontSize:11, textAlign:"center", borderBottom:"1px solid #1e1e2e" }}>{p.name.toUpperCase()}</th>)}
              </tr>
            </thead>
            <tbody>
              {[
                ["Monitored pages",        "15",         "30",          "Unlimited"],
                ["Keywords per page",      "25",         "Unlimited",   "Unlimited"],
                ["Scan interval",          "30 min",     "15 min",      "5 min"],
                ["Facebook monitoring",    "✓",          "✓",           "✓"],
                ["Reddit monitoring",      "✓",          "✓",           "✓"],
                ["Nextdoor monitoring",  "✓",          "✓",           "✓"],
                ["Email alerts",           "✓",          "✓",           "✓"],
                ["SMS alerts",             "✗",          "✓",           "✓"],
                ["WhatsApp alerts",        "✗",          "✓",           "✓"],
                ["Voice call alerts",      "✗",          "✗",           "✗"],
                ["Reply templates",        "✓",          "✓",           "✓"],
                ["Page inbox monitor",     "✓",          "✓",           "✓"],
                ["Analytics dashboard",    "✗",          "✓",           "✓"],
                ["White-label branding",   "✗",          "✗",           "✓"],
                ["API access",             "✗",          "✗",           "✓"],
              ].map(([feat,...vals])=>(
                <tr key={feat} style={{ borderBottom:"1px solid #111120" }}>
                  <td style={{ padding:"10px 14px", color:"#8888aa" }}>{feat}</td>
                  {vals.map((v,i)=>(
                    <td key={i} style={{ padding:"10px 14px", textAlign:"center", color:v==="✓"?PLANS[i].color:v==="✗"?"#2a2a3e":"#ccc", fontWeight:v==="✓"||v==="✗"?700:400 }}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        {[
          ["Can I cancel anytime?", "Yes — cancel with one click from your account settings. No contracts, no cancellation fees."],
          ["Do you offer refunds?", "We offer a 7-day money-back guarantee on all plans, no questions asked."],
          ["What payment methods?", "We accept all major credit cards via Stripe. All payments are processed securely."],
          ["Can I switch plans?", "Yes — upgrade or downgrade anytime. Charges are prorated automatically."],
          ["How does the coupon work?", "Enter your coupon code above and click Apply. The discount is shown instantly on each plan card before you checkout."],
          ["Is there a free trial?", "We offer a free tier with 2 monitored pages so you can test the product before upgrading."],
        ].map(([q,a])=>(
          <div key={q} style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px" }}>
            <div style={{ color:"#a5b4fc", fontSize:13, fontWeight:600, marginBottom:6 }}>{q}</div>
            <div style={{ color:"#4a4a6a", fontSize:12, lineHeight:1.7 }}>{a}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Coupon Manager (inside Admin Panel) ───────────────────────────────────────
function CouponManager({ api, toast }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showRedemptions, setShowRedemptions] = useState(null);
  const [redemptionList, setRedemptionList] = useState([]);
  const [bulkCodes, setBulkCodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: "", description: "", discount_type: "percent",
    discount_value: 20, valid_for_plan: "", max_uses: "", expires_at: ""
  });
  const [bulkForm, setBulkForm] = useState({
    count: 10, discount_type: "percent", discount_value: 20, valid_for_plan: "", max_uses: 1
  });

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };

  useEffect(() => {
    api.get("/coupons").then(d => setCoupons(d||[])).catch(()=>toast.error("Failed to load coupons")).finally(()=>setLoading(false));
  }, []);

  const createCoupon = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_value: parseInt(form.discount_value),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        valid_for_plan: form.valid_for_plan || null,
        expires_at: form.expires_at || null,
      };
      const c = await api.post("/coupons", payload);
      setCoupons(p => [c, ...p]);
      setShowCreate(false);
      setForm({ code:"", description:"", discount_type:"percent", discount_value:20, valid_for_plan:"", max_uses:"", expires_at:"" });
      toast.success(`Coupon ${c.code} created`);
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const bulkGenerate = async () => {
    setSaving(true);
    try {
      const payload = {
        ...bulkForm,
        discount_value: parseInt(bulkForm.discount_value),
        max_uses: parseInt(bulkForm.max_uses),
        valid_for_plan: bulkForm.valid_for_plan || null,
      };
      const result = await api.post(
        `/coupons/bulk-generate?count=${payload.count}&discount_type=${payload.discount_type}&discount_value=${payload.discount_value}&max_uses=${payload.max_uses}${payload.valid_for_plan?"&valid_for_plan="+payload.valid_for_plan:""}`
      );
      setBulkCodes(result.codes);
      toast.success(`${result.created} codes generated`);
      api.get("/coupons").then(d=>setCoupons(d||[]));
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      const updated = await api.patch(`/coupons/${c.id}`, { active: !c.active });
      setCoupons(p => p.map(x => x.id===c.id ? updated : x));
      toast.success(updated.active ? "Coupon activated" : "Coupon deactivated");
    } catch(e) { toast.error(e.message); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon? Existing redemptions are kept.")) return;
    try {
      await api.del(`/coupons/${id}`);
      setCoupons(p => p.filter(x => x.id!==id));
      toast.success("Coupon deleted");
    } catch(e) { toast.error(e.message); }
  };

  const viewRedemptions = async (c) => {
    try {
      const r = await api.get(`/coupons/${c.id}/redemptions`);
      setRedemptionList(r);
      setShowRedemptions(c);
    } catch(e) { toast.error(e.message); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  const copyAllCodes = () => {
    navigator.clipboard.writeText(bulkCodes.join("\n"));
    toast.success(`${bulkCodes.length} codes copied to clipboard`);
  };

  const discountLabel = (c) => {
    if (c.discount_type === "percent") return `${c.discount_value}% off`;
    return `${c.discount_value} month${c.discount_value>1?"s":""} free`;
  };

  const isExpired = (c) => c.expires_at && new Date(c.expires_at) < new Date();
  const isMaxed   = (c) => c.max_uses !== null && c.times_used >= c.max_uses;

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <h3 style={{ color:"#fff", margin:"0 0 4px", fontSize:15, fontFamily:"'DM Mono',monospace" }}>Coupon Codes</h3>
          <p style={{ color:"#4a4a6a", margin:0, fontSize:12 }}>{coupons.length} coupon{coupons.length!==1?"s":""} total · {coupons.filter(c=>c.active&&!isExpired(c)&&!isMaxed(c)).length} active</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={()=>{setShowBulk(!showBulk);setShowCreate(false);}} style={{ padding:"8px 14px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#f59e0b", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>⚡ Bulk Generate</button>
          <button onClick={()=>{setShowCreate(!showCreate);setShowBulk(false);}} style={{ padding:"8px 16px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>+ New Coupon</button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:12, padding:20, marginBottom:20 }}>
          <div style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", marginBottom:16 }}>New Coupon</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
            <div>
              <label style={lS}>CODE (blank = auto-generate)</label>
              <input style={iS} placeholder="e.g. LAUNCH50" value={form.code} onChange={e=>setForm(f=>({...f,code:e.target.value.toUpperCase()}))}/>
            </div>
            <div>
              <label style={lS}>DISCOUNT TYPE</label>
              <select style={iS} value={form.discount_type} onChange={e=>setForm(f=>({...f,discount_type:e.target.value}))}>
                <option value="percent">Percentage off</option>
                <option value="months_free">Months free</option>
              </select>
            </div>
            <div>
              <label style={lS}>{form.discount_type==="percent"?"PERCENT OFF (e.g. 20 = 20% off)":"MONTHS FREE"}</label>
              <input style={iS} type="number" min={1} max={form.discount_type==="percent"?100:12} value={form.discount_value} onChange={e=>setForm(f=>({...f,discount_value:e.target.value}))}/>
            </div>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={lS}>DESCRIPTION (shown to customers)</label>
              <input style={iS} placeholder="e.g. Launch special — 20% off your first month" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/>
            </div>
            <div>
              <label style={lS}>VALID FOR PLAN (blank = all plans)</label>
              <select style={iS} value={form.valid_for_plan} onChange={e=>setForm(f=>({...f,valid_for_plan:e.target.value}))}>
                <option value="">All plans</option>
                <option value="starter">Starter only</option>
                <option value="pro">Pro only</option>
                
              </select>
            </div>
            <div>
              <label style={lS}>MAX USES (blank = unlimited)</label>
              <input style={iS} type="number" min={1} placeholder="e.g. 100" value={form.max_uses} onChange={e=>setForm(f=>({...f,max_uses:e.target.value}))}/>
            </div>
            <div>
              <label style={lS}>EXPIRY DATE (blank = no expiry)</label>
              <input style={iS} type="date" value={form.expires_at} onChange={e=>setForm(f=>({...f,expires_at:e.target.value}))}/>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:16 }}>
            <button onClick={createCoupon} disabled={saving} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:8 }}>{saving?<><Spinner size={12}/>Creating…</>:"Create Coupon"}</button>
            <button onClick={()=>setShowCreate(false)} style={{ padding:"9px 20px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Bulk generate form */}
      {showBulk && (
        <div style={{ background:"#0f0f1a", border:"1px solid #f59e0b30", borderRadius:12, padding:20, marginBottom:20 }}>
          <div style={{ color:"#f59e0b", fontSize:13, fontFamily:"'DM Mono',monospace", marginBottom:16 }}>⚡ Bulk Generate Codes</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
            <div><label style={lS}>HOW MANY</label><input style={iS} type="number" min={1} max={100} value={bulkForm.count} onChange={e=>setBulkForm(f=>({...f,count:e.target.value}))}/></div>
            <div><label style={lS}>DISCOUNT TYPE</label>
              <select style={iS} value={bulkForm.discount_type} onChange={e=>setBulkForm(f=>({...f,discount_type:e.target.value}))}>
                <option value="percent">Percent off</option>
                <option value="months_free">Months free</option>
              </select>
            </div>
            <div><label style={lS}>VALUE</label><input style={iS} type="number" min={1} value={bulkForm.discount_value} onChange={e=>setBulkForm(f=>({...f,discount_value:e.target.value}))}/></div>
            <div><label style={lS}>PLAN</label>
              <select style={iS} value={bulkForm.valid_for_plan} onChange={e=>setBulkForm(f=>({...f,valid_for_plan:e.target.value}))}>
                <option value="">All</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                
              </select>
            </div>
            <div><label style={lS}>USES PER CODE</label><input style={iS} type="number" min={1} value={bulkForm.max_uses} onChange={e=>setBulkForm(f=>({...f,max_uses:e.target.value}))}/></div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <button onClick={bulkGenerate} disabled={saving} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#f59e0b,#d97706)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:8 }}>{saving?<><Spinner size={12}/>Generating…</>:`Generate ${bulkForm.count} Codes`}</button>
            <button onClick={()=>{setShowBulk(false);setBulkCodes([]);}} style={{ padding:"9px 20px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
          {bulkCodes.length > 0 && (
            <div style={{ marginTop:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <span style={{ color:"#22c55e", fontSize:12, fontFamily:"'DM Mono',monospace" }}>✓ {bulkCodes.length} codes generated</span>
                <button onClick={copyAllCodes} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #22c55e30", background:"#22c55e10", color:"#22c55e", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>📋 Copy All</button>
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {bulkCodes.map(code=>(
                  <span key={code} onClick={()=>copyToClipboard(code)} style={{ padding:"4px 10px", borderRadius:4, background:"#1e1e2e", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{code}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Redemptions modal */}
      {showRedemptions && (
        <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:12, padding:20, marginBottom:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <span style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace" }}>Redemptions for {showRedemptions.code}</span>
            <button onClick={()=>setShowRedemptions(null)} style={{ background:"transparent", border:"none", color:"#4a4a6a", cursor:"pointer", fontSize:18 }}>×</button>
          </div>
          {redemptionList.length===0 ? <div style={{ color:"#3a3a5a", fontSize:12 }}>No redemptions yet</div>
          : redemptionList.map((r,i)=>(
            <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #1a1a2a", fontSize:12 }}>
              <span style={{ color:"#ccc" }}>{r.user_email}</span>
              <span style={{ color:"#6366f1", fontFamily:"'DM Mono',monospace", textTransform:"capitalize" }}>{r.plan}</span>
              <span style={{ color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>{new Date(r.redeemed_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Coupon list */}
      {loading ? <div style={{ display:"flex", justifyContent:"center", padding:40 }}><Spinner size={28}/></div>
      : coupons.length===0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace", fontSize:13 }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🏷️</div>
          No coupons yet — create one above
        </div>
      ) : coupons.map(c => {
        const expired = isExpired(c);
        const maxed   = isMaxed(c);
        const statusColor = !c.active?"#3a3a5a":expired?"#f59e0b":maxed?"#f59e0b":"#22c55e";
        const statusLabel = !c.active?"INACTIVE":expired?"EXPIRED":maxed?"MAXED OUT":"ACTIVE";
        return (
          <div key={c.id} style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px", marginBottom:10, opacity:(!c.active||expired||maxed)?0.6:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              {/* Code */}
              <div style={{ background:"#1e1e2e", borderRadius:6, padding:"6px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }} onClick={()=>copyToClipboard(c.code)}>
                <span style={{ color:"#a5b4fc", fontSize:15, fontFamily:"'DM Mono',monospace", fontWeight:700, letterSpacing:2 }}>{c.code}</span>
                <span style={{ color:"#4a4a6a", fontSize:10 }}>📋</span>
              </div>

              {/* Discount badge */}
              <div style={{ padding:"4px 10px", borderRadius:6, background:"#6366f120", border:"1px solid #6366f130", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", fontWeight:700 }}>
                {discountLabel(c)}
              </div>

              <div style={{ flex:1 }}>
                {c.description && <div style={{ color:"#ccc", fontSize:12, marginBottom:3 }}>{c.description}</div>}
                <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", display:"flex", gap:12, flexWrap:"wrap" }}>
                  <span>Used: {c.times_used}{c.max_uses?"/"+c.max_uses:" (unlimited)"}</span>
                  {c.valid_for_plan && <span>Plan: {c.valid_for_plan}</span>}
                  {c.expires_at && <span>Expires: {new Date(c.expires_at).toLocaleDateString()}</span>}
                  {!c.expires_at && <span>No expiry</span>}
                </div>
              </div>

              {/* Status */}
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:statusColor, boxShadow:`0 0 5px ${statusColor}` }}/>
                <span style={{ color:statusColor, fontSize:9, fontFamily:"'DM Mono',monospace" }}>{statusLabel}</span>
              </div>

              {/* Actions */}
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={()=>viewRedemptions(c)} style={{ padding:"5px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>History</button>
                <button onClick={()=>toggleActive(c)} style={{ padding:"5px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:c.active?"#f59e0b":"#22c55e", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{c.active?"Disable":"Enable"}</button>
                <button onClick={()=>deleteCoupon(c.id)} style={{ padding:"5px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#ff4d4d", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Del</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// MATCH STATUS / CRM PIPELINE CARD
// ════════════════════════════════════════════════════════════════════════════

const STATUS_CONFIG = {
  new:       { label:"New",       color:"#6366f1", bg:"#6366f120" },
  contacted: { label:"Contacted", color:"#3b82f6", bg:"#3b82f620" },
  quoted:    { label:"Quoted",    color:"#f59e0b", bg:"#f59e0b20" },
  won:       { label:"Won",       color:"#22c55e", bg:"#22c55e20" },
  lost:      { label:"Lost",      color:"#ff4d4d", bg:"#ff4d4d20" },
  ignored:   { label:"Ignored",   color:"#3a3a5a", bg:"#3a3a5a20" },
};

function PipelineView({ api, pages, toast }) {
  const [pipeline, setPipeline] = useState({});
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState("new");
  const [followUps, setFollowUps] = useState([]);
  const [editingMatch, setEditingMatch] = useState(null);
  const [editForm, setEditForm] = useState({ status:"", notes:"", follow_up_at:"" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/matches/pipeline"),
      api.get("/matches?limit=100"),
      api.get("/matches/follow-ups"),
    ]).then(([p, m, f]) => {
      setPipeline(p || {});
      setMatches(m?.matches || []);
      setFollowUps(f || []);
    }).catch(() => toast.error("Failed to load pipeline"))
    .finally(() => setLoading(false));
  }, []);

  const openEdit = (match) => {
    setEditingMatch(match);
    setEditForm({
      status: match.status || "new",
      notes: match.notes || "",
      follow_up_at: match.follow_up_at ? match.follow_up_at.slice(0,10) : "",
    });
  };

  const saveStatus = async () => {
    if (!editingMatch) return;
    setSaving(true);
    try {
      await api.patch(`/matches/${editingMatch.id}/status`, {
        status: editForm.status,
        notes: editForm.notes,
        follow_up_at: editForm.follow_up_at || null,
      });
      setMatches(prev => prev.map(m => m.id === editingMatch.id ? {
        ...m, status: editForm.status, notes: editForm.notes, follow_up_at: editForm.follow_up_at
      } : m));
      // Auto-claim when marking as contacted/booked — notify if others also reached out
      if (["contacted","booked","won"].includes(editForm.status)) {
        try {
          const claim = await api.post(`/matches/${editingMatch.id}/claim`);
          if (claim.other_claimants > 0) {
            toast.success(`Heads up: ${claim.other_claimants} other contractor(s) have also contacted this lead`);
          }
        } catch { /* non-critical */ }
      }
      setPipeline(prev => {
        const updated = {...prev};
        const old = editingMatch.status || "new";
        updated[old] = Math.max(0, (updated[old]||0) - 1);
        updated[editForm.status] = (updated[editForm.status]||0) + 1;
        return updated;
      });
      toast.success("Updated");
      setEditingMatch(null);
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const statusMatches = matches.filter(m => (m.status || "new") === activeStatus);
  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };

  if (loading) return <div style={{ display:"flex", justifyContent:"center", padding:60 }}><Spinner size={32}/></div>;

  return (
    <div>
      {/* Follow-up reminders */}
      {followUps.length > 0 && (
        <div style={{ background:"#1a100a", border:"1px solid #f59e0b30", borderRadius:10, padding:"14px 18px", marginBottom:20 }}>
          <div style={{ color:"#f59e0b", fontSize:12, fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:10 }}>⏰ Follow-up Reminders ({followUps.length})</div>
          {followUps.map(f => (
            <div key={f.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #2a1a0a" }}>
              <div style={{ flex:1 }}>
                <div style={{ color:"#ccc", fontSize:12 }}>{f.post_text.slice(0,100)}…</div>
                <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", marginTop:2 }}>
                  {f.page_name} · Due: {f.follow_up_at ? new Date(f.follow_up_at).toLocaleDateString() : "?"}
                </div>
                {f.notes && <div style={{ color:"#6366f1", fontSize:11, marginTop:2, fontStyle:"italic" }}>Note: {f.notes}</div>}
              </div>
              <button onClick={() => openEdit(f)} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #f59e0b30", background:"#f59e0b10", color:"#f59e0b", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Update</button>
            </div>
          ))}
        </div>
      )}

      {/* Pipeline kanban header */}
      <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8, marginBottom:20 }}>
        {Object.entries(STATUS_CONFIG).map(([status, cfg]) => (
          <button key={status} onClick={() => setActiveStatus(status)} style={{
            padding:"12px 8px", borderRadius:8, border:`2px solid ${activeStatus===status ? cfg.color : "#1e1e2e"}`,
            background: activeStatus===status ? cfg.bg : "#0f0f1a",
            cursor:"pointer", transition:"all 0.2s", textAlign:"center",
          }}>
            <div style={{ color:cfg.color, fontSize:22, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{pipeline[status]||0}</div>
            <div style={{ color:activeStatus===status ? cfg.color : "#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", marginTop:4, letterSpacing:1 }}>{cfg.label.toUpperCase()}</div>
          </button>
        ))}
      </div>

      {/* Edit modal */}
      {editingMatch && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setEditingMatch(null)}>
          <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:12, padding:24, width:460, maxWidth:"90vw" }} onClick={e=>e.stopPropagation()}>
            <div style={{ color:"#fff", fontSize:14, fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:6 }}>Update Lead Status</div>
            <p style={{ color:"#4a4a6a", fontSize:12, marginBottom:16, lineHeight:1.5 }}>{(editingMatch.post_text||"").slice(0,120)}…</p>
            <div style={{ display:"grid", gap:12 }}>
              <div>
                <label style={lS}>STATUS</label>
                <select style={iS} value={editForm.status} onChange={e=>setEditForm(f=>({...f,status:e.target.value}))}>
                  {Object.entries(STATUS_CONFIG).map(([s,c])=><option key={s} value={s}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lS}>NOTES</label>
                <textarea style={{...iS, minHeight:70, resize:"vertical"}} placeholder="e.g. Called Mike, he wants a quote by Friday..." value={editForm.notes} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))}/>
              </div>
              <div>
                <label style={lS}>FOLLOW-UP REMINDER DATE</label>
                <input style={iS} type="date" value={editForm.follow_up_at} onChange={e=>setEditForm(f=>({...f,follow_up_at:e.target.value}))}/>
              </div>
            </div>
            <ContactInfoPanel match={editingMatch} api={api} toast={toast}/>
            <div style={{ display:"flex", gap:8, marginTop:16 }}>
              <button onClick={saveStatus} disabled={saving} style={{ flex:1, padding:"10px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>{saving?<><Spinner size={12}/>Saving…</>:"Save"}</button>
              <button onClick={()=>setEditingMatch(null)} style={{ padding:"10px 18px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Match cards for active status */}
      <div style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", marginBottom:12 }}>
        {STATUS_CONFIG[activeStatus]?.label.toUpperCase()} — {statusMatches.length} lead{statusMatches.length!==1?"s":""}
      </div>
      {statusMatches.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace", fontSize:13 }}>
          No leads in {STATUS_CONFIG[activeStatus]?.label} status
        </div>
      ) : statusMatches.map(m => {
        const cfg = STATUS_CONFIG[m.status||"new"];
        const pageName = pages.find(p=>p.id===m.page_id)?.name || m.page_name || "Unknown";
        const PICONS = {facebook:"🔵",reddit:"🟠",nextdoor:"🟢"};
        return (
          <div key={m.id} style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderLeft:`3px solid ${cfg.color}`, borderRadius:10, padding:"14px 18px", marginBottom:8 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <span style={{ fontSize:12 }}>{PICONS[m.platform||"facebook"]}</span>
                  <span style={{ color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{pageName}</span>
                  <span style={{ color:"#2a2a3e" }}>·</span>
                  <span style={{ color:"#3a3a5a", fontSize:11 }}>{timeAgo(m.found_at)}</span>
                  <span style={{ padding:"1px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.color}30` }}>{cfg.label}</span>
                </div>
                <p style={{ color:"#8888aa", fontSize:13, lineHeight:1.5, margin:"0 0 6px" }}>{(m.post_text||"").slice(0,180)}{(m.post_text||"").length>180?"…":""}</p>
                {m.notes && <div style={{ color:"#6366f1", fontSize:11, fontStyle:"italic" }}>📝 {m.notes}</div>}
                <QuickReplyBar match={m} templates={templates} api={api} toast={toast}/>
                {m.follow_up_at && <div style={{ color:"#f59e0b", fontSize:10, fontFamily:"'DM Mono',monospace", marginTop:4 }}>⏰ Follow up: {new Date(m.follow_up_at).toLocaleDateString()}</div>}
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                <div style={{ padding:"3px 8px", borderRadius:4, fontSize:10, fontWeight:700, background:`${scoreColor(m.score)}20`, color:scoreColor(m.score), fontFamily:"'DM Mono',monospace" }}>{m.score}</div>
                <button onClick={()=>openEdit(m)} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Update</button>
                {m.post_url && m.post_url !== "#" && (
                  <a href={m.post_url} target="_blank" rel="noreferrer" style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", textDecoration:"none", display:"inline-flex", alignItems:"center", gap:4 }}>
                    ↗ Reply
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SETTINGS — WHITE LABEL + WEEKLY EMAIL + GEO (added to existing Settings)
// ════════════════════════════════════════════════════════════════════════════

function WhiteLabelSettings({ api, toast, userPlan }) {
  const [branding, setBranding] = useState({ app_name:"Socialisten", logo_url:"", primary_color:"#6366f1", support_email:"" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewColor, setPreviewColor] = useState("#6366f1");

  useEffect(() => {
    api.get("/settings/branding")
      .then(d => { if(d){ setBranding(d); setPreviewColor(d.primary_color||"#6366f1"); } })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/branding", branding);
      toast.success("Branding saved");
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };

  return (
    <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:24, marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
        <span style={{ fontSize:24 }}>🎨</span>
        <div>
          <div style={{ color:"#fff", fontSize:14, fontWeight:600 }}>White-Label Branding</div>
          <div style={{ color:"#4a4a6a", fontSize:11 }}>Customize the app name, colors, and logo for your customers</div>
        </div>
        <div style={{ marginLeft:"auto" }}>
          <span style={{ padding:"3px 10px", borderRadius:5, background:"#6366f120", color:"#a5b4fc", fontSize:10, fontFamily:"'DM Mono',monospace", border:"1px solid #6366f130" }}>PRO FEATURE</span>
        </div>
      </div>

      
        <>
          {recent.map(m => {
            const pageName = pages.find(p=>p.id===m.page_id)?.name || m.page_name || "";
            const sc = m.score>=40?"#ff4d4d":m.score>=25?"#f59e0b":"#22c55e";
            return (
              <div key={m.id} onClick={()=>{ onNavigate("feed"); onClose(); }} style={{
                padding:"12px 16px", borderBottom:"1px solid #111120",
                cursor:"pointer", transition:"background 0.15s",
                display:"flex", gap:10, alignItems:"flex-start",
              }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:sc, marginTop:5, flexShrink:0, boxShadow:`0 0 5px ${sc}` }}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                    <span style={{ fontSize:11 }}>{PICONS[m.platform||"facebook"]}</span>
                    <span style={{ color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{pageName}</span>
                    <span style={{ color:"#3a3a5a", fontSize:10, marginLeft:"auto", whiteSpace:"nowrap" }}>{timeAgo(m.found_at)}</span>
                  </div>
                  <p style={{ color:"#8888aa", fontSize:12, margin:0, lineHeight:1.5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {m.post_text}
                  </p>
                  <div style={{ display:"flex", gap:4, marginTop:4 }}>
                    {(m.matched_terms||[]).slice(0,3).map(t=>(
                      <span key={t} style={{ background:"#1e1e3e", color:"#a5b4fc", fontSize:9, padding:"1px 6px", borderRadius:3, fontFamily:"'DM Mono',monospace" }}>{t}</span>
                    ))}
                  </div>
                </div>
                <div style={{ padding:"2px 6px", borderRadius:3, fontSize:9, fontWeight:700, background:`${sc}20`, color:sc, fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap" }}>
                  {m.score}
                </div>
              </div>
            );
          })}
          <div onClick={()=>{ onNavigate("feed"); onClose(); }} style={{
            padding:"12px 16px", textAlign:"center", color:"#6366f1",
            fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer",
            borderTop:"1px solid #1e1e2e",
          }}>
            View all matches →
          </div>
        </>
          </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EMAIL VERIFICATION BANNER
// ════════════════════════════════════════════════════════════════════════════
function VerificationBanner({ api, toast }) {
  const [show, setShow] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/usage").then(d => {
      if (d && !d.email_verified) setShow(true);
    }).catch(() => {});
  }, []);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/send-verification");
      setSent(true);
      toast.success("Verification email sent — check your inbox");
    } catch(e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  if (!show) return null;

  return (
    <div style={{
      background:"#1a100a", borderBottom:"1px solid #f59e0b30",
      padding:"10px 32px", display:"flex", alignItems:"center", gap:12,
    }}>
      <span style={{ fontSize:16 }}>⚠️</span>
      <span style={{ color:"#f59e0b", fontSize:12, fontFamily:"'DM Mono',monospace", flex:1 }}>
        Please verify your email address to unlock all features and receive alerts.
      </span>
      {!sent ? (
        <button onClick={resend} disabled={sending} style={{
          padding:"5px 14px", borderRadius:5, border:"1px solid #f59e0b50",
          background:"#f59e0b10", color:"#f59e0b", fontSize:11,
          fontFamily:"'DM Mono',monospace", cursor:sending?"not-allowed":"pointer",
          display:"flex", alignItems:"center", gap:6, flexShrink:0,
        }}>
          {sending ? <><Spinner size={10}/> Sending…</> : "Resend Email"}
        </button>
      ) : (
        <span style={{ color:"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace" }}>✓ Sent!</span>
      )}
      <button onClick={() => setShow(false)} style={{
        background:"transparent", border:"none", color:"#4a4a6a", cursor:"pointer", fontSize:16,
      }}>×</button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// USAGE LIMIT BAR (shown at top of Monitored Pages)
// ════════════════════════════════════════════════════════════════════════════
function UsageLimitBar({ api, onUpgrade }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/usage").then(setUsage).catch(() => {});
  }, []);

  if (!usage || usage.pages_limit >= 999) return null;

  const pct = Math.min(usage.pages_pct, 100);
  const isNearLimit = pct >= 80;
  const isAtLimit   = pct >= 100;
  const color = isAtLimit ? "#ff4d4d" : isNearLimit ? "#f59e0b" : "#6366f1";

  return (
    <div style={{
      background:isAtLimit?"#1a0a0a":isNearLimit?"#1a1200":"#0f0f1a",
      border:`1px solid ${color}20`, borderRadius:10, padding:"14px 18px", marginBottom:20,
    }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <span style={{ color:"#ccc", fontSize:12 }}>
          Page usage: <strong style={{ color }}>{usage.pages_used}</strong>
          <span style={{ color:"#4a4a6a" }}> / {usage.pages_limit} pages</span>
          <span style={{ color:"#3a3a5a", fontSize:11, fontFamily:"'DM Mono',monospace", textTransform:"capitalize", marginLeft:8 }}>({usage.plan} plan)</span>
        </span>
        {(isNearLimit || isAtLimit) && (
          <button onClick={onUpgrade} style={{
            padding:"5px 14px", borderRadius:5, border:"none",
            background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
            color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer",
          }}>
            {isAtLimit ? "Upgrade to add more" : "Upgrade plan"}
          </button>
        )}
      </div>
      <div style={{ height:6, background:"#1e1e2e", borderRadius:3, overflow:"hidden" }}>
        <div style={{
          height:"100%", width:`${pct}%`,
          background:isAtLimit?"#ff4d4d":isNearLimit?"#f59e0b":"linear-gradient(90deg,#6366f1,#8b5cf6)",
          borderRadius:3, transition:"width 0.5s",
        }}/>
      </div>
      {isAtLimit && (
        <div style={{ color:"#ff4d4d", fontSize:11, marginTop:6 }}>
          You've reached your page limit. Upgrade to add more monitored pages.
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ERROR BOUNDARY + ERROR PAGES
// ════════════════════════════════════════════════════════════════════════════
function ErrorPage({ code = 500, message = "Something went wrong", onRetry }) {
  const configs = {
    404: { emoji:"🔍", title:"Page Not Found",         msg:"This page doesn't exist or was moved." },
    500: { emoji:"⚡", title:"Something Went Wrong",   msg:"An unexpected error occurred. Our team has been notified." },
    403: { emoji:"🔒", title:"Access Denied",          msg:"You don't have permission to view this." },
    503: { emoji:"🔧", title:"Service Unavailable",    msg:"The server is temporarily unavailable. Please try again shortly." },
  };
  const cfg = configs[code] || configs[500];

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"80px 24px", minHeight:400, textAlign:"center" }}>
      <div style={{ fontSize:64, marginBottom:16 }}>{cfg.emoji}</div>
      <div style={{ color:"#fff", fontSize:22, fontWeight:700, fontFamily:"'DM Mono',monospace", marginBottom:8 }}>{cfg.title}</div>
      <div style={{ color:"#4a4a6a", fontSize:14, maxWidth:360, lineHeight:1.7, marginBottom:28 }}>
        {message || cfg.msg}
      </div>
      <div style={{ display:"flex", gap:10 }}>
        {onRetry && (
          <button onClick={onRetry} style={{ padding:"10px 24px", borderRadius:8, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
            Try Again
          </button>
        )}
        <button onClick={() => window.location.href = "/"} style={{ padding:"10px 24px", borderRadius:8, border:"1px solid #2a2a3e", background:"transparent", color:"#ccc", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
          Go Home
        </button>
      </div>
      <div style={{ marginTop:32, color:"#3a3a5a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
        Error code: {code} · <span style={{ color:"#6366f1", cursor:"pointer" }} onClick={() => window.location.reload()}>Reload page</span>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// FORGOT / RESET PASSWORD MODAL
// ════════════════════════════════════════════════════════════════════════════
function ForgotPasswordModal({ onClose, toast }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await fetch(`${API_BASE}/auth/forgot-password`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } catch(e) { toast.error("Failed — please try again"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:16, padding:32, width:400, maxWidth:"90vw" }} onClick={e=>e.stopPropagation()}>
        <div style={{ color:"#fff", fontSize:16, fontWeight:600, fontFamily:"'DM Mono',monospace", marginBottom:8 }}>Reset Password</div>
        {!sent ? (
          <>
            <p style={{ color:"#4a4a6a", fontSize:13, marginBottom:20, lineHeight:1.6 }}>
              Enter your email and we'll send you a link to reset your password.
            </p>
            <input
              type="email" placeholder="your@email.com" value={email}
              onChange={e=>setEmail(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&submit()}
              style={{ width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:8, padding:"11px 14px", color:"#ccc", fontSize:13, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box", marginBottom:16 }}
            />
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={submit} disabled={loading||!email} style={{ flex:1, padding:"11px", borderRadius:8, border:"none", background:(!email||loading)?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:(!email||loading)?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                {loading?<><Spinner size={14}/> Sending…</>:"Send Reset Link"}
              </button>
              <button onClick={onClose} style={{ padding:"11px 16px", borderRadius:8, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📬</div>
            <div style={{ color:"#22c55e", fontSize:14, marginBottom:8 }}>Email sent!</div>
            <p style={{ color:"#4a4a6a", fontSize:13, lineHeight:1.6 }}>
              Check your inbox for a password reset link. It expires in 2 hours.
            </p>
            <button onClick={onClose} style={{ marginTop:16, padding:"10px 24px", borderRadius:8, border:"1px solid #2a2a3e", background:"transparent", color:"#ccc", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// RESET PASSWORD PAGE (shown when ?reset_token=xxx in URL)
// ════════════════════════════════════════════════════════════════════════════
function ResetPasswordPage({ token, toast }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      setDone(true);
      toast.success("Password updated — you can now log in");
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:8, padding:"11px 14px", color:"#ccc", fontSize:13, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box", marginBottom:12 };

  return (
    <div style={{ minHeight:"100vh", background:"#050508", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Mono',monospace" }}>
      <div style={{ position:"fixed", inset:0, backgroundImage:"linear-gradient(#0f0f1a 1px,transparent 1px),linear-gradient(90deg,#0f0f1a 1px,transparent 1px)", backgroundSize:"40px 40px", opacity:0.4, pointerEvents:"none" }}/>
      <div style={{ position:"relative", width:420, padding:40, background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:16 }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ width:48, height:48, borderRadius:12, background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:700, margin:"0 auto 14px", color:"#fff" }}>S</div>
          <div style={{ color:"#fff", fontSize:16, fontWeight:700, letterSpacing:2, marginBottom:4 }}>Set New Password</div>
        </div>
        {!done ? (
          <>
            <input type="password" placeholder="New password (min 8 chars)" value={password} onChange={e=>setPassword(e.target.value)} style={iS}/>
            <input type="password" placeholder="Confirm new password" value={confirm} onChange={e=>setConfirm(e.target.value)} style={iS} onKeyDown={e=>e.key==="Enter"&&submit()}/>
            {error && <div style={{ color:"#ff4d4d", fontSize:11, marginBottom:12 }}>{error}</div>}
            <button onClick={submit} disabled={loading} style={{ width:"100%", padding:"12px", borderRadius:8, border:"none", background:loading?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:loading?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              {loading?<><Spinner size={16}/>Updating…</>:"Update Password"}
            </button>
          </>
        ) : (
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
            <div style={{ color:"#22c55e", fontSize:14, marginBottom:8 }}>Password updated!</div>
            <button onClick={()=>window.location.href="/"} style={{ marginTop:16, padding:"10px 24px", borderRadius:8, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// ONBOARDING CHECKLIST
// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
// ONBOARDING V2 — Full welcome + checklist experience
// Replaces the basic OnboardingChecklist component
// ════════════════════════════════════════════════════════════════════════════
// ONBOARDING — Polished welcome flow + persistent progress checklist
// Phase 1: Full-screen welcome wizard (steps 1–3, shown once on first login)
// Phase 2: Compact sidebar checklist (persists until all steps done)
// ════════════════════════════════════════════════════════════════════════════

const OB_STEPS = [
  {
    id:      "verify",
    icon:    "📧",
    label:   "Verify your email",
    desc:    "Click the link in your welcome email to activate your account.",
    detail:  "Check your inbox — the email is from noreply@socialisten.com. Check spam if you don't see it.",
    nav:     "settings",
    cta:     "Resend email",
    skippable: false,
    color:   "#6366f1",
  },
  {
    id:      "add_page",
    icon:    "📡",
    label:   "Add your first monitored page",
    desc:    "Add a Facebook group, subreddit, or Nextdoor feed to watch for leads.",
    detail:  "Start with your city's main homeowners Facebook group. Search for '[city] homeowners' on Facebook and copy the URL.",
    nav:     "pages",
    cta:     "Add a page",
    skippable: false,
    color:   "#22c55e",
    example: "https://www.facebook.com/groups/detroithomeowners",
  },
  {
    id:      "add_keywords",
    icon:    "🎯",
    label:   "Add keywords",
    desc:    "Type phrases homeowners use when they need your service.",
    detail:  "Think like a frustrated homeowner: 'furnace not working', 'pipe burst', 'no hot water'. Use the suggestions panel for pre-built sets.",
    nav:     "pages",
    cta:     "Add keywords",
    skippable: false,
    color:   "#f59e0b",
    examples: ["furnace not working", "no heat", "HVAC help", "pipe burst"],
  },
  {
    id:      "setup_alerts",
    icon:    "🔔",
    label:   "Enable instant alerts",
    desc:    "Get alerted within seconds of a match — not minutes.",
    detail:  "Email is too slow to win leads. Set up push notifications (free, works on all devices) or Telegram (free, works on iPhone). Both take under a minute.",
    nav:     "settings",
    cta:     "Set up alerts",
    skippable: true,
    color:   "#0ea5e9",
    options: [
      { icon:"🔔", label:"Push notifications", desc:"Works on all phones and desktops", free:true },
      { icon:"✈️", label:"Telegram",           desc:"Best for iPhone — instant delivery", free:true },
      { icon:"💬", label:"SMS",                desc:"Text alerts (requires Twilio)", free:false },
    ],
  },
  {
    id:      "install_app",
    icon:    "📲",
    label:   "Install on your phone",
    desc:    "Add Socialisten to your home screen for native app experience.",
    detail:  "iPhone: Open in Safari → Share → Add to Home Screen. Android: Chrome → three-dot menu → Add to Home screen.",
    nav:     "settings",
    cta:     "Install now",
    skippable: true,
    color:   "#8b5cf6",
    pwa_step: true,
  },
  {
    id:      "first_scan",
    icon:    "▶",
    label:   "Run your first scan",
    desc:    "Click Scan Now on any page to see results immediately.",
    detail:  "Your first scan happens within seconds. Matches appear in your Feed with a score from 0–100 based on how urgent the post is.",
    nav:     "pages",
    cta:     "Scan now",
    skippable: false,
    color:   "#8b5cf6",
  },
  {
    id:      "first_match",
    icon:    "🎯",
    label:   "Get your first lead",
    desc:    "Your first keyword match will appear in the Feed.",
    detail:  "When a match arrives, tap Reply Now to respond to the original post. Respond within 5 minutes to maximize your chances of winning the job.",
    nav:     "feed",
    cta:     "View feed",
    skippable: false,
    color:   "#22c55e",
  },
];

// Determine which steps are done from API status + local state
function buildStepStatus(apiStatus, localFlags) {
  return OB_STEPS.map(step => {
    let done = false;
    const apiStep = apiStatus?.steps?.find(s => s.id === step.id);
    if (apiStep) done = apiStep.done || apiStep.skipped;
    // Override with local flags
    if (step.id === "install_app") done = localFlags.pwaInstalled;
    if (step.id === "setup_alerts") done = localFlags.alertsEnabled || done;
    return { ...step, done, skipped: apiStep?.skipped || false };
  });
}

// ── Welcome Wizard (full-screen, shown once) ─────────────────────────────────
function OnboardingWizard({ api, toast, onComplete, onNavigate }) {
  const [step,      setStep]      = useState(0);
  const [animDir,   setAnimDir]   = useState(1);   // 1=forward -1=back
  const [visible,   setVisible]   = useState(false);
  const [apiStatus, setApiStatus] = useState(null);

  useEffect(() => {
    setTimeout(() => setVisible(true), 50);
    api.get("/onboarding/status").then(setApiStatus).catch(() => {});
  }, []);

  const localFlags = {
    pwaInstalled:  localStorage.getItem("pwa_installed") === "1",
    alertsEnabled: localStorage.getItem("sl_push_enabled") === "1",
  };
  const steps = buildStepStatus(apiStatus, localFlags);
  const current = steps[step];
  const total   = steps.length;
  const pct     = Math.round(((step + 1) / total) * 100);

  const go = (dir) => {
    setAnimDir(dir);
    setTimeout(() => setStep(s => Math.max(0, Math.min(total - 1, s + dir))), 80);
  };
  const skip = async () => {
    if (current.skippable) {
      try { await api.post(`/onboarding/skip/${current.id}`); } catch {}
    }
    if (step < total - 1) go(1);
    else finish();
  };
  const finish = () => {
    setVisible(false);
    setTimeout(onComplete, 300);
  };
  const handleCTA = () => {
    if (current.pwa_step) {
      window.dispatchEvent(new CustomEvent("sl-show-pwa-install", {}));
    } else if (current.nav) {
      onNavigate(current.nav);
      if (step < total - 1) go(1);
      else finish();
    } else {
      if (step < total - 1) go(1);
      else finish();
    }
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9000,
      background:"rgba(5,5,8,0.92)", backdropFilter:"blur(8px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:16, opacity:visible?1:0, transition:"opacity 0.3s ease",
    }}>
      <div style={{
        background:"#0f0f1a", border:"1px solid #2a2a3e",
        borderRadius:20, width:"100%", maxWidth:520,
        overflow:"hidden", boxShadow:"0 24px 80px rgba(0,0,0,0.6)",
      }}>
        {/* Progress bar */}
        <div style={{ height:3, background:"#1e1e2e" }}>
          <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,#6366f1,${current.color})`, transition:"width 0.4s ease" }}/>
        </div>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 24px 0" }}>
          <div style={{ color:"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
            Step {step + 1} of {total}
          </div>
          <button onClick={finish}
            style={{ background:"transparent", border:"none", color:"#4a4a6a", fontSize:20, cursor:"pointer", lineHeight:1 }}>×</button>
        </div>

        {/* Step content */}
        <div style={{ padding:"20px 28px 28px" }}>
          {/* Icon + title */}
          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:18 }}>
            <div style={{ width:52, height:52, borderRadius:14, background:`${current.color}20`, border:`1px solid ${current.color}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, flexShrink:0 }}>
              {current.done ? "✅" : current.icon}
            </div>
            <div>
              <div style={{ color:current.done?"#22c55e":"#f8fafc", fontSize:18, fontWeight:800, marginBottom:4 }}>
                {current.done ? "Done! " : ""}{current.label}
              </div>
              <div style={{ color:"#64748b", fontSize:13, lineHeight:1.5 }}>{current.desc}</div>
            </div>
          </div>

          {/* Detail box */}
          <div style={{ background:"#070710", border:"1px solid #1e1e2e", borderRadius:10, padding:"14px 16px", marginBottom:18 }}>
            <div style={{ color:"#94a3b8", fontSize:13, lineHeight:1.7 }}>{current.detail}</div>

            {/* Keyword examples */}
            {current.examples && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:12 }}>
                {current.examples.map(ex => (
                  <span key={ex} style={{ background:"#1e1e2e", border:"1px solid #2a2a3e", borderRadius:6, padding:"3px 10px", color:"#a5b4fc", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                    {ex}
                  </span>
                ))}
              </div>
            )}

            {/* URL example */}
            {current.example && (
              <div style={{ marginTop:10, background:"#0a0a14", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", fontFamily:"'DM Mono',monospace", fontSize:11, color:"#6366f1", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {current.example}
              </div>
            )}

            {/* Alert options */}
            {current.options && (
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:12 }}>
                {current.options.map(opt => (
                  <div key={opt.label} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"#1e1e2e", borderRadius:8 }}>
                    <span style={{ fontSize:18 }}>{opt.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600 }}>{opt.label}</div>
                      <div style={{ color:"#64748b", fontSize:11 }}>{opt.desc}</div>
                    </div>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:opt.free?"#22c55e20":"#1e1e2e", color:opt.free?"#22c55e":"#64748b", fontWeight:700 }}>
                      {opt.free ? "FREE" : "PAID"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Step dots */}
          <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:20 }}>
            {steps.map((s, i) => (
              <div key={i} style={{ width: i === step ? 20 : 8, height:8, borderRadius:4, background: s.done ? "#22c55e" : i === step ? current.color : "#1e1e2e", transition:"all 0.3s" }}/>
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display:"flex", gap:8 }}>
            {step > 0 && (
              <button onClick={() => go(-1)}
                style={{ padding:"11px 18px", borderRadius:10, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:13, cursor:"pointer" }}>
                ← Back
              </button>
            )}
            {current.skippable && !current.done && (
              <button onClick={skip}
                style={{ padding:"11px 16px", borderRadius:10, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:13, cursor:"pointer" }}>
                Skip
              </button>
            )}
            <button onClick={step < total - 1 ? handleCTA : finish}
              style={{ flex:1, padding:"12px 20px", borderRadius:10, border:"none", background:`linear-gradient(135deg,#6366f1,${current.color})`, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              {current.done
                ? (step < total - 1 ? "Next →" : "Go to dashboard →")
                : step < total - 1 ? current.cta + " →" : "Start monitoring →"}
            </button>
          </div>

          {step === total - 1 && (
            <div style={{ textAlign:"center", marginTop:12 }}>
              <button onClick={finish} style={{ background:"transparent", border:"none", color:"#3a3a5a", fontSize:12, cursor:"pointer" }}>
                Skip to dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Compact checklist (shown after wizard, until all steps done) ─────────────
function OnboardingChecklist({ api, toast, onNavigate }) {
  const LS_WIZARD    = "sl_ob_wizard_done";
  const LS_DISMISSED = "sl_ob_dismissed";

  const [apiStatus,  setApiStatus]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [minimised,  setMinimised]  = useState(false);
  const [dismissed,  setDismissed]  = useState(() => localStorage.getItem(LS_DISMISSED) === "1");
  const [showWizard, setShowWizard] = useState(() => !localStorage.getItem(LS_WIZARD) && !localStorage.getItem(LS_DISMISSED));
  const [celebrate,  setCelebrate]  = useState(false);

  useEffect(() => {
    api.get("/onboarding/status")
      .then(s => { setApiStatus(s); if (s.dismissed) setDismissed(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [showWizard]);

  const localFlags = {
    pwaInstalled:  localStorage.getItem("pwa_installed") === "1",
    alertsEnabled: localStorage.getItem("sl_push_enabled") === "1",
  };
  const steps     = buildStepStatus(apiStatus, localFlags);
  const completed = steps.filter(s => s.done).length;
  const allDone   = completed === steps.length;
  const pct       = Math.round((completed / steps.length) * 100);
  const nextStep  = steps.find(s => !s.done && !s.skipped);

  const dismiss = async () => {
    localStorage.setItem(LS_DISMISSED, "1");
    setDismissed(true);
    try { await api.post("/onboarding/dismiss"); } catch {}
  };

  const skipStep = async (id) => {
    try {
      await api.post(`/onboarding/skip/${id}`);
      setApiStatus(s => s ? { ...s, steps: s.steps.map(st => st.id===id ? {...st, done:true, skipped:true} : st) } : s);
    } catch {}
  };

  const handleWizardComplete = () => {
    localStorage.setItem(LS_WIZARD, "1");
    setShowWizard(false);
    // Reload status after wizard
    api.get("/onboarding/status").then(setApiStatus).catch(() => {});
  };

  // Celebrate on completion
  useEffect(() => {
    if (allDone && !celebrate && !dismissed) {
      setCelebrate(true);
      setTimeout(dismiss, 5000);
    }
  }, [allDone]);

  if (loading) return null;
  if (dismissed) return null;
  if (apiStatus?.dismissed) return null;

  return (
    <>
      {/* Welcome wizard — full screen, shown once */}
      {showWizard && (
        <OnboardingWizard
          api={api} toast={toast}
          onComplete={handleWizardComplete}
          onNavigate={(v) => { handleWizardComplete(); onNavigate(v); }}
        />
      )}

      {/* Completion celebration */}
      {celebrate && !showWizard && (
        <div style={{ background:"linear-gradient(135deg,#0a1a0a,#051505)", border:"1px solid #22c55e40", borderRadius:14, padding:"20px 24px", marginBottom:20, display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ fontSize:36 }}>🎉</div>
          <div style={{ flex:1 }}>
            <div style={{ color:"#22c55e", fontSize:16, fontWeight:800, marginBottom:4 }}>Setup complete!</div>
            <div style={{ color:"#4a6a4a", fontSize:13 }}>You're all set. Leads will start appearing in your feed. Respond fast — the first reply wins.</div>
          </div>
          <button onClick={()=>{ setCelebrate(false); dismiss(); }} style={{ padding:"10px 20px", borderRadius:8, border:"none", background:"#22c55e", color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            Go to feed →
          </button>
        </div>
      )}

      {/* Persistent checklist card */}
      {!celebrate && !showWizard && (
        <div style={{ background:"#0a0a1a", border:"1px solid #6366f130", borderRadius:14, padding:"16px 18px", marginBottom:20 }}>

          {/* Header row */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: minimised ? 0 : 14 }}>
            <div style={{ width:34, height:34, borderRadius:9, background:"#6366f120", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>🚀</div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ color:"#f1f5f9", fontSize:13, fontWeight:700 }}>Get set up</span>
                <span style={{ color:"#4a4a6a", fontSize:11 }}>{completed}/{steps.length} done</span>
              </div>
              {/* Mini progress bar */}
              <div style={{ width:"100%", height:4, background:"#1e1e2e", borderRadius:2, marginTop:5, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${pct}%`, background:"linear-gradient(90deg,#6366f1,#22c55e)", borderRadius:2, transition:"width 0.5s ease" }}/>
              </div>
            </div>
            <div style={{ display:"flex", gap:4, flexShrink:0 }}>
              <button onClick={() => setShowWizard(true)}
                style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:"#6366f1", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                Guide
              </button>
              <button onClick={() => setMinimised(p=>!p)}
                style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:10, cursor:"pointer" }}>
                {minimised ? "▼" : "▲"}
              </button>
              <button onClick={dismiss}
                style={{ background:"transparent", border:"none", color:"#3a3a5a", fontSize:16, cursor:"pointer", padding:"4px" }}>
                ×
              </button>
            </div>
          </div>

          {/* Step list */}
          {!minimised && (
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              {steps.map((step, i) => {
                const isNext = step.id === nextStep?.id;
                return (
                  <div key={step.id}
                    onClick={() => !step.done && (step.pwa_step ? window.dispatchEvent(new CustomEvent("sl-show-pwa-install")) : onNavigate(step.nav))}
                    style={{
                      display:"flex", alignItems:"center", gap:10,
                      padding:"10px 12px", borderRadius:9, cursor: step.done ? "default" : "pointer",
                      background: step.done ? "#050f05" : isNext ? "#0a0a20" : "transparent",
                      border:`1px solid ${step.done?"#22c55e18":isNext?`${step.color}30`:"transparent"}`,
                      transition:"all 0.15s",
                    }}
                    onMouseEnter={e=>{ if(!step.done) e.currentTarget.style.background="#0f0f1a"; }}
                    onMouseLeave={e=>{ if(!step.done) e.currentTarget.style.background=step.done?"#050f05":isNext?"#0a0a20":"transparent"; }}
                  >
                    {/* Check / icon */}
                    <div style={{
                      width:26, height:26, borderRadius:"50%", flexShrink:0,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      background: step.done?"#22c55e":isNext?`${step.color}20`:"#1e1e2e",
                      border:`2px solid ${step.done?"#22c55e":isNext?step.color:"#2a2a3e"}`,
                      fontSize:12, color: step.done?"#fff":isNext?step.color:"#4a4a6a",
                    }}>
                      {step.done ? "✓" : step.icon}
                    </div>

                    {/* Label + desc */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ color:step.done?"#22c55e":isNext?"#f1f5f9":"#64748b", fontSize:12, fontWeight:isNext||step.done?600:400 }}>
                        {step.label}
                      </div>
                      {isNext && (
                        <div style={{ color:"#4a4a6a", fontSize:10, marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {step.desc}
                        </div>
                      )}
                    </div>

                    {/* CTA */}
                    {!step.done && (
                      <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                        <button
                          onClick={e=>{ e.stopPropagation(); step.pwa_step ? window.dispatchEvent(new CustomEvent("sl-show-pwa-install")) : onNavigate(step.nav); }}
                          style={{ padding:"4px 10px", borderRadius:5, border:"none", background:isNext?step.color:"#2a2a3e", color:"#fff", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                          {isNext ? step.cta+" →" : "→"}
                        </button>
                        {step.skippable && (
                          <button onClick={e=>{ e.stopPropagation(); skipStep(step.id); }}
                            style={{ padding:"4px 8px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:10, cursor:"pointer" }}>
                            Skip
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Re-open wizard link */}
          {!minimised && (
            <div style={{ textAlign:"center", marginTop:10 }}>
              <button onClick={() => setShowWizard(true)}
                style={{ background:"transparent", border:"none", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>
                Open setup guide →
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// ADMIN ONBOARDING FUNNEL
// ════════════════════════════════════════════════════════════════════════════
function OnboardingFunnel({ api, toast }) {
  const [data,     setData]     = useState(null);
  const [dripLog,  setDripLog]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState("funnel");
  const [sending,  setSending]  = useState(null);
  const [selected, setSelected] = useState({userId:null,template:""});

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/onboarding/funnel"),
      api.get("/admin/onboarding/drip-log"),
    ]).then(([f,d])=>{ setData(f); setDripLog(d||[]); })
      .catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  },[]);

  const sendDrip = async () => {
    if(!selected.userId||!selected.template){toast.error("Select user and template");return;}
    setSending(selected.userId);
    try {
      await api.post(`/admin/onboarding/send-drip?user_id=${selected.userId}&template_key=${selected.template}`);
      toast.success("Drip email queued");
      setSelected({userId:null,template:""});
    } catch(e){toast.error(e.message);}
    finally{setSending(null);}
  };

  const resetOnboarding = async (userId) => {
    try {
      await api.post(`/onboarding/reset?user_id=${userId}`);
      toast.success("Onboarding reset — checklist will reappear for user");
    } catch(e){toast.error(e.message);}
  };

  const cardS = {background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"20px 24px",marginBottom:16};
  const PLAN_COLORS = {free:"#4a4a6a",starter:"#22c55e",pro:"#6366f1"};

  const ACT_CFG = {
    pending:    {color:"#ff4d4d", label:"Pending",    desc:"Signed up but not verified",       icon:"⏳"},
    registered: {color:"#f59e0b", label:"Registered", desc:"Verified but not set up",          icon:"✉️"},
    partial:    {color:"#6366f1", label:"Partial",    desc:"Started setup, not complete",      icon:"🔧"},
    active:     {color:"#0ea5e9", label:"Active",     desc:"Set up, awaiting first match",     icon:"📡"},
    activated:  {color:"#22c55e", label:"Activated",  desc:"Set up and getting leads",         icon:"🎯"},
  };

  const TEMPLATES = [
    {id:"drip_day1",  label:"Day 1 — Get started (3 steps)"},
    {id:"drip_day3",  label:"Day 3 — More keywords nudge"},
    {id:"drip_day7",  label:"Day 7 — Trial ending soon"},
    {id:"drip_day14", label:"Day 14 — Tips for more matches"},
  ];

  if (loading) return <div style={{display:"flex",justifyContent:"center",padding:60}}><Spinner size={32}/></div>;

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{display:"flex",gap:6,marginBottom:20}}>
        {["funnel","drip","stuck"].map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:"7px 16px",borderRadius:6,border:`1px solid ${tab===t?"#6366f1":"#1e1e2e"}`,background:tab===t?"#6366f120":"transparent",color:tab===t?"#a5b4fc":"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer",textTransform:"capitalize"}}>
            {t==="drip"?"Drip Emails":t==="stuck"?`Stuck Users (${data?.stuck_users?.length||0})`:t}
          </button>
        ))}
      </div>

      {/* Funnel tab */}
      {tab==="funnel" && (
        <div>
          {/* KPI row */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}}>
            <div style={{...cardS,marginBottom:0}}>
              <div style={{color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,marginBottom:8}}>ACTIVATION RATE</div>
              <div style={{color:"#22c55e",fontSize:32,fontWeight:700,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{data?.activated_pct||0}%</div>
              <div style={{color:"#3a3a5a",fontSize:11,marginTop:6}}>of users fully set up</div>
            </div>
            <div style={{...cardS,marginBottom:0}}>
              <div style={{color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,marginBottom:8}}>TOTAL USERS</div>
              <div style={{color:"#6366f1",fontSize:32,fontWeight:700,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{data?.total||0}</div>
            </div>
            <div style={{...cardS,marginBottom:0}}>
              <div style={{color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,marginBottom:8}}>STUCK IN SETUP</div>
              <div style={{color:data?.stuck_users?.length>0?"#f59e0b":"#22c55e",fontSize:32,fontWeight:700,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{data?.stuck_users?.length||0}</div>
              <div style={{color:"#3a3a5a",fontSize:11,marginTop:6}}>3+ days, not complete</div>
            </div>
          </div>

          {/* Stage funnel */}
          {data?.stages && Object.entries(ACT_CFG).map(([stage, cfg])=>{
            const stageData = data.stages[stage]||{count:0,users:[]};
            const barWidth  = data.total>0?Math.round((stageData.count/data.total)*100):0;
            return (
              <div key={stage} style={{...cardS,marginBottom:10}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                  <span style={{fontSize:20}}>{cfg.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{color:"#fff",fontSize:13,fontWeight:600}}>{cfg.label}</span>
                      <span style={{color:cfg.color,fontSize:22,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>{stageData.count}</span>
                      <span style={{color:"#4a4a6a",fontSize:11}}>users</span>
                    </div>
                    <div style={{color:"#4a4a6a",fontSize:11}}>{cfg.desc}</div>
                  </div>
                  <span style={{color:"#4a4a6a",fontSize:12,fontFamily:"'DM Mono',monospace"}}>{barWidth}%</span>
                </div>
                <div style={{height:8,background:"#1e1e2e",borderRadius:4,overflow:"hidden",marginBottom:stageData.users?.length>0?12:0}}>
                  <div style={{height:"100%",width:`${barWidth}%`,background:cfg.color,borderRadius:4}}/>
                </div>
                {stageData.users?.length>0&&(
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {stageData.users.slice(0,8).map(u=>(
                      <div key={u.id} style={{padding:"4px 10px",borderRadius:5,background:"#0a0a0f",border:"1px solid #1e1e2e",display:"flex",gap:6,alignItems:"center"}}>
                        <span style={{color:"#ccc",fontSize:11}}>{u.email.split("@")[0]}</span>
                        <span style={{color:PLAN_COLORS[u.plan]||"#4a4a6a",fontSize:9,fontFamily:"'DM Mono',monospace"}}>{u.plan}</span>
                        <span style={{color:"#3a3a5a",fontSize:9,fontFamily:"'DM Mono',monospace"}}>{u.pct}%</span>
                      </div>
                    ))}
                    {stageData.count>8&&<div style={{color:"#3a3a5a",fontSize:11,display:"flex",alignItems:"center"}}>+{stageData.count-8} more</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Drip emails tab */}
      {tab==="drip" && (
        <div>
          {/* Manual send */}
          <div style={{...cardS}}>
            <div style={{color:"#fff",fontSize:13,fontWeight:600,marginBottom:14}}>Send Drip Email Manually</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10,alignItems:"end"}}>
              <div>
                <label style={{color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5}}>USER ID</label>
                <input type="number" placeholder="e.g. 42" value={selected.userId||""} onChange={e=>setSelected(s=>({...s,userId:parseInt(e.target.value)||null}))} style={{width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5}}>TEMPLATE</label>
                <select value={selected.template} onChange={e=>setSelected(s=>({...s,template:e.target.value}))} style={{width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none"}}>
                  <option value="">Select template...</option>
                  {TEMPLATES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <button onClick={sendDrip} disabled={!!sending} style={{padding:"9px 18px",borderRadius:6,border:"none",background:sending?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:sending?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
                {sending?<><Spinner size={11}/>Sending…</>:"📧 Send"}
              </button>
            </div>
          </div>

          {/* Templates preview */}
          <div style={{...cardS}}>
            <div style={{color:"#fff",fontSize:13,fontWeight:600,marginBottom:14}}>Drip Sequence</div>
            {TEMPLATES.map((t,i)=>(
              <div key={t.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid #111120"}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12,fontWeight:700,flexShrink:0}}>{i+1}</div>
                <div style={{flex:1}}>
                  <div style={{color:"#ccc",fontSize:12,fontWeight:600}}>{t.label}</div>
                  <div style={{color:"#3a3a5a",fontSize:10,fontFamily:"'DM Mono',monospace"}}>{t.id}</div>
                </div>
                <div style={{color:"#4a4a6a",fontSize:11}}>
                  {dripLog.filter(d=>d.template===t.id).length} sent
                </div>
              </div>
            ))}
            <div style={{marginTop:12,padding:"10px 12px",background:"#0a0a1a",borderRadius:8,fontSize:11,color:"#4a4a6a",lineHeight:1.8}}>
              Drip emails are sent automatically by Celery beat every day at 9am. They fire once per user per template — a user who receives Day 1 will never get it again even if they don't open it.
            </div>
          </div>

          {/* Recent sends */}
          {dripLog.length > 0 && (
            <div style={{...cardS}}>
              <div style={{color:"#fff",fontSize:13,fontWeight:600,marginBottom:14}}>Recent Sends ({dripLog.length})</div>
              {dripLog.slice(0,20).map((d,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #111"}}>
                  <div style={{flex:1}}>
                    <span style={{color:"#ccc",fontSize:12}}>{d.email}</span>
                    <span style={{color:"#4a4a6a",fontSize:11,marginLeft:8}}>{d.template}</span>
                  </div>
                  <span style={{color:"#3a3a5a",fontSize:10,fontFamily:"'DM Mono',monospace"}}>{d.sent_at?new Date(d.sent_at).toLocaleDateString():""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stuck users tab */}
      {tab==="stuck" && (
        <div>
          <div style={{color:"#4a4a6a",fontSize:12,marginBottom:16,lineHeight:1.7}}>
            These users signed up 3+ days ago but haven't completed onboarding. They're the highest-priority for manual outreach — a single email or phone call converts most of them.
          </div>
          {!data?.stuck_users?.length ? (
            <div style={{textAlign:"center",padding:"60px 0",color:"#3a3a5a",fontFamily:"'DM Mono',monospace"}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              No stuck users — everyone is either set up or just signed up
            </div>
          ) : data.stuck_users.map(u=>(
            <div key={u.id} style={{...cardS,marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:38,height:38,borderRadius:"50%",background:"linear-gradient(135deg,#f59e0b,#d97706)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:16,fontWeight:700,flexShrink:0}}>{u.email[0].toUpperCase()}</div>
                <div style={{flex:1}}>
                  <div style={{color:"#fff",fontSize:13,fontWeight:600}}>{u.email}</div>
                  <div style={{display:"flex",gap:12,color:"#4a4a6a",fontSize:11,marginTop:3}}>
                    <span>Day {u.days} of trial</span>
                    <span style={{textTransform:"capitalize"}}>Stage: {u.activation}</span>
                    <span style={{color:PLAN_COLORS[u.plan]||"#4a4a6a",textTransform:"capitalize"}}>{u.plan} plan</span>
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <select onChange={e=>setSelected({userId:u.id,template:e.target.value})} style={{background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:5,padding:"5px 8px",color:"#ccc",fontSize:11,fontFamily:"'DM Mono',monospace",outline:"none"}}>
                    <option value="">Send drip...</option>
                    {TEMPLATES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  {selected.userId===u.id&&selected.template&&(
                    <button onClick={sendDrip} disabled={!!sending} style={{padding:"5px 12px",borderRadius:5,border:"none",background:"#6366f1",color:"#fff",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                      {sending===u.id?<Spinner size={10}/>:"Send"}
                    </button>
                  )}
                  <button onClick={()=>resetOnboarding(u.id)} style={{padding:"5px 12px",borderRadius:5,border:"1px solid #2a2a3e",background:"transparent",color:"#4a4a6a",fontSize:11,cursor:"pointer"}}>Reset</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Feature Flags ─────────────────────────────────────────────────────────────
function CMSFeatureFlags({ api, toast }) {
  const [flags, setFlags]   = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/cms/flags").then(setFlags).catch(()=>toast.error("Failed to load")); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/cms/flags", flags); toast.success("Flags saved"); }
    catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    try { await api.del("/admin/cms/flags"); const d = await api.get("/admin/cms/flags"); setFlags(d); toast.success("Reset"); }
    catch(e) { toast.error(e.message); }
  };

  if (!flags) return <div style={{ display:"flex", justifyContent:"center", padding:40 }}><Spinner size={28}/></div>;

  const GROUPS = {
    "Platforms": ["facebook_enabled","reddit_enabled","nextdoor_enabled"],
    "Alert Types": ["sms_alerts_enabled","voice_alerts_enabled","whatsapp_enabled"],
    "App Features": ["pipeline_enabled","analytics_enabled","inbox_enabled"],
    "Business": ["pricing_visible","free_plan_enabled","new_signups_enabled"],
    "Maintenance": ["maintenance_mode"],
  };

  const LABELS = {
    facebook_enabled:"Facebook monitoring", reddit_enabled:"Reddit monitoring", nextdoor_enabled:"Nextdoor monitoring",
    sms_alerts_enabled:"SMS alerts", voice_alerts_enabled:"Voice call alerts", whatsapp_enabled:"WhatsApp alerts",
    pipeline_enabled:"Lead pipeline (CRM)", analytics_enabled:"Analytics dashboard", inbox_enabled:"Page inbox",
    pricing_visible:"Show pricing page", free_plan_enabled:"Allow free signups", new_signups_enabled:"Allow new registrations",
    maintenance_mode:"🚨 Maintenance mode",
  };

  return (
    <div>
      <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:16, lineHeight:1.7 }}>
        Turn features on or off instantly without redeploying. Changes take effect immediately for all users.
        <span style={{ color:"#ff4d4d" }}> Turning off a feature hides it from the UI — it doesn't delete any data.</span>
      </div>

      {flags.maintenance_mode && (
        <div style={{ ...cardS, borderColor:"#ff4d4d30", background:"#1a0808", marginBottom:20 }}>
          <div style={{ color:"#ff4d4d", fontSize:14, fontWeight:700, marginBottom:10 }}>🚨 MAINTENANCE MODE IS ON</div>
          <div style={{ color:"#ff8888", fontSize:12, marginBottom:12 }}>All logged-out visitors see the maintenance message. Logged-in users still have access.</div>
          <div><label style={lS}>MAINTENANCE MESSAGE</label>
            <input style={{...iS, borderColor:"#ff4d4d30"}} value={flags.maintenance_message||""} onChange={e=>setFlags(f=>({...f,maintenance_message:e.target.value}))}/>
          </div>
        </div>
      )}

      {Object.entries(GROUPS).map(([group, keys]) => (
        <div key={group} style={cardS}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>{group}</div>
          <div style={{ display:"grid", gap:10 }}>
            {keys.map(key => (
              <div key={key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:"#0a0a0f", borderRadius:8 }}>
                <div>
                  <div style={{ color:"#ccc", fontSize:13 }}>{LABELS[key]||key}</div>
                  <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{key}</div>
                </div>
                <div onClick={()=>setFlags(f=>({...f,[key]:!f[key]}))} style={{ width:44, height:24, borderRadius:12, background:flags[key]?"#6366f1":"#2a2a3e", position:"relative", cursor:"pointer", transition:"all 0.2s", flexShrink:0 }}>
                  <div style={{ position:"absolute", top:3, left:flags[key]?22:3, width:18, height:18, borderRadius:"50%", background:"#fff", transition:"all 0.2s", boxShadow:"0 1px 4px rgba(0,0,0,0.3)" }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <SaveBar saving={saving} onSave={save} onReset={reset} section="feature flags"/>
    </div>
  );
}

// ── Keyword Presets Editor ────────────────────────────────────────────────────
function CMSPresetsEditor({ api, toast }) {
  const [presets, setPresets] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [open, setOpen]       = useState(null);

  useEffect(() => { api.get("/admin/cms/presets").then(setPresets).catch(()=>toast.error("Failed to load")); }, []);

  const set = (i, k, v) => setPresets(p => p.map((item,idx)=>idx===i?{...item,[k]:v}:item));
  const add = () => setPresets(p => [...p, { id:`preset_${Date.now()}`, label:"🔧 New Industry", keywords:[], negative:[] }]);
  const remove = (i) => setPresets(p => p.filter((_,idx)=>idx!==i));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/cms/presets", presets); toast.success("Presets saved"); }
    catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    try { await api.del("/admin/cms/presets"); const d = await api.get("/admin/cms/presets"); setPresets(d); toast.success("Reset"); }
    catch(e) { toast.error(e.message); }
  };

  if (!presets) return <div style={{ display:"flex", justifyContent:"center", padding:40 }}><Spinner size={28}/></div>;

  return (
    <div>
      <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:16, lineHeight:1.7 }}>
        Keyword presets appear when customers add a new page. They click a preset to auto-fill their keyword list. Edit these to match your target industries.
      </div>

      {presets.map((preset, i) => (
        <div key={preset.id} style={cardS}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: open===i ? 14 : 0 }}>
            <span style={{ fontSize:18 }}>{preset.label?.split(" ")[0]||"🔧"}</span>
            <span style={{ color:"#fff", fontSize:13, fontWeight:600, flex:1 }}>{preset.label}</span>
            <span style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{preset.keywords?.length||0} keywords</span>
            <button onClick={()=>setOpen(open===i?null:i)} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, cursor:"pointer" }}>{open===i?"▲ Close":"▼ Edit"}</button>
            <button onClick={()=>remove(i)} style={{ padding:"5px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#ff4d4d", fontSize:11, cursor:"pointer" }}>✕</button>
          </div>
          {open===i && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
              <div><label style={lS}>LABEL (with emoji)</label><input style={iS} value={preset.label} onChange={e=>set(i,"label",e.target.value)}/></div>
              <div><label style={lS}>ID (internal)</label><input style={iS} value={preset.id} onChange={e=>set(i,"id",e.target.value)}/></div>
              <div/>
              <div><label style={lS}>KEYWORDS (comma-separated)</label><textarea style={{...iS, minHeight:80, resize:"vertical"}} value={(preset.keywords||[]).join(", ")} onChange={e=>set(i,"keywords",e.target.value.split(",").map(k=>k.trim()).filter(Boolean))}/></div>
              <div><label style={lS}>NEGATIVE KEYWORDS</label><textarea style={{...iS, minHeight:80, resize:"vertical"}} value={(preset.negative||[]).join(", ")} onChange={e=>set(i,"negative",e.target.value.split(",").map(k=>k.trim()).filter(Boolean))}/></div>
              <div style={{ background:"#0a0a0f", borderRadius:8, padding:12 }}>
                <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", marginBottom:8 }}>PREVIEW</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                  {(preset.keywords||[]).map(k=><span key={k} style={{ background:"#1e1e3e", color:"#a5b4fc", fontSize:9, padding:"2px 7px", borderRadius:3, fontFamily:"'DM Mono',monospace" }}>{k}</span>)}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      <button onClick={add} style={{ width:"100%", padding:"12px", borderRadius:8, border:"2px dashed #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", marginBottom:16 }}>+ Add Industry Preset</button>

      <SaveBar saving={saving} onSave={save} onReset={reset} section="keyword presets"/>
    </div>
  );
}

// ── Email Templates Editor ────────────────────────────────────────────────────
function CMSEmailTemplates({ api, toast }) {
  const [templates, setTemplates] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [active, setActive]       = useState("welcome");
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => { api.get("/admin/cms/emails").then(setTemplates).catch(()=>toast.error("Failed to load")); }, []);

  const setField = (tmpl, field, val) => setTemplates(t => ({...t, [tmpl]: {...t[tmpl], [field]:val}}));

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(Object.entries(templates).map(([id, data]) => api.put(`/admin/cms/emails/${id}`, data)));
      toast.success("Email templates saved");
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post(`/admin/cms/emails/${active}/preview`);
      toast.success(`Preview sent to ${r.sent_to}`);
    } catch(e) { toast.error(e.message); }
    finally { setPreviewing(false); }
  };

  const reset = async () => {
    try { await api.del("/admin/cms/emails"); const d = await api.get("/admin/cms/emails"); setTemplates(d); toast.success("Reset"); }
    catch(e) { toast.error(e.message); }
  };

  if (!templates) return <div style={{ display:"flex", justifyContent:"center", padding:40 }}><Spinner size={28}/></div>;

  const TEMPLATE_GROUPS = [
    {
      label: "Transactional",
      color: "#6366f1",
      templates: [
        { id:"welcome",               label:"Welcome Email",            vars:["{app_name}","{app_url}"] },
        { id:"match_alert",           label:"Match Alert",              vars:["{app_name}","{app_url}","{score}","{page_name}","{post_text}","{matched_terms}"] },
        { id:"weekly_summary",        label:"Weekly Summary",           vars:["{app_name}","{app_url}","{total}","{urgent}","{pages}","{top_keywords}"] },
        { id:"payment_failed",        label:"Payment Failed",           vars:["{app_name}","{portal_url}"] },
        { id:"subscription_cancelled",label:"Subscription Cancelled",   vars:["{app_name}","{plan_name}","{period_end}","{portal_url}"] },
        { id:"trial_ending",          label:"Trial Ending",             vars:["{app_name}","{days_left}","{upgrade_url}"] },
        { id:"password_reset",        label:"Password Reset",           vars:["{app_name}","{user_email}","{reset_url}"] },
        { id:"email_verify",          label:"Email Verification",       vars:["{app_name}","{verify_url}"] },
      ],
    },
    {
      label: "Session Alerts",
      color: "#f59e0b",
      templates: [
        { id:"facebook_session_expired", label:"Facebook Session Expired", vars:["{app_name}","{app_url}"] },
        { id:"nextdoor_session_expired", label:"Nextdoor Session Expired",  vars:["{app_name}","{app_url}"] },
      ],
    },
    {
      label: "Health Alerts",
      color: "#ff4d4d",
      templates: [
        { id:"health_no_matches",    label:"No Matches (48h)",     vars:["{app_name}","{app_url}"] },
        { id:"health_never_scanned", label:"Never Scanned",        vars:["{app_name}","{app_url}"] },
        { id:"no_alert_channel",     label:"No Alert Channel Set", vars:["{app_name}","{app_url}"] },
      ],
    },
    {
      label: "Onboarding Drip",
      color: "#22c55e",
      templates: [
        { id:"drip_day1",  label:"Day 1 — First steps",       vars:["{app_name}","{app_url}"] },
        { id:"drip_day3",  label:"Day 3 — More keywords",     vars:["{app_name}","{app_url}"] },
        { id:"drip_day7",  label:"Day 7 — Trial ending",      vars:["{app_name}","{app_url}","{upgrade_url}"] },
        { id:"drip_day14", label:"Day 14 — Power tips",       vars:["{app_name}","{app_url}"] },
      ],
    },
  ];

  const ALL_TEMPLATES = TEMPLATE_GROUPS.flatMap(g => g.templates);
  const TEMPLATE_LABELS = Object.fromEntries(ALL_TEMPLATES.map(t => [t.id, t.label]));
  const VARS = Object.fromEntries(ALL_TEMPLATES.map(t => [t.id, t.vars]));

  return (
    <div>
      <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:16, lineHeight:1.7 }}>
        Edit the content of transactional emails. Use the variables listed for each template to insert dynamic content.
        Click "Send Preview" to receive a test email with sample data.
      </div>

      {/* Grouped template selector */}
      <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:20 }}>
        {TEMPLATE_GROUPS.map(group => (
          <div key={group.label}>
            <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:6 }}>{group.label.toUpperCase()}</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {group.templates.map(t => (
                <button key={t.id} onClick={()=>setActive(t.id)} style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${active===t.id?group.color:"#1e1e2e"}`, background:active===t.id?`${group.color}20`:"transparent", color:active===t.id?group.color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", transition:"all 0.15s" }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {templates[active] && (
        <div style={cardS}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{TEMPLATE_LABELS[active]||active}</div>
            <button onClick={preview} disabled={previewing} style={{ padding:"7px 16px", borderRadius:6, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:previewing?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {previewing?<><Spinner size={10}/> Sending…</>:"📨 Send Preview Email"}
            </button>
          </div>

          {/* Available variables */}
          <div style={{ marginBottom:14, padding:"10px 12px", background:"#0a0a0f", borderRadius:8 }}>
            <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", marginBottom:6 }}>AVAILABLE VARIABLES</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
              {(VARS[active]||[]).map(v=><span key={v} style={{ background:"#1e1e3e", color:"#a5b4fc", fontSize:10, padding:"2px 8px", borderRadius:3, fontFamily:"'DM Mono',monospace", cursor:"pointer" }} onClick={()=>{ navigator.clipboard.writeText(v); toast.success(`Copied ${v}`); }}>{v}</span>)}
            </div>
          </div>

          <div style={{ display:"grid", gap:12 }}>
            <div><label style={lS}>SUBJECT LINE</label><input style={iS} value={templates[active].subject||""} onChange={e=>setField(active,"subject",e.target.value)}/></div>
            <div><label style={lS}>EMAIL BODY</label><textarea style={{...iS, minHeight:160, resize:"vertical", lineHeight:1.7}} value={templates[active].body||""} onChange={e=>setField(active,"body",e.target.value)}/></div>
          </div>
        </div>
      )}

      <SaveBar saving={saving} onSave={save} onReset={reset} section="email templates"/>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 1 — REVENUE DASHBOARD
// ════════════════════════════════════════════════════════════════════════════
function RevenueDashboard({ api, toast }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics/revenue")
      .then(setData).catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  }, []);

  const PLAN_COLORS = { starter:"#22c55e", pro:"#6366f1", unknown:"#4a4a6a" };
  const fmt = (n) => `$${Number(n||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px" };

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;
  if (!data)   return <div style={{ color:"#ff4d4d",padding:40 }}>Failed to load revenue data</div>;

  const EVT_ICONS = { "subscription.created":"🆕","subscription.deleted":"❌","payment_failed":"⚠️","payment_succeeded":"✅" };

  return (
    <div>
      {!data.stripe_configured && (
        <div style={{ ...cardS, borderColor:"#f59e0b30", background:"#1a1200", marginBottom:20 }}>
          <div style={{ color:"#f59e0b", fontSize:13, fontWeight:600, marginBottom:6 }}>⚠️ Stripe not configured</div>
          <div style={{ color:"#d4a76a", fontSize:12 }}>Add STRIPE_SECRET_KEY to your .env to see live revenue data. Showing estimates from database plan data.</div>
        </div>
      )}
      {data.note && (
        <div style={{ ...cardS, borderColor:"#6366f130", marginBottom:20 }}>
          <div style={{ color:"#a5b4fc", fontSize:12 }}>ℹ️ {data.note}</div>
        </div>
      )}

      {/* Top KPI cards */}
      <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        {[
          { label:"MRR",          value:fmt(data.mrr),     sub:`ARR: ${fmt(data.arr)}`,                   color:"#22c55e" },
          { label:"ACTIVE SUBS",  value:data.active_subscriptions, sub:`${data.trialing} trialing`,       color:"#6366f1" },
          { label:"CHURN RATE",   value:`${data.churn_rate}%`,     sub:`${data.canceled_this_month} canceled`, color:data.churn_rate>5?"#ff4d4d":"#22c55e" },
          { label:"ARPU",         value:fmt(data.arpu),    sub:`${data.new_this_month} new this month`,   color:"#f59e0b" },
        ].map(k => (
          <div key={k.label} style={{ ...cardS }}>
            <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
            <div style={{ color:k.color, fontSize:28, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{k.value}</div>
            <div style={{ color:"#3a3a5a", fontSize:11, marginTop:6 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="sl-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        {/* Revenue by plan */}
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Revenue by Plan</div>
          {Object.keys(data.by_plan||{}).length === 0 ? (
            <div style={{ color:"#3a3a5a", fontSize:12 }}>No paid subscriptions yet</div>
          ) : Object.entries(data.by_plan).map(([plan, info]) => {
            const pct = data.mrr > 0 ? Math.round((info.mrr/data.mrr)*100) : 0;
            const c   = PLAN_COLORS[plan]||"#6366f1";
            return (
              <div key={plan} style={{ marginBottom:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                  <span style={{ color:"#ccc", fontSize:12, textTransform:"capitalize" }}>{plan}</span>
                  <span style={{ color:c, fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                    {fmt(info.mrr)}/mo · {info.count} user{info.count!==1?"s":""}
                  </span>
                </div>
                <div style={{ height:8, background:"#1e1e2e", borderRadius:4, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:c, borderRadius:4, transition:"width 0.5s" }}/>
                </div>
              </div>
            );
          })}
          {data.past_due > 0 && (
            <div style={{ marginTop:12, padding:"10px 12px", background:"#ff4d4d10", borderRadius:8, border:"1px solid #ff4d4d20" }}>
              <span style={{ color:"#ff4d4d", fontSize:12 }}>⚠️ {data.past_due} past-due subscription{data.past_due!==1?"s":""} — {fmt(data.past_due * (data.arpu||0))} at risk</span>
            </div>
          )}
        </div>

        {/* Upcoming renewals */}
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Upcoming Renewals (7 days)</div>
          {!data.upcoming_renewals?.length ? (
            <div style={{ color:"#3a3a5a", fontSize:12 }}>No renewals in next 7 days</div>
          ) : data.upcoming_renewals.map((r,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #111120" }}>
              <div style={{ flex:1 }}>
                <div style={{ color:"#ccc", fontSize:12 }}>{r.email}</div>
                <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                  {r.date} · {r.status}
                </div>
              </div>
              <span style={{ color:"#22c55e", fontSize:12, fontFamily:"'DM Mono',monospace" }}>{fmt(r.amount)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Stripe events */}
      {data.recent_events?.length > 0 && (
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>Recent Stripe Events</div>
          {data.recent_events.map((e,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid #111120", fontSize:12 }}>
              <span style={{ fontSize:16 }}>{EVT_ICONS[e.type]||"📌"}</span>
              <span style={{ color:"#ccc", fontFamily:"'DM Mono',monospace", flex:1 }}>{e.type.replace(/_/g," ")}</span>
              <span style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{e.created}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// FEATURE 2 — CUSTOMER HEALTH SCORES
// ════════════════════════════════════════════════════════════════════════════
function CustomerHealth({ api, toast }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("");

  const load = (risk="") => {
    setLoading(true);
    setFilter(risk);
    api.get(`/admin/analytics/health${risk?`?risk_filter=${risk}`:""}`)
      .then(setData).catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  };

  useEffect(()=>load(),[]);

  const RISK_CFG = {
    high:   { color:"#ff4d4d", bg:"#ff4d4d15", label:"HIGH RISK",   icon:"🔴" },
    medium: { color:"#f59e0b", bg:"#f59e0b15", label:"MEDIUM RISK", icon:"🟡" },
    low:    { color:"#22c55e", bg:"#22c55e15", label:"LOW RISK",    icon:"🟢" },
  };

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px" };

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;

  return (
    <div>
      {/* Summary */}
      {data?.summary && (
        <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
          <div style={{ ...cardS }}>
            <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>AVG HEALTH SCORE</div>
            <div style={{ color: data.summary.avg_score>=70?"#22c55e":data.summary.avg_score>=40?"#f59e0b":"#ff4d4d", fontSize:32, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{data.summary.avg_score}</div>
            <div style={{ height:4, background:"#1e1e2e", borderRadius:2, marginTop:8 }}>
              <div style={{ height:"100%", width:`${data.summary.avg_score}%`, background:data.summary.avg_score>=70?"#22c55e":data.summary.avg_score>=40?"#f59e0b":"#ff4d4d", borderRadius:2 }}/>
            </div>
          </div>
          {[
            { key:"high_risk",   label:"HIGH RISK",   color:"#ff4d4d" },
            { key:"medium_risk", label:"MEDIUM RISK", color:"#f59e0b" },
            { key:"low_risk",    label:"LOW RISK",    color:"#22c55e" },
          ].map(k=>(
            <div key={k.key} style={{ ...cardS, borderColor:`${k.color}20`, cursor:"pointer" }} onClick={()=>load(filter===k.key.replace("_risk","")?"":k.key.replace("_risk",""))}>
              <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
              <div style={{ color:k.color, fontSize:32, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{data.summary[k.key]}</div>
              <div style={{ color:"#3a3a5a", fontSize:11, marginTop:6 }}>{filter===k.key.replace("_risk","")?"▼ Filtered":"Click to filter"}</div>
            </div>
          ))}
        </div>
      )}

      {/* User list */}
      {data?.users?.map(u => {
        const cfg = RISK_CFG[u.risk];
        return (
          <div key={u.id} style={{ ...cardS, marginBottom:10, borderLeft:`3px solid ${cfg.color}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              {/* Score dial */}
              <div style={{ width:52, height:52, borderRadius:"50%", border:`3px solid ${cfg.color}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0, background:`${cfg.color}10` }}>
                <div style={{ color:cfg.color, fontSize:16, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{u.score}</div>
                <div style={{ color:cfg.color, fontSize:8, fontFamily:"'DM Mono',monospace" }}>/ 100</div>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{u.email}</span>
                  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${PLAN_CFG[u.plan]||"#4a4a6a"}20`, color:PLAN_CFG[u.plan]||"#4a4a6a", textTransform:"capitalize" }}>{u.plan}</span>
                  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:cfg.bg, color:cfg.color }}>{cfg.icon} {cfg.label}</span>
                </div>
                <div style={{ display:"flex", gap:16, color:"#4a4a6a", fontSize:11, flexWrap:"wrap" }}>
                  <span>📡 {u.page_count} pages</span>
                  <span>🎯 {u.matches_7d} matches/week</span>
                  <span>🕐 {u.last_scan ? timeAgo(u.last_scan) : "Never scanned"}</span>
                </div>
                {u.flags?.length > 0 && (
                  <div style={{ display:"flex", gap:5, marginTop:6, flexWrap:"wrap" }}>
                    {u.flags.map((f,i)=>(
                      <span key={i} style={{ padding:"2px 8px", borderRadius:4, fontSize:10, background:"#1e1e2e", color:"#6666aa" }}>{f}</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                {u.risk==="high" && (
                  <button style={{ padding:"6px 14px", borderRadius:6, border:"1px solid #ff4d4d30", background:"#ff4d4d10", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    📧 Reach Out
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {data?.users?.length===0 && (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
          No users match this filter
        </div>
      )}
    </div>
  );
}

const PLAN_CFG = { free:"#4a4a6a", starter:"#22c55e", pro:"#6366f1" };

// ════════════════════════════════════════════════════════════════════════════
// FEATURE 3 — SCAN PERFORMANCE MAP
// ════════════════════════════════════════════════════════════════════════════
function ScanPerformanceMap({ api, toast }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [statusFilter, setStatus] = useState("");
  const [search, setSearch]     = useState("");

  useEffect(()=>{
    api.get("/admin/analytics/scans")
      .then(setData).catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  },[]);

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px" };
  const STATUS_CFG = {
    ok:      { color:"#22c55e", icon:"●", label:"OK"      },
    error:   { color:"#ff4d4d", icon:"✕", label:"ERROR"   },
    never:   { color:"#4a4a6a", icon:"○", label:"NEVER"   },
    blocked: { color:"#f59e0b", icon:"⚠", label:"BLOCKED" },
  };
  const PLAT_ICONS = { facebook:"🔵", reddit:"🟠", };

  const filtered = (data?.pages||[])
    .filter(p=>!statusFilter||p.status===statusFilter)
    .filter(p=>!search||p.name.toLowerCase().includes(search.toLowerCase())||p.user_email.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;

  return (
    <div>
      {/* Summary cards */}
      {data?.summary && (
        <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:20 }}>
          {[
            { label:"TOTAL PAGES", value:data.summary.total,      color:"#6366f1" },
            { label:"SCANNING OK", value:data.summary.ok,         color:"#22c55e" },
            { label:"ERRORS",      value:data.summary.errors,     color:"#ff4d4d" },
            { label:"STALE >1hr",  value:data.summary.stale,      color:"#f59e0b" },
            { label:"ERROR RATE",  value:`${data.summary.error_rate}%`, color:data.summary.error_rate>10?"#ff4d4d":"#22c55e" },
          ].map(k=>(
            <div key={k.label} style={{ ...cardS, cursor:"pointer" }} onClick={()=>setStatus(statusFilter===k.label.toLowerCase().split(" ")[0]?"":k.label.toLowerCase().split(" ")[0])}>
              <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
              <div style={{ color:k.color, fontSize:28, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-platform stats */}
      {data?.by_platform && (
        <div style={{ ...cardS, marginBottom:16 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>Platform Performance</div>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
            {Object.entries(data.by_platform).map(([plat, stats])=>(
              <div key={plat} style={{ flex:"1 1 180px", background:"#0a0a0f", borderRadius:8, padding:"14px 16px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <span style={{ fontSize:18 }}>{PLAT_ICONS[plat]||"📡"}</span>
                  <span style={{ color:"#fff", fontSize:13, textTransform:"capitalize" }}>{plat}</span>
                </div>
                {[
                  ["Pages",   stats.total],
                  ["OK",      stats.ok,      "#22c55e"],
                  ["Errors",  stats.errors,  "#ff4d4d"],
                  ["Posts",   stats.total_posts],
                  ["Matches", stats.total_matches, "#6366f1"],
                ].map(([k,v,c])=>(
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"3px 0", fontSize:11 }}>
                    <span style={{ color:"#4a4a6a" }}>{k}</span>
                    <span style={{ color:c||"#ccc", fontFamily:"'DM Mono',monospace" }}>{v}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search page or user..." style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", width:220 }}/>
        {["","ok","error","never"].map(s=>(
          <button key={s||"all"} onClick={()=>setStatus(s)} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${statusFilter===s?"#6366f1":"#1e1e2e"}`, background:statusFilter===s?"#6366f120":"transparent", color:statusFilter===s?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
            {s||"All"} {s&&`(${(data?.pages||[]).filter(p=>p.status===s).length})`}
          </button>
        ))}
      </div>

      {/* Page table */}
      <div style={{ ...cardS, padding:0, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #1e1e2e" }}>
              {["Status","Page","Platform","User","Last Scan","Posts","Matches","Error"].map(h=>(
                <th key={h} style={{ padding:"12px 14px", textAlign:"left", color:"#4a4a6a", fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:1, fontWeight:400 }}>{h.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0,50).map(p=>{
              const sc = STATUS_CFG[p.status]||STATUS_CFG.never;
              const staleWarn = p.stale_minutes && p.stale_minutes > 60;
              return (
                <tr key={p.id} style={{ borderBottom:"1px solid #111120" }}>
                  <td style={{ padding:"10px 14px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ color:sc.color, fontSize:10 }}>{sc.icon}</span>
                      <span style={{ color:sc.color, fontSize:9, fontFamily:"'DM Mono',monospace" }}>{sc.label}</span>
                    </div>
                  </td>
                  <td style={{ padding:"10px 14px", color:"#ccc", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</td>
                  <td style={{ padding:"10px 14px" }}><span style={{ fontSize:14 }}>{PLAT_ICONS[p.platform]||"📡"}</span></td>
                  <td style={{ padding:"10px 14px", color:"#6366f1", fontSize:11, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.user_email}</td>
                  <td style={{ padding:"10px 14px", color:staleWarn?"#f59e0b":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap" }}>
                    {p.last_checked ? timeAgo(p.last_checked) : "Never"}
                    {staleWarn&&<span style={{ color:"#f59e0b", marginLeft:4 }}>⚠</span>}
                  </td>
                  <td style={{ padding:"10px 14px", color:"#4a4a6a", fontFamily:"'DM Mono',monospace" }}>{p.posts_checked||0}</td>
                  <td style={{ padding:"10px 14px", color: p.matches_found>0?"#22c55e":"#4a4a6a", fontFamily:"'DM Mono',monospace" }}>{p.matches_found||0}</td>
                  <td style={{ padding:"10px 14px", color:"#ff8888", fontSize:10, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={p.error||""}>{p.error||"—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length===0&&<div style={{ padding:"40px 20px", textAlign:"center", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>No pages match filter</div>}
        {filtered.length>50&&<div style={{ padding:"12px 20px", color:"#3a3a5a", fontSize:11, textAlign:"center" }}>Showing 50 of {filtered.length} — use filters to narrow</div>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// WRAPPER — Admin Advanced Panel (tabs for all 3 features)
// ════════════════════════════════════════════════════════════════════════════
function AdminAdvanced({ api, toast }) {
  const [tab, setTab] = useState("revenue");
  const TABS = [
    { id:"revenue",  icon:"💰", label:"Revenue"         },
    { id:"health",   icon:"❤️",  label:"Customer Health" },
    { id:"scans",    icon:"📡", label:"Scan Monitor"    },
    { id:"queue",    icon:"⚡", label:"Job Queue"       },
    { id:"segments", icon:"🎯", label:"Segments"        },
    { id:"audit",    icon:"📋", label:"Audit Log"        },
    { id:"rules",    icon:"⚙️", label:"Rules Engine"     },
    { id:"exports",  icon:"📤", label:"Data Exports"     },
    { id:"maint",    icon:"🔧", label:"Maintenance"      },
    { id:"subs",     icon:"💳", label:"Subscriptions"    },
    { id:"platform", icon:"📊", label:"Platform Analytics"},
    { id:"ob_funnel", icon:"🚀", label:"Onboarding Funnel"},
    { id:"apikeys",  icon:"🔑", label:"API Keys"          },
    { id:"affiliate",icon:"🤝", label:"Affiliates"        },
    { id:"tags",     icon:"🏷️", label:"User Tags"         },
    { id:"calls",    icon:"📞", label:"Call Log"          },
    { id:"inbox",    icon:"💬", label:"Support Inbox"     },
    { id:"promo",    icon:"🎁", label:"Promo Accounts"    },
    { id:"abtests",  icon:"🧪", label:"A/B Tests"         },
    { id:"proxies",    icon:"🌐", label:"Proxy Pool"        },
    { id:"protection", icon:"🛡️", label:"FB Protection"    },
    { id:"zones",      icon:"🗺️", label:"Zones"             },
    { id:"trades",     icon:"🛠️", label:"Trades"            },
    { id:"approvals",  icon:"✅", label:"Approvals"         },
  ];
  return (
    <div>
      <div style={{ display:"flex", gap:6, marginBottom:24 }}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"8px 18px", borderRadius:8, border:`1px solid ${tab===t.id?"#6366f1":"#1e1e2e"}`, background:tab===t.id?"#6366f120":"transparent", color:tab===t.id?"#a5b4fc":"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      {tab==="revenue"  && <RevenueDashboard   api={api} toast={toast}/>}
      {tab==="health"   && <CustomerHealth     api={api} toast={toast}/>}
      {tab==="scans"    && <ScanPerformanceMap  api={api} toast={toast}/>}
      {tab==="queue"    && <QueueMonitor        api={api} toast={toast}/>}
      {tab==="segments" && <UserSegments        api={api} toast={toast}/>}
      {tab==="audit"    && <AuditLog              api={api} toast={toast}/>}
      {tab==="rules"    && <RulesEngine           api={api} toast={toast}/>}
      {tab==="exports"  && <ExportCenter          api={api} toast={toast}/>}
      {tab==="maint"    && <MaintenanceManager    api={api} toast={toast}/>}
      {tab==="subs"     && <SubscriptionManager   api={api} toast={toast}/>}
      {tab==="platform"  && <PlatformAnalytics     api={api} toast={toast}/>}
      {tab==="ob_funnel" && <OnboardingFunnel      api={api} toast={toast}/>}
      {tab==="apikeys"  && <ApiKeyManager         api={api} toast={toast} isAdmin={true} userPlan={userPlan}/>}
      {tab==="affiliate"&& <AffiliateAdmin        api={api} toast={toast}/>}
      {tab==="tags"     && <TagDashboard          api={api} toast={toast}/>}
      {tab==="calls"    && <CallLog               api={api} toast={toast}/>}
      {tab==="inbox"    && <SupportInbox          api={api} toast={toast}/>}
      {tab==="promo"    && <PromoAccountManager   api={api} toast={toast}/>}
      {tab==="abtests"  && <ABTestManager         api={api} toast={toast}/>}
      {tab==="proxies"    && <ProxyManager          api={api} toast={toast}/>}
      {tab==="protection" && <ProtectionDashboard  api={api} toast={toast}/>}
      {tab==="zones"      && <ZoneManager           api={api} toast={toast}/>}
      {tab==="trades"     && <TradeManager          api={api} toast={toast}/>}
      {tab==="approvals"  && <ApprovalQueue         api={api} toast={toast}/>}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 4 — JOB QUEUE MONITOR
// ════════════════════════════════════════════════════════════════════════════
function QueueMonitor({ api, toast }) {
  const [stats,    setStats]    = useState(null);
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [clearing, setClearing] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/queue/stats"),
      api.get("/admin/queue/history?hours=24"),
    ]).then(([s, h]) => { setStats(s); setHistory(h||[]); })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return ()=>clearInterval(t); }, []);

  const clearFailed = async () => {
    setClearing(true);
    try { const r = await api.post("/admin/queue/retry-failed"); toast.success(`Cleared ${r.cleared} failed tasks`); load(); }
    catch(e) { toast.error(e.message); }
    finally { setClearing(false); }
  };

  const triggerScan = async () => {
    setScanning(true);
    try { const r = await api.post("/admin/queue/trigger-scan-all"); toast.success(r.message); }
    catch(e) { toast.error(e.message); }
    finally { setScanning(false); }
  };

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };
  const maxH = Math.max(...history.map(h=>h.scans||0), 1);

  if (loading && !stats) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;

  return (
    <div>
      {/* Connection status */}
      <div style={{ ...cardS, borderColor: stats?.connected?"#22c55e30":"#ff4d4d30", background: stats?.connected?"#0a1a0a":"#1a0808", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:12, height:12, borderRadius:"50%", background:stats?.connected?"#22c55e":"#ff4d4d", boxShadow:`0 0 8px ${stats?.connected?"#22c55e":"#ff4d4d"}` }}/>
          <div>
            <div style={{ color:stats?.connected?"#22c55e":"#ff4d4d", fontSize:13, fontWeight:600 }}>
              {stats?.connected ? "Redis Connected — Queue Active" : "Redis Offline — Queue Not Running"}
            </div>
            {stats?.error && <div style={{ color:"#ff8888", fontSize:11 }}>{stats.error}</div>}
            {!stats?.connected && <div style={{ color:"#4a4a6a", fontSize:11 }}>Check that Redis is running: docker compose ps</div>}
          </div>
          <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
            <button onClick={triggerScan} disabled={scanning} style={{ padding:"7px 16px", borderRadius:6, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:scanning?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {scanning?<><Spinner size={11}/> Queuing…</>:"▶ Trigger Full Scan"}
            </button>
            <button onClick={load} style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>↺ Refresh</button>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
        {[
          { label:"PENDING",   value:stats?.pending||0,   color:"#f59e0b", sub:"Jobs in queue" },
          { label:"ACTIVE",    value:stats?.active||0,    color:"#6366f1", sub:"Running now" },
          { label:"FAILED",    value:stats?.failed||0,    color:"#ff4d4d", sub:"Need attention", action: stats?.failed > 0 },
          { label:"SCHEDULED", value:stats?.scheduled||0, color:"#22c55e", sub:"Beat tasks" },
        ].map(k=>(
          <div key={k.label} style={{ ...cardS, marginBottom:0, borderColor:`${k.color}20`, cursor:k.action?"pointer":"default" }} onClick={k.action?clearFailed:undefined}>
            <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
            <div style={{ color:k.color, fontSize:32, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{k.value}</div>
            <div style={{ color:"#3a3a5a", fontSize:11, marginTop:6 }}>{k.action?"Click to clear":k.sub}</div>
          </div>
        ))}
      </div>

      {/* Queues breakdown */}
      {stats?.queues && Object.keys(stats.queues).length > 0 && (
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>Queue Depths</div>
          {Object.entries(stats.queues).map(([name, count]) => {
            const pct = Math.min(100, (count / Math.max(stats.pending||1, 1)) * 100);
            const c   = count > 100 ? "#ff4d4d" : count > 20 ? "#f59e0b" : "#22c55e";
            return (
              <div key={name} style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace" }}>{name}</span>
                  <span style={{ color:c, fontSize:12, fontFamily:"'DM Mono',monospace" }}>{count} job{count!==1?"s":""}</span>
                </div>
                <div style={{ height:6, background:"#1e1e2e", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:c, borderRadius:3, transition:"width 0.5s" }}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Scan throughput chart */}
      {history.length > 0 && (
        <div style={{ ...cardS }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>Scan Throughput — Last 24 Hours</div>
            <div style={{ display:"flex", gap:14, fontSize:10, fontFamily:"'DM Mono',monospace" }}>
              <span style={{ color:"#6366f1" }}>■ Scans</span>
              <span style={{ color:"#22c55e" }}>■ Matches</span>
              <span style={{ color:"#ff4d4d" }}>■ Errors</span>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:100 }}>
            {history.map((h,i) => {
              const scanH  = Math.max(4, (h.scans/maxH)*90);
              const matchH = Math.max(0, ((h.matches||0)/Math.max(maxH,1))*90);
              const errH   = Math.max(0, ((h.errors||0)/Math.max(maxH,1))*90);
              return (
                <div key={i} title={`${h.hour?.slice(11,16)||""} — ${h.scans} scans, ${h.matches} matches, ${h.errors} errors`}
                     style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-end", gap:1, height:"100%", cursor:"pointer" }}>
                  {h.errors > 0 && <div style={{ width:"100%", height:`${errH}%`, background:"#ff4d4d", borderRadius:"2px 2px 0 0", minHeight:2 }}/>}
                  {h.matches > 0 && <div style={{ width:"100%", height:`${matchH}%`, background:"#22c55e", minHeight:2 }}/>}
                  <div style={{ width:"100%", height:`${scanH}%`, background:"#6366f1", borderRadius: h.errors||h.matches?"0":"2px 2px 0 0", minHeight:4 }}/>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop:8, display:"flex", justifyContent:"space-between", fontSize:10, color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
            <span>{history[0]?.hour?.slice(11,16)||"24h ago"}</span>
            <span>Total: {history.reduce((a,h)=>a+(h.scans||0),0)} scans · {history.reduce((a,h)=>a+(h.matches||0),0)} matches</span>
            <span>now</span>
          </div>
        </div>
      )}

      {/* Failed tasks */}
      {stats?.failed_tasks?.length > 0 && (
        <div style={{ ...cardS, borderColor:"#ff4d4d20" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ color:"#ff4d4d", fontSize:13, fontWeight:600 }}>Failed Tasks ({stats.failed})</div>
            <button onClick={clearFailed} disabled={clearing} style={{ padding:"6px 14px", borderRadius:5, border:"1px solid #ff4d4d30", background:"#ff4d4d10", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:clearing?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {clearing?<><Spinner size={10}/> Clearing…</>:"Clear All Failed"}
            </button>
          </div>
          {stats.failed_tasks.map((t,i)=>(
            <div key={i} style={{ padding:"8px 0", borderBottom:"1px solid #111120" }}>
              <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{t.id} · {t.name||"unknown task"}</div>
              <div style={{ color:"#ff8888", fontSize:11 }}>{t.error}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// FEATURE 5 — INTERNAL NOTES ON USERS
// ════════════════════════════════════════════════════════════════════════════
function UserNotes({ api, toast, userId, userEmail }) {
  const [notes,   setNotes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [tag,     setTag]     = useState("");
  const [pinned,  setPinned]  = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [editing, setEditing] = useState(null);

  const TAGS = [
    { id:"",        label:"No tag",  color:"#4a4a6a" },
    { id:"support", label:"Support", color:"#6366f1" },
    { id:"sales",   label:"Sales",   color:"#22c55e" },
    { id:"risk",    label:"Risk",    color:"#ff4d4d" },
    { id:"vip",     label:"VIP",     color:"#f59e0b" },
  ];
  const TAG_CFG = Object.fromEntries(TAGS.map(t=>[t.id,t]));

  const load = () => {
    api.get(`/admin/notes/${userId}`)
      .then(setNotes).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(()=>{ if(userId) load(); },[userId]);

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/notes/${editing}`, { content, tag:tag||null, pinned });
        setNotes(n => n.map(note => note.id===editing ? {...note,content,tag,pinned} : note));
        setEditing(null);
      } else {
        const note = await api.post(`/admin/notes/${userId}`, { content, tag:tag||null, pinned });
        setNotes(n => [note, ...n]);
      }
      setContent(""); setTag(""); setPinned(false);
      toast.success(editing?"Note updated":"Note saved");
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.del(`/admin/notes/${id}`); setNotes(n=>n.filter(note=>note.id!==id)); }
    catch(e) { toast.error(e.message); }
  };

  const startEdit = (note) => {
    setEditing(note.id); setContent(note.content);
    setTag(note.tag||""); setPinned(note.pinned||false);
  };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };

  return (
    <div>
      {/* Add note form */}
      <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:16, marginBottom:16 }}>
        <div style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", marginBottom:10 }}>
          {editing ? "EDITING NOTE" : `ADD NOTE — ${userEmail}`}
        </div>
        <textarea
          style={{ ...iS, minHeight:80, resize:"vertical", marginBottom:10 }}
          placeholder="Internal note visible to admins only — customer cannot see this..."
          value={content}
          onChange={e=>setContent(e.target.value)}
        />
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          {/* Tag selector */}
          <div style={{ display:"flex", gap:5 }}>
            {TAGS.map(t=>(
              <button key={t.id} onClick={()=>setTag(t.id)} style={{ padding:"4px 10px", borderRadius:4, border:`1px solid ${tag===t.id?t.color:"#2a2a3e"}`, background:tag===t.id?`${t.color}20`:"transparent", color:tag===t.id?t.color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                {t.label}
              </button>
            ))}
          </div>
          {/* Pin toggle */}
          <div onClick={()=>setPinned(p=>!p)} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
            <div style={{ width:32,height:18,borderRadius:9,background:pinned?"#f59e0b":"#2a2a3e",position:"relative",transition:"all 0.2s" }}>
              <div style={{ position:"absolute",top:2,left:pinned?14:2,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/>
            </div>
            <span style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>📌 PIN</span>
          </div>
          <div style={{ flex:1 }}/>
          {editing && (
            <button onClick={()=>{setEditing(null);setContent("");setTag("");setPinned(false);}} style={{ padding:"7px 14px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          )}
          <button onClick={save} disabled={!content.trim()||saving} style={{ padding:"7px 16px", borderRadius:5, border:"none", background:(!content.trim()||saving)?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:(!content.trim()||saving)?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
            {saving?<><Spinner size={10}/> Saving…</>:editing?"Update Note":"Add Note"}
          </button>
        </div>
      </div>

      {/* Notes list */}
      {loading ? <div style={{ display:"flex",justifyContent:"center",padding:20 }}><Spinner size={20}/></div>
      : notes.length===0 ? (
        <div style={{ textAlign:"center", padding:"30px 0", color:"#3a3a5a", fontSize:12, fontFamily:"'DM Mono',monospace" }}>No notes yet — add the first one above</div>
      ) : notes.map(note => {
        const tc = TAG_CFG[note.tag||""] || TAG_CFG[""];
        return (
          <div key={note.id} style={{ background:"#0f0f1a", border:`1px solid ${note.pinned?"#f59e0b30":"#1e1e2e"}`, borderLeft:`3px solid ${note.pinned?"#f59e0b":tc.color||"#2a2a3e"}`, borderRadius:8, padding:"12px 14px", marginBottom:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              {note.pinned && <span style={{ color:"#f59e0b", fontSize:12 }}>📌</span>}
              {note.tag && <span style={{ padding:"1px 8px", borderRadius:3, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${tc.color}20`, color:tc.color, border:`1px solid ${tc.color}30` }}>{tc.label}</span>}
              <span style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{note.admin_email}</span>
              <span style={{ color:"#3a3a5a", fontSize:10 }}>·</span>
              <span style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{note.created_at?new Date(note.created_at).toLocaleDateString():""}</span>
              <div style={{ flex:1 }}/>
              <button onClick={()=>startEdit(note)} style={{ background:"transparent",border:"none",color:"#4a4a6a",cursor:"pointer",fontSize:12 }}>✏️</button>
              <button onClick={()=>del(note.id)} style={{ background:"transparent",border:"none",color:"#ff4d4d",cursor:"pointer",fontSize:12 }}>✕</button>
            </div>
            <div style={{ color:"#ccc", fontSize:12, lineHeight:1.7, whiteSpace:"pre-wrap" }}>{note.content}</div>
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// FEATURE 6 — TARGETED USER SEGMENTS
// ════════════════════════════════════════════════════════════════════════════
function UserSegments({ api, toast }) {
  const [segments,  setSegments]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [members,   setMembers]   = useState([]);
  const [loadingM,  setLoadingM]  = useState(false);
  const [subject,   setSubject]   = useState("");
  const [message,   setMessage]   = useState("");
  const [preview,   setPreview]   = useState(null);
  const [blasting,  setBlasting]  = useState(false);
  const [previewing,setPreviewing] = useState(false);

  useEffect(()=>{
    api.get("/admin/segments").then(setSegments).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  },[]);

  const selectSegment = async (seg) => {
    setSelected(seg); setMembers([]); setPreview(null);
    setLoadingM(true);
    try {
      const r = await api.get(`/admin/segments/${seg.id}/members`);
      setMembers(r.members||[]);
    } catch(e) { toast.error(e.message); }
    finally { setLoadingM(false); }
  };

  const doPreview = async () => {
    if(!subject||!message){toast.error("Subject and message required");return;}
    setPreviewing(true);
    try {
      const r = await api.post(`/admin/segments/${selected.id}/blast`, {subject,message,preview_only:true});
      setPreview(r);
    } catch(e) { toast.error(e.message); }
    finally { setPreviewing(false); }
  };

  const doBlast = async () => {
    if (!subject||!message){toast.error("Subject and message required");return;}
    if (!window.confirm(`Send to ${preview?.would_send_to||members.length} users in "${selected?.label}"?`)) return;
    setBlasting(true);
    try {
      const r = await api.post(`/admin/segments/${selected.id}/blast`, {subject,message,preview_only:false});
      toast.success(`Email queued for ${r.recipient_count} users`);
      setSubject(""); setMessage(""); setPreview(null);
    } catch(e) { toast.error(e.message); }
    finally { setBlasting(false); }
  };

  const RISK_COLORS = { high:"#ff4d4d", medium:"#f59e0b", low:"#22c55e", upsell:"#6366f1", urgent:"#ff4d4d" };
  const PLAN_COLORS = { free:"#4a4a6a", starter:"#22c55e", pro:"#6366f1" };
  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;

  return (
    <div style={{ display:"grid", gridTemplateColumns:"320px 1fr", gap:20 }}>
      {/* Segment list */}
      <div>
        <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:12 }}>SEGMENTS ({segments.length})</div>
        {segments.map(seg => {
          const rc = RISK_COLORS[seg.risk]||"#6366f1";
          const isSelected = selected?.id===seg.id;
          return (
            <div key={seg.id} onClick={()=>selectSegment(seg)} style={{
              background:isSelected?"#1a1a2e":"#0f0f1a",
              border:`1px solid ${isSelected?seg.color||"#6366f1":"#1e1e2e"}`,
              borderRadius:10, padding:"14px 16px", marginBottom:8,
              cursor:"pointer", transition:"all 0.15s",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:20 }}>{seg.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{seg.label}</div>
                  <div style={{ color:"#4a4a6a", fontSize:10, marginTop:2 }}>{seg.description}</div>
                </div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ color:rc, fontSize:20, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{seg.count}</div>
                  <div style={{ color:rc, fontSize:8, fontFamily:"'DM Mono',monospace", marginTop:2 }}>{seg.risk?.toUpperCase()}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Segment detail */}
      <div>
        {!selected ? (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:300, color:"#3a3a5a", fontFamily:"'DM Mono',monospace", fontSize:13 }}>
            <div style={{ fontSize:40, marginBottom:12 }}>←</div>
            Select a segment to see members and send targeted emails
          </div>
        ) : (
          <div>
            {/* Segment header */}
            <div style={{ background:"#0f0f1a", border:`1px solid ${selected.color||"#6366f1"}30`, borderRadius:12, padding:"18px 20px", marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10 }}>
                <span style={{ fontSize:28 }}>{selected.icon}</span>
                <div>
                  <div style={{ color:"#fff", fontSize:15, fontWeight:700 }}>{selected.label}</div>
                  <div style={{ color:"#4a4a6a", fontSize:12 }}>{selected.description}</div>
                </div>
                <div style={{ marginLeft:"auto", color:RISK_COLORS[selected.risk]||"#6366f1", fontSize:28, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>
                  {members.length}
                </div>
              </div>
            </div>

            {/* Email blast form */}
            <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"18px 20px", marginBottom:16 }}>
              <div style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", marginBottom:12 }}>📧 SEND EMAIL TO THIS SEGMENT</div>
              <div style={{ marginBottom:10 }}>
                <label style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", display:"block", marginBottom:5 }}>SUBJECT</label>
                <input style={iS} placeholder="e.g. We noticed you haven't scanned yet — here's how to get started" value={subject} onChange={e=>setSubject(e.target.value)}/>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", display:"block", marginBottom:5 }}>MESSAGE</label>
                <textarea style={{ ...iS, minHeight:100, resize:"vertical" }} placeholder="Write your email here..." value={message} onChange={e=>setMessage(e.target.value)}/>
              </div>
              {preview && (
                <div style={{ marginBottom:12, padding:"10px 14px", background:"#0a1a0a", borderRadius:8, border:"1px solid #22c55e30" }}>
                  <div style={{ color:"#22c55e", fontSize:12 }}>Will send to {preview.would_send_to} users</div>
                  {preview.sample?.length>0 && <div style={{ color:"#4a4a6a", fontSize:11, marginTop:4 }}>Sample: {preview.sample.join(", ")}</div>}
                </div>
              )}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={doPreview} disabled={!subject||!message||previewing} style={{ padding:"8px 16px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:(!subject||!message||previewing)?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
                  {previewing?<><Spinner size={10}/>Checking…</>:"Preview"}
                </button>
                <button onClick={doBlast} disabled={!subject||!message||blasting} style={{ padding:"8px 18px", borderRadius:6, border:"none", background:(!subject||!message||blasting)?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:(!subject||!message||blasting)?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
                  {blasting?<><Spinner size={10}/> Sending…</>:"📤 Send to Segment"}
                </button>
              </div>
            </div>

            {/* Members list */}
            <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"18px 20px" }}>
              <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>
                Members ({members.length})
              </div>
              {loadingM ? <div style={{ display:"flex",justifyContent:"center",padding:20 }}><Spinner size={20}/></div>
              : members.length===0 ? <div style={{ color:"#3a3a5a", fontSize:12 }}>No users in this segment</div>
              : members.slice(0,30).map(m=>(
                <div key={m.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #111120" }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:12, fontWeight:700, flexShrink:0 }}>
                    {m.email[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ color:"#ccc", fontSize:12 }}>{m.email}</div>
                    <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>ID #{m.id} · {m.created_at?new Date(m.created_at).toLocaleDateString():""}</div>
                  </div>
                  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${PLAN_COLORS[m.plan]||"#4a4a6a"}20`, color:PLAN_COLORS[m.plan]||"#4a4a6a", textTransform:"capitalize" }}>{m.plan}</span>
                </div>
              ))}
              {members.length>30&&<div style={{ color:"#3a3a5a",fontSize:11,padding:"10px 0",textAlign:"center" }}>+ {members.length-30} more — email will reach all {members.length}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 7 — AUDIT LOG
// ════════════════════════════════════════════════════════════════════════════
function AuditLog({ api, toast }) {
  const [entries,  setEntries]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [days,     setDays]     = useState(30);
  const [actions,  setActions]  = useState([]);
  const [page,     setPage]     = useState(0);
  const PER_PAGE = 50;

  const load = (pg=0) => {
    setLoading(true);
    const params = `?limit=${PER_PAGE}&offset=${pg*PER_PAGE}&days=${days}${actionFilter?`&action=${actionFilter}`:""}${search?`&admin=${search}`:""}`;
    Promise.all([
      api.get(`/admin/audit${params}`),
      actions.length===0 ? api.get("/admin/audit/actions") : Promise.resolve(null),
    ]).then(([r, acts]) => {
      setEntries(r.entries||[]);
      setTotal(r.total||0);
      if (acts) setActions(acts);
    }).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  };

  useEffect(()=>{ load(0); setPage(0); }, [days, actionFilter, search]);

  const ACTION_ICONS = {
    "rule":      "⚙️", "rules":    "⚙️",
    "export":    "📤",
    "refund":    "💸",
    "user":      "👤",
    "announce":  "📣",
    "coupon":    "🏷️",
    "login":     "🔑",
    "plan":      "💰",
    "segment":   "🎯",
  };

  const getIcon = (action) => {
    const key = Object.keys(ACTION_ICONS).find(k => action?.includes(k));
    return ACTION_ICONS[key] || "📋";
  };

  const ACTION_COLORS = {
    "delete": "#ff4d4d", "remove": "#ff4d4d",
    "create": "#22c55e", "add":    "#22c55e",
    "update": "#6366f1", "change": "#6366f1",
    "export": "#f59e0b", "run":    "#8b5cf6",
  };

  const getColor = (action) => {
    const key = Object.keys(ACTION_COLORS).find(k => action?.includes(k));
    return ACTION_COLORS[key] || "#4a4a6a";
  };

  const exportAudit = () => { window.open(`${window.API_BASE||"https://socipulses.com"}/admin/export/audit?days=${days}`, "_blank"); };

  return (
    <div>
      {/* Controls */}
      <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap", alignItems:"center" }}>
        <input
          value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Filter by admin email..."
          style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", width:220 }}
        />
        <select value={actionFilter} onChange={e=>setActionFilter(e.target.value)}
          style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none" }}>
          <option value="">All actions</option>
          {actions.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        {[7,14,30,90].map(d=>(
          <button key={d} onClick={()=>setDays(d)} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${days===d?"#6366f1":"#1e1e2e"}`, background:days===d?"#6366f120":"transparent", color:days===d?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{d}d</button>
        ))}
        <div style={{ flex:1 }}/>
        <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{total} entries</span>
        <button onClick={exportAudit} style={{ padding:"7px 16px", borderRadius:6, border:"1px solid #f59e0b30", background:"#f59e0b10", color:"#f59e0b", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
          📤 Export CSV
        </button>
      </div>

      {/* Log table */}
      <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #1e1e2e", background:"#0a0a0f" }}>
              {["Time","Admin","Action","Target","Detail","IP"].map(h=>(
                <th key={h} style={{ padding:"11px 14px", textAlign:"left", color:"#4a4a6a", fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:1, fontWeight:400 }}>{h.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding:40, textAlign:"center" }}><Spinner size={24}/></td></tr>
            ) : entries.length===0 ? (
              <tr><td colSpan={6} style={{ padding:"40px 20px", textAlign:"center", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>No audit entries found</td></tr>
            ) : entries.map(e=>(
              <tr key={e.id} style={{ borderBottom:"1px solid #0a0a12" }}>
                <td style={{ padding:"9px 14px", color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap" }}>
                  {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
                </td>
                <td style={{ padding:"9px 14px", color:"#6366f1", fontSize:11, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {e.admin_email}
                </td>
                <td style={{ padding:"9px 14px", whiteSpace:"nowrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:14 }}>{getIcon(e.action)}</span>
                    <span style={{ color:getColor(e.action), fontSize:11, fontFamily:"'DM Mono',monospace" }}>{e.action}</span>
                  </div>
                </td>
                <td style={{ padding:"9px 14px", color:"#4a4a6a", fontSize:11 }}>
                  {e.target_type && <span style={{ fontFamily:"'DM Mono',monospace" }}>{e.target_type}{e.target_id?` #${e.target_id}`:""}</span>}
                </td>
                <td style={{ padding:"9px 14px", color:"#8888aa", fontSize:11, maxWidth:240, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={e.detail||""}>
                  {e.detail||"—"}
                </td>
                <td style={{ padding:"9px 14px", color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{e.ip_address||"—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PER_PAGE && (
        <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:16 }}>
          <button onClick={()=>{ const p=Math.max(0,page-1); setPage(p); load(p); }} disabled={page===0} style={{ padding:"6px 14px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:page===0?"#2a2a3e":"#ccc", cursor:page===0?"not-allowed":"pointer", fontSize:12 }}>← Prev</button>
          <span style={{ color:"#4a4a6a", fontSize:12, padding:"6px 12px" }}>Page {page+1} of {Math.ceil(total/PER_PAGE)}</span>
          <button onClick={()=>{ const p=page+1; setPage(p); load(p); }} disabled={(page+1)*PER_PAGE>=total} style={{ padding:"6px 14px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:(page+1)*PER_PAGE>=total?"#2a2a3e":"#ccc", cursor:(page+1)*PER_PAGE>=total?"not-allowed":"pointer", fontSize:12 }}>Next →</button>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 8 — AUTOMATED RULES ENGINE
// ════════════════════════════════════════════════════════════════════════════
function RulesEngine({ api, toast }) {
  const [data,      setData]    = useState(null);
  const [loading,   setLoading] = useState(true);
  const [showForm,  setShowForm] = useState(false);
  const [editing,   setEditing]  = useState(null);
  const [running,   setRunning]  = useState(false);
  const [saving,    setSaving]   = useState(false);

  const BLANK = { name:"", enabled:true, trigger:"user_inactive_days", trigger_value:14, action:"send_email", action_value:"", action_body:"" };
  const [form, setForm] = useState(BLANK);

  useEffect(()=>{
    api.get("/admin/rules").then(setData).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  },[]);

  const save = async () => {
    if (!form.name) { toast.error("Rule name required"); return; }
    setSaving(true);
    try {
      let updated;
      if (editing) {
        updated = await api.patch(`/admin/rules/${editing}`, form);
        setData(d => ({ ...d, rules: d.rules.map(r=>r.id===editing?updated:r) }));
        toast.success("Rule updated");
      } else {
        const newRule = await api.post("/admin/rules", form);
        setData(d => ({ ...d, rules: [...(d.rules||[]), newRule] }));
        toast.success("Rule created");
      }
      setShowForm(false); setEditing(null); setForm(BLANK);
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete rule "${name}"?`)) return;
    try { await api.del(`/admin/rules/${id}`); setData(d=>({...d,rules:d.rules.filter(r=>r.id!==id)})); toast.success("Deleted"); }
    catch(e) { toast.error(e.message); }
  };

  const toggle = async (rule) => {
    try {
      const updated = await api.patch(`/admin/rules/${rule.id}`, {...rule, enabled:!rule.enabled});
      setData(d=>({...d,rules:d.rules.map(r=>r.id===rule.id?updated:r)}));
    } catch(e) { toast.error(e.message); }
  };

  const runNow = async () => {
    setRunning(true);
    try { await api.post("/admin/rules/run"); toast.success("Rules engine running in background"); }
    catch(e) { toast.error(e.message); }
    finally { setRunning(false); }
  };

  const startEdit = (rule) => { setEditing(rule.id); setForm({...rule}); setShowForm(true); };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };

  const triggers = data?.triggers || {};
  const actions  = data?.actions  || {};
  const rules    = data?.rules    || [];

  const NEEDS_BODY = ["send_email","send_admin_email","add_note"];

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
        <div>
          <div style={{ color:"#fff", fontSize:14, fontWeight:600 }}>Automation Rules</div>
          <div style={{ color:"#4a4a6a", fontSize:12 }}>Rules run automatically every day at 6am. Email templates support {"{email}"} and {"{plan}"} variables.</div>
        </div>
        <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
          <button onClick={runNow} disabled={running} style={{ padding:"8px 16px", borderRadius:6, border:"1px solid #8b5cf630", background:"#8b5cf610", color:"#a78bfa", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:running?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
            {running?<><Spinner size={11}/> Running…</>:"▶ Run Now"}
          </button>
          <button onClick={()=>{setShowForm(true);setEditing(null);setForm(BLANK);}} style={{ padding:"8px 18px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>+ New Rule</button>
        </div>
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:12, padding:22, marginBottom:20 }}>
          <div style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", marginBottom:16 }}>{editing?"Edit Rule":"New Rule"}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={lS}>RULE NAME</label>
              <input style={iS} placeholder="e.g. Nudge inactive users" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
            </div>
            <div>
              <label style={lS}>TRIGGER</label>
              <select style={iS} value={form.trigger} onChange={e=>setForm(f=>({...f,trigger:e.target.value}))}>
                {Object.entries(triggers).map(([id,label])=><option key={id} value={id}>{label}</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>TRIGGER VALUE (days / % / score)</label>
              <input style={iS} type="number" min={1} value={form.trigger_value} onChange={e=>setForm(f=>({...f,trigger_value:parseInt(e.target.value)||1}))}/>
            </div>
            <div>
              <label style={lS}>ACTION</label>
              <select style={iS} value={form.action} onChange={e=>setForm(f=>({...f,action:e.target.value}))}>
                {Object.entries(actions).map(([id,label])=><option key={id} value={id}>{label}</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>{form.action==="change_plan"?"PLAN NAME":form.action==="add_note"?"NOTE TITLE":"EMAIL SUBJECT"}</label>
              <input style={iS} placeholder={form.action==="change_plan"?"starter / pro":form.action==="add_note"?"Auto-flagged: inactive":"e.g. We noticed you haven't scanned recently"} value={form.action_value} onChange={e=>setForm(f=>({...f,action_value:e.target.value}))}/>
            </div>
            {NEEDS_BODY.includes(form.action) && (
              <div style={{ gridColumn:"1/-1" }}>
                <label style={lS}>{form.action==="add_note"?"NOTE BODY":"EMAIL BODY (supports {email}, {plan} variables)"}</label>
                <textarea style={{...iS, minHeight:80, resize:"vertical"}} value={form.action_body} onChange={e=>setForm(f=>({...f,action_body:e.target.value}))} placeholder={form.action==="add_note"?"Auto-flagged by rule: user has been inactive for 14+ days.":"Hey, we noticed you haven't used Socialisten in a while. Here's a quick tip to get more matches..."}/>
              </div>
            )}
          </div>
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <button onClick={save} disabled={saving} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:saving?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {saving?<><Spinner size={11}/> Saving…</>:editing?"Update Rule":"Create Rule"}
            </button>
            <button onClick={()=>{setShowForm(false);setEditing(null);}} style={{ padding:"9px 16px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Rules list */}
      {rules.length===0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>⚙️</div>
          No rules yet — create your first automation above
        </div>
      ) : rules.map(rule=>{
        const tc = rule.enabled ? "#22c55e" : "#3a3a5a";
        return (
          <div key={rule.id} style={{ background:"#0f0f1a", border:`1px solid ${rule.enabled?"#1e1e2e":"#111118"}`, borderLeft:`3px solid ${tc}`, borderRadius:10, padding:"16px 18px", marginBottom:10, opacity:rule.enabled?1:0.55 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                  <span style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{rule.name}</span>
                  {!rule.enabled && <span style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>DISABLED</span>}
                </div>
                <div style={{ color:"#4a4a6a", fontSize:11, lineHeight:1.6 }}>
                  <span style={{ color:"#a5b4fc" }}>IF</span> {triggers[rule.trigger]||rule.trigger} {rule.trigger_value && `(${rule.trigger_value})`}
                  <span style={{ color:"#a5b4fc", marginLeft:6 }}>→</span>
                  <span style={{ marginLeft:6 }}>{actions[rule.action]||rule.action}</span>
                  {rule.action_value && <span style={{ color:"#6366f1", marginLeft:4 }}>"{rule.action_value}"</span>}
                </div>
                {(rule.last_run || rule.run_count > 0) && (
                  <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", marginTop:4 }}>
                    {rule.run_count > 0 && `Fired ${rule.run_count} times`}
                    {rule.last_run && ` · Last: ${new Date(rule.last_run).toLocaleDateString()}`}
                  </div>
                )}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                {/* Toggle */}
                <div onClick={()=>toggle(rule)} style={{ width:40,height:22,borderRadius:11,background:rule.enabled?"#22c55e":"#2a2a3e",position:"relative",cursor:"pointer",transition:"all 0.2s" }}>
                  <div style={{ position:"absolute",top:3,left:rule.enabled?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/>
                </div>
                <button onClick={()=>startEdit(rule)} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Edit</button>
                <button onClick={()=>del(rule.id, rule.name)} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Delete</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 9 — DATA EXPORT CENTER
// ════════════════════════════════════════════════════════════════════════════
function ExportCenter({ api, toast }) {
  const API_BASE_URL = typeof window !== "undefined"
    ? (window.REACT_APP_API_URL || "https://socipulses.com")
    : "https://socipulses.com";

  const [matchDays,    setMatchDays]    = useState(30);
  const [matchPlatform,setMatchPlatform]= useState("");
  const [matchScore,   setMatchScore]   = useState(0);
  const [userPlan,     setUserPlan]     = useState("");
  const [auditDays,    setAuditDays]    = useState(30);
  const [downloading,  setDownloading]  = useState({});

  const download = async (key, path) => {
    setDownloading(d=>({...d,[key]:true}));
    try {
      const token = localStorage.getItem("sl_token");
      const res = await fetch(`${API_BASE_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Export failed — " + res.status);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = path.split("?")[0].split("/").pop() + `_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch(e) { toast.error(e.message); }
    finally { setDownloading(d=>({...d,[key]:false})); }
  };

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"22px 24px", marginBottom:14 };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };
  const sS = { background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 10px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none" };
  const DLBtn = ({id, label, loading}) => (
    <button onClick={()=>download(id, label)} disabled={!!downloading[id]} style={{ padding:"9px 18px", borderRadius:6, border:"none", background:downloading[id]?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:downloading[id]?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
      {downloading[id]?<><Spinner size={12}/> Preparing…</>:"📥 Download CSV"}
    </button>
  );

  const EXPORTS = [
    {
      key:   "users",
      icon:  "👥",
      title: "User List",
      desc:  "All registered users with plan, join date, page count, and match count.",
      controls: (
        <div>
          <label style={lS}>FILTER BY PLAN</label>
          <select style={sS} value={userPlan} onChange={e=>setUserPlan(e.target.value)}>
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            
          </select>
        </div>
      ),
      path: () => `/admin/export/users?format=csv${userPlan?`&plan=${userPlan}`:""}`,
      cols: ["id","email","plan","pages_limit","active","joined","pages","matches"],
    },
    {
      key:   "matches",
      icon:  "🎯",
      title: "Keyword Matches",
      desc:  "All matches across all users for the selected time range. Includes post text, score, platform, and CRM status.",
      controls: (
        <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
          <div>
            <label style={lS}>DATE RANGE</label>
            <select style={sS} value={matchDays} onChange={e=>setMatchDays(parseInt(e.target.value))}>
              {[7,14,30,60,90].map(d=><option key={d} value={d}>Last {d} days</option>)}
            </select>
          </div>
          <div>
            <label style={lS}>PLATFORM</label>
            <select style={sS} value={matchPlatform} onChange={e=>setMatchPlatform(e.target.value)}>
              <option value="">All</option>
              <option value="facebook">Facebook</option>
              <option value="reddit">Reddit</option>
              <option value="nextdoor">Nextdoor</option>
              <option value="nextdoor">Nextdoor</option>
            </select>
          </div>
          <div>
            <label style={lS}>MIN SCORE</label>
            <select style={sS} value={matchScore} onChange={e=>setMatchScore(parseInt(e.target.value))}>
              <option value={0}>Any score</option>
              <option value={25}>25+ (High)</option>
              <option value={40}>40+ (Urgent)</option>
            </select>
          </div>
        </div>
      ),
      path: () => `/admin/export/matches?format=csv&days=${matchDays}${matchPlatform?`&platform=${matchPlatform}`:""}${matchScore?`&min_score=${matchScore}`:""}`,
      cols: ["id","user_email","page","platform","score","post_text","post_url","keywords","found_at","status"],
    },
    {
      key:   "revenue",
      icon:  "💰",
      title: "Revenue Report",
      desc:  "All paying users with plan, MRR, ARR, Stripe subscription status, and Stripe customer ID.",
      controls: null,
      path: () => `/admin/export/revenue?format=csv`,
      cols: ["user_id","email","plan","joined","sub_status","stripe_id","mrr","arr"],
    },
    {
      key:   "pages",
      icon:  "📡",
      title: "Monitored Pages",
      desc:  "Every page across all users — platform, scan status, posts checked, matches found, and keyword count.",
      controls: null,
      path: () => `/admin/export/pages?format=csv`,
      cols: ["id","user_email","name","platform","active","last_scan","status","posts","matches","keywords"],
    },
    {
      key:   "audit",
      icon:  "📋",
      title: "Audit Log",
      desc:  "All admin actions for the selected time range — who did what, when, from which IP.",
      controls: (
        <div>
          <label style={lS}>DATE RANGE</label>
          <select style={sS} value={auditDays} onChange={e=>setAuditDays(parseInt(e.target.value))}>
            {[7,14,30,90,365].map(d=><option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
      ),
      path: () => `/admin/export/audit?format=csv&days=${auditDays}`,
      cols: ["timestamp","admin","action","target_type","target_id","detail","ip"],
    },
  ];

  return (
    <div>
      <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:20, lineHeight:1.7 }}>
        All exports are CSV files that open directly in Excel or Google Sheets.
        Export actions are recorded in the audit log. Files are generated on-demand — no storage used.
      </div>

      {EXPORTS.map(exp=>(
        <div key={exp.key} style={{ ...cardS }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:10, background:"#6366f120", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{exp.icon}</div>
            <div style={{ flex:1 }}>
              <div style={{ color:"#fff", fontSize:14, fontWeight:600, marginBottom:4 }}>{exp.title}</div>
              <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:12, lineHeight:1.6 }}>{exp.desc}</div>

              {/* Column preview */}
              <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:12 }}>
                {exp.cols.map(c=>(
                  <span key={c} style={{ padding:"2px 8px", borderRadius:3, background:"#1e1e2e", color:"#6666aa", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{c}</span>
                ))}
              </div>

              {/* Controls + download */}
              <div style={{ display:"flex", alignItems:"flex-end", gap:16, flexWrap:"wrap" }}>
                {exp.controls}
                <DLBtn id={exp.key} label={exp.path()} loading={downloading[exp.key]}/>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 10 — GLOBAL SEARCH
// ════════════════════════════════════════════════════════════════════════════
function GlobalSearch({ api, toast, onNavigate }) {
  const [query,    setQuery]    = useState("");
  const [results,  setResults]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [focused,  setFocused]  = useState(false);
  const debounceRef = useRef(null);

  const search = (q) => {
    if (!q || q.length < 2) { setResults(null); return; }
    setLoading(true);
    api.get(`/admin/search?q=${encodeURIComponent(q)}&limit=6`)
      .then(setResults)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const onChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 300);
  };

  const ICONS = { user:"👤", page:"📡", match:"🎯", coupon:"🏷️" };
  const PLAN_COLORS = { free:"#4a4a6a", starter:"#22c55e", pro:"#6366f1" };
  const PLAT_ICONS  = { facebook:"🔵", reddit:"🟠", };

  const sections = results ? [
    { key:"users",   label:"Users",   items:results.results.users   },
    { key:"pages",   label:"Pages",   items:results.results.pages   },
    { key:"matches", label:"Matches", items:results.results.matches },
    { key:"coupons", label:"Coupons", items:results.results.coupons },
  ].filter(s => s.items.length > 0) : [];

  const handleSelect = (item) => {
    setQuery(""); setResults(null); setFocused(false);
    if (item._type === "user")   onNavigate("admin");
    if (item._type === "page")   onNavigate("pages");
    if (item._type === "match")  onNavigate("feed");
    if (item._type === "coupon") onNavigate("coupons");
  };

  return (
    <div style={{ position:"relative" }}>
      {/* Search input */}
      <div style={{ position:"relative", display:"flex", alignItems:"center" }}>
        <span style={{ position:"absolute", left:12, color:"#4a4a6a", fontSize:14, pointerEvents:"none" }}>🔍</span>
        <input
          value={query}
          onChange={onChange}
          onFocus={()=>setFocused(true)}
          onBlur={()=>setTimeout(()=>setFocused(false),200)}
          placeholder="Search users, pages, matches, coupons..."
          style={{ width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:8, padding:"10px 14px 10px 36px", color:"#ccc", fontSize:13, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" }}
        />
        {loading && <span style={{ position:"absolute", right:12 }}><Spinner size={14}/></span>}
        {query && !loading && <span onClick={()=>{setQuery("");setResults(null);}} style={{ position:"absolute", right:12, color:"#4a4a6a", cursor:"pointer", fontSize:16 }}>×</span>}
      </div>

      {/* Results dropdown */}
      {focused && results && sections.length > 0 && (
        <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, right:0, background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:10, zIndex:3000, boxShadow:"0 8px 32px rgba(0,0,0,0.6)", overflow:"hidden", maxHeight:480, overflowY:"auto" }}>
          <div style={{ padding:"8px 14px", borderBottom:"1px solid #1e1e2e", display:"flex", justifyContent:"space-between" }}>
            <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
              {results.total} result{results.total!==1?"s":""} for "{results.query}"
            </span>
          </div>
          {sections.map(sec=>(
            <div key={sec.key}>
              <div style={{ padding:"8px 14px 4px", color:"#3a3a5a", fontSize:9, fontFamily:"'DM Mono',monospace", letterSpacing:1 }}>
                {ICONS[sec.key]} {sec.label.toUpperCase()}
              </div>
              {sec.items.map(item=>(
                <div
                  key={`${item._type}-${item.id}`}
                  onClick={()=>handleSelect(item)}
                  style={{ padding:"10px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:10, borderBottom:"1px solid #0a0a12" }}
                >
                  <span style={{ fontSize:16, flexShrink:0 }}>{ICONS[item._type]||"📌"}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ color:"#fff", fontSize:12, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {item._type==="page"&&<span style={{ marginRight:6 }}>{PLAT_ICONS[item.platform]||"📡"}</span>}
                      {item._label}
                    </div>
                    <div style={{ color:"#4a4a6a", fontSize:11, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item._sub}</div>
                  </div>
                  {item._type==="user" && (
                    <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, background:`${PLAN_COLORS[item.plan]||"#4a4a6a"}20`, color:PLAN_COLORS[item.plan]||"#4a4a6a", fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{item.plan}</span>
                  )}
                  {item._type==="match" && (
                    <span style={{ color: item.score>=40?"#ff4d4d":item.score>=25?"#f59e0b":"#22c55e", fontSize:12, fontFamily:"'DM Mono',monospace", fontWeight:700, flexShrink:0 }}>{item.score}</span>
                  )}
                  {item._type==="coupon" && (
                    <span style={{ color:item.active?"#22c55e":"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{item.active?"ACTIVE":"OFF"}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {focused && results && results.total===0 && (
        <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, right:0, background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:10, zIndex:3000, padding:"20px", textAlign:"center", color:"#3a3a5a", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
          No results for "{query}"
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 11 — SCHEDULED MAINTENANCE WINDOWS
// ════════════════════════════════════════════════════════════════════════════
function MaintenanceManager({ api, toast }) {
  const [windows,    setWindows]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [activating, setActivating] = useState(null);
  const [notifying,  setNotifying]  = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [modeOn,     setModeOn]     = useState(false);

  const BLANK = {
    title:"", message:"", scheduled_at:"",
    duration_minutes:60, notify_users:true, notify_hours_before:24
  };
  const [form, setForm] = useState(BLANK);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/maintenance/windows"),
      api.get("/admin/cms/flags"),
    ]).then(([w, f]) => {
      setWindows(w||[]);
      setModeOn(f?.maintenance_mode||false);
    }).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  };

  useEffect(()=>load(),[]);

  const save = async () => {
    if (!form.title||!form.message||!form.scheduled_at) { toast.error("Title, message, and time required"); return; }
    setSaving(true);
    try {
      const w = await api.post("/admin/maintenance/windows", form);
      setWindows(p=>[...p, w]);
      setShowForm(false); setForm(BLANK);
      toast.success("Maintenance window scheduled");
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this maintenance window?")) return;
    try { await api.del(`/admin/maintenance/windows/${id}`); setWindows(p=>p.filter(w=>w.id!==id)); toast.success("Deleted"); }
    catch(e) { toast.error(e.message); }
  };

  const notify = async (id) => {
    setNotifying(id);
    try { await api.post(`/admin/maintenance/windows/${id}/notify`); toast.success("Notification queued for all users"); load(); }
    catch(e) { toast.error(e.message); }
    finally { setNotifying(null); }
  };

  const activate = async (id) => {
    setActivating(id);
    try {
      await api.post(`/admin/maintenance/windows/${id}/activate`);
      setModeOn(true);
      toast.success("Maintenance mode activated");
    } catch(e) { toast.error(e.message); }
    finally { setActivating(null); }
  };

  const deactivate = async () => {
    try { await api.post("/admin/maintenance/deactivate"); setModeOn(false); toast.success("Maintenance mode deactivated"); }
    catch(e) { toast.error(e.message); }
  };

  const STATUS_CFG = {
    active:    { color:"#ff4d4d", bg:"#ff4d4d15", label:"ACTIVE NOW" },
    scheduled: { color:"#22c55e", bg:"#22c55e15", label:"SCHEDULED"  },
    pending:   { color:"#f59e0b", bg:"#f59e0b15", label:"PENDING"    },
    completed: { color:"#4a4a6a", bg:"#1e1e2e",   label:"COMPLETED"  },
  };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };

  return (
    <div>
      {/* Live maintenance mode toggle */}
      <div style={{ background: modeOn?"#1a0808":"#0f0f1a", border:`1px solid ${modeOn?"#ff4d4d30":"#1e1e2e"}`, borderRadius:12, padding:"18px 20px", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:24 }}>{modeOn?"🚨":"🔧"}</span>
          <div>
            <div style={{ color:modeOn?"#ff4d4d":"#fff", fontSize:14, fontWeight:700 }}>
              Maintenance Mode — {modeOn?"ACTIVE":"OFF"}
            </div>
            <div style={{ color:"#4a4a6a", fontSize:12 }}>
              {modeOn ? "Logged-out visitors see the maintenance page. Logged-in users still have access." : "Site is fully operational. Turn on during planned maintenance."}
            </div>
          </div>
          <div style={{ marginLeft:"auto" }}>
            {modeOn ? (
              <button onClick={deactivate} style={{ padding:"9px 20px", borderRadius:7, border:"none", background:"#22c55e", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:600 }}>
                ✓ End Maintenance
              </button>
            ) : (
              <button onClick={()=>setShowForm(true)} style={{ padding:"9px 20px", borderRadius:7, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                + Schedule Window
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Schedule form */}
      {showForm && (
        <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:12, padding:22, marginBottom:16 }}>
          <div style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", marginBottom:16 }}>Schedule Maintenance Window</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={lS}>TITLE (shown in notification email)</label>
              <input style={iS} placeholder="e.g. Database upgrade" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}/>
            </div>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={lS}>MESSAGE TO USERS</label>
              <textarea style={{...iS,minHeight:60,resize:"vertical"}} placeholder="We will be performing scheduled maintenance to improve performance..." value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))}/>
            </div>
            <div>
              <label style={lS}>SCHEDULED START (your local time)</label>
              <input style={iS} type="datetime-local" value={form.scheduled_at} onChange={e=>setForm(f=>({...f,scheduled_at:e.target.value}))}/>
            </div>
            <div>
              <label style={lS}>ESTIMATED DURATION (minutes)</label>
              <input style={iS} type="number" min={5} max={480} value={form.duration_minutes} onChange={e=>setForm(f=>({...f,duration_minutes:parseInt(e.target.value)||60}))}/>
            </div>
            <div>
              <label style={lS}>NOTIFY USERS HOW MANY HOURS BEFORE?</label>
              <select style={iS} value={form.notify_hours_before} onChange={e=>setForm(f=>({...f,notify_hours_before:parseInt(e.target.value)}))}>
                {[2,6,12,24,48].map(h=><option key={h} value={h}>{h} hours before</option>)}
              </select>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10, paddingTop:22 }}>
              <div onClick={()=>setForm(f=>({...f,notify_users:!f.notify_users}))} style={{ width:40,height:22,borderRadius:11,background:form.notify_users?"#6366f1":"#2a2a3e",position:"relative",cursor:"pointer",transition:"all 0.2s" }}>
                <div style={{ position:"absolute",top:3,left:form.notify_users?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/>
              </div>
              <span style={{ color:"#ccc", fontSize:12 }}>Email all users</span>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <button onClick={save} disabled={saving} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:saving?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {saving?<><Spinner size={11}/> Saving…</>:"Schedule Window"}
            </button>
            <button onClick={()=>{setShowForm(false);setForm(BLANK);}} style={{ padding:"9px 16px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Window list */}
      {loading ? <div style={{ display:"flex",justifyContent:"center",padding:40 }}><Spinner size={28}/></div>
      : windows.length===0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🗓️</div>
          No maintenance windows scheduled
        </div>
      ) : windows.map(w=>{
        const sc = STATUS_CFG[w.status]||STATUS_CFG.pending;
        const isCompleted = w.status==="completed";
        return (
          <div key={w.id} style={{ background:"#0f0f1a", border:`1px solid ${isCompleted?"#111118":"#1e1e2e"}`, borderLeft:`3px solid ${sc.color}`, borderRadius:10, padding:"16px 18px", marginBottom:10, opacity:isCompleted?0.5:1 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                  <span style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{w.title}</span>
                  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:sc.bg, color:sc.color }}>{sc.label}</span>
                  {w.notification_sent && <span style={{ color:"#22c55e", fontSize:10 }}>✉ Notified {w.users_notified||0} users</span>}
                </div>
                <div style={{ color:"#8888aa", fontSize:12, marginBottom:6 }}>{w.message}</div>
                <div style={{ display:"flex", gap:16, color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", flexWrap:"wrap" }}>
                  <span>📅 {new Date(w.scheduled_at).toLocaleString()}</span>
                  <span>⏱️ {w.duration_minutes} min</span>
                  <span>👤 {w.created_by}</span>
                </div>
              </div>
              {!isCompleted && (
                <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                  {!w.notification_sent && w.notify_users && (
                    <button onClick={()=>notify(w.id)} disabled={notifying===w.id} style={{ padding:"6px 12px", borderRadius:5, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:notifying===w.id?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:4 }}>
                      {notifying===w.id?<Spinner size={10}/>:"📧"} Notify
                    </button>
                  )}
                  <button onClick={()=>activate(w.id)} disabled={activating===w.id||modeOn} style={{ padding:"6px 12px", borderRadius:5, border:"1px solid #f59e0b30", background:"#f59e0b10", color:"#f59e0b", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:(activating===w.id||modeOn)?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:4 }}>
                    {activating===w.id?<Spinner size={10}/>:"🚨"} Activate
                  </button>
                  <button onClick={()=>del(w.id)} style={{ padding:"6px 12px", borderRadius:5, border:"1px solid #ff4d4d20", background:"transparent", color:"#ff4d4d", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>✕</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 12 — SUBSCRIPTION MANAGEMENT
// ════════════════════════════════════════════════════════════════════════════
function SubscriptionManager({ api, toast }) {
  const [subs,       setSubs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [selected,   setSelected]   = useState(null);
  const [action,     setAction]     = useState(null);  // "extend"|"plan"|"free_month"|"cancel"
  const [saving,     setSaving]     = useState(false);
  const [form,       setForm]       = useState({});

  useEffect(()=>{
    api.get("/admin/subscriptions/overview").then(setSubs).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  },[]);

  const reload = () => api.get("/admin/subscriptions/overview").then(setSubs).catch(()=>{});

  const PLAN_COLORS = { free:"#4a4a6a", starter:"#22c55e", pro:"#6366f1" };
  const STATUS_COLORS = { active:"#22c55e", trialing:"#6366f1", past_due:"#ff4d4d", canceled:"#ff4d4d", "—":"#4a4a6a" };

  const filtered = subs
    .filter(s=>!planFilter||s.plan===planFilter)
    .filter(s=>!search||s.email.toLowerCase().includes(search.toLowerCase()));

  const openAction = (sub, act) => { setSelected(sub); setAction(act); setForm({}); };

  const run = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      let result;
      if (action==="extend") {
        if (!form.days||form.days<1) { toast.error("Enter number of days"); setSaving(false); return; }
        result = await api.post("/admin/subscriptions/extend-trial",
          { user_id:selected.id, days:parseInt(form.days), reason:form.reason||"" });
        toast.success(`Trial extended ${form.days} days for ${selected.email}`);
      } else if (action==="plan") {
        if (!form.plan) { toast.error("Select a plan"); setSaving(false); return; }
        result = await api.post("/admin/subscriptions/override-plan",
          { user_id:selected.id, plan:form.plan, pages_limit:form.pages_limit?parseInt(form.pages_limit):null, reason:form.reason||"" });
        toast.success(`Plan changed to ${form.plan} for ${selected.email}`);
      } else if (action==="free_month") {
        if (!form.months||form.months<1) { toast.error("Enter number of months"); setSaving(false); return; }
        result = await api.post(`/admin/subscriptions/give-free-month?user_id=${selected.id}&months=${form.months}&reason=${encodeURIComponent(form.reason||"")}`);
        toast.success(result.applied_to_stripe ? `${form.months} free month(s) applied via Stripe` : result.note||"Done");
      } else if (action==="cancel") {
        if (!window.confirm(`Cancel ${selected.email}'s subscription at period end?`)) { setSaving(false); return; }
        result = await api.post(`/admin/subscriptions/cancel-at-period-end?user_id=${selected.id}`);
        toast.success(`Cancellation scheduled for ${selected.email}`);
      }
      setAction(null); setSelected(null); reload();
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const iS = { width:"100%", background:"#0a0a15", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };

  const ACTION_CFG = {
    extend:     { label:"Extend Trial",     color:"#6366f1", icon:"⏰" },
    plan:       { label:"Change Plan",      color:"#22c55e", icon:"💰" },
    free_month: { label:"Give Free Month",  color:"#f59e0b", icon:"🎁" },
    cancel:     { label:"Cancel Sub",       color:"#ff4d4d", icon:"✕"  },
  };

  return (
    <div>
      {/* Action modal */}
      {action && selected && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setAction(null)}>
          <div style={{ background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:12, padding:24, width:440, maxWidth:"90vw" }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <span style={{ fontSize:22 }}>{ACTION_CFG[action]?.icon}</span>
              <div>
                <div style={{ color:"#fff", fontSize:14, fontWeight:600 }}>{ACTION_CFG[action]?.label}</div>
                <div style={{ color:"#4a4a6a", fontSize:12 }}>{selected.email} · {selected.plan}</div>
              </div>
            </div>

            {action==="extend" && (
              <div style={{ display:"grid", gap:12 }}>
                <div><label style={lS}>DAYS TO ADD</label><input style={iS} type="number" min={1} max={90} placeholder="e.g. 7" value={form.days||""} onChange={e=>setForm(f=>({...f,days:e.target.value}))}/></div>
                <div><label style={lS}>REASON (sent to user)</label><input style={iS} placeholder="e.g. Onboarding support" value={form.reason||""} onChange={e=>setForm(f=>({...f,reason:e.target.value}))}/></div>
              </div>
            )}
            {action==="plan" && (
              <div style={{ display:"grid", gap:12 }}>
                <div><label style={lS}>NEW PLAN</label>
                  <select style={iS} value={form.plan||""} onChange={e=>setForm(f=>({...f,plan:e.target.value}))}>
                    <option value="">Select plan...</option>
                    {["free","starter","pro"].map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                  </select>
                </div>
                <div><label style={lS}>CUSTOM PAGE LIMIT (leave blank for plan default)</label><input style={iS} type="number" min={1} placeholder="e.g. 25" value={form.pages_limit||""} onChange={e=>setForm(f=>({...f,pages_limit:e.target.value}))}/></div>
                <div><label style={lS}>REASON (internal note only)</label><input style={iS} placeholder="e.g. Retention — churned twice" value={form.reason||""} onChange={e=>setForm(f=>({...f,reason:e.target.value}))}/></div>
                <div style={{ padding:"10px 12px", background:"#1a1200", borderRadius:7, border:"1px solid #f59e0b20", fontSize:11, color:"#d4a76a" }}>
                  ⚠️ This changes the plan in the database only. If the user has an active Stripe subscription, update it in the Stripe dashboard too.
                </div>
              </div>
            )}
            {action==="free_month" && (
              <div style={{ display:"grid", gap:12 }}>
                <div><label style={lS}>NUMBER OF FREE MONTHS</label><input style={iS} type="number" min={1} max={12} placeholder="e.g. 1" value={form.months||""} onChange={e=>setForm(f=>({...f,months:e.target.value}))}/></div>
                <div><label style={lS}>REASON (sent to user in email)</label><input style={iS} placeholder="e.g. Thank you for being a loyal customer" value={form.reason||""} onChange={e=>setForm(f=>({...f,reason:e.target.value}))}/></div>
                <div style={{ padding:"10px 12px", background:"#0a1a0a", borderRadius:7, border:"1px solid #22c55e20", fontSize:11, color:"#4a6a4a" }}>
                  ✓ A 100% discount coupon is created in Stripe and applied to the user's next {form.months||"N"} billing cycle(s). Customer receives a notification email.
                </div>
              </div>
            )}
            {action==="cancel" && (
              <div style={{ padding:"14px", background:"#1a0808", borderRadius:8, border:"1px solid #ff4d4d20", fontSize:12, color:"#ff8888", lineHeight:1.7 }}>
                This will schedule cancellation at the end of the current billing period.<br/>
                The user keeps access until their period ends. Their data is preserved.
              </div>
            )}

            <div style={{ display:"flex", gap:8, marginTop:16 }}>
              <button onClick={run} disabled={saving} style={{ flex:1, padding:"10px", borderRadius:7, border:"none", background:saving?"#2a2a3e":`linear-gradient(135deg,${ACTION_CFG[action]?.color},${ACTION_CFG[action]?.color}cc)`, color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                {saving?<><Spinner size={12}/> Processing…</>:`${ACTION_CFG[action]?.icon} ${ACTION_CFG[action]?.label}`}
              </button>
              <button onClick={()=>setAction(null)} style={{ padding:"10px 16px", borderRadius:7, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by email..." style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", width:220 }}/>
        {["","free","starter","pro"].map(p=>(
          <button key={p||"all"} onClick={()=>setPlanFilter(p)} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${planFilter===p?"#6366f1":"#1e1e2e"}`, background:planFilter===p?"#6366f120":"transparent", color:planFilter===p?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", textTransform:"capitalize" }}>
            {p||"All"} {p&&`(${subs.filter(s=>s.plan===p).length})`}
          </button>
        ))}
        <span style={{ marginLeft:"auto", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", display:"flex", alignItems:"center" }}>
          {filtered.length} users
        </span>
      </div>

      {/* Subscription table */}
      {loading ? <div style={{ display:"flex",justifyContent:"center",padding:40 }}><Spinner size={28}/></div> : (
        <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#0a0a0f", borderBottom:"1px solid #1e1e2e" }}>
                {["User","Plan","Status","Pages","Joined","Actions"].map(h=>(
                  <th key={h} style={{ padding:"11px 14px", textAlign:"left", color:"#4a4a6a", fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:1, fontWeight:400 }}>{h.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0,100).map(s=>(
                <tr key={s.id} style={{ borderBottom:"1px solid #0a0a12" }}>
                  <td style={{ padding:"10px 14px" }}>
                    <div style={{ color:"#fff", fontSize:12, fontWeight:600 }}>{s.email}</div>
                    {s.stripe_cid && <div style={{ color:"#3a3a5a", fontSize:9, fontFamily:"'DM Mono',monospace" }}>#{s.id}</div>}
                  </td>
                  <td style={{ padding:"10px 14px" }}>
                    <span style={{ padding:"3px 10px", borderRadius:5, fontSize:10, fontFamily:"'DM Mono',monospace", background:`${PLAN_COLORS[s.plan]||"#4a4a6a"}20`, color:PLAN_COLORS[s.plan]||"#4a4a6a", textTransform:"capitalize" }}>{s.plan}</span>
                  </td>
                  <td style={{ padding:"10px 14px" }}>
                    <span style={{ color:STATUS_COLORS[s.sub_status]||"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{s.sub_status}</span>
                    {s.trial_ends_at && <div style={{ color:"#6366f1", fontSize:9, fontFamily:"'DM Mono',monospace" }}>trial ends {new Date(s.trial_ends_at).toLocaleDateString()}</div>}
                  </td>
                  <td style={{ padding:"10px 14px", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                    {s.page_count}/{s.pages_limit===999?"∞":s.pages_limit}
                  </td>
                  <td style={{ padding:"10px 14px", color:"#3a3a5a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{s.joined}</td>
                  <td style={{ padding:"10px 14px" }}>
                    <div style={{ display:"flex", gap:4 }}>
                      {[
                        { act:"extend",     color:"#6366f1", label:"Extend" },
                        { act:"plan",       color:"#22c55e", label:"Plan"   },
                        { act:"free_month", color:"#f59e0b", label:"Gift"   },
                        { act:"cancel",     color:"#ff4d4d", label:"Cancel" },
                      ].map(a=>(
                        <button key={a.act} onClick={()=>openAction(s,a.act)} style={{ padding:"4px 9px", borderRadius:4, border:`1px solid ${a.color}30`, background:`${a.color}10`, color:a.color, fontSize:9, fontFamily:"'DM Mono',monospace", cursor:"pointer", whiteSpace:"nowrap" }}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length===0 && <div style={{ padding:"40px 20px", textAlign:"center", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>No users match filter</div>}
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 13 — PLATFORM ANALYTICS
// ════════════════════════════════════════════════════════════════════════════
function PlatformAnalytics({ api, toast }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [days,    setDays]    = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/platform-analytics?days=${days}`)
      .then(setData).catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  const PLAT_COLORS  = { facebook:"#1877F2", reddit:"#ff4500", };
  const PLAT_ICONS   = { facebook:"🔵", reddit:"🟠", };
  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;
  if (!data)   return <div style={{ color:"#ff4d4d",padding:40 }}>Failed to load</div>;

  const maxHour = Math.max(...(data.by_hour||[]).map(h => h.count), 1);
  const maxDow  = Math.max(...(data.by_day_of_week||[]).map(d => d.count), 1);
  const maxKw   = data.top_keywords?.[0]?.count || 1;
  const maxHist = Math.max(...(data.score_histogram||[]).map(b => b.count), 1);
  const eng     = data.user_engagement || {};

  return (
    <div>
      {/* Day range selector */}
      <div style={{ display:"flex", gap:6, marginBottom:20 }}>
        {[7,14,30,60,90].map(d => (
          <button key={d} onClick={() => setDays(d)} style={{ padding:"6px 14px", borderRadius:6, border:`1px solid ${days===d?"#6366f1":"#1e1e2e"}`, background:days===d?"#6366f120":"transparent", color:days===d?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>{d}d</button>
        ))}
      </div>

      {/* Engagement summary */}
      <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
        {[
          { label:"ACTIVE USERS",    value:eng.active_users||0,    sub:`of ${eng.total_users||0} total`,     color:"#22c55e" },
          { label:"ENGAGEMENT RATE", value:`${eng.engagement_rate||0}%`, sub:"users with matches",           color:"#6366f1" },
          { label:"TOTAL MATCHES",   value:(eng.total_matches||0).toLocaleString(), sub:`last ${days} days`, color:"#f59e0b" },
          { label:"AVG / USER",      value:eng.avg_matches_per_user||0, sub:"matches per active user",       color:"#8b5cf6" },
        ].map(k => (
          <div key={k.label} style={{ ...cardS, marginBottom:0 }}>
            <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
            <div style={{ color:k.color, fontSize:28, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{k.value}</div>
            <div style={{ color:"#3a3a5a", fontSize:11, marginTop:6 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Per-platform breakdown */}
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Platform Performance</div>
        <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
          {Object.entries(data.by_platform||{}).map(([plat, stats]) => {
            const c = PLAT_COLORS[plat]||"#6366f1";
            return (
              <div key={plat} style={{ flex:"1 1 200px", background:"#0a0a0f", borderRadius:10, padding:"16px 18px", borderTop:`3px solid ${c}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                  <span style={{ fontSize:20 }}>{PLAT_ICONS[plat]||"📡"}</span>
                  <span style={{ color:"#fff", fontSize:13, fontWeight:600, textTransform:"capitalize" }}>{plat}</span>
                </div>
                {[
                  ["Matches",      stats.total_matches, c],
                  ["Avg Score",    stats.avg_score,     "#ccc"],
                  ["Max Score",    stats.max_score,     stats.max_score>=40?"#ff4d4d":"#ccc"],
                  ["Urgent",       stats.urgent,        "#ff4d4d"],
                  ["High",         stats.high,          "#f59e0b"],
                  ["Pages",        stats.active_pages,  "#ccc"],
                  ["Posts Checked",stats.total_posts,   "#4a4a6a"],
                  ["Match Rate",   `${stats.match_rate}%`, "#22c55e"],
                ].map(([k,v,color]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #111" }}>
                    <span style={{ color:"#4a4a6a", fontSize:11 }}>{k}</span>
                    <span style={{ color:color||"#ccc", fontSize:11, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })}
          {Object.keys(data.by_platform||{}).length===0 && (
            <div style={{ color:"#3a3a5a", fontSize:12, padding:"20px 0" }}>No data yet — run some scans first</div>
          )}
        </div>
      </div>

      <div className="sl-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        {/* Peak hours heatmap */}
        <div style={{ ...cardS, marginBottom:0 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:6 }}>Peak Hours (UTC)</div>
          <div style={{ color:"#4a4a6a", fontSize:11, marginBottom:14 }}>When leads are most active — best times to respond</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(12,1fr)", gap:3 }}>
            {(data.by_hour||[]).map(h => {
              const pct = maxHour > 0 ? h.count/maxHour : 0;
              const bg  = pct > 0.8?"#6366f1":pct > 0.5?"#6366f1aa":pct > 0.2?"#6366f166":"#1e1e2e";
              return (
                <div key={h.hour} title={`${h.label}: ${h.count} matches`} style={{ aspectRatio:"1", borderRadius:4, background:bg, cursor:"default", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <span style={{ color:"#fff", fontSize:7, fontFamily:"'DM Mono',monospace" }}>{h.hour}</span>
                </div>
              );
            })}
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:8, fontSize:10, color:"#3a3a5a" }}>
            <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
          </div>
          {data.by_hour?.length > 0 && (() => {
            const peak = [...data.by_hour].sort((a,b)=>b.count-a.count)[0];
            return peak?.count > 0 ? <div style={{ color:"#a5b4fc", fontSize:11, marginTop:10 }}>🔥 Peak: {peak.label} ({peak.count} matches)</div> : null;
          })()}
        </div>

        {/* Day of week */}
        <div style={{ ...cardS, marginBottom:0 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:6 }}>Best Days</div>
          <div style={{ color:"#4a4a6a", fontSize:11, marginBottom:14 }}>Which days produce the most leads</div>
          {(data.by_day_of_week||[]).map(d => {
            const pct = maxDow > 0 ? Math.round((d.count/maxDow)*100) : 0;
            const isTop = d.count === maxDow && maxDow > 0;
            return (
              <div key={d.day} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <span style={{ color:isTop?"#f59e0b":"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", width:32 }}>{d.day}</span>
                <div style={{ flex:1, height:8, background:"#1e1e2e", borderRadius:4, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:isTop?"#f59e0b":"#6366f1", borderRadius:4 }}/>
                </div>
                <span style={{ color:isTop?"#f59e0b":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", width:36, textAlign:"right" }}>{d.count}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sl-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        {/* Top keywords */}
        <div style={{ ...cardS, marginBottom:0 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Top Keywords (Global)</div>
          {(data.top_keywords||[]).slice(0,15).map((k, i) => {
            const pct = Math.round((k.count/maxKw)*100);
            return (
              <div key={k.keyword} style={{ marginBottom:8 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                  <span style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace" }}>#{i+1} {k.keyword}</span>
                  <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{k.count}</span>
                </div>
                <div style={{ height:5, background:"#1e1e2e", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:"linear-gradient(90deg,#6366f1,#8b5cf6)", borderRadius:3 }}/>
                </div>
              </div>
            );
          })}
          {(data.top_keywords||[]).length===0 && <div style={{ color:"#3a3a5a", fontSize:12 }}>No data yet</div>}
        </div>

        {/* Score histogram */}
        <div style={{ ...cardS, marginBottom:0 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Score Distribution</div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:4, height:120, paddingBottom:20, position:"relative" }}>
            {(data.score_histogram||[]).map((b, i) => {
              const h   = maxHist>0 ? Math.max(4,(b.count/maxHist)*100) : 4;
              const lo  = i * 10;
              const bg  = lo>=40?"#ff4d4d":lo>=25?"#f59e0b":"#22c55e";
              return (
                <div key={b.range} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end", height:"100%" }}>
                  <div title={`${b.range}: ${b.count}`} style={{ width:"100%", height:`${h}%`, background:bg, borderRadius:"3px 3px 0 0", minHeight:4, cursor:"pointer" }}/>
                  <div style={{ color:"#3a3a5a", fontSize:8, marginTop:3, fontFamily:"'DM Mono',monospace" }}>{lo}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display:"flex", gap:10, marginTop:4 }}>
            {[["#22c55e","Match (0-24)"],["#f59e0b","High (25-39)"],["#ff4d4d","Urgent (40+)"]].map(([c,l])=>(
              <div key={l} style={{ display:"flex", alignItems:"center", gap:4 }}>
                <div style={{ width:8,height:8,borderRadius:2,background:c }}/>
                <span style={{ color:"#4a4a6a", fontSize:9 }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top performing pages */}
      {(data.top_pages||[]).length > 0 && (
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>Top Pages by Match Count</div>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:"1px solid #1e1e2e" }}>
                {["Page","Platform","User","Matches","Avg Score","Max Score"].map(h=>(
                  <th key={h} style={{ padding:"8px 12px", textAlign:"left", color:"#4a4a6a", fontFamily:"'DM Mono',monospace", fontSize:10, fontWeight:400 }}>{h.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.top_pages.map((p,i) => (
                <tr key={i} style={{ borderBottom:"1px solid #0a0a12" }}>
                  <td style={{ padding:"9px 12px", color:"#ccc", fontWeight:600 }}>{p.name}</td>
                  <td style={{ padding:"9px 12px" }}>{PLAT_ICONS[p.platform]||"📡"}</td>
                  <td style={{ padding:"9px 12px", color:"#6366f1", fontSize:11 }}>{p.user_email}</td>
                  <td style={{ padding:"9px 12px", color:"#22c55e", fontFamily:"'DM Mono',monospace", fontWeight:700 }}>{p.matches}</td>
                  <td style={{ padding:"9px 12px", color:"#ccc", fontFamily:"'DM Mono',monospace" }}>{p.avg_score}</td>
                  <td style={{ padding:"9px 12px", color:p.max_score>=40?"#ff4d4d":p.max_score>=25?"#f59e0b":"#22c55e", fontFamily:"'DM Mono',monospace", fontWeight:700 }}>{p.max_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 14 — API KEY MANAGEMENT
// ════════════════════════════════════════════════════════════════════════════
function ApiKeyManager({ api, toast, isAdmin, userPlan }) {
  const [keys,     setKeys]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKey,   setNewKey]   = useState(null);
  const [form,     setForm]     = useState({ name:"", expires_in_days:"" });

  const hasApiAccess = isAdmin || userPlan === "pro";

  const load = () => {
    const path = isAdmin ? "/admin/api-keys" : "/api-keys";
    api.get(path).then(setKeys).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setCreating(true);
    try {
      const result = await api.post("/api-keys", {
        name: form.name,
        expires_in_days: form.expires_in_days ? parseInt(form.expires_in_days) : null,
      });
      setNewKey(result);
      setForm({ name:"", expires_in_days:"" });
      load();
      toast.success("API key created — copy it now, it won't be shown again");
    } catch(e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  const revoke = async (id, isAdminRevoke=false) => {
    if (!window.confirm("Revoke this API key? Any integrations using it will stop working.")) return;
    try {
      if (isAdminRevoke) {
        await api.patch(`/admin/api-keys/${id}/revoke`);
      } else {
        await api.del(`/api-keys/${id}`);
      }
      setKeys(k => k.map(key => key.id===id ? {...key, active:false} : key));
      toast.success("API key revoked");
    } catch(e) { toast.error(e.message); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied!"); };

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };
  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };

  if (!hasApiAccess) return (
    <div style={{ ...cardS, textAlign:"center", padding:"60px 0" }}>
      <div style={{ fontSize:40, marginBottom:16 }}>🔑</div>
      <div style={{ color:"#fff", fontSize:15, fontWeight:600, marginBottom:8 }}>API Keys require Agency Plan</div>
      <div style={{ color:"#4a4a6a", fontSize:13 }}>Upgrade to Agency to generate API keys for integrating Socialisten with your own tools, Zapier, or HubSpot.</div>
    </div>
  );

  return (
    <div>
      <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:16, lineHeight:1.7 }}>
        API keys let you access Socialisten data from your own tools, Zapier workflows, or custom integrations.
        Include the key in requests as: <code style={{ background:"#1e1e2e", padding:"1px 6px", borderRadius:3, color:"#a5b4fc", fontSize:11 }}>Authorization: Bearer sl_live_...</code>
      </div>

      {/* New key flash */}
      {newKey && (
        <div style={{ ...cardS, borderColor:"#22c55e30", background:"#0a1a0a", marginBottom:20 }}>
          <div style={{ color:"#22c55e", fontSize:13, fontWeight:600, marginBottom:10 }}>✓ New API Key Created — Copy Now</div>
          <div style={{ color:"#f59e0b", fontSize:12, marginBottom:12 }}>⚠️ This key will not be shown again. Copy it now and store it securely.</div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <code style={{ flex:1, background:"#0a0a0f", border:"1px solid #22c55e30", borderRadius:6, padding:"10px 14px", color:"#22c55e", fontSize:12, fontFamily:"'DM Mono',monospace", wordBreak:"break-all" }}>
              {newKey.key}
            </code>
            <button onClick={()=>copy(newKey.key)} style={{ padding:"10px 16px", borderRadius:6, border:"1px solid #22c55e30", background:"#22c55e10", color:"#22c55e", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", whiteSpace:"nowrap" }}>📋 Copy</button>
          </div>
          <button onClick={()=>setNewKey(null)} style={{ marginTop:12, background:"transparent", border:"none", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>Dismiss</button>
        </div>
      )}

      {/* Create form */}
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>Create New API Key</div>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:12 }}>
          <div><label style={lS}>KEY NAME (what it's for)</label><input style={iS} placeholder="e.g. Zapier Integration" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div>
          <div>
            <label style={lS}>EXPIRES IN (days, blank = never)</label>
            <input style={iS} type="number" min={1} placeholder="Never" value={form.expires_in_days} onChange={e=>setForm(f=>({...f,expires_in_days:e.target.value}))}/>
          </div>
        </div>
        <button onClick={create} disabled={creating||!form.name.trim()} style={{ marginTop:14, padding:"9px 20px", borderRadius:6, border:"none", background:(creating||!form.name.trim())?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:(creating||!form.name.trim())?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
          {creating?<><Spinner size={11}/> Creating…</>:"+ Generate Key"}
        </button>
      </div>

      {/* Keys list */}
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>
          {isAdmin ? "All API Keys" : "Your API Keys"} ({keys.filter(k=>k.active).length} active)
        </div>
        {loading ? <div style={{ display:"flex",justifyContent:"center",padding:20 }}><Spinner size={20}/></div>
        : keys.length===0 ? (
          <div style={{ color:"#3a3a5a", fontSize:12, padding:"20px 0", textAlign:"center" }}>No API keys yet — create one above</div>
        ) : keys.map(k => (
          <div key={k.id} style={{ padding:"14px 0", borderBottom:"1px solid #111120", display:"flex", alignItems:"center", gap:12, opacity:k.active?1:0.4 }}>
            <div style={{ width:36, height:36, borderRadius:8, background:"#1e1e2e", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>🔑</div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ color:"#fff", fontSize:12, fontWeight:600 }}>{k.name}</span>
                {!k.active && <span style={{ color:"#ff4d4d", fontSize:9, fontFamily:"'DM Mono',monospace" }}>REVOKED</span>}
              </div>
              <div style={{ display:"flex", gap:14, color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                <span>{k.key_prefix}••••••••</span>
                {isAdmin && k.user_email && <span style={{ color:"#6366f1" }}>{k.user_email}</span>}
                <span>Used {k.uses||0}x</span>
                {k.last_used && <span>Last: {new Date(k.last_used).toLocaleDateString()}</span>}
                {k.expires_at && <span>Expires: {new Date(k.expires_at).toLocaleDateString()}</span>}
                {!k.expires_at && <span>Never expires</span>}
              </div>
            </div>
            {k.active && (
              <button onClick={()=>revoke(k.id, isAdmin)} style={{ padding:"6px 14px", borderRadius:5, border:"1px solid #ff4d4d30", background:"#ff4d4d10", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Revoke</button>
            )}
          </div>
        ))}
      </div>

      {/* API docs stub */}
      <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px" }}>
        <div style={{ color:"#fff", fontSize:12, fontWeight:600, marginBottom:10 }}>Quick Reference</div>
        {[
          ["GET /matches",        "List your recent keyword matches"],
          ["GET /pages",          "List your monitored pages"],
          ["GET /analytics",      "Get your match analytics"],
          ["GET /usage",          "Check your plan usage"],
        ].map(([endpoint, desc]) => (
          <div key={endpoint} style={{ display:"flex", gap:12, padding:"6px 0", borderBottom:"1px solid #111" }}>
            <code style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", width:160, flexShrink:0 }}>{endpoint}</code>
            <span style={{ color:"#4a4a6a", fontSize:11 }}>{desc}</span>
          </div>
        ))}
        <div style={{ color:"#3a3a5a", fontSize:11, marginTop:10 }}>Base URL: {typeof window!=="undefined"?(window.REACT_APP_API_URL||"https://socipulses.com"):""}</div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 15 — AFFILIATE / REFERRAL PROGRAM
// Two views: Admin panel + User-facing referral page
// ════════════════════════════════════════════════════════════════════════════
function AffiliateAdmin({ api, toast }) {
  const [stats,      setStats]      = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [convs,      setConvs]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState("overview");
  const [selected,   setSelected]   = useState([]);
  const [marking,    setMarking]    = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/affiliates/stats"),
      api.get("/admin/affiliates"),
      api.get("/admin/affiliates/conversions"),
    ]).then(([s,a,c]) => { setStats(s); setAffiliates(a||[]); setConvs(c||[]); })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggleAff = async (code, active) => {
    try {
      const aff = affiliates.find(a=>a.code===code);
      await api.patch(`/admin/affiliates/${code}`, {
        discount_pct: aff.discount_pct, commission_pct: aff.commission_pct, active
      });
      setAffiliates(prev => prev.map(a => a.code===code ? {...a,active} : a));
      toast.success(active?"Affiliate activated":"Affiliate deactivated");
    } catch(e) { toast.error(e.message); }
  };

  const markPaid = async () => {
    if (!selected.length) { toast.error("Select conversions to mark paid"); return; }
    setMarking(true);
    try {
      await api.post("/admin/affiliates/mark-paid", { conversion_ids: selected });
      setConvs(prev => prev.map(c => selected.includes(c.id) ? {...c,status:"paid"} : c));
      setSelected([]);
      toast.success(`${selected.length} commission(s) marked as paid`);
    } catch(e) { toast.error(e.message); }
    finally { setMarking(false); }
  };

  const pendingPayout = convs.filter(c=>c.status==="pending").reduce((a,c)=>a+c.commission,0);
  const PLAN_COLORS   = { free:"#4a4a6a",starter:"#22c55e",pro:"#6366f1" };
  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:20 }}>
        {["overview","affiliates","conversions"].map(t => (
          <button key={t} onClick={()=>setTab(t)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${tab===t?"#6366f1":"#1e1e2e"}`, background:tab===t?"#6366f120":"transparent", color:tab===t?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", textTransform:"capitalize" }}>{t}</button>
        ))}
      </div>

      {/* Overview */}
      {tab==="overview" && (
        <div>
          <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
            {[
              { label:"ACTIVE AFFILIATES",   value:stats?.total_affiliates||0,   color:"#6366f1" },
              { label:"TOTAL CONVERSIONS",   value:stats?.total_conversions||0,  color:"#22c55e" },
              { label:"PENDING PAYOUT",      value:`$${(stats?.pending_commissions||0).toFixed(2)}`, color:"#f59e0b" },
              { label:"PAID TO DATE",        value:`$${(stats?.paid_commissions||0).toFixed(2)}`,   color:"#22c55e" },
            ].map(k => (
              <div key={k.label} style={{ ...cardS, marginBottom:0 }}>
                <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
                <div style={{ color:k.color, fontSize:28, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{k.value}</div>
              </div>
            ))}
          </div>
          <div style={{ ...cardS }}>
            <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:10 }}>How It Works</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
              {[
                ["🔗","Share Link","Users get a personal referral URL. Anyone who signs up through it is credited to them."],
                ["💰","Earn Commission","When their referral upgrades to a paid plan, the affiliate earns a % of that first payment."],
                ["🎁","Referee Gets Discount","The person signing up also gets a discount on their first month — win/win."],
              ].map(([icon,title,desc]) => (
                <div key={title} style={{ background:"#0a0a0f", borderRadius:8, padding:"14px 16px" }}>
                  <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
                  <div style={{ color:"#fff", fontSize:12, fontWeight:600, marginBottom:6 }}>{title}</div>
                  <div style={{ color:"#4a4a6a", fontSize:11, lineHeight:1.7 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Affiliates list */}
      {tab==="affiliates" && (
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>All Affiliates ({affiliates.length})</div>
          {affiliates.length===0 ? (
            <div style={{ color:"#3a3a5a", fontSize:12, padding:"30px 0", textAlign:"center" }}>
              No affiliates yet — users get their referral code automatically when they visit their profile settings.
            </div>
          ) : affiliates.map(a => (
            <div key={a.code} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:"1px solid #111120", opacity:a.active?1:0.4 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <code style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", fontWeight:700 }}>{a.code}</code>
                  <span style={{ color:"#6366f1", fontSize:11 }}>{a.user_email}</span>
                  <span style={{ padding:"2px 7px", borderRadius:4, fontSize:9, background:`${PLAN_COLORS[a.user_plan]||"#4a4a6a"}20`, color:PLAN_COLORS[a.user_plan]||"#4a4a6a", fontFamily:"'DM Mono',monospace" }}>{a.user_plan}</span>
                </div>
                <div style={{ display:"flex", gap:16, color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                  <span>🎁 {a.discount_pct}% off for referee</span>
                  <span>💰 {a.commission_pct}% commission</span>
                  <span>🔗 {a.uses} use{a.uses!==1?"s":""}</span>
                  <span style={{ color:"#22c55e" }}>Earned: ${(a.earnings||0).toFixed(2)}</span>
                </div>
              </div>
              <div onClick={()=>toggleAff(a.code,!a.active)} style={{ width:40,height:22,borderRadius:11,background:a.active?"#22c55e":"#2a2a3e",position:"relative",cursor:"pointer",transition:"all 0.2s" }}>
                <div style={{ position:"absolute",top:3,left:a.active?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Conversions */}
      {tab==="conversions" && (
        <div>
          {pendingPayout > 0 && (
            <div style={{ ...cardS, borderColor:"#f59e0b30", background:"#1a1200", marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ color:"#f59e0b", fontSize:13, fontWeight:600 }}>Pending Payouts: ${pendingPayout.toFixed(2)}</div>
                  <div style={{ color:"#d4a76a", fontSize:12, marginTop:4 }}>Select conversions below and click "Mark Paid" after sending payment</div>
                </div>
                <button onClick={markPaid} disabled={!selected.length||marking} style={{ padding:"9px 18px", borderRadius:6, border:"none", background:(!selected.length||marking)?"#2a2a3e":"linear-gradient(135deg,#22c55e,#16a34a)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:(!selected.length||marking)?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
                  {marking?<><Spinner size={11}/> Marking…</>:`✓ Mark ${selected.length} Paid`}
                </button>
              </div>
            </div>
          )}
          <div style={{ ...cardS }}>
            <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>Conversion History ({convs.length})</div>
            {convs.length===0 ? (
              <div style={{ color:"#3a3a5a", fontSize:12, padding:"20px 0", textAlign:"center" }}>No conversions yet</div>
            ) : convs.map(c => (
              <div key={c.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:"1px solid #111120" }}>
                {c.status==="pending" && (
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={e=>setSelected(p=>e.target.checked?[...p,c.id]:p.filter(x=>x!==c.id))} style={{ cursor:"pointer" }}/>
                )}
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:3 }}>
                    <span style={{ color:"#ccc", fontSize:12 }}>{c.referrer}</span>
                    <span style={{ color:"#3a3a5a", fontSize:11 }}>→</span>
                    <span style={{ color:"#6366f1", fontSize:12 }}>{c.referred}</span>
                    <span style={{ padding:"1px 7px", borderRadius:4, fontSize:9, background:`${PLAN_COLORS[c.plan]||"#4a4a6a"}20`, color:PLAN_COLORS[c.plan]||"#4a4a6a", fontFamily:"'DM Mono',monospace" }}>{c.plan}</span>
                  </div>
                  <span style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{c.date}</span>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace" }}>${c.amount.toFixed(2)}</div>
                  <div style={{ color:"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace" }}>+${c.commission.toFixed(2)}</div>
                </div>
                <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:c.status==="paid"?"#22c55e20":"#f59e0b20", color:c.status==="paid"?"#22c55e":"#f59e0b" }}>{c.status.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// User-facing referral page (shown in Settings or as own tab)
function MyReferralPage({ api, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referral/my-code").then(setData).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied!"); };
  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"22px 24px", marginBottom:16 };

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:40 }}><Spinner size={24}/></div>;
  if (!data)   return null;

  const pendingTotal = (data.conversions||[]).filter(c=>c.status==="pending").reduce((a,c)=>a+c.commission,0);

  return (
    <div style={{ maxWidth:600 }}>
      <div style={{ ...cardS, background:"linear-gradient(135deg,#0f0f2a,#0a0a1a)", borderColor:"#6366f130" }}>
        <div style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", letterSpacing:2, marginBottom:10 }}>YOUR REFERRAL CODE</div>
        <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:14 }}>
          <code style={{ flex:1, background:"#0a0a0f", border:"1px solid #2a2a3e", borderRadius:8, padding:"14px 18px", color:"#fff", fontSize:22, fontFamily:"'DM Mono',monospace", fontWeight:700, letterSpacing:4 }}>{data.code}</code>
          <button onClick={()=>copy(data.code)} style={{ padding:"12px 16px", borderRadius:8, border:"1px solid #6366f130", background:"#6366f120", color:"#a5b4fc", fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>📋 Copy</button>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:16 }}>
          <input readOnly value={data.referral_url} style={{ flex:1, background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", outline:"none" }}/>
          <button onClick={()=>copy(data.referral_url)} style={{ padding:"9px 14px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>Copy Link</button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          {[
            { label:"Your Commission",  value:`${data.commission_pct}%`, sub:"of their first payment", color:"#22c55e" },
            { label:"Their Discount",   value:`${data.discount_pct}%`,  sub:"off first month",        color:"#6366f1" },
            { label:"Total Earned",     value:`$${data.total_earnings.toFixed(2)}`, sub:`${data.total_uses} referral${data.total_uses!==1?"s":""}`, color:"#f59e0b" },
          ].map(k => (
            <div key={k.label} style={{ background:"#0a0a0f", borderRadius:8, padding:"12px 14px", textAlign:"center" }}>
              <div style={{ color:k.color, fontSize:22, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{k.value}</div>
              <div style={{ color:"#fff", fontSize:11, fontWeight:600, marginTop:4 }}>{k.label}</div>
              <div style={{ color:"#4a4a6a", fontSize:10, marginTop:2 }}>{k.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {pendingTotal > 0 && (
        <div style={{ ...cardS, borderColor:"#22c55e30", background:"#0a1a0a" }}>
          <div style={{ color:"#22c55e", fontSize:13, fontWeight:600 }}>💰 Pending Earnings: ${pendingTotal.toFixed(2)}</div>
          <div style={{ color:"#4a6a4a", fontSize:12, marginTop:6 }}>Commissions are paid monthly. Contact support to arrange payment.</div>
        </div>
      )}

      {data.conversions?.length > 0 && (
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>Your Referrals</div>
          {data.conversions.map((c,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 0", borderBottom:"1px solid #111" }}>
              <div style={{ flex:1 }}>
                <div style={{ color:"#ccc", fontSize:12 }}>{c.plan} plan</div>
                <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{c.date}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ color:"#22c55e", fontSize:12, fontFamily:"'DM Mono',monospace" }}>+${c.commission.toFixed(2)}</div>
                <div style={{ color:c.status==="paid"?"#22c55e":"#f59e0b", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{c.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:10, padding:"14px 16px", fontSize:12, color:"#4a4a6a", lineHeight:1.8 }}>
        📤 Share your link on Facebook contractor groups, HVAC forums, trade associations, or with other contractors you know.<br/>
        💬 For every contractor who signs up and upgrades, you earn {data.commission_pct}% of their first payment automatically.
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 16 — USER TAGGING
// Standalone tag manager + embedded tag strip for user detail view
// ════════════════════════════════════════════════════════════════════════════
function TagBadge({ tag, onRemove }) {
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 8px", borderRadius:5, background:`${tag.tag_color||"#6366f1"}20`, border:`1px solid ${tag.tag_color||"#6366f1"}40`, marginRight:5, marginBottom:5 }}>
      <span style={{ color:tag.tag_color||"#6366f1", fontSize:12 }}>{tag.tag_label}</span>
      {onRemove && (
        <span onClick={onRemove} style={{ color:tag.tag_color||"#6366f1", fontSize:11, cursor:"pointer", opacity:0.7, lineHeight:1 }}>×</span>
      )}
    </div>
  );
}

function UserTagStrip({ api, toast, userId }) {
  const [tags,      setTags]      = useState([]);
  const [sysTags,   setSysTags]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [adding,    setAdding]    = useState(false);
  const [showPicker,setShowPicker]= useState(false);

  const load = () => {
    Promise.all([
      api.get(`/admin/tags/user/${userId}`),
      api.get("/admin/tags/system"),
    ]).then(([t, s]) => { setTags(t||[]); setSysTags(s||[]); })
      .catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(()=>{ if(userId) load(); },[userId]);

  const addTag = async (tagId) => {
    setAdding(true);
    try {
      const r = await api.post(`/admin/tags/user/${userId}/${tagId}`);
      setTags(t => [...t.filter(x=>x.tag_id!==tagId), {
        tag_id:tagId, tag_label:r.tag_label,
        tag_color: sysTags.find(s=>s.id===tagId)?.color||"#6366f1",
      }]);
      setShowPicker(false);
    } catch(e){ toast.error(e.message); }
    finally { setAdding(false); }
  };

  const removeTag = async (tagId) => {
    try {
      await api.del(`/admin/tags/user/${userId}/${tagId}`);
      setTags(t=>t.filter(x=>x.tag_id!==tagId));
    } catch(e){ toast.error(e.message); }
  };

  if (loading) return <Spinner size={14}/>;

  const usedIds = new Set(tags.map(t=>t.tag_id));

  return (
    <div style={{ marginBottom:4 }}>
      <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:0 }}>
        {tags.map(t => <TagBadge key={t.tag_id} tag={t} onRemove={()=>removeTag(t.tag_id)}/>)}
        <div style={{ position:"relative" }}>
          <button onClick={()=>setShowPicker(p=>!p)} style={{ padding:"3px 10px", borderRadius:5, border:"1px dashed #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer", marginBottom:5 }}>
            {adding?<Spinner size={10}/>:"+ Tag"}
          </button>
          {showPicker && (
            <div style={{ position:"absolute", top:"100%", left:0, background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:8, padding:10, zIndex:100, minWidth:180, boxShadow:"0 8px 24px rgba(0,0,0,0.5)" }}>
              {sysTags.filter(s=>!usedIds.has(s.id)).map(s=>(
                <div key={s.id} onClick={()=>addTag(s.id)} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:5, cursor:"pointer" }}>
                  <span>{s.emoji}</span>
                  <span style={{ color:s.color, fontSize:12 }}>{s.label}</span>
                </div>
              ))}
              {sysTags.filter(s=>!usedIds.has(s.id)).length===0 && (
                <div style={{ color:"#3a3a5a", fontSize:11, padding:"4px 8px" }}>All tags applied</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TagDashboard({ api, toast }) {
  const [summary, setSummary] = useState([]);
  const [sysTags, setSysTags] = useState([]);
  const [selected,setSelected]= useState(null);
  const [members, setMembers] = useState([]);
  const [loadingM,setLoadingM]= useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/tags/summary"),
      api.get("/admin/tags/system"),
    ]).then(([s,sys])=>{ setSummary(s||[]); setSysTags(sys||[]); })
      .catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  },[]);

  const selectTag = async (tagId) => {
    setSelected(tagId); setMembers([]); setLoadingM(true);
    try { const r=await api.get(`/admin/tags/by-tag/${tagId}`); setMembers(r||[]); }
    catch(e){toast.error(e.message);}
    finally { setLoadingM(false); }
  };

  const PLAN_COLORS = {free:"#4a4a6a",starter:"#22c55e",pro:"#6366f1"};
  const cardS = {background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"20px 24px",marginBottom:16};

  if (loading) return <div style={{display:"flex",justifyContent:"center",padding:60}}><Spinner size={32}/></div>;

  // Merge summary counts into sysTags
  const summaryMap = Object.fromEntries(summary.map(s=>[s.tag_id,s.count]));

  return (
    <div style={{ display:"grid", gridTemplateColumns:"280px 1fr", gap:20 }}>
      {/* Tag list */}
      <div>
        <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:12 }}>ALL TAGS</div>
        {sysTags.map(tag => {
          const count = summaryMap[tag.id]||0;
          const isSelected = selected===tag.id;
          return (
            <div key={tag.id} onClick={()=>selectTag(tag.id)} style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 14px", background:isSelected?"#1a1a2e":"#0f0f1a", border:`1px solid ${isSelected?tag.color:"#1e1e2e"}`, borderRadius:9, marginBottom:7, cursor:"pointer", transition:"all 0.15s" }}>
              <span style={{ fontSize:18 }}>{tag.emoji}</span>
              <div style={{ flex:1 }}>
                <div style={{ color:isSelected?tag.color:"#ccc", fontSize:13, fontWeight:600 }}>{tag.label}</div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ color:tag.color, fontSize:20, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{count}</div>
                <div style={{ color:"#4a4a6a", fontSize:9 }}>users</div>
              </div>
            </div>
          );
        })}
        {summary.filter(s=>!sysTags.find(t=>t.id===s.tag_id)).map(s=>(
          <div key={s.tag_id} onClick={()=>selectTag(s.tag_id)} style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 14px", background:selected===s.tag_id?"#1a1a2e":"#0f0f1a", border:`1px solid ${selected===s.tag_id?"#6366f1":"#1e1e2e"}`, borderRadius:9, marginBottom:7, cursor:"pointer" }}>
            <span style={{ fontSize:18 }}>🏷️</span>
            <div style={{ flex:1, color:"#ccc", fontSize:13 }}>{s.tag_label}</div>
            <div style={{ color:"#6366f1", fontSize:18, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{s.count}</div>
          </div>
        ))}
        {sysTags.length===0&&summary.length===0&&<div style={{color:"#3a3a5a",fontSize:12,padding:"20px 0"}}>No tags applied yet — open a user and click + Tag</div>}
      </div>

      {/* Members */}
      <div>
        {!selected ? (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:300, color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🏷️</div>
            Select a tag to see all users with that label
          </div>
        ) : (
          <div style={{ ...cardS }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              {(() => { const t=sysTags.find(s=>s.id===selected); return t?<span style={{ fontSize:22 }}>{t.emoji}</span>:null; })()}
              <div style={{ color:"#fff", fontSize:14, fontWeight:600 }}>{sysTags.find(s=>s.id===selected)?.label||selected}</div>
              <div style={{ color:"#4a4a6a", fontSize:12, marginLeft:4 }}>({members.length} users)</div>
            </div>
            {loadingM ? <div style={{display:"flex",justifyContent:"center",padding:20}}><Spinner size={20}/></div>
            : members.length===0 ? <div style={{color:"#3a3a5a",fontSize:12}}>No users with this tag</div>
            : members.map(m=>(
              <div key={m.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 0", borderBottom:"1px solid #111" }}>
                <div style={{ width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,fontWeight:700,flexShrink:0 }}>{m.email[0].toUpperCase()}</div>
                <div style={{ flex:1 }}>
                  <div style={{ color:"#ccc", fontSize:12 }}>{m.email}</div>
                  <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>Tagged {m.tagged_at}</div>
                </div>
                <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${PLAN_COLORS[m.plan]||"#4a4a6a"}20`, color:PLAN_COLORS[m.plan]||"#4a4a6a", textTransform:"capitalize" }}>{m.plan}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 17 — CALL LOG
// ════════════════════════════════════════════════════════════════════════════
function CallLog({ api, toast }) {
  const [calls,     setCalls]     = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [tab,       setTab]       = useState("log");
  const [search,    setSearch]    = useState("");

  const BLANK = { user_id:"", direction:"outbound", outcome:"reached", duration_min:0, notes:"", follow_up_date:"", follow_up_note:"" };
  const [form, setForm] = useState(BLANK);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/calls?days=30"),
      api.get("/admin/calls/follow-ups"),
      api.get("/admin/calls/stats?days=30"),
    ]).then(([c,f,s])=>{setCalls(c||[]);setFollowUps(f||[]);setStats(s);})
      .catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  };
  useEffect(load,[]);

  const save = async () => {
    if (!form.user_id) { toast.error("User ID required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/calls", {
        ...form, user_id:parseInt(form.user_id),
        duration_min:parseInt(form.duration_min)||0,
        follow_up_date: form.follow_up_date||null,
        follow_up_note: form.follow_up_note||null,
      });
      setCalls(p=>[r,...p]);
      setShowForm(false); setForm(BLANK);
      toast.success("Call logged");
      load();
    } catch(e){toast.error(e.message);}
    finally{setSaving(false);}
  };

  const del = async (id) => {
    try { await api.del(`/admin/calls/${id}`); setCalls(p=>p.filter(c=>c.id!==id)); toast.success("Deleted"); }
    catch(e){toast.error(e.message);}
  };

  const OUTCOME_CFG = {
    reached:      {color:"#22c55e",icon:"✅",label:"Reached"},
    voicemail:    {color:"#f59e0b",icon:"📱",label:"Voicemail"},
    "no-answer":  {color:"#ff4d4d",icon:"❌",label:"No Answer"},
    "wrong-number":{color:"#6b7280",icon:"🚫",label:"Wrong #"},
  };
  const DIR_CFG = { outbound:{icon:"📤",color:"#6366f1"}, inbound:{icon:"📥",color:"#22c55e"} };

  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS={color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};
  const cardS={background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"20px 24px",marginBottom:16};

  const filtered = calls.filter(c=>!search||c.user_email?.toLowerCase().includes(search.toLowerCase())||c.notes?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div className="sl-grid-4" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
          {[
            {label:"CALLS (30d)",     value:stats.total,             color:"#6366f1"},
            {label:"REACHED",         value:stats.reached,           color:"#22c55e"},
            {label:"REACH RATE",      value:`${stats.reach_rate}%`,  color:stats.reach_rate>50?"#22c55e":"#f59e0b"},
            {label:"FOLLOW-UPS DUE",  value:stats.pending_follow_ups,color:stats.pending_follow_ups>0?"#f59e0b":"#22c55e"},
          ].map(k=>(
            <div key={k.label} style={{...cardS,marginBottom:0}}>
              <div style={{color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,marginBottom:8}}>{k.label}</div>
              <div style={{color:k.color,fontSize:28,fontWeight:700,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs + actions */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
        {["log","follow-ups"].map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:"7px 16px",borderRadius:6,border:`1px solid ${tab===t?"#6366f1":"#1e1e2e"}`,background:tab===t?"#6366f120":"transparent",color:tab===t?"#a5b4fc":"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer",textTransform:"capitalize"}}>
            {t==="follow-ups"?`Follow-Ups${followUps.filter(f=>f.overdue).length>0?` (${followUps.filter(f=>f.overdue).length} overdue)`:""}`:t}
          </button>
        ))}
        {tab==="log" && <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by email or notes..." style={{background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"7px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",width:220}}/>}
        <div style={{flex:1}}/>
        <button onClick={()=>setShowForm(p=>!p)} style={{padding:"8px 18px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>+ Log Call</button>
      </div>

      {/* Log call form */}
      {showForm && (
        <div style={{...cardS,borderColor:"#2a2a3e",marginBottom:20}}>
          <div style={{color:"#a5b4fc",fontSize:13,fontFamily:"'DM Mono',monospace",marginBottom:14}}>Log a Call</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
            <div><label style={lS}>USER ID</label><input style={iS} type="number" placeholder="e.g. 42 (from Admin → Users)" value={form.user_id} onChange={e=>setForm(f=>({...f,user_id:e.target.value}))}/></div>
            <div><label style={lS}>DIRECTION</label>
              <select style={iS} value={form.direction} onChange={e=>setForm(f=>({...f,direction:e.target.value}))}>
                <option value="outbound">Outbound (I called them)</option>
                <option value="inbound">Inbound (They called me)</option>
              </select>
            </div>
            <div><label style={lS}>OUTCOME</label>
              <select style={iS} value={form.outcome} onChange={e=>setForm(f=>({...f,outcome:e.target.value}))}>
                <option value="reached">Reached</option>
                <option value="voicemail">Left Voicemail</option>
                <option value="no-answer">No Answer</option>
                <option value="wrong-number">Wrong Number</option>
              </select>
            </div>
            <div><label style={lS}>DURATION (minutes)</label><input style={iS} type="number" min={0} value={form.duration_min} onChange={e=>setForm(f=>({...f,duration_min:e.target.value}))}/></div>
            <div><label style={lS}>FOLLOW-UP DATE</label><input style={iS} type="date" value={form.follow_up_date} onChange={e=>setForm(f=>({...f,follow_up_date:e.target.value}))}/></div>
            <div><label style={lS}>FOLLOW-UP NOTE</label><input style={iS} placeholder="What to do on follow-up..." value={form.follow_up_note} onChange={e=>setForm(f=>({...f,follow_up_note:e.target.value}))}/></div>
            <div style={{gridColumn:"1/-1"}}><label style={lS}>CALL NOTES</label><textarea style={{...iS,minHeight:70,resize:"vertical"}} placeholder="What was discussed, what they said, next steps..." value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={save} disabled={saving||!form.user_id} style={{padding:"9px 20px",borderRadius:6,border:"none",background:(saving||!form.user_id)?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:(saving||!form.user_id)?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
              {saving?<><Spinner size={11}/> Saving…</>:"Save Call"}
            </button>
            <button onClick={()=>{setShowForm(false);setForm(BLANK);}} style={{padding:"9px 16px",borderRadius:6,border:"1px solid #2a2a3e",background:"transparent",color:"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Call log tab */}
      {tab==="log" && (
        loading?<div style={{display:"flex",justifyContent:"center",padding:40}}><Spinner size={28}/></div>
        : filtered.length===0?<div style={{textAlign:"center",padding:"60px 0",color:"#3a3a5a",fontFamily:"'DM Mono',monospace"}}>No calls logged yet</div>
        : filtered.map(c=>{
          const oc=OUTCOME_CFG[c.outcome]||OUTCOME_CFG.reached;
          const dc=DIR_CFG[c.direction]||DIR_CFG.outbound;
          return (
            <div key={c.id} style={{background:"#0f0f1a",border:"1px solid #1e1e2e",borderLeft:`3px solid ${oc.color}`,borderRadius:9,padding:"14px 16px",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                <div style={{fontSize:20,flexShrink:0}}>{oc.icon}</div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexWrap:"wrap"}}>
                    <span style={{color:"#fff",fontSize:13,fontWeight:600}}>{c.user_email}</span>
                    <span style={{color:dc.color,fontSize:11}}>{dc.icon} {c.direction}</span>
                    <span style={{color:oc.color,fontSize:11,fontFamily:"'DM Mono',monospace"}}>{oc.label}</span>
                    {c.duration_min>0&&<span style={{color:"#4a4a6a",fontSize:11}}>{c.duration_min}min</span>}
                    <span style={{color:"#3a3a5a",fontSize:10,fontFamily:"'DM Mono',monospace"}}>{c.created_at?new Date(c.created_at).toLocaleString():""}</span>
                    <span style={{color:"#3a3a5a",fontSize:10}}>by {c.admin_email}</span>
                  </div>
                  {c.notes&&<div style={{color:"#8888aa",fontSize:12,lineHeight:1.6,marginBottom:c.follow_up_date?6:0}}>{c.notes}</div>}
                  {c.follow_up_date&&(
                    <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"3px 10px",background:"#f59e0b15",borderRadius:5,border:"1px solid #f59e0b30"}}>
                      <span style={{color:"#f59e0b",fontSize:11}}>📅 Follow up {c.follow_up_date}</span>
                      {c.follow_up_note&&<span style={{color:"#d4a76a",fontSize:11}}>— {c.follow_up_note}</span>}
                    </div>
                  )}
                </div>
                <button onClick={()=>del(c.id)} style={{background:"transparent",border:"none",color:"#3a3a5a",cursor:"pointer",fontSize:16,flexShrink:0}}>✕</button>
              </div>
            </div>
          );
        })
      )}

      {/* Follow-ups tab */}
      {tab==="follow-ups" && (
        <div>
          {followUps.length===0?<div style={{textAlign:"center",padding:"60px 0",color:"#3a3a5a",fontFamily:"'DM Mono',monospace"}}>No upcoming follow-ups</div>
          : followUps.map(f=>(
            <div key={f.id} style={{background:f.overdue?"#1a0808":"#0f0f1a",border:`1px solid ${f.overdue?"#ff4d4d30":"#1e1e2e"}`,borderLeft:`3px solid ${f.overdue?"#ff4d4d":"#f59e0b"}`,borderRadius:9,padding:"14px 16px",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                <div style={{fontSize:20}}>{f.overdue?"🔴":"📅"}</div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                    <span style={{color:"#fff",fontSize:13,fontWeight:600}}>{f.user_email}</span>
                    <span style={{padding:"2px 8px",borderRadius:4,fontSize:9,background:`${f.overdue?"#ff4d4d":"#f59e0b"}20`,color:f.overdue?"#ff4d4d":"#f59e0b",fontFamily:"'DM Mono',monospace"}}>{f.overdue?"OVERDUE":f.follow_up_date}</span>
                    <span style={{color:"#4a4a6a",fontSize:10,textTransform:"capitalize"}}>{f.user_plan} plan</span>
                  </div>
                  {f.follow_up_note&&<div style={{color:"#a5b4fc",fontSize:12,marginBottom:4}}>📌 {f.follow_up_note}</div>}
                  {f.call_notes&&<div style={{color:"#4a4a6a",fontSize:11}}>Last call: {f.call_notes}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 18 — SUPPORT INBOX (Crisp)
// ════════════════════════════════════════════════════════════════════════════
function SupportInbox({ api, toast }) {
  const [convs,      setConvs]      = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [messages,   setMessages]   = useState([]);
  const [reply,      setReply]      = useState("");
  const [loading,    setLoading]    = useState(true);
  const [loadingMsgs,setLoadingMsgs]= useState(false);
  const [sending,    setSending]    = useState(false);
  const [resolving,  setResolving]  = useState(false);
  const [configured, setConfigured] = useState(true);
  const [statusFilter,setStatusFilter]= useState("unresolved");

  const load = () => {
    setLoading(true);
    api.get(`/admin/inbox/conversations?status=${statusFilter}`)
      .then(r=>{
        setConfigured(r.configured!==false);
        setConvs(r.conversations||[]);
      }).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  };
  useEffect(load,[statusFilter]);

  const selectConv = async (conv) => {
    setSelected(conv); setMessages([]); setLoadingMsgs(true);
    try {
      const msgs = await api.get(`/admin/inbox/conversations/${conv.session_id}/messages`);
      setMessages(msgs||[]);
    } catch(e){ toast.error(e.message); }
    finally { setLoadingMsgs(false); }
  };

  const sendReply = async () => {
    if(!reply.trim()||!selected) return;
    setSending(true);
    try {
      await api.post(`/admin/inbox/conversations/${selected.session_id}/reply`, {content:reply, type:"text"});
      setMessages(p=>[...p,{from:"operator",type:"text",content:reply,timestamp:Date.now()}]);
      setReply("");
      toast.success("Reply sent");
    } catch(e){toast.error(e.message);}
    finally{setSending(false);}
  };

  const resolve = async () => {
    if(!selected) return;
    setResolving(true);
    try {
      await api.patch(`/admin/inbox/conversations/${selected.session_id}/resolve`);
      setConvs(p=>p.filter(c=>c.session_id!==selected.session_id));
      setSelected(null); setMessages([]);
      toast.success("Conversation resolved");
    } catch(e){toast.error(e.message);}
    finally{setResolving(false);}
  };

  if (!configured) return (
    <div style={{background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"40px",textAlign:"center"}}>
      <div style={{fontSize:48,marginBottom:16}}>💬</div>
      <div style={{color:"#fff",fontSize:15,fontWeight:600,marginBottom:10}}>Crisp Inbox Not Configured</div>
      <div style={{color:"#4a4a6a",fontSize:13,lineHeight:1.8,marginBottom:20}}>
        Add these two variables to your backend <code style={{background:"#1e1e2e",padding:"1px 6px",borderRadius:3,color:"#a5b4fc"}}>.env</code> file:
      </div>
      {[["CRISP_WEBSITE_ID","Your website ID from crisp.chat → Settings → Website → Setup"],
        ["CRISP_API_KEY","token:secret from crisp.chat → Settings → Website → Integrations → API"]
      ].map(([k,v])=>(
        <div key={k} style={{background:"#0a0a0f",borderRadius:8,padding:"12px 16px",marginBottom:8,textAlign:"left"}}>
          <code style={{color:"#a5b4fc",fontSize:12,fontFamily:"'DM Mono',monospace",display:"block"}}>{k}=your-value-here</code>
          <div style={{color:"#4a4a6a",fontSize:11,marginTop:4}}>{v}</div>
        </div>
      ))}
      <div style={{color:"#4a4a6a",fontSize:12,marginTop:16}}>
        The Crisp chat widget already loads for logged-in users — you just need the API key to read conversations here.
      </div>
    </div>
  );

  return (
    <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:0,border:"1px solid #1e1e2e",borderRadius:12,overflow:"hidden",minHeight:500}}>
      {/* Conversation list */}
      <div style={{background:"#0a0a0f",borderRight:"1px solid #1e1e2e"}}>
        <div style={{padding:"14px 16px",borderBottom:"1px solid #1e1e2e",display:"flex",gap:6}}>
          {["unresolved","resolved"].map(s=>(
            <button key={s} onClick={()=>setStatusFilter(s)} style={{flex:1,padding:"6px",borderRadius:5,border:`1px solid ${statusFilter===s?"#6366f1":"#1e1e2e"}`,background:statusFilter===s?"#6366f120":"transparent",color:statusFilter===s?"#a5b4fc":"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer",textTransform:"capitalize"}}>{s}</button>
          ))}
        </div>
        <div style={{overflowY:"auto",maxHeight:520}}>
          {loading?<div style={{display:"flex",justifyContent:"center",padding:30}}><Spinner size={20}/></div>
          :convs.length===0?<div style={{padding:"30px 16px",textAlign:"center",color:"#3a3a5a",fontSize:12}}>No {statusFilter} conversations</div>
          :convs.map(c=>(
            <div key={c.session_id} onClick={()=>selectConv(c)} style={{padding:"12px 16px",borderBottom:"1px solid #0f0f1a",cursor:"pointer",background:selected?.session_id===c.session_id?"#1a1a2e":"transparent",borderLeft:selected?.session_id===c.session_id?"3px solid #6366f1":"3px solid transparent"}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12,fontWeight:700,flexShrink:0}}>
                  {(c.email||c.nickname||"?")[0].toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:"#ccc",fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.email||c.nickname||"Visitor"}</div>
                </div>
                {c.unread>0&&<div style={{width:18,height:18,borderRadius:"50%",background:"#ff4d4d",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:9,fontWeight:700,flexShrink:0}}>{c.unread}</div>}
              </div>
              <div style={{color:"#4a4a6a",fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingLeft:34}}>{c.preview||"No messages"}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Message thread */}
      <div style={{background:"#0f0f1a",display:"flex",flexDirection:"column"}}>
        {!selected?(
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",color:"#3a3a5a",fontFamily:"'DM Mono',monospace",gap:12}}>
            <div style={{fontSize:40}}>💬</div>
            <div>Select a conversation</div>
          </div>
        ):(
          <>
            {/* Header */}
            <div style={{padding:"14px 18px",borderBottom:"1px solid #1e1e2e",display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1}}>
                <div style={{color:"#fff",fontSize:13,fontWeight:600}}>{selected.email||selected.nickname||"Visitor"}</div>
                <div style={{color:"#4a4a6a",fontSize:11}}>Assigned to: {selected.assigned_to||"Unassigned"}</div>
              </div>
              <button onClick={resolve} disabled={resolving} style={{padding:"7px 16px",borderRadius:6,border:"none",background:resolving?"#2a2a3e":"#22c55e",color:"#fff",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:resolving?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
                {resolving?<><Spinner size={10}/> Resolving…</>:"✓ Resolve"}
              </button>
            </div>
            {/* Messages */}
            <div style={{flex:1,overflowY:"auto",padding:"16px 18px",display:"flex",flexDirection:"column",gap:10,maxHeight:340}}>
              {loadingMsgs?<div style={{display:"flex",justifyContent:"center",padding:20}}><Spinner size={20}/></div>
              :messages.length===0?<div style={{color:"#3a3a5a",fontSize:12,textAlign:"center",padding:20}}>No messages</div>
              :messages.map((m,i)=>{
                const isOp = m.from==="operator";
                return (
                  <div key={i} style={{display:"flex",justifyContent:isOp?"flex-end":"flex-start"}}>
                    <div style={{maxWidth:"75%",padding:"9px 13px",borderRadius:isOp?"12px 12px 2px 12px":"12px 12px 12px 2px",background:isOp?"#6366f1":"#1e1e2e"}}>
                      <div style={{color:"#fff",fontSize:12,lineHeight:1.6}}>{m.content}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Reply box */}
            <div style={{padding:"12px 18px",borderTop:"1px solid #1e1e2e",display:"flex",gap:8}}>
              <textarea value={reply} onChange={e=>setReply(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&(e.metaKey||e.ctrlKey))sendReply();}} placeholder="Type a reply... (Cmd+Enter to send)" style={{flex:1,background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:8,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",resize:"none",minHeight:60}}/>
              <button onClick={sendReply} disabled={!reply.trim()||sending} style={{padding:"0 18px",borderRadius:8,border:"none",background:(!reply.trim()||sending)?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:(!reply.trim()||sending)?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6,alignSelf:"flex-end",height:40}}>
                {sending?<Spinner size={11}/>:"Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// Compact call history for user detail panel
function UserCallHistory({ api, toast, userId }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ direction:"outbound", outcome:"reached", duration_min:0, notes:"", follow_up_date:"", follow_up_note:"" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/admin/calls?user_id=${userId}&days=365`)
      .then(setCalls).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(()=>{ if(userId) load(); },[userId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/calls", { ...form, user_id:userId, duration_min:parseInt(form.duration_min)||0, follow_up_date:form.follow_up_date||null, follow_up_note:form.follow_up_note||null });
      setCalls(p=>[r,...p]); setShowForm(false);
      setForm({ direction:"outbound", outcome:"reached", duration_min:0, notes:"", follow_up_date:"", follow_up_note:"" });
      toast.success("Call logged");
    } catch(e){toast.error(e.message);}
    finally{setSaving(false);}
  };

  const OUTCOME_CFG = { reached:{color:"#22c55e",icon:"✅"}, voicemail:{color:"#f59e0b",icon:"📱"}, "no-answer":{color:"#ff4d4d",icon:"❌"}, "wrong-number":{color:"#6b7280",icon:"🚫"} };
  const iS = {width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"8px 10px",color:"#ccc",fontSize:11,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};

  if (loading) return <Spinner size={14}/>;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
        <button onClick={()=>setShowForm(p=>!p)} style={{ padding:"5px 14px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>+ Log Call</button>
      </div>
      {showForm && (
        <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:8, padding:12, marginBottom:12 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:8 }}>
            <div><select style={iS} value={form.direction} onChange={e=>setForm(f=>({...f,direction:e.target.value}))}><option value="outbound">Outbound</option><option value="inbound">Inbound</option></select></div>
            <div><select style={iS} value={form.outcome} onChange={e=>setForm(f=>({...f,outcome:e.target.value}))}><option value="reached">Reached</option><option value="voicemail">Voicemail</option><option value="no-answer">No Answer</option></select></div>
            <div><input style={iS} type="number" min={0} placeholder="Duration (min)" value={form.duration_min} onChange={e=>setForm(f=>({...f,duration_min:e.target.value}))}/></div>
            <div style={{gridColumn:"1/-1"}}><textarea style={{...iS,minHeight:50,resize:"vertical"}} placeholder="Notes..." value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
            <div><input style={iS} type="date" value={form.follow_up_date} onChange={e=>setForm(f=>({...f,follow_up_date:e.target.value}))}/></div>
            <div style={{gridColumn:"2/-1"}}><input style={iS} placeholder="Follow-up note..." value={form.follow_up_note} onChange={e=>setForm(f=>({...f,follow_up_note:e.target.value}))}/></div>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={save} disabled={saving} style={{ padding:"6px 14px", borderRadius:5, border:"none", background:saving?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:4 }}>
              {saving?<><Spinner size={10}/>Saving…</>:"Save"}
            </button>
            <button onClick={()=>setShowForm(false)} style={{ padding:"6px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}
      {calls.length===0 ? (
        <div style={{ color:"#3a3a5a", fontSize:12 }}>No calls logged — click "+ Log Call" to add one</div>
      ) : calls.map(c=>{
        const oc = OUTCOME_CFG[c.outcome]||OUTCOME_CFG.reached;
        return (
          <div key={c.id} style={{ padding:"9px 0", borderBottom:"1px solid #111120" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
              <span style={{ fontSize:14 }}>{oc.icon}</span>
              <span style={{ color:oc.color, fontSize:11, textTransform:"capitalize" }}>{c.outcome}</span>
              <span style={{ color:"#4a4a6a", fontSize:10 }}>· {c.direction} · {c.duration_min}min</span>
              <span style={{ color:"#3a3a5a", fontSize:10, marginLeft:"auto" }}>{c.created_at?new Date(c.created_at).toLocaleDateString():""}</span>
            </div>
            {c.notes&&<div style={{ color:"#8888aa", fontSize:11, paddingLeft:22 }}>{c.notes}</div>}
            {c.follow_up_date&&<div style={{ color:"#f59e0b", fontSize:10, paddingLeft:22, marginTop:2 }}>📅 Follow up {c.follow_up_date}{c.follow_up_note?` — ${c.follow_up_note}`:""}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// FEATURE 19 — GIFT / PROMO ACCOUNT MANAGER
// ════════════════════════════════════════════════════════════════════════════
function PromoAccountManager({ api, toast }) {
  const [promos,    setPromos]    = useState([]);
  const [expiring,  setExpiring]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [extending, setExtending] = useState(null);
  const [showExpired, setShowExpired] = useState(false);

  const BLANK = { email:"", plan:"pro", purpose:"testing", notes:"", expires_in_days:"30", pages_limit:"50" };
  const [form, setForm] = useState(BLANK);

  const PLAN_COLORS = { free:"#4a4a6a", starter:"#22c55e", pro:"#6366f1" };
  const PURPOSE_CFG = {
    testing:     { label:"Testing",     icon:"🧪", color:"#6366f1" },
    partner:     { label:"Partner",     icon:"🤝", color:"#22c55e" },
    influencer:  { label:"Influencer",  icon:"📢", color:"#f59e0b" },
    conference:  { label:"Conference",  icon:"🎪", color:"#8b5cf6" },
    beta:        { label:"Beta Tester", icon:"🔬", color:"#0ea5e9" },
    support:     { label:"Support",     icon:"💬", color:"#ec4899" },
    other:       { label:"Other",       icon:"📦", color:"#6b7280" },
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/promo-accounts?include_expired=${showExpired}`),
      api.get("/admin/promo-accounts/expiring?days=7"),
    ]).then(([p,e]) => { setPromos(p||[]); setExpiring(e||[]); })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [showExpired]);

  const save = async () => {
    if (!form.email||!form.purpose) { toast.error("Email and purpose required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/promo-accounts", {
        ...form,
        expires_in_days: form.expires_in_days ? parseInt(form.expires_in_days) : null,
        pages_limit: parseInt(form.pages_limit)||50,
      });
      toast.success(r.new_account
        ? `Account created for ${r.email} — login details emailed`
        : `${r.email} upgraded to ${form.plan}`);
      setShowForm(false); setForm(BLANK); load();
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const extend = async (id, days) => {
    setExtending(id);
    try {
      await api.patch(`/admin/promo-accounts/${id}/extend?days=${days}`);
      toast.success(`Extended by ${days} days`); load();
    } catch(e) { toast.error(e.message); }
    finally { setExtending(null); }
  };

  const revoke = async (id, email) => {
    if (!window.confirm(`Revoke promo access for ${email}? They will be downgraded to the free plan.`)) return;
    try {
      await api.del(`/admin/promo-accounts/${id}?downgrade_to_free=true`);
      toast.success("Promo access revoked"); load();
    } catch(e) { toast.error(e.message); }
  };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };
  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };

  return (
    <div>
      {/* Expiring soon warning */}
      {expiring.length > 0 && (
        <div style={{ ...cardS, borderColor:"#f59e0b30", background:"#1a1200", marginBottom:20 }}>
          <div style={{ color:"#f59e0b", fontSize:13, fontWeight:600, marginBottom:10 }}>
            ⚠️ {expiring.length} promo account{expiring.length!==1?"s":""} expiring within 7 days
          </div>
          {expiring.map(p => (
            <div key={p.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid #2a1a00" }}>
              <div style={{ flex:1 }}>
                <span style={{ color:"#ccc", fontSize:12 }}>{p.email}</span>
                <span style={{ color:"#4a4a6a", fontSize:11, marginLeft:8 }}>{p.plan} · {p.purpose}</span>
              </div>
              <span style={{ color:p.days_left===0?"#ff4d4d":"#f59e0b", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                {p.days_left===0?"Expires today":`${p.days_left}d left`}
              </span>
              <button onClick={()=>extend(p.id,30)} disabled={extending===p.id} style={{ padding:"4px 12px", borderRadius:5, border:"1px solid #22c55e30", background:"#22c55e10", color:"#22c55e", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                +30 days
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
        <div style={{ flex:1 }}>
          <div style={{ color:"#fff", fontSize:14, fontWeight:600 }}>Promo Accounts ({promos.filter(p=>!p.expired).length} active)</div>
          <div style={{ color:"#4a4a6a", fontSize:12 }}>Complimentary access for testing, partners, influencers, and beta testers</div>
        </div>
        <div onClick={()=>setShowExpired(p=>!p)} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
          <div style={{ width:34,height:18,borderRadius:9,background:showExpired?"#6366f1":"#2a2a3e",position:"relative",transition:"all 0.2s" }}>
            <div style={{ position:"absolute",top:2,left:showExpired?16:2,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/>
          </div>
          <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>Show expired</span>
        </div>
        <button onClick={()=>setShowForm(p=>!p)} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
          + Create Promo Account
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div style={{ ...cardS, borderColor:"#2a2a3e" }}>
          <div style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", marginBottom:16 }}>New Promo Account</div>
          <div style={{ background:"#0a1a0a", border:"1px solid #22c55e20", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:11, color:"#4a6a4a", lineHeight:1.8 }}>
            ✓ If the email doesn't exist yet, a new account is created and login details are emailed automatically.<br/>
            ✓ If the email already exists, their plan is upgraded — no new account created.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={lS}>EMAIL ADDRESS</label>
              <input style={iS} type="email" placeholder="contractor@example.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/>
            </div>
            <div>
              <label style={lS}>PLAN TO GRANT</label>
              <select style={iS} value={form.plan} onChange={e=>setForm(f=>({...f,plan:e.target.value}))}>
                {["starter","pro"].map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>PURPOSE</label>
              <select style={iS} value={form.purpose} onChange={e=>setForm(f=>({...f,purpose:e.target.value}))}>
                {Object.entries(PURPOSE_CFG).map(([id,{label}])=><option key={id} value={id}>{label}</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>EXPIRES IN (days, blank = never)</label>
              <input style={iS} type="number" min={1} placeholder="30" value={form.expires_in_days} onChange={e=>setForm(f=>({...f,expires_in_days:e.target.value}))}/>
            </div>
            <div>
              <label style={lS}>PAGE LIMIT (default: plan limit)</label>
              <input style={iS} type="number" min={1} value={form.pages_limit} onChange={e=>setForm(f=>({...f,pages_limit:e.target.value}))}/>
            </div>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={lS}>INTERNAL NOTES</label>
              <input style={iS} placeholder="e.g. Met at HVAC Summit 2024, promised 60-day trial" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <button onClick={save} disabled={saving||!form.email} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:(saving||!form.email)?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:(saving||!form.email)?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {saving?<><Spinner size={11}/> Creating…</>:"Create Account"}
            </button>
            <button onClick={()=>{setShowForm(false);setForm(BLANK);}} style={{ padding:"9px 16px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Promos list */}
      {loading ? <div style={{ display:"flex",justifyContent:"center",padding:40 }}><Spinner size={28}/></div>
      : promos.length===0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🎁</div>
          No promo accounts yet — create one above
        </div>
      ) : promos.map(p => {
        const purpose = PURPOSE_CFG[p.purpose]||PURPOSE_CFG.other;
        const planColor = PLAN_COLORS[p.plan]||"#6366f1";
        const isExpired = p.expired;
        return (
          <div key={p.id} style={{ background:"#0f0f1a", border:`1px solid ${isExpired?"#111118":"#1e1e2e"}`, borderLeft:`3px solid ${isExpired?"#3a3a5a":planColor}`, borderRadius:10, padding:"16px 18px", marginBottom:10, opacity:isExpired?0.45:1 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ width:40,height:40,borderRadius:10,background:`${purpose.color}20`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0 }}>
                {purpose.icon}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                  <span style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{p.email}</span>
                  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${planColor}20`, color:planColor, textTransform:"capitalize" }}>{p.plan}</span>
                  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${purpose.color}20`, color:purpose.color }}>{purpose.label}</span>
                  {isExpired && <span style={{ color:"#ff4d4d", fontSize:10, fontFamily:"'DM Mono',monospace" }}>EXPIRED</span>}
                  {!isExpired && p.days_left!==null && p.days_left<=7 && <span style={{ color:"#f59e0b", fontSize:10, fontFamily:"'DM Mono',monospace" }}>{p.days_left===0?"Expires today":`${p.days_left}d left`}</span>}
                </div>
                <div style={{ display:"flex", gap:14, color:"#4a4a6a", fontSize:11, flexWrap:"wrap" }}>
                  {p.expires_at && <span>📅 Expires {new Date(p.expires_at).toLocaleDateString()}</span>}
                  {!p.expires_at && <span>🔁 Never expires</span>}
                  <span>📡 {p.page_count} pages · 🎯 {p.match_count} matches</span>
                  <span>👤 {p.created_by}</span>
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                {p.notes && <div style={{ color:"#6666aa", fontSize:11, marginTop:5, fontStyle:"italic" }}>{p.notes}</div>}
              </div>
              {!isExpired && (
                <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                  <button onClick={()=>extend(p.id,30)} disabled={extending===p.id} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #22c55e30", background:"#22c55e10", color:"#22c55e", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    {extending===p.id?<Spinner size={10}/>:"+30d"}
                  </button>
                  <button onClick={()=>revoke(p.id,p.email)} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #ff4d4d20", background:"transparent", color:"#ff4d4d", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Revoke</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FEATURE 20 — A/B TEST MANAGER
// ════════════════════════════════════════════════════════════════════════════
function ABTestManager({ api, toast }) {
  const [tests,    setTests]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [results,  setResults]  = useState(null);
  const [viewing,  setViewing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [acting,   setActing]   = useState(null);

  const BLANK_TEST = { name:"", description:"", traffic_pct:100,
    variants:[{id:"control",name:"Control",description:"Current version",weight:50},
              {id:"variant_a",name:"Variant A",description:"New version",weight:50}]};
  const [form, setForm] = useState(BLANK_TEST);

  const load = () => {
    setLoading(true);
    api.get("/admin/ab-tests").then(setTests).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));
  };
  useEffect(load,[]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Test name required"); return; }
    const totalWeight = form.variants.reduce((a,v)=>a+parseInt(v.weight||0),0);
    if (totalWeight!==100) { toast.error(`Variant weights must sum to 100 (currently ${totalWeight})`); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/ab-tests", {
        ...form,
        variants: form.variants.map(v=>({...v,weight:parseInt(v.weight)})),
      });
      setTests(p=>[r,...p]);
      setShowForm(false); setForm(BLANK_TEST);
      toast.success("Test created — click Start to begin assigning users");
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const start = async (id) => {
    setActing(id);
    try {
      const r = await api.post(`/admin/ab-tests/${id}/start`);
      toast.success(`Test started — ${r.users_assigned} users assigned`);
      load();
    } catch(e) { toast.error(e.message); }
    finally { setActing(null); }
  };

  const end = async (test) => {
    const winner = window.prompt(`Enter the winning variant ID to declare a winner:\n${test.variants.map(v=>`  ${v.id} — ${v.name}`).join("\n")}`);
    if (!winner) return;
    setActing(test.id);
    try {
      await api.post(`/admin/ab-tests/${test.id}/end?winner=${winner}`);
      toast.success(`Test ended — winner: ${winner}`); load();
    } catch(e) { toast.error(e.message); }
    finally { setActing(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this test? All assignments and events will be lost.")) return;
    try { await api.del(`/admin/ab-tests/${id}`); setTests(p=>p.filter(t=>t.id!==id)); }
    catch(e) { toast.error(e.message); }
  };

  const viewResults = async (test) => {
    setViewing(test.id);
    try {
      const r = await api.get(`/admin/ab-tests/${test.id}/results`);
      setResults(r);
    } catch(e) { toast.error(e.message); }
    finally { setViewing(null); }
  };

  const setVariant = (i,k,v) => setForm(f=>({...f,variants:f.variants.map((va,idx)=>idx===i?{...va,[k]:v}:va)}));
  const addVariant = () => {
    const id=`variant_${String.fromCharCode(97+form.variants.length)}`;
    setForm(f=>({...f,variants:[...f.variants,{id,name:`Variant ${f.variants.length}`,description:"",weight:0}]}));
  };
  const removeVariant = (i) => setForm(f=>({...f,variants:f.variants.filter((_,idx)=>idx!==i)}));

  const STATUS_CFG = {
    draft:   {color:"#4a4a6a", bg:"#1e1e2e",   label:"Draft",   icon:"📝"},
    running: {color:"#22c55e", bg:"#22c55e15", label:"Running", icon:"▶"},
    ended:   {color:"#6366f1", bg:"#6366f115", label:"Ended",   icon:"✓"},
  };
  const iS = {width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS = {color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};
  const cardS = {background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"20px 24px",marginBottom:16};

  return (
    <div>
      {/* Results modal */}
      {results && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }} onClick={()=>setResults(null)}>
          <div style={{ background:"#0f0f1a",border:"1px solid #2a2a3e",borderRadius:12,padding:28,width:600,maxWidth:"95vw",maxHeight:"80vh",overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20 }}>
              <div>
                <div style={{ color:"#fff",fontSize:15,fontWeight:700 }}>{results.test_name}</div>
                <div style={{ color:"#4a4a6a",fontSize:12,marginTop:3 }}>Results · {results.status}</div>
              </div>
              <button onClick={()=>setResults(null)} style={{ background:"transparent",border:"none",color:"#4a4a6a",fontSize:20,cursor:"pointer" }}>×</button>
            </div>

            {/* Variant results */}
            {results.results.map(v=>{
              const isWinner = v.is_winner;
              const isLeading = !results.winner && results.results.length>0 && v.conversion_rate===Math.max(...results.results.map(r=>r.conversion_rate));
              return (
                <div key={v.variant_id} style={{ background:isWinner?"#0a1a0a":isLeading?"#0a0a1a":"#0a0a0f", border:`1px solid ${isWinner?"#22c55e30":isLeading?"#6366f130":"#1e1e2e"}`, borderRadius:10, padding:"16px 18px", marginBottom:12 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                        <span style={{ color:"#fff",fontSize:13,fontWeight:600 }}>{v.variant_name}</span>
                        <code style={{ color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace" }}>{v.variant_id}</code>
                        {isWinner&&<span style={{ padding:"2px 8px",borderRadius:4,background:"#22c55e20",color:"#22c55e",fontSize:9,fontFamily:"'DM Mono',monospace" }}>🏆 WINNER</span>}
                        {isLeading&&!results.winner&&<span style={{ padding:"2px 8px",borderRadius:4,background:"#6366f120",color:"#a5b4fc",fontSize:9,fontFamily:"'DM Mono',monospace" }}>LEADING</span>}
                      </div>
                    </div>
                    <span style={{ color:"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace" }}>{v.weight}% of traffic</span>
                  </div>
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10 }}>
                    {[
                      {label:"USERS",       value:v.users,                       color:"#ccc"},
                      {label:"CONVERSIONS", value:v.conversions,                  color:"#22c55e"},
                      {label:"CONV. RATE",  value:`${v.conversion_rate}%`,        color:isLeading?"#22c55e":"#ccc"},
                      {label:"UPGRADES",    value:v.upgrades,                     color:"#6366f1"},
                    ].map(k=>(
                      <div key={k.label} style={{ background:"#0a0a0f",borderRadius:7,padding:"10px 12px",textAlign:"center" }}>
                        <div style={{ color:"#4a4a6a",fontSize:9,fontFamily:"'DM Mono',monospace",marginBottom:4 }}>{k.label}</div>
                        <div style={{ color:k.color,fontSize:18,fontWeight:700,fontFamily:"'DM Mono',monospace" }}>{k.value}</div>
                      </div>
                    ))}
                  </div>
                  {/* Conversion rate bar */}
                  <div style={{ marginTop:10 }}>
                    <div style={{ height:6,background:"#1e1e2e",borderRadius:3,overflow:"hidden" }}>
                      <div style={{ height:"100%",width:`${Math.min(100,v.conversion_rate*5)}%`,background:isWinner?"#22c55e":isLeading?"#6366f1":"#4a4a6a",borderRadius:3 }}/>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Statistical note */}
            <div style={{ padding:"12px 14px",background:"#0a0a0f",borderRadius:8,border:"1px solid #1e1e2e",marginTop:4 }}>
              <div style={{ color:"#a5b4fc",fontSize:11,fontFamily:"'DM Mono',monospace" }}>📊 {results.stat_note}</div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:20 }}>
        <div>
          <div style={{ color:"#fff",fontSize:14,fontWeight:600 }}>A/B Test Manager</div>
          <div style={{ color:"#4a4a6a",fontSize:12 }}>Split-test onboarding flows, email subject lines, and pricing pages</div>
        </div>
        <div style={{ marginLeft:"auto" }}>
          <button onClick={()=>setShowForm(p=>!p)} style={{ padding:"9px 20px",borderRadius:6,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer" }}>+ New Test</button>
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <div style={{ ...cardS, borderColor:"#2a2a3e",marginBottom:20 }}>
          <div style={{ color:"#a5b4fc",fontSize:13,fontFamily:"'DM Mono',monospace",marginBottom:16 }}>New A/B Test</div>
          <div style={{ display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:16 }}>
            <div><label style={lS}>TEST NAME (unique identifier)</label><input style={iS} placeholder="e.g. onboarding_step3_video" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value.toLowerCase().replace(/\s+/g,"_")}))}/></div>
            <div><label style={lS}>TRAFFIC % IN TEST</label><input style={iS} type="number" min={1} max={100} value={form.traffic_pct} onChange={e=>setForm(f=>({...f,traffic_pct:parseInt(e.target.value)||100}))}/><div style={{ color:"#3a3a5a",fontSize:10,marginTop:3 }}>% of all users included in test</div></div>
            <div style={{ gridColumn:"1/-1" }}><label style={lS}>DESCRIPTION (what you're testing)</label><input style={iS} placeholder="Testing whether adding a video to step 3 improves completion rate" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
          </div>

          <div style={{ color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,marginBottom:10 }}>
            VARIANTS (weights must sum to 100 — currently {form.variants.reduce((a,v)=>a+parseInt(v.weight||0),0)})
          </div>
          {form.variants.map((v,i)=>(
            <div key={i} style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr 80px 40px",gap:8,marginBottom:8,alignItems:"end" }}>
              <div><label style={lS}>VARIANT ID</label><input style={iS} value={v.id} onChange={e=>setVariant(i,"id",e.target.value.toLowerCase().replace(/\s+/g,"_"))}/></div>
              <div><label style={lS}>NAME (readable)</label><input style={iS} value={v.name} onChange={e=>setVariant(i,"name",e.target.value)}/></div>
              <div><label style={lS}>DESCRIPTION</label><input style={iS} value={v.description} onChange={e=>setVariant(i,"description",e.target.value)}/></div>
              <div><label style={lS}>WEIGHT %</label><input style={iS} type="number" min={0} max={100} value={v.weight} onChange={e=>setVariant(i,"weight",parseInt(e.target.value)||0)}/></div>
              <button onClick={()=>removeVariant(i)} disabled={form.variants.length<=2} style={{ padding:"9px",borderRadius:5,border:"1px solid #2a2a3e",background:"transparent",color:form.variants.length<=2?"#2a2a3e":"#ff4d4d",cursor:form.variants.length<=2?"not-allowed":"pointer",fontSize:14,height:38 }}>✕</button>
            </div>
          ))}
          <button onClick={addVariant} style={{ padding:"6px 14px",borderRadius:5,border:"1px dashed #2a2a3e",background:"transparent",color:"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer",marginBottom:14 }}>+ Add Variant</button>

          <div style={{ background:"#0a0a1a",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:11,color:"#4a4a6a",lineHeight:1.8 }}>
            💡 After starting the test, use <code style={{ color:"#a5b4fc" }}>GET /ab/assignment/{"{"}{form.name||"test_name"}{"}"}</code> in your frontend to get the user's assigned variant, then render accordingly.
          </div>

          <div style={{ display:"flex",gap:8 }}>
            <button onClick={save} disabled={saving||!form.name} style={{ padding:"9px 20px",borderRadius:6,border:"none",background:(saving||!form.name)?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:(saving||!form.name)?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6 }}>
              {saving?<><Spinner size={11}/>Saving…</>:"Create Test"}
            </button>
            <button onClick={()=>{setShowForm(false);setForm(BLANK_TEST);}} style={{ padding:"9px 16px",borderRadius:6,border:"1px solid #2a2a3e",background:"transparent",color:"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Tests list */}
      {loading?<div style={{ display:"flex",justifyContent:"center",padding:40 }}><Spinner size={28}/></div>
      :tests.length===0?(
        <div style={{ textAlign:"center",padding:"60px 0",color:"#3a3a5a",fontFamily:"'DM Mono',monospace" }}>
          <div style={{ fontSize:40,marginBottom:12 }}>📊</div>
          No tests yet — create your first A/B test above
        </div>
      ):tests.map(test=>{
        const sc = STATUS_CFG[test.status]||STATUS_CFG.draft;
        return (
          <div key={test.id} style={{ background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:10,padding:"16px 18px",marginBottom:10 }}>
            <div style={{ display:"flex",alignItems:"flex-start",gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap" }}>
                  <code style={{ color:"#fff",fontSize:13,fontWeight:700,fontFamily:"'DM Mono',monospace" }}>{test.name}</code>
                  <span style={{ padding:"2px 8px",borderRadius:4,fontSize:9,fontFamily:"'DM Mono',monospace",background:sc.bg,color:sc.color }}>{sc.icon} {sc.label}</span>
                  {test.status==="running"&&<span style={{ color:"#22c55e",fontSize:11 }}>{test.participants} users enrolled</span>}
                  {test.winner&&<span style={{ color:"#f59e0b",fontSize:11 }}>🏆 Winner: {test.winner}</span>}
                </div>
                {test.description&&<div style={{ color:"#8888aa",fontSize:12,marginBottom:8 }}>{test.description}</div>}
                <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                  {(test.variants||[]).map(v=>(
                    <div key={v.id} style={{ padding:"4px 10px",background:"#1e1e2e",borderRadius:5,fontSize:11 }}>
                      <span style={{ color:"#ccc" }}>{v.name}</span>
                      <span style={{ color:"#4a4a6a",marginLeft:6,fontFamily:"'DM Mono',monospace" }}>{v.weight}%</span>
                    </div>
                  ))}
                  <span style={{ color:"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace",display:"flex",alignItems:"center" }}>{test.traffic_pct}% traffic</span>
                </div>
              </div>
              <div style={{ display:"flex",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end" }}>
                <button onClick={()=>viewResults(test)} disabled={viewing===test.id} style={{ padding:"6px 12px",borderRadius:5,border:"1px solid #6366f130",background:"#6366f110",color:"#a5b4fc",fontSize:10,fontFamily:"'DM Mono',monospace",cursor:"pointer",display:"flex",alignItems:"center",gap:4 }}>
                  {viewing===test.id?<Spinner size={10}/>:"📊"} Results
                </button>
                {test.status==="draft"&&(
                  <button onClick={()=>start(test.id)} disabled={acting===test.id} style={{ padding:"6px 12px",borderRadius:5,border:"none",background:acting===test.id?"#2a2a3e":"#22c55e",color:"#fff",fontSize:10,fontFamily:"'DM Mono',monospace",cursor:acting===test.id?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:4 }}>
                    {acting===test.id?<Spinner size={10}/>:"▶"} Start
                  </button>
                )}
                {test.status==="running"&&(
                  <button onClick={()=>end(test)} disabled={acting===test.id} style={{ padding:"6px 12px",borderRadius:5,border:"1px solid #f59e0b30",background:"#f59e0b10",color:"#f59e0b",fontSize:10,fontFamily:"'DM Mono',monospace",cursor:acting===test.id?"not-allowed":"pointer" }}>
                    ✓ End Test
                  </button>
                )}
                <button onClick={()=>del(test.id)} style={{ padding:"6px 10px",borderRadius:5,border:"1px solid #ff4d4d20",background:"transparent",color:"#ff4d4d",fontSize:10,cursor:"pointer" }}>✕</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// PROXY POOL MANAGER — Admin panel tab
// Upload, validate, monitor, and rotate residential proxies
// ════════════════════════════════════════════════════════════════════════════
function ProxyManager({ api, toast }) {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("pool");
  const [testing,   setTesting]   = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [deleting,  setDeleting]  = useState(false);
  const [filter,    setFilter]    = useState("all");  // all | ok | failed | untested
  const [search,    setSearch]    = useState("");

  // Upload form state
  const [uploadText, setUploadText] = useState("");
  const [label,      setLabel]      = useState("");
  const [provider,   setProvider]   = useState("");
  const [country,    setCountry]    = useState("US");
  const [overwrite,  setOverwrite]  = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const COUNTRIES = [
    "US","GB","CA","AU","DE","FR","NL","SE","NO","DK","FI","JP","SG","BR","MX"
  ];

  const load = () => {
    setLoading(true);
    api.get(`/admin/proxies${filter!=="all"?`?health=${filter}`:""}`)
      .then(setData).catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  };
  useEffect(load,[filter]);

  const upload = async () => {
    if (!uploadText.trim()) { toast.error("Paste at least one proxy"); return; }
    setUploading(true); setUploadResult(null);
    try {
      const r = await api.post("/admin/proxies/upload", {
        proxies_text: uploadText,
        label:        label||null,
        provider:     provider||null,
        country,
        overwrite_existing: overwrite,
      });
      setUploadResult(r);
      if (r.added > 0) {
        toast.success(`${r.added} proxies added to pool`);
        setUploadText(""); setLabel(""); setProvider("");
        load();
        setTab("pool");
      } else {
        toast.error(r.message || "No proxies were added");
      }
    } catch(e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const testOne = async (id) => {
    setTestingId(id);
    try {
      const r = await api.post(`/admin/proxies/${id}/test`);
      if (r.success) toast.success(`✓ Proxy OK — ${r.latency_ms}ms · IP: ${r.ip}`);
      else           toast.error(`✗ Proxy failed — ${r.error}`);
      load();
    } catch(e) { toast.error(e.message); }
    finally { setTestingId(null); }
  };

  const testAll = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/proxies/test-all?limit=50");
      toast.success(`Test complete — ${r.ok} healthy, ${r.failed} failed`);
      load();
    } catch(e) { toast.error(e.message); }
    finally { setTesting(false); }
  };

  const deleteFailed = async () => {
    if (!window.confirm("Delete all failed proxies? This cannot be undone.")) return;
    setDeleting(true);
    try {
      const r = await api.del("/admin/proxies/bulk/failed");
      toast.success(`${r.deleted} failed proxies removed`);
      load();
    } catch(e) { toast.error(e.message); }
    finally { setDeleting(false); }
  };

  const deleteOne = async (id) => {
    try { await api.del(`/admin/proxies/${id}`); load(); toast.success("Proxy removed"); }
    catch(e) { toast.error(e.message); }
  };

  const toggleActive = async (id, current) => {
    try { await api.patch(`/admin/proxies/${id}`, { active: !current }); load(); }
    catch(e) { toast.error(e.message); }
  };

  const HEALTH_CFG = {
    ok:       { color:"#22c55e", icon:"●", label:"Healthy"  },
    failed:   { color:"#ff4d4d", icon:"✕", label:"Failed"   },
    untested: { color:"#4a4a6a", icon:"○", label:"Untested" },
  };
  const PROTO_COLORS = { http:"#6366f1", https:"#8b5cf6", socks5:"#f59e0b" };

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };
  const iS    = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS    = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };

  const proxies = (data?.proxies||[]).filter(p =>
    !search ||
    p.host?.toLowerCase().includes(search.toLowerCase()) ||
    p.label?.toLowerCase().includes(search.toLowerCase()) ||
    p.provider?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Stats bar */}
      {data?.stats && (
        <div className="sl-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:20 }}>
          {[
            { label:"TOTAL",    value:data.stats.total,    color:"#6366f1" },
            { label:"ACTIVE",   value:data.stats.active,   color:"#22c55e" },
            { label:"HEALTHY",  value:data.stats.healthy,  color:"#22c55e" },
            { label:"FAILED",   value:data.stats.failed,   color:"#ff4d4d" },
            { label:"UNTESTED", value:data.stats.untested, color:"#f59e0b" },
          ].map(k => (
            <div key={k.label} onClick={()=>k.label!=="TOTAL"&&k.label!=="ACTIVE"&&setFilter(k.label.toLowerCase())} style={{ ...cardS, marginBottom:0, cursor:k.label!=="TOTAL"&&k.label!=="ACTIVE"?"pointer":"default", borderColor:`${k.color}20` }}>
              <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
              <div style={{ color:k.color, fontSize:28, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:20 }}>
        {[
          { id:"pool",   label:"Proxy Pool",   icon:"📋" },
          { id:"upload", label:"Upload Proxies", icon:"📤" },
          { id:"guide",  label:"Format Guide",   icon:"📖" },
        ].map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"8px 18px", borderRadius:8, border:`1px solid ${tab===t.id?"#6366f1":"#1e1e2e"}`, background:tab===t.id?"#6366f120":"transparent", color:tab===t.id?"#a5b4fc":"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
            {t.icon} {t.label}
          </button>
        ))}
        <div style={{ flex:1 }}/>
        {tab==="pool" && (
          <>
            <button onClick={testAll} disabled={testing} style={{ padding:"8px 18px", borderRadius:6, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:testing?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {testing?<><Spinner size={11}/> Testing…</>:"🔬 Test All (50)"}
            </button>
            {(data?.stats?.failed||0) > 0 && (
              <button onClick={deleteFailed} disabled={deleting} style={{ padding:"8px 18px", borderRadius:6, border:"1px solid #ff4d4d30", background:"#ff4d4d10", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:deleting?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
                {deleting?<><Spinner size={11}/> Deleting…</>:`🗑️ Remove Failed (${data?.stats?.failed})`}
              </button>
            )}
          </>
        )}
      </div>

      {/* ── PROXY POOL TAB ─────────────────────────────────────────────── */}
      {tab==="pool" && (
        <div>
          {/* Filters */}
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search host, label, provider..." style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", width:240 }}/>
            {["all","ok","failed","untested"].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${filter===f?(HEALTH_CFG[f]?.color||"#6366f1"):"#1e1e2e"}`, background:filter===f?`${HEALTH_CFG[f]?.color||"#6366f1"}20`:"transparent", color:filter===f?(HEALTH_CFG[f]?.color||"#a5b4fc"):"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", textTransform:"capitalize" }}>
                {f==="all"?"All":HEALTH_CFG[f]?.label} {f!=="all"?`(${data?.stats?.[f]||0})`:""}
              </button>
            ))}
            <span style={{ color:"#4a4a6a", fontSize:11, marginLeft:"auto", fontFamily:"'DM Mono',monospace" }}>{proxies.length} shown</span>
          </div>

          {loading ? <div style={{ display:"flex",justifyContent:"center",padding:40 }}><Spinner size={28}/></div>
          : proxies.length===0 ? (
            <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🌐</div>
              {filter!=="all" ? `No ${filter} proxies` : "No proxies yet — click Upload Proxies to add your first batch"}
            </div>
          ) : (
            <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, overflow:"hidden" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr style={{ background:"#0a0a0f", borderBottom:"1px solid #1e1e2e" }}>
                    {["Health","Host:Port","Protocol","Label / Provider","Country","Latency","Stats","Actions"].map(h=>(
                      <th key={h} style={{ padding:"11px 12px", textAlign:"left", color:"#4a4a6a", fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:1, fontWeight:400, whiteSpace:"nowrap" }}>{h.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proxies.map(p => {
                    const hc = HEALTH_CFG[p.health]||HEALTH_CFG.untested;
                    const pc = PROTO_COLORS[p.protocol]||"#6366f1";
                    return (
                      <tr key={p.id} style={{ borderBottom:"1px solid #0a0a12", opacity:p.active?1:0.4 }}>
                        <td style={{ padding:"10px 12px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                            <div style={{ width:8,height:8,borderRadius:"50%",background:hc.color,boxShadow:`0 0 5px ${hc.color}` }}/>
                            <span style={{ color:hc.color, fontSize:10, fontFamily:"'DM Mono',monospace" }}>{hc.label}</span>
                          </div>
                        </td>
                        <td style={{ padding:"10px 12px" }}>
                          <div style={{ color:"#ccc", fontFamily:"'DM Mono',monospace", fontSize:11 }}>{p.host}:{p.port}</div>
                          {p.username && <div style={{ color:"#3a3a5a", fontSize:10 }}>auth: {p.username}</div>}
                        </td>
                        <td style={{ padding:"10px 12px" }}>
                          <span style={{ padding:"2px 7px", borderRadius:4, background:`${pc}20`, color:pc, fontSize:9, fontFamily:"'DM Mono',monospace" }}>{p.protocol}</span>
                        </td>
                        <td style={{ padding:"10px 12px" }}>
                          {p.label&&<div style={{ color:"#ccc", fontSize:11 }}>{p.label}</div>}
                          {p.provider&&<div style={{ color:"#4a4a6a", fontSize:10 }}>{p.provider}</div>}
                        </td>
                        <td style={{ padding:"10px 12px", color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{p.country||"—"}</td>
                        <td style={{ padding:"10px 12px", color:p.avg_latency_ms>3000?"#ff4d4d":p.avg_latency_ms>1500?"#f59e0b":"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                          {p.avg_latency_ms ? `${p.avg_latency_ms}ms` : "—"}
                        </td>
                        <td style={{ padding:"10px 12px" }}>
                          <div style={{ color:"#22c55e", fontSize:10, fontFamily:"'DM Mono',monospace" }}>✓ {p.success_count}</div>
                          <div style={{ color:"#ff4d4d", fontSize:10, fontFamily:"'DM Mono',monospace" }}>✗ {p.fail_count}</div>
                        </td>
                        <td style={{ padding:"10px 12px" }}>
                          <div style={{ display:"flex", gap:5 }}>
                            <button onClick={()=>testOne(p.id)} disabled={testingId===p.id} title="Test this proxy" style={{ padding:"4px 10px", borderRadius:4, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:testingId===p.id?"not-allowed":"pointer" }}>
                              {testingId===p.id?<Spinner size={9}/>:"Test"}
                            </button>
                            <div onClick={()=>toggleActive(p.id,p.active)} title={p.active?"Disable":"Enable"} style={{ width:32,height:18,borderRadius:9,background:p.active?"#22c55e":"#2a2a3e",position:"relative",cursor:"pointer",transition:"all 0.2s",flexShrink:0,marginTop:1 }}>
                              <div style={{ position:"absolute",top:2,left:p.active?14:2,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/>
                            </div>
                            <button onClick={()=>deleteOne(p.id)} title="Remove" style={{ padding:"4px 8px", borderRadius:4, border:"1px solid #ff4d4d20", background:"transparent", color:"#ff4d4d", fontSize:12, cursor:"pointer" }}>✕</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── UPLOAD TAB ─────────────────────────────────────────────────── */}
      {tab==="upload" && (
        <div>
          <div style={{ ...cardS }}>
            <div style={{ color:"#fff", fontSize:14, fontWeight:600, marginBottom:6 }}>Upload Proxy List</div>
            <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:18, lineHeight:1.7 }}>
              Paste one proxy per line. Supported formats: <code style={{ color:"#a5b4fc", background:"#1e1e2e", padding:"1px 6px", borderRadius:3, fontSize:11 }}>host:port</code>, <code style={{ color:"#a5b4fc", background:"#1e1e2e", padding:"1px 6px", borderRadius:3, fontSize:11 }}>user:pass@host:port</code>, <code style={{ color:"#a5b4fc", background:"#1e1e2e", padding:"1px 6px", borderRadius:3, fontSize:11 }}>http://user:pass@host:port</code>. Blank lines and # comments are ignored.
            </div>

            {/* Main textarea */}
            <div style={{ marginBottom:14 }}>
              <label style={lS}>PROXY LIST (one per line)</label>
              <textarea
                style={{ ...iS, minHeight:220, resize:"vertical", fontFamily:"'DM Mono',monospace", lineHeight:1.8, fontSize:11 }}
                placeholder={`# Bright Data format\nhttp://user-123:pass456@brd.superproxy.io:22225\nhttp://user-123:pass456@brd.superproxy.io:22226\n\n# Oxylabs format\nhttp://customer-xyz:pass@pr.oxylabs.io:7777\n\n# Simple host:port:user:pass\n192.168.1.100:8080:myuser:mypass\n\n# No auth\n10.0.0.1:3128\n10.0.0.2:3128`}
                value={uploadText}
                onChange={e=>setUploadText(e.target.value)}
              />
              <div style={{ color:"#3a3a5a", fontSize:10, marginTop:4, fontFamily:"'DM Mono',monospace" }}>
                {uploadText.split("\n").filter(l=>l.trim()&&!l.trim().startsWith("#")).length} lines detected
              </div>
            </div>

            {/* Metadata */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
              <div>
                <label style={lS}>LABEL (optional, e.g. "Batch Jan 2025")</label>
                <input style={iS} placeholder="Batch 1" value={label} onChange={e=>setLabel(e.target.value)}/>
              </div>
              <div>
                <label style={lS}>PROVIDER (optional, e.g. "Bright Data")</label>
                <input style={iS} placeholder="Bright Data" value={provider} onChange={e=>setProvider(e.target.value)}/>
              </div>
              <div>
                <label style={lS}>COUNTRY / REGION</label>
                <select style={iS} value={country} onChange={e=>setCountry(e.target.value)}>
                  {COUNTRIES.map(c=><option key={c} value={c}>{c}</option>)}
                  <option value="MIXED">Mixed / Various</option>
                </select>
              </div>
            </div>

            {/* Overwrite toggle */}
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
              <div onClick={()=>setOverwrite(p=>!p)} style={{ width:36,height:20,borderRadius:10,background:overwrite?"#f59e0b":"#2a2a3e",position:"relative",cursor:"pointer",transition:"all 0.2s" }}>
                <div style={{ position:"absolute",top:2,left:overwrite?16:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/>
              </div>
              <div>
                <div style={{ color:"#ccc", fontSize:12 }}>Update existing proxies</div>
                <div style={{ color:"#4a4a6a", fontSize:11 }}>If a proxy already exists, overwrite its password and metadata. Otherwise duplicates are skipped.</div>
              </div>
            </div>

            <button onClick={upload} disabled={uploading||!uploadText.trim()} style={{ padding:"10px 24px", borderRadius:7, border:"none", background:(uploading||!uploadText.trim())?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:(uploading||!uploadText.trim())?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:8 }}>
              {uploading?<><Spinner size={13}/> Importing…</>:"📤 Import Proxies"}
            </button>
          </div>

          {/* Upload result */}
          {uploadResult && (
            <div style={{ ...cardS, borderColor: uploadResult.added>0?"#22c55e30":"#f59e0b30", background: uploadResult.added>0?"#0a1a0a":"#1a1200" }}>
              <div style={{ color: uploadResult.added>0?"#22c55e":"#f59e0b", fontSize:13, fontWeight:600, marginBottom:10 }}>
                {uploadResult.added>0 ? "✓ Import Complete" : "⚠ Import Issues"}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:12 }}>
                {[
                  { label:"Added",      value:uploadResult.added,     color:"#22c55e" },
                  { label:"Duplicates", value:uploadResult.duplicate,  color:"#f59e0b" },
                  { label:"Invalid",    value:uploadResult.invalid,    color:"#ff4d4d" },
                  { label:"Errors",     value:uploadResult.skipped,    color:"#ff4d4d" },
                ].map(k=>(
                  <div key={k.label} style={{ background:"#0a0a0f", borderRadius:7, padding:"10px 12px", textAlign:"center" }}>
                    <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{k.label.toUpperCase()}</div>
                    <div style={{ color:k.color, fontSize:22, fontWeight:700, fontFamily:"'DM Mono',monospace" }}>{k.value}</div>
                  </div>
                ))}
              </div>
              {uploadResult.errors?.length>0 && (
                <div>
                  <div style={{ color:"#ff8888", fontSize:11, marginBottom:6 }}>Parse errors:</div>
                  {uploadResult.errors.map((e,i)=>(
                    <div key={i} style={{ color:"#ff8888", fontSize:11, fontFamily:"'DM Mono',monospace", marginBottom:3 }}>• {e}</div>
                  ))}
                </div>
              )}
              <div style={{ color:"#4a4a6a", fontSize:12, marginTop:8 }}>{uploadResult.message}</div>
              {uploadResult.added>0&&<div style={{ color:"#22c55e", fontSize:12, marginTop:6 }}>→ Go to Proxy Pool tab to test the new proxies.</div>}
            </div>
          )}
        </div>
      )}

      {/* ── FORMAT GUIDE TAB ───────────────────────────────────────────── */}
      {tab==="guide" && (
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:14, fontWeight:600, marginBottom:18 }}>Supported Proxy Formats</div>
          {[
            {
              format:  "host:port",
              example: "192.168.1.100:8080\n10.0.0.2:3128",
              desc:    "No authentication — public or internal proxies only",
              color:   "#4a4a6a",
            },
            {
              format:  "host:port:user:pass",
              example: "proxy.example.com:8080:myuser:mypassword\npr.oxylabs.io:7777:customer-xyz:s3cr3t",
              desc:    "Colon-separated format — common with Oxylabs and IPRoyal",
              color:   "#6366f1",
            },
            {
              format:  "user:pass@host:port",
              example: "myuser:mypassword@proxy.example.com:8080\nbrd-customer-abc:pass123@zproxy.lum-superproxy.io:22225",
              desc:    "At-sign format — used by Bright Data (Luminati) and Smartproxy",
              color:   "#8b5cf6",
            },
            {
              format:  "http://user:pass@host:port",
              example: "http://user:pass@gate.dc.smartproxy.com:10000\nhttps://user:pass@proxy.example.com:443",
              desc:    "Full URL format with protocol prefix — HTTP or HTTPS proxies",
              color:   "#22c55e",
            },
            {
              format:  "socks5://user:pass@host:port",
              example: "socks5://user:pass@socks.example.com:1080",
              desc:    "SOCKS5 proxies — better for Playwright but less common",
              color:   "#f59e0b",
            },
          ].map(item=>(
            <div key={item.format} style={{ marginBottom:20, paddingBottom:20, borderBottom:"1px solid #1e1e2e" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <code style={{ color:item.color, fontSize:12, fontFamily:"'DM Mono',monospace", fontWeight:700 }}>{item.format}</code>
              </div>
              <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:8 }}>{item.desc}</div>
              <pre style={{ background:"#0a0a0f", border:`1px solid ${item.color}20`, borderRadius:7, padding:"12px 14px", margin:0, color:item.color, fontSize:11, fontFamily:"'DM Mono',monospace", lineHeight:1.8, overflowX:"auto" }}>{item.example}</pre>
            </div>
          ))}

          <div style={{ background:"#0a0a15", border:"1px solid #6366f130", borderRadius:8, padding:"14px 16px", marginTop:8 }}>
            <div style={{ color:"#a5b4fc", fontSize:12, fontWeight:600, marginBottom:8 }}>Tips for bulk uploads</div>
            <ul style={{ color:"#4a4a6a", fontSize:12, lineHeight:2, paddingLeft:18, margin:0 }}>
              <li>Lines starting with <code style={{ color:"#ccc" }}>#</code> are treated as comments and ignored</li>
              <li>Blank lines are ignored — paste directly from your provider's export</li>
              <li>Mix formats in the same file — each line is parsed independently</li>
              <li>After uploading, click "Test All" to validate the batch</li>
              <li>Failed proxies can be bulk-deleted with "Remove Failed"</li>
              <li>The scraper automatically uses the healthiest, least-recently-used proxy for each scan</li>
            </ul>
          </div>

          <div style={{ background:"#0a1a0a", border:"1px solid #22c55e20", borderRadius:8, padding:"14px 16px", marginTop:12 }}>
            <div style={{ color:"#22c55e", fontSize:12, fontWeight:600, marginBottom:8 }}>Recommended Providers</div>
            {[
              ["Bright Data",  "https://brightdata.com",  "$15/GB", "Best quality, Social Media pool, best FB success rate"],
              ["Oxylabs",      "https://oxylabs.io",       "$15/GB", "Similar quality to Bright Data, good API"],
              ["Smartproxy",   "https://smartproxy.com",   "$80/8GB","Best value starter plan"],
              ["IPRoyal",      "https://iproyal.com",      "$7/GB",  "Budget option, lower reliability"],
            ].map(([name,url,price,note])=>(
              <div key={name} style={{ display:"flex", gap:12, padding:"7px 0", borderBottom:"1px solid #0f2a0f" }}>
                <span style={{ color:"#22c55e", fontSize:12, width:100, flexShrink:0 }}>{name}</span>
                <span style={{ color:"#6366f1", fontSize:11, width:60, flexShrink:0 }}>{price}</span>
                <span style={{ color:"#4a4a6a", fontSize:11 }}>{note}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// FB SESSION STATUS PANEL — embedded in Admin → Users → User Detail
// Shows per-user Facebook session health at a glance
// ════════════════════════════════════════════════════════════════════════════
function FBSessionStatus({ api, toast, userId, userEmail }) {
  const [status,   setStatus]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [clearing, setClearing] = useState(false);

  const STATUS_CFG = {
    connected:    { color:"#22c55e", icon:"🟢", label:"Connected",      desc:"Session active — scans running" },
    disconnected: { color:"#4a4a6a", icon:"⚫", label:"Disconnected",   desc:"No Facebook session — user needs to reconnect" },
    checkpoint:   { color:"#f59e0b", icon:"🟡", label:"Checkpoint",     desc:"Facebook is asking for identity verification" },
    blocked:      { color:"#ff4d4d", icon:"🔴", label:"Blocked",        desc:"Temporarily blocked by Facebook — will recover in hours" },
    login:        { color:"#f59e0b", icon:"🟡", label:"Session Expired","desc":"Cookies expired — user needs to log in again" },
    login_failed: { color:"#ff4d4d", icon:"🔴", label:"Login Failed",   desc:"Wrong credentials — check email/password" },
    error:        { color:"#ff4d4d", icon:"🔴", label:"Error",          desc:"Unexpected error during scan" },
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${userId}`);
      setStatus({
        status:      r.fb_session_status || "disconnected",
        updated_at:  r.fb_session_updated_at,
        session_ip:  r.fb_session_ip,
        has_session: !!r.fb_session_data,
      });
    } catch(e) {
      setStatus({ status:"disconnected", has_session:false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if(userId) load(); }, [userId]);

  const clearSession = async () => {
    if (!window.confirm(`Clear Facebook session for ${userEmail}? They will need to reconnect.`)) return;
    setClearing(true);
    try {
      await api.patch(`/admin/users/${userId}`, {
        fb_session_status: "disconnected",
        fb_session_data:   null,
      });
      toast.success("Session cleared — user will need to reconnect Facebook");
      load();
    } catch(e) { toast.error(e.message); }
    finally { setClearing(false); }
  };

  if (loading) return <Spinner size={14}/>;

  const cfg = STATUS_CFG[status?.status] || STATUS_CFG.disconnected;
  const needsAction = ["disconnected","checkpoint","login","login_failed"].includes(status?.status);

  return (
    <div style={{ background: needsAction ? "#1a1208" : "#0a1a0a", border:`1px solid ${cfg.color}30`, borderRadius:9, padding:"12px 14px", marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:18 }}>{cfg.icon}</span>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ color:cfg.color, fontSize:12, fontWeight:600 }}>Facebook: {cfg.label}</span>
            {status?.updated_at && (
              <span style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                · updated {new Date(status.updated_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>{cfg.desc}</div>
          {status?.session_ip && (
            <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", marginTop:2 }}>
              Last proxy: {status.session_ip}
            </div>
          )}
        </div>
        {status?.has_session && (
          <button onClick={clearSession} disabled={clearing} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #ff4d4d30", background:"transparent", color:"#ff4d4d", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:clearing?"not-allowed":"pointer" }}>
            {clearing?<Spinner size={10}/>:"Clear"}
          </button>
        )}
      </div>
      {needsAction && status?.status !== "disconnected" && (
        <div style={{ marginTop:8, padding:"7px 10px", background:"#f59e0b15", borderRadius:6, border:"1px solid #f59e0b20", fontSize:11, color:"#d4a76a", lineHeight:1.7 }}>
          ⚠️ Action needed: Ask this user to go to <strong>Settings → Facebook Connection</strong> and reconnect their account.
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// PROTECTION DASHBOARD — Admin panel tab
// Covers P6 (rate limits), P7 (block log), P8 (scan windows)
// ════════════════════════════════════════════════════════════════════════════
function ProtectionDashboard({ api, toast }) {
  const [tab, setTab] = useState("blocks");

  const TABS = [
    { id:"blocks",  icon:"🚨", label:"Block Log"     },
    { id:"limits",  icon:"⏱️", label:"Rate Limits"   },
    { id:"windows", icon:"🕐", label:"Scan Windows"  },
  ];

  return (
    <div>
      <div style={{ display:"flex", gap:6, marginBottom:20 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"8px 18px", borderRadius:8, border:`1px solid ${tab===t.id?"#6366f1":"#1e1e2e"}`, background:tab===t.id?"#6366f120":"transparent", color:tab===t.id?"#a5b4fc":"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      {tab==="blocks"  && <BlockLog        api={api} toast={toast}/>}
      {tab==="limits"  && <RateLimitConfig api={api} toast={toast}/>}
      {tab==="windows" && <ScanWindows     api={api} toast={toast}/>}
    </div>
  );
}


// ── P7: Block Log ─────────────────────────────────────────────────────────────
function BlockLog({ api, toast }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [resolving, setResolving] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/blocks?unresolved_only=${unresolvedOnly}&limit=100`)
      .then(setData).catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  };
  useEffect(load, [unresolvedOnly]);

  const resolve = async (id) => {
    setResolving(id);
    try {
      await api.post(`/admin/blocks/${id}/resolve`);
      toast.success("Block resolved");
      load();
    } catch(e) { toast.error(e.message); }
    finally { setResolving(null); }
  };

  const BLOCK_CFG = {
    captcha:     { color:"#f59e0b", icon:"🤖", label:"CAPTCHA",     desc:"CAPTCHA served — proxy may be flagged" },
    checkpoint:  { color:"#ff4d4d", icon:"🔒", label:"Checkpoint",  desc:"Identity verification required — user must act" },
    blocked:     { color:"#ff4d4d", icon:"🚫", label:"Blocked",     desc:"Temporarily blocked — auto-recovers" },
    login:       { color:"#f59e0b", icon:"🔑", label:"Expired",     desc:"Session expired — user must reconnect" },
    login_failed:{ color:"#ff4d4d", icon:"❌", label:"Login Failed", desc:"Wrong credentials" },
    error:       { color:"#6b7280", icon:"⚠️", label:"Error",       desc:"Unexpected scan error" },
    timeout:     { color:"#6b7280", icon:"⏰", label:"Timeout",     desc:"Page load timed out" },
    no_session:  { color:"#4a4a6a", icon:"🔌", label:"No Session",  desc:"User hasn't connected Facebook" },
  };

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };

  const summary = data?.summary;

  return (
    <div>
      {/* Summary cards */}
      {summary && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
          {[
            { label:"ACTIVE BLOCKS",   value:summary.total_active,            color:"#ff4d4d" },
            { label:"LAST 24H",        value:summary.last_24h,                color:"#f59e0b" },
            { label:"HIGH RISK USERS", value:summary.high_risk_users?.length||0, color:"#ff4d4d" },
            { label:"CHECKPOINTS",     value:summary.active_blocks?.checkpoint||0, color:"#f59e0b" },
          ].map(k=>(
            <div key={k.label} style={{ ...cardS, marginBottom:0, borderColor:`${k.color}20` }}>
              <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
              <div style={{ color:k.color, fontSize:28, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1 }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* High risk users */}
      {summary?.high_risk_users?.length > 0 && (
        <div style={{ ...cardS, borderColor:"#ff4d4d20", background:"#1a0808", marginBottom:20 }}>
          <div style={{ color:"#ff4d4d", fontSize:13, fontWeight:600, marginBottom:10 }}>
            ⚠️ High-Risk Accounts (3+ blocks in 7 days)
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {summary.high_risk_users.map(u=>(
              <div key={u.user_id} style={{ padding:"6px 14px", borderRadius:6, background:"#ff4d4d15", border:"1px solid #ff4d4d30", fontSize:12 }}>
                <span style={{ color:"#ff8888" }}>User #{u.user_id}</span>
                <span style={{ color:"#ff4d4d", marginLeft:8, fontFamily:"'DM Mono',monospace" }}>{u.block_count} blocks</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
        <div onClick={()=>setUnresolvedOnly(p=>!p)} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
          <div style={{ width:36,height:20,borderRadius:10,background:unresolvedOnly?"#6366f1":"#2a2a3e",position:"relative",transition:"all 0.2s" }}>
            <div style={{ position:"absolute",top:2,left:unresolvedOnly?16:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s" }}/>
          </div>
          <span style={{ color:"#ccc", fontSize:12 }}>Unresolved only</span>
        </div>
        <button onClick={load} style={{ marginLeft:"auto", padding:"6px 14px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>↺ Refresh</button>
      </div>

      {/* Block list */}
      {loading ? <div style={{ display:"flex",justifyContent:"center",padding:40 }}><Spinner size={28}/></div>
      : !data?.blocks?.length ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
          No {unresolvedOnly?"active":""}  blocks — all sessions healthy
        </div>
      ) : data.blocks.map(b => {
        const bc = BLOCK_CFG[b.block_type]||BLOCK_CFG.error;
        const inCooldown = b.in_cooldown;
        return (
          <div key={b.id} style={{ background:"#0f0f1a", border:`1px solid ${b.resolved?"#1e1e2e":bc.color+"30"}`, borderLeft:`3px solid ${b.resolved?"#2a2a3e":bc.color}`, borderRadius:9, padding:"14px 16px", marginBottom:8, opacity:b.resolved?0.45:1 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <span style={{ fontSize:20, flexShrink:0 }}>{bc.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5, flexWrap:"wrap" }}>
                  <span style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{b.user_email}</span>
                  <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${bc.color}20`, color:bc.color }}>{bc.label}</span>
                  <span style={{ color:"#4a4a6a", fontSize:10, textTransform:"capitalize" }}>{b.platform}</span>
                  {inCooldown && (
                    <span style={{ padding:"2px 8px", borderRadius:4, fontSize:9, background:"#f59e0b20", color:"#f59e0b", fontFamily:"'DM Mono',monospace" }}>
                      Cooling down until {new Date(b.cooldown_until).toLocaleTimeString()}
                    </span>
                  )}
                  {b.resolved && <span style={{ color:"#22c55e", fontSize:10, fontFamily:"'DM Mono',monospace" }}>RESOLVED</span>}
                </div>
                <div style={{ color:"#4a4a6a", fontSize:11, marginBottom:4 }}>{bc.desc}</div>
                {b.page_url && <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:400 }}>{b.page_url}</div>}
                <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", marginTop:4 }}>
                  {new Date(b.created_at).toLocaleString()}
                  {b.admin_alerted && <span style={{ color:"#6366f1", marginLeft:8 }}>📧 Admin alerted</span>}
                </div>
              </div>
              {!b.resolved && (
                <button onClick={()=>resolve(b.id)} disabled={resolving===b.id} style={{ padding:"6px 14px", borderRadius:5, border:"1px solid #22c55e30", background:"#22c55e10", color:"#22c55e", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:resolving===b.id?"not-allowed":"pointer", flexShrink:0, display:"flex", alignItems:"center", gap:6 }}>
                  {resolving===b.id?<Spinner size={10}/>:"✓ Resolve"}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ── P6: Rate Limit Config ─────────────────────────────────────────────────────
function RateLimitConfig({ api, toast }) {
  const [config,  setConfig]  = useState(null);
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours,   setHours]   = useState(24);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/rate-limits/config"),
      api.get(`/admin/scan-stats?hours=${hours}`),
    ]).then(([c,s])=>{ setConfig(c); setStats(s); })
      .catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  }, [hours]);

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;

  return (
    <div>
      {/* Current config */}
      {config && (
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>Current Rate Limit Settings</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
            {[
              { label:"MAX SCANS / HOUR",  value:config.max_fb_scans_per_hour,    color:"#6366f1", env:"MAX_FB_SCANS_PER_HOUR" },
              { label:"MAX SCANS / DAY",   value:config.max_fb_scans_per_day,     color:"#8b5cf6", env:"MAX_FB_SCANS_PER_DAY" },
              { label:"PAGES / SESSION",   value:config.max_pages_per_session,    color:"#22c55e", env:"MAX_PAGES_PER_SESSION" },
              { label:"MIN SESSION GAP",   value:`${config.min_between_sessions_min}m`, color:"#f59e0b", env:"MIN_BETWEEN_SESSIONS_MIN" },
            ].map(k=>(
              <div key={k.label} style={{ background:"#0a0a0f", borderRadius:9, padding:"14px 16px" }}>
                <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>{k.label}</div>
                <div style={{ color:k.color, fontSize:26, fontWeight:700, fontFamily:"'DM Mono',monospace", lineHeight:1, marginBottom:6 }}>{k.value}</div>
                <code style={{ color:"#3a3a5a", fontSize:9, fontFamily:"'DM Mono',monospace" }}>{k.env}</code>
              </div>
            ))}
          </div>
          <div style={{ background:"#0a0a1a", border:"1px solid #6366f120", borderRadius:8, padding:"12px 14px", fontSize:11, color:"#4a4a6a", lineHeight:1.9 }}>
            To change these limits, edit your <code style={{ color:"#a5b4fc" }}>.env</code> file and restart the API container:<br/>
            <code style={{ color:"#22c55e", fontSize:11, display:"block", marginTop:6 }}>
              MAX_FB_SCANS_PER_HOUR=10<br/>
              MAX_FB_SCANS_PER_DAY=60<br/>
              MAX_PAGES_PER_SESSION=8<br/>
              MIN_BETWEEN_SESSIONS_MIN=25
            </code>
            <div style={{ marginTop:8 }}>These limits protect each customer's Facebook account from detection. Increasing them significantly raises ban risk.</div>
          </div>
        </div>
      )}

      {/* Scan stats */}
      {stats && (
        <div style={{ ...cardS }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>Scan Statistics</div>
            <div style={{ flex:1 }}/>
            {[24,48,168].map(h=>(
              <button key={h} onClick={()=>setHours(h)} style={{ padding:"5px 12px", borderRadius:5, border:`1px solid ${hours===h?"#6366f1":"#1e1e2e"}`, background:hours===h?"#6366f120":"transparent", color:hours===h?"#a5b4fc":"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                {h===168?"7d":`${h}h`}
              </button>
            ))}
          </div>
          {!stats.by_platform?.length ? (
            <div style={{ color:"#3a3a5a", fontSize:12 }}>No scan data yet — scans will appear here after the first run</div>
          ) : (
            <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
              {stats.by_platform.map(p => (
                <div key={p.platform} style={{ flex:"1 1 200px", background:"#0a0a0f", borderRadius:9, padding:"14px 16px" }}>
                  <div style={{ color:"#fff", fontSize:13, fontWeight:600, textTransform:"capitalize", marginBottom:12 }}>
                    {{facebook:"🔵",reddit:"🟠",}[p.platform]||"📡"} {p.platform}
                  </div>
                  {[
                    ["Total scans",   p.total,              "#ccc"],
                    ["Successful",    p.ok,                 "#22c55e"],
                    ["Errors",        p.errors,             p.errors>0?"#ff4d4d":"#4a4a6a"],
                    ["Error rate",    `${p.error_rate}%`,   p.error_rate>15?"#ff4d4d":p.error_rate>5?"#f59e0b":"#22c55e"],
                    ["Avg posts",     p.avg_posts,          "#ccc"],
                    ["Avg duration",  `${(p.avg_duration_ms/1000).toFixed(1)}s`, "#4a4a6a"],
                    ["Unique users",  p.unique_users,       "#6366f1"],
                  ].map(([k,v,c])=>(
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #111", fontSize:11 }}>
                      <span style={{ color:"#4a4a6a" }}>{k}</span>
                      <span style={{ color:c, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── P8: Scan Windows ──────────────────────────────────────────────────────────
function ScanWindows({ api, toast }) {
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    api.get("/admin/rate-limits/config")
      .then(setConfig).catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  },[]);

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };

  // Calculate current hour in Detroit time for the clock visual
  const [nowHour, setNowHour] = useState(new Date().getHours());
  useEffect(()=>{
    const t = setInterval(()=>setNowHour(new Date().getHours()),60000);
    return ()=>clearInterval(t);
  },[]);

  const PLATFORM_CFG = {
    facebook:   { icon:"🔵", color:"#1877F2", name:"Facebook" },
    reddit:     { icon:"🟠", color:"#ff4500", name:"Reddit"   },
    nextdoor: { icon:"🟣", color:"#7c3aed", name:"Nextdoor"},
  };

  const windows = config?.scan_windows || {
    facebook:[7,21], reddit:[0,24], nextdoor:[6,23]
  };

  if (loading) return <div style={{ display:"flex",justifyContent:"center",padding:60 }}><Spinner size={32}/></div>;

  return (
    <div>
      <div style={{ ...cardS }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:6 }}>Scan Windows</div>
        <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:20, lineHeight:1.7 }}>
          Facebook scans only run during business hours — leads are posted during waking hours, and scanning at 3am from a server looks very robotic. Reddit and Nextdoor are API-based and run 24/7 safely.
        </div>

        {Object.entries(windows).map(([platform, window_val]) => {
          const [start, end] = Array.isArray(window_val) ? window_val : [window_val[0], window_val[1]];
          const pc     = PLATFORM_CFG[platform]||{icon:"📡",color:"#6366f1",name:platform};
          const is24   = start===0 && end===24;
          const isOpen = is24 || (nowHour>=start && nowHour<end);

          return (
            <div key={platform} style={{ background:"#0a0a0f", borderRadius:10, padding:"18px 20px", marginBottom:12, borderLeft:`3px solid ${isOpen?pc.color:"#3a3a5a"}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                <span style={{ fontSize:22 }}>{pc.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{pc.name}</div>
                  <div style={{ color:isOpen?"#22c55e":"#4a4a6a", fontSize:11 }}>
                    {is24 ? "Runs 24/7" : isOpen ? `Currently scanning (${start}:00–${end}:00)` : `Paused until ${start}:00`}
                  </div>
                </div>
                <div style={{ width:10,height:10,borderRadius:"50%",background:isOpen?"#22c55e":"#3a3a5a",boxShadow:isOpen?"0 0 8px #22c55e":"none" }}/>
              </div>

              {/* 24-hour timeline visualization */}
              <div style={{ position:"relative", height:32 }}>
                {/* Background track */}
                <div style={{ position:"absolute", top:10, left:0, right:0, height:12, background:"#1e1e2e", borderRadius:6 }}/>
                {/* Active window */}
                {!is24 && (
                  <div style={{ position:"absolute", top:10, left:`${(start/24)*100}%`, width:`${((end-start)/24)*100}%`, height:12, background:pc.color, borderRadius:6, opacity:0.8 }}/>
                )}
                {is24 && (
                  <div style={{ position:"absolute", top:10, left:0, right:0, height:12, background:pc.color, borderRadius:6, opacity:0.8 }}/>
                )}
                {/* Current time marker */}
                <div style={{ position:"absolute", top:5, left:`${(nowHour/24)*100}%`, width:3, height:22, background:"#fff", borderRadius:2, transform:"translateX(-50%)", boxShadow:"0 0 6px rgba(255,255,255,0.5)" }}/>
                {/* Hour labels */}
                {[0,6,12,18,24].map(h=>(
                  <div key={h} style={{ position:"absolute", top:26, left:`${(h/24)*100}%`, transform:"translateX(-50%)", color:"#3a3a5a", fontSize:9, fontFamily:"'DM Mono',monospace" }}>
                    {h===24?"":h===0?"12a":h===12?"12p":h<12?`${h}a`:`${h-12}p`}
                  </div>
                ))}
              </div>
              <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", marginTop:14 }}>
                {is24 ? "Window: All hours" : `Window: ${start}:00 – ${end}:00 local time · ${end-start} hours/day · saves ~${Math.round((24-(end-start))/24*100)}% of potential scans`}
              </div>
            </div>
          );
        })}

        <div style={{ background:"#0a0a1a", border:"1px solid #6366f120", borderRadius:8, padding:"12px 14px", fontSize:11, color:"#4a4a6a", lineHeight:1.9, marginTop:8 }}>
          Scan windows are configured in <code style={{ color:"#a5b4fc" }}>crawler/rate_limiter.py</code> → <code style={{ color:"#22c55e" }}>SCAN_WINDOWS</code>. The default timezone is <code style={{ color:"#a5b4fc" }}>America/Detroit</code> — set <code style={{ color:"#22c55e" }}>DEFAULT_TIMEZONE</code> in your <code style={{ color:"#a5b4fc" }}>.env</code> to change it for all users, or store a timezone per user in the database for per-customer windows.
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// PLAN MANAGER — Full package/plan management admin panel
// Create, edit, delete plans with Stripe sync and live enforcement
// ════════════════════════════════════════════════════════════════════════════
function PlanManager({ api, toast }) {
  const [plans,    setPlans]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);   // plan object being edited
  const [showForm, setShowForm] = useState(false);
  const [syncing,  setSyncing]  = useState(null);
  const [saving,   setSaving]   = useState(false);

  const BLANK_PLAN = {
    id:"", name:"", slug:"", price_monthly:0, price_yearly:null,
    color:"#6366f1", badge:"", highlight:false, active:true,
    description:"", stripe_price_id_monthly:"", stripe_price_id_yearly:"",
    stripe_product_id:"",
    features:[],
    limits:{
      pages:15, keywords_per_page:25, scan_interval_min:30, matches_per_day:200,
      alert_email:true, alert_sms:false, alert_voice:false, alert_whatsapp:false,
      platforms:["facebook","reddit"],
      pipeline:false, analytics:false, api_access:false, white_label:false,
      support:"email",
    },
  };
  const [form, setForm] = useState(BLANK_PLAN);

  const load = () => {
    setLoading(true);
    api.get("/admin/plans").then(setPlans).catch(e=>toast.error(e.message))
      .finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const startEdit = (plan) => {
    setEditing(plan.id);
    setForm({...BLANK_PLAN, ...plan, limits:{...BLANK_PLAN.limits,...plan.limits}});
    setShowForm(true);
    window.scrollTo(0,0);
  };

  const startCreate = () => {
    setEditing(null);
    setForm(BLANK_PLAN);
    setShowForm(true);
    window.scrollTo(0,0);
  };

  const save = async () => {
    if (!form.id || !form.name) { toast.error("Plan ID and name are required"); return; }
    if (!/^[a-z0-9_-]+$/.test(form.id)) { toast.error("Plan ID must be lowercase letters, numbers, hyphens only"); return; }
    setSaving(true);
    try {
      const payload = { ...form, features: typeof form.features==="string" ? form.features.split("\n").filter(Boolean) : form.features };
      let r;
      if (editing) {
        r = await api.put(`/admin/plans/${editing}`, payload);
        setPlans(p => p.map(pl => pl.id===editing ? r : pl));
        toast.success(`Plan "${r.name}" updated`);
      } else {
        r = await api.post("/admin/plans", payload);
        setPlans(p => [...p, r]);
        toast.success(`Plan "${r.name}" created`);
      }
      setShowForm(false); setEditing(null); setForm(BLANK_PLAN);
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try {
      const r = await api.patch(`/admin/plans/${id}/toggle`);
      setPlans(p => p.map(pl => pl.id===id ? {...pl, active:r.active} : pl));
    } catch(e) { toast.error(e.message); }
  };

  const reorder = async (id, dir) => {
    try {
      await api.patch(`/admin/plans/${id}/reorder?direction=${dir}`);
      load();
    } catch(e) { toast.error(e.message); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete the "${name}" plan? Cannot be undone.`)) return;
    try {
      await api.del(`/admin/plans/${id}`);
      setPlans(p => p.filter(pl => pl.id!==id));
      toast.success("Plan deleted");
    } catch(e) { toast.error(e.message); }
  };

  const stripeSync = async (id) => {
    setSyncing(id);
    try {
      const r = await api.post(`/admin/plans/${id}/stripe-sync`);
      toast.success(r.message);
      load();
    } catch(e) { toast.error(e.message); }
    finally { setSyncing(null); }
  };

  const setLimit = (k, v) => setForm(f => ({...f, limits:{...f.limits,[k]:v}}));
  const setLimitToggle = (k) => setLimit(k, !form.limits[k]);
  const togglePlatform = (plat) => {
    const cur = form.limits.platforms || [];
    setLimit("platforms", cur.includes(plat) ? cur.filter(p=>p!==plat) : [...cur, plat]);
  };

  const PLAN_COLORS = {free:"#4a4a6a",starter:"#22c55e",pro:"#6366f1"};
  const iS = {width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS = {color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};
  const cardS = {background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"20px 24px",marginBottom:16};
  const Toggle = ({label, val, onChange}) => (
    <div onClick={onChange} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"6px 0"}}>
      <div style={{width:36,height:20,borderRadius:10,background:val?"#6366f1":"#2a2a3e",position:"relative",flexShrink:0,transition:"all 0.2s"}}>
        <div style={{position:"absolute",top:2,left:val?16:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s"}}/>
      </div>
      <span style={{color:"#ccc",fontSize:12}}>{label}</span>
    </div>
  );

  return (
    <div>
      {/* Plan edit / create form */}
      {showForm && (
        <div style={{...cardS, borderColor:"#2a2a3e", marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
            <div style={{width:16,height:16,borderRadius:4,background:form.color||"#6366f1"}}/>
            <div style={{color:"#a5b4fc",fontSize:14,fontFamily:"'DM Mono',monospace",fontWeight:600}}>
              {editing ? `Editing: ${form.name}` : "New Plan"}
            </div>
            <div style={{flex:1}}/>
            <button onClick={()=>{setShowForm(false);setEditing(null);setForm(BLANK_PLAN);}} style={{background:"transparent",border:"none",color:"#4a4a6a",fontSize:20,cursor:"pointer"}}>×</button>
          </div>

          {/* Basic info */}
          <div style={{background:"#0a0a0f",borderRadius:10,padding:"16px",marginBottom:16}}>
            <div style={{color:"#fff",fontSize:12,fontWeight:600,marginBottom:14}}>Basic Info</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:10}}>
              <div><label style={lS}>PLAN ID (unique, no spaces)</label><input style={iS} placeholder="e.g. starter_plus" value={form.id} onChange={e=>setForm(f=>({...f,id:e.target.value.toLowerCase().replace(/\s+/g,"-")}))} disabled={!!editing}/></div>
              <div><label style={lS}>DISPLAY NAME</label><input style={iS} placeholder="e.g. Starter Plus" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div>
              <div><label style={lS}>MONTHLY PRICE ($)</label><input style={iS} type="number" min={0} value={form.price_monthly} onChange={e=>setForm(f=>({...f,price_monthly:parseFloat(e.target.value)||0}))}/></div>
              <div><label style={lS}>YEARLY PRICE ($ — blank = 10× monthly)</label><input style={iS} type="number" min={0} placeholder={`${form.price_monthly*10}`} value={form.price_yearly||""} onChange={e=>setForm(f=>({...f,price_yearly:e.target.value?parseFloat(e.target.value):null}))}/></div>
              <div><label style={lS}>BADGE TEXT</label><input style={iS} placeholder="MOST POPULAR" value={form.badge} onChange={e=>setForm(f=>({...f,badge:e.target.value}))}/></div>
              <div>
                <label style={lS}>COLOR</label>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input type="color" value={form.color||"#6366f1"} onChange={e=>setForm(f=>({...f,color:e.target.value}))} style={{width:38,height:36,borderRadius:6,border:"1px solid #1e1e2e",cursor:"pointer",padding:2,background:"#0a0a0f"}}/>
                  <input style={{...iS,flex:1}} value={form.color||""} onChange={e=>setForm(f=>({...f,color:e.target.value}))} placeholder="#6366f1"/>
                </div>
              </div>
              <div style={{gridColumn:"3/-1"}}><label style={lS}>SHORT DESCRIPTION</label><input style={iS} placeholder="Perfect for solo contractors" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
            </div>
            <div style={{display:"flex",gap:20}}>
              <Toggle label="Active (visible on pricing page)" val={form.active} onChange={()=>setForm(f=>({...f,active:!f.active}))}/>
              <Toggle label="Highlight (featured border)" val={form.highlight} onChange={()=>setForm(f=>({...f,highlight:!f.highlight}))}/>
            </div>
          </div>

          {/* Limits */}
          <div style={{background:"#0a0a0f",borderRadius:10,padding:"16px",marginBottom:16}}>
            <div style={{color:"#fff",fontSize:12,fontWeight:600,marginBottom:14}}>Enforced Limits</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:14}}>
              <div><label style={lS}>MAX PAGES</label><input style={iS} type="number" min={0} value={form.limits.pages} onChange={e=>setLimit("pages",parseInt(e.target.value)||0)}/></div>
              <div><label style={lS}>KEYWORDS / PAGE</label><input style={iS} type="number" min={1} value={form.limits.keywords_per_page} onChange={e=>setLimit("keywords_per_page",parseInt(e.target.value)||1)}/></div>
              <div><label style={lS}>SCAN INTERVAL (min)</label><input style={iS} type="number" min={5} value={form.limits.scan_interval_min} onChange={e=>setLimit("scan_interval_min",parseInt(e.target.value)||15)}/></div>
              <div><label style={lS}>MATCHES / DAY</label><input style={iS} type="number" min={1} value={form.limits.matches_per_day} onChange={e=>setLimit("matches_per_day",parseInt(e.target.value)||100)}/></div>
            </div>

            {/* Platforms */}
            <div style={{marginBottom:14}}>
              <label style={lS}>PLATFORMS INCLUDED</label>
              <div style={{display:"flex",gap:10}}>
                {["facebook","reddit"].map(p=>{
                  const icons = {facebook:"🔵",reddit:"🟠",};
                  const inc = (form.limits.platforms||[]).includes(p);
                  return (
                    <div key={p} onClick={()=>togglePlatform(p)} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:7,border:`1px solid ${inc?"#6366f1":"#2a2a3e"}`,background:inc?"#6366f120":"transparent",cursor:"pointer"}}>
                      <span>{icons[p]}</span>
                      <span style={{color:inc?"#a5b4fc":"#4a4a6a",fontSize:12,textTransform:"capitalize"}}>{p}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Feature toggles */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:4}}>
              <Toggle label="Email alerts"    val={form.limits.alert_email}    onChange={()=>setLimitToggle("alert_email")}/>
              <Toggle label="SMS alerts"      val={form.limits.alert_sms}      onChange={()=>setLimitToggle("alert_sms")}/>
              <Toggle label="WhatsApp alerts" val={form.limits.alert_whatsapp} onChange={()=>setLimitToggle("alert_whatsapp")}/>
              <Toggle label="Lead pipeline"   val={form.limits.pipeline}       onChange={()=>setLimitToggle("pipeline")}/>
              <Toggle label="Analytics"       val={form.limits.analytics}      onChange={()=>setLimitToggle("analytics")}/>
              <Toggle label="API access"      val={form.limits.api_access}     onChange={()=>setLimitToggle("api_access")}/>
              <Toggle label="White-label"     val={form.limits.white_label}    onChange={()=>setLimitToggle("white_label")}/>
            </div>
            <div style={{marginTop:10}}>
              <label style={lS}>SUPPORT LEVEL</label>
              <select style={{...iS,width:"auto"}} value={form.limits.support||"email"} onChange={e=>setLimit("support",e.target.value)}>
                <option value="community">Community only</option>
                <option value="email">Email support</option>
                <option value="priority">Priority support</option>
                <option value="dedicated">Dedicated account manager</option>
              </select>
            </div>
          </div>

          {/* Feature bullets */}
          <div style={{background:"#0a0a0f",borderRadius:10,padding:"16px",marginBottom:16}}>
            <div style={{color:"#fff",fontSize:12,fontWeight:600,marginBottom:8}}>Feature List (shown on pricing page)</div>
            <div style={{color:"#4a4a6a",fontSize:11,marginBottom:8}}>One feature per line — shown as bullet points to customers</div>
            <textarea style={{...iS,minHeight:100,resize:"vertical",lineHeight:1.8}} value={Array.isArray(form.features)?form.features.join("\n"):form.features} onChange={e=>setForm(f=>({...f,features:e.target.value.split("\n")}))} placeholder={"15 monitored pages\n25 keywords per page\nEmail alerts\n30-minute scans"}/>
          </div>

          {/* Stripe */}
          <div style={{background:"#0a0a0f",borderRadius:10,padding:"16px",marginBottom:16}}>
            <div style={{color:"#fff",fontSize:12,fontWeight:600,marginBottom:8}}>Stripe Configuration</div>
            <div style={{color:"#4a4a6a",fontSize:11,marginBottom:12,lineHeight:1.7}}>
              Paste Stripe price IDs here, or click "Sync to Stripe" to auto-create them.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
              <div><label style={lS}>STRIPE PRODUCT ID</label><input style={iS} placeholder="prod_..." value={form.stripe_product_id||""} onChange={e=>setForm(f=>({...f,stripe_product_id:e.target.value}))}/></div>
              <div><label style={lS}>MONTHLY PRICE ID</label><input style={iS} placeholder="price_..." value={form.stripe_price_id_monthly||""} onChange={e=>setForm(f=>({...f,stripe_price_id_monthly:e.target.value}))}/></div>
              <div><label style={lS}>YEARLY PRICE ID</label><input style={iS} placeholder="price_..." value={form.stripe_price_id_yearly||""} onChange={e=>setForm(f=>({...f,stripe_price_id_yearly:e.target.value}))}/></div>
            </div>
          </div>

          {/* Actions */}
          <div style={{display:"flex",gap:8}}>
            <button onClick={save} disabled={saving} style={{padding:"10px 24px",borderRadius:7,border:"none",background:saving?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:saving?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:8}}>
              {saving?<><Spinner size={12}/> Saving…</>:editing?"💾 Update Plan":"✨ Create Plan"}
            </button>
            <button onClick={()=>{setShowForm(false);setEditing(null);setForm(BLANK_PLAN);}} style={{padding:"10px 16px",borderRadius:7,border:"1px solid #2a2a3e",background:"transparent",color:"#4a4a6a",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
        <div>
          <div style={{color:"#fff",fontSize:14,fontWeight:600}}>Plan Manager</div>
          <div style={{color:"#4a4a6a",fontSize:12}}>{plans.length} plans · {plans.filter(p=>p.active).length} active</div>
        </div>
        <div style={{flex:1}}/>
        {!showForm && (
          <button onClick={startCreate} style={{padding:"9px 20px",borderRadius:7,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>+ New Plan</button>
        )}
      </div>

      {/* Plans list */}
      {loading ? <div style={{display:"flex",justifyContent:"center",padding:60}}><Spinner size={32}/></div>
      : plans.map((plan, idx) => {
        const c = plan.color || PLAN_COLORS[plan.id] || "#6366f1";
        const isBuiltin = ["free","starter","pro"].includes(plan.id);
        return (
          <div key={plan.id} style={{background:"#0f0f1a",border:`1px solid ${plan.active?"#1e1e2e":"#111118"}`,borderLeft:`4px solid ${plan.active?c:"#2a2a3e"}`,borderRadius:12,padding:"18px 20px",marginBottom:12,opacity:plan.active?1:0.55}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:14}}>
              {/* Color swatch + reorder */}
              <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0,alignItems:"center"}}>
                <div style={{width:40,height:40,borderRadius:10,background:c,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:16,fontWeight:700}}>
                  {plan.name[0]}
                </div>
                <button onClick={()=>reorder(plan.id,"up")} disabled={idx===0} style={{background:"transparent",border:"none",color:idx===0?"#2a2a3e":"#4a4a6a",cursor:idx===0?"not-allowed":"pointer",fontSize:14,padding:"2px"}}>↑</button>
                <button onClick={()=>reorder(plan.id,"down")} disabled={idx===plans.length-1} style={{background:"transparent",border:"none",color:idx===plans.length-1?"#2a2a3e":"#4a4a6a",cursor:idx===plans.length-1?"not-allowed":"pointer",fontSize:14,padding:"2px"}}>↓</button>
              </div>

              {/* Plan info */}
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                  <span style={{color:"#fff",fontSize:15,fontWeight:700}}>{plan.name}</span>
                  <code style={{color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace"}}>#{plan.id}</code>
                  {plan.badge && <span style={{padding:"2px 8px",borderRadius:4,fontSize:9,fontFamily:"'DM Mono',monospace",background:`${c}20`,color:c}}>{plan.badge}</span>}
                  {!plan.active && <span style={{padding:"2px 8px",borderRadius:4,fontSize:9,fontFamily:"'DM Mono',monospace",background:"#2a2a2a",color:"#4a4a6a"}}>INACTIVE</span>}
                  {isBuiltin && <span style={{padding:"2px 8px",borderRadius:4,fontSize:9,fontFamily:"'DM Mono',monospace",background:"#6366f115",color:"#6366f1"}}>BUILT-IN</span>}
                </div>

                {/* Price + key stats */}
                <div style={{display:"flex",gap:20,flexWrap:"wrap",marginBottom:8}}>
                  <div>
                    <span style={{color:c,fontSize:24,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>${plan.price_monthly}</span>
                    <span style={{color:"#4a4a6a",fontSize:12}}>/mo</span>
                    {plan.price_yearly > 0 && <span style={{color:"#4a4a6a",fontSize:11,marginLeft:8}}>(${plan.price_yearly}/yr)</span>}
                  </div>
                  <div style={{display:"flex",gap:16,color:"#4a4a6a",fontSize:11,alignItems:"center",flexWrap:"wrap"}}>
                    <span>📡 {plan.limits?.pages===999?"Unlimited":plan.limits?.pages} pages</span>
                    <span>⏱️ {plan.limits?.scan_interval_min}min scans</span>
                    {plan.limits?.alert_sms && <span>📱 SMS</span>}
                    {plan.limits?.api_access && <span>🔑 API</span>}
                    {plan.limits?.white_label && <span>🏷️ White-label</span>}
                  </div>
                </div>

                {/* Subscriber count */}
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{color:"#22c55e",fontSize:12,fontFamily:"'DM Mono',monospace"}}>
                    {plan.subscriber_count || 0} subscriber{plan.subscriber_count!==1?"s":""}
                  </div>
                  {/* Stripe status */}
                  {plan.price_monthly > 0 && (
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <div style={{width:6,height:6,borderRadius:"50%",background:plan.stripe_price_id_monthly?"#22c55e":"#f59e0b"}}/>
                      <span style={{color:plan.stripe_price_id_monthly?"#22c55e":"#f59e0b",fontSize:10,fontFamily:"'DM Mono',monospace"}}>
                        {plan.stripe_price_id_monthly ? "Stripe connected" : "No Stripe price"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                <button onClick={()=>startEdit(plan)} style={{padding:"6px 14px",borderRadius:6,border:"1px solid #2a2a3e",background:"transparent",color:"#a5b4fc",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>✏️ Edit</button>

                {plan.price_monthly > 0 && (
                  <button onClick={()=>stripeSync(plan.id)} disabled={syncing===plan.id} style={{padding:"6px 14px",borderRadius:6,border:"1px solid #22c55e30",background:"#22c55e10",color:"#22c55e",fontSize:11,fontFamily:"'DM Mono',monospace",cursor:syncing===plan.id?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:5}}>
                    {syncing===plan.id?<><Spinner size={10}/>Syncing…</>:"⚡ Sync to Stripe"}
                  </button>
                )}

                <div onClick={()=>toggle(plan.id)} style={{width:40,height:22,borderRadius:11,background:plan.active?"#22c55e":"#2a2a3e",position:"relative",cursor:"pointer",transition:"all 0.2s",flexShrink:0,marginTop:2}} title={plan.active?"Deactivate":"Activate"}>
                  <div style={{position:"absolute",top:3,left:plan.active?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s"}}/>
                </div>

                {!isBuiltin && (
                  <button onClick={()=>del(plan.id,plan.name)} style={{padding:"6px 10px",borderRadius:6,border:"1px solid #ff4d4d20",background:"transparent",color:"#ff4d4d",fontSize:11,cursor:"pointer"}}>✕</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function ChangePasswordForm({ api, toast }) {
  const [current, setCurrent] = useState("");
  const [next,    setNext]    = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(false);
  const submit = async () => {
    if (!current) { toast.error("Enter your current password"); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password:current, new_password:next });
      toast.success("Password changed"); setCurrent(""); setNext(""); setConfirm(""); setDone(true);
      setTimeout(()=>setDone(false),4000);
    } catch(e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"9px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS={color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};
  return (
    <div style={{background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"20px 24px",marginBottom:16}}>
      <div style={{color:"#fff",fontSize:13,fontWeight:600,marginBottom:16}}>🔐 Change Password</div>
      {done&&<div style={{background:"#0a1a0a",border:"1px solid #22c55e30",borderRadius:8,padding:"10px 14px",marginBottom:12,color:"#22c55e",fontSize:12}}>✓ Password changed</div>}
      <div style={{display:"flex",flexDirection:"column",gap:10,maxWidth:360}}>
        <div><label style={lS}>CURRENT PASSWORD</label><input type="password" style={iS} value={current} onChange={e=>setCurrent(e.target.value)} placeholder="Your current password"/></div>
        <div><label style={lS}>NEW PASSWORD (min 8 chars)</label><input type="password" style={iS} value={next} onChange={e=>setNext(e.target.value)} placeholder="New password"/></div>
        <div><label style={lS}>CONFIRM NEW PASSWORD</label><input type="password" style={iS} value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Repeat new password" onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
        {next&&confirm&&next!==confirm&&<div style={{color:"#ff4d4d",fontSize:11}}>⚠ Passwords don't match</div>}
        <button onClick={submit} disabled={saving||!current||!next||!confirm} style={{padding:"9px 20px",borderRadius:6,border:"none",background:(saving||!current||!next||!confirm)?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:(saving||!current||!next||!confirm)?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6,alignSelf:"flex-start"}}>
          {saving?<><Spinner size={11}/>Saving…</>:"Update Password"}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// KNOWLEDGE BASE + FAQ + HOW-TO-RUN
// Three surfaces:
//   1. HelpCenter      — in-app help tab (sidebar → Help)
//   2. KBAdminEditor   — admin editor for articles
//   3. LandingKB       — public-facing FAQ + KB section on landing/auth page
//   4. SetupGuide      — step-by-step how-to-run section
// ════════════════════════════════════════════════════════════════════════════


// ── Markdown renderer (no external deps) ──────────────────────────────────────
function renderMarkdown(text) {
  if (!text) return "";
  let html = text
    // Code blocks
    .replace(/```[\w]*\n?([\s\S]*?)```/g, '<pre style="background:#0a0a0f;border:1px solid #1e1e2e;borderRadius:7px;padding:14px;overflowX:auto;fontSize:12px;lineHeight:1.7;fontFamily:\'DM Mono\',monospace;color:#22c55e;margin:12px 0">$1</pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code style="background:#1e1e2e;color:#a5b4fc;padding:1px 6px;borderRadius:3px;fontSize:11px;fontFamily:\'DM Mono\',monospace">$1</code>')
    // Tables
    .replace(/\|(.+)\|/g, (m) => {
      const cells = m.split('|').filter(Boolean).map(c => c.trim());
      return '<div style="display:flex;gap:0;borderBottom:1px solid #1e1e2e">' +
        cells.map(c => `<div style="flex:1;padding:8px 12px;color:#ccc;fontSize:12px">${c}</div>`).join('') +
        '</div>';
    })
    // Headers
    .replace(/^### (.+)$/gm, '<div style="color:#fff;fontSize:14px;fontWeight:700;margin:20px 0 8px">$1</div>')
    .replace(/^## (.+)$/gm,  '<div style="color:#fff;fontSize:16px;fontWeight:700;margin:24px 0 10px;paddingBottom:8px;borderBottom:1px solid #1e1e2e">$1</div>')
    .replace(/^# (.+)$/gm,   '<div style="color:#fff;fontSize:20px;fontWeight:700;margin:0 0 16px">$1</div>')
    // Blockquotes
    .replace(/^> (.+)$/gm, '<div style="borderLeft:3px solid #6366f1;paddingLeft:14px;color:#8888aa;fontSize:13px;margin:10px 0;fontStyle:italic">$1</div>')
    // Bold / italic
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#fff">$1</strong>')
    .replace(/\*(.+?)\*/g,     '<em style="color:#a5b4fc">$1</em>')
    // Lists
    .replace(/^- (.+)$/gm, '<div style="display:flex;gap:8px;margin:4px 0"><span style="color:#6366f1;flexShrink:0">•</span><span style="color:#8888aa;fontSize:13px;lineHeight:1.7">$1</span></div>')
    // Paragraphs
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  return html;
}

function ArticleContent({ content }) {
  return <div style={{ lineHeight:1.8 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}/>;
}


// ════════════════════════════════════════════════════════════════════════════
// 1. HELP CENTER — in-app (sidebar → Help)
// ════════════════════════════════════════════════════════════════════════════
function HelpCenter({ api, toast }) {
  const [categories, setCategories] = useState([]);
  const [articles,   setArticles]   = useState([]);
  const [selected,   setSelected]   = useState(null);  // article object
  const [article,    setArticle]    = useState(null);   // full article with content
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [searchRes,  setSearchRes]  = useState(null);   // null = no search
  const [searching,  setSearching]  = useState(false);
  const [feedback,   setFeedback]   = useState(null);   // "helpful" | "not_helpful"
  const [activeCat,  setActiveCat]  = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/kb/categories"),
      api.get("/kb/articles"),
    ]).then(([cats, arts]) => {
      setCategories(cats || []);
      setArticles(arts || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const doSearch = async (q) => {
    setSearch(q);
    if (!q || q.length < 2) { setSearchRes(null); return; }
    setSearching(true);
    try {
      const r = await api.get(`/kb/search?q=${encodeURIComponent(q)}`);
      setSearchRes(r || []);
    } catch(e) {}
    finally { setSearching(false); }
  };

  const openArticle = async (art) => {
    setSelected(art);
    setArticle(null);
    setFeedback(null);
    setSearchRes(null);
    setSearch("");
    try {
      const full = await api.get(`/kb/articles/${art.slug}`);
      setArticle(full);
    } catch(e) { toast.error("Could not load article"); }
  };

  const submitFeedback = async (helpful) => {
    if (!article || feedback) return;
    setFeedback(helpful ? "helpful" : "not_helpful");
    try { await api.post(`/kb/articles/${article.slug}/feedback?helpful=${helpful}`); }
    catch(e) {}
  };

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };
  const catColors = { "getting-started":"#22c55e","keywords":"#6366f1","alerts":"#f59e0b","troubleshooting":"#ff4d4d","billing":"#0ea5e9" };

  const listByCategory = activeCat
    ? articles.filter(a => a.category === activeCat)
    : articles;

  return (
    <div style={{ maxWidth:820 }}>
      {/* Header + search */}
      <div style={{ marginBottom:24 }}>
        <div style={{ color:"#fff", fontSize:22, fontWeight:700, marginBottom:6 }}>Help Center</div>
        <div style={{ color:"#4a4a6a", fontSize:13, marginBottom:16 }}>Find answers, tutorials, and guides</div>
        <div style={{ position:"relative" }}>
          <input
            value={search}
            onChange={e => doSearch(e.target.value)}
            placeholder="Search for anything — keywords, alerts, Facebook, billing..."
            style={{ width:"100%", background:"#0a0a0f", border:"1px solid #2a2a3e", borderRadius:10, padding:"12px 44px 12px 16px", color:"#ccc", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
          />
          <span style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", color:"#4a4a6a", fontSize:18 }}>
            {searching ? "⏳" : "🔍"}
          </span>
        </div>
      </div>

      {/* Article view */}
      {selected && article && (
        <div>
          <button onClick={() => { setSelected(null); setArticle(null); }} style={{ display:"flex", alignItems:"center", gap:6, background:"transparent", border:"none", color:"#6366f1", fontSize:12, cursor:"pointer", marginBottom:16, fontFamily:"'DM Mono',monospace" }}>
            ← Back to Help Center
          </button>
          <div style={{ ...cardS }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
              <span style={{ padding:"2px 9px", borderRadius:5, fontSize:10, fontFamily:"'DM Mono',monospace", background:`${catColors[article.category]||"#6366f1"}20`, color:catColors[article.category]||"#6366f1" }}>{article.category_label}</span>
              {article.pinned && <span style={{ color:"#f59e0b", fontSize:11 }}>📌 Pinned</span>}
            </div>
            <div style={{ color:"#fff", fontSize:20, fontWeight:700, marginBottom:20 }}>{article.title}</div>
            <div style={{ color:"#8888aa", fontSize:13 }}>
              <ArticleContent content={article.content}/>
            </div>
            {/* Feedback */}
            <div style={{ marginTop:28, paddingTop:18, borderTop:"1px solid #1e1e2e", display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ color:"#4a4a6a", fontSize:12 }}>Was this helpful?</span>
              <button onClick={() => submitFeedback(true)} disabled={!!feedback} style={{ padding:"5px 14px", borderRadius:6, border:`1px solid ${feedback==="helpful"?"#22c55e":"#2a2a3e"}`, background:feedback==="helpful"?"#22c55e20":"transparent", color:feedback==="helpful"?"#22c55e":"#4a4a6a", fontSize:12, cursor:feedback?"default":"pointer" }}>
                👍 Yes {article.helpful > 0 ? `(${article.helpful})` : ""}
              </button>
              <button onClick={() => submitFeedback(false)} disabled={!!feedback} style={{ padding:"5px 14px", borderRadius:6, border:`1px solid ${feedback==="not_helpful"?"#ff4d4d":"#2a2a3e"}`, background:feedback==="not_helpful"?"#ff4d4d20":"transparent", color:feedback==="not_helpful"?"#ff4d4d":"#4a4a6a", fontSize:12, cursor:feedback?"default":"pointer" }}>
                👎 No {article.not_helpful > 0 ? `(${article.not_helpful})` : ""}
              </button>
              {feedback && <span style={{ color:"#22c55e", fontSize:12 }}>Thanks for the feedback</span>}
            </div>
          </div>
        </div>
      )}

      {/* Search results */}
      {!selected && searchRes !== null && (
        <div style={{ ...cardS }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>
            {searchRes.length === 0 ? "No results found" : `${searchRes.length} result${searchRes.length!==1?"s":""} for "${search}"`}
          </div>
          {searchRes.length === 0 && (
            <div style={{ color:"#4a4a6a", fontSize:12 }}>Try different keywords or browse the categories below.</div>
          )}
          {searchRes.map(a => (
            <div key={a.id} onClick={() => openArticle(a)} style={{ padding:"12px 0", borderBottom:"1px solid #111", cursor:"pointer" }}>
              <div style={{ color:"#a5b4fc", fontSize:12, marginBottom:4 }}>{a.title}</div>
              <div style={{ display:"flex", gap:8 }}>
                <span style={{ color:`${catColors[a.category]||"#6366f1"}`, fontSize:10, fontFamily:"'DM Mono',monospace" }}>{a.category_label}</span>
                {a.excerpt && <span style={{ color:"#3a3a5a", fontSize:11, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.excerpt.replace(/[#*>`]/g,"").substring(0,80)}…</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category browser */}
      {!selected && searchRes === null && (
        <>
          {/* Category tabs */}
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20 }}>
            <button onClick={() => setActiveCat(null)} style={{ padding:"6px 14px", borderRadius:6, border:`1px solid ${!activeCat?"#6366f1":"#1e1e2e"}`, background:!activeCat?"#6366f120":"transparent", color:!activeCat?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              All Articles
            </button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setActiveCat(activeCat===cat.id?null:cat.id)} style={{ padding:"6px 14px", borderRadius:6, border:`1px solid ${activeCat===cat.id?catColors[cat.id]||"#6366f1":"#1e1e2e"}`, background:activeCat===cat.id?`${catColors[cat.id]||"#6366f1"}20`:"transparent", color:activeCat===cat.id?catColors[cat.id]||"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
                {cat.icon} {cat.label} <span style={{ color:"#3a3a5a" }}>({cat.article_count})</span>
              </button>
            ))}
          </div>

          {/* Pinned articles first */}
          {!activeCat && articles.filter(a=>a.pinned).length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ color:"#f59e0b", fontSize:11, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:10 }}>📌 PINNED ARTICLES</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {articles.filter(a=>a.pinned).map(a => (
                  <div key={a.id} onClick={() => openArticle(a)} style={{ background:"#0a0a1a", border:"1px solid #6366f130", borderRadius:10, padding:"14px 16px", cursor:"pointer" }}>
                    <div style={{ color:"#a5b4fc", fontSize:13, fontWeight:600, marginBottom:6 }}>{a.title}</div>
                    <div style={{ color:`${catColors[a.category]||"#6366f1"}`, fontSize:10, fontFamily:"'DM Mono',monospace" }}>{a.category_label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Articles by category */}
          {loading ? <div style={{ display:"flex", justifyContent:"center", padding:40 }}><Spinner size={24}/></div>
          : (activeCat ? [categories.find(c=>c.id===activeCat)].filter(Boolean) : categories).map(cat => {
            const catArts = listByCategory.filter(a => a.category === cat.id);
            if (catArts.length === 0) return null;
            return (
              <div key={cat.id} style={{ ...cardS, marginBottom:14 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                  <span style={{ fontSize:18 }}>{cat.icon}</span>
                  <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{cat.label}</div>
                  <div style={{ color:"#3a3a5a", fontSize:11 }}>({catArts.length} article{catArts.length!==1?"s":""})</div>
                </div>
                {catArts.map(a => (
                  <div key={a.id} onClick={() => openArticle(a)} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:"1px solid #0a0a12", cursor:"pointer" }}>
                    <span style={{ color:catColors[a.category]||"#6366f1", fontSize:14 }}>→</span>
                    <div style={{ flex:1 }}>
                      <div style={{ color:"#ccc", fontSize:13 }}>{a.title}</div>
                    </div>
                    <div style={{ display:"flex", gap:8, color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                      {a.views > 0 && <span>{a.views} views</span>}
                      {a.helpful > 0 && <span style={{ color:"#22c55e" }}>👍 {a.helpful}</span>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// 2. KB ADMIN EDITOR
// ════════════════════════════════════════════════════════════════════════════
function KBAdminEditor({ api, toast }) {
  const [tab,       setTab]       = useState("articles");  // "articles" | "categories"
  const [cats,      setCats]      = useState([]);
  const [articles,  setArticles]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [editArt,   setEditArt]   = useState(null);   // article being edited (obj) or "new"
  const [editCat,   setEditCat]   = useState(null);   // category being edited (obj) or "new"
  const [preview,   setPreview]   = useState(false);
  const [searchQ,   setSearchQ]   = useState("");
  const [filterCat, setFilterCat] = useState("all");

  const BLANK_ART = { title:"", slug:"", category_slug:"getting-started", content:"", published:true, pinned:false, order:0 };
  const BLANK_CAT = { name:"", slug:"", icon:"📄", description:"", order:99 };
  const [artForm,   setArtForm]   = useState(BLANK_ART);
  const [catForm,   setCatForm]   = useState(BLANK_CAT);

  const ICON_PICKS = ["🚀","📡","🔔","🎯","📋","💳","🌐","🔒","🔧","💡","📚","❓","⚡","🏠","🛠️","📝","🗂️","🌟","💬","📣","🔑","📦","🧹","🌿","🐛","🎨","🔌","🪵","☀️","🚪","📊","🧰","🔐","📮","🤝"];

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/kb/categories").catch(()=>[]),
      api.get("/admin/kb/articles").catch(()=>[]),
    ]).then(([c, a]) => {
      setCats(Array.isArray(c) ? c : []);
      setArticles(Array.isArray(a) ? a : []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // ── Auto-generate slug ──────────────────────────────────────────────────
  const slugify = str => str.toLowerCase().trim().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");

  // ── Article save ──────────────────────────────────────────────────────
  const saveArticle = async () => {
    if (!artForm.title || !artForm.content) { toast.error("Title and content required"); return; }
    if (!artForm.slug) setArtForm(f => ({...f, slug: slugify(f.title)}));
    setSaving(true);
    try {
      const payload = { ...artForm, slug: artForm.slug || slugify(artForm.title) };
      if (editArt && editArt !== "new") {
        await api.put(`/admin/kb/articles/${editArt.id}`, payload);
        toast.success("Article updated");
      } else {
        await api.post("/admin/kb/articles", payload);
        toast.success("Article created");
      }
      setEditArt(null); setArtForm(BLANK_ART); load();
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const deleteArticle = async (id) => {
    if (!confirm("Delete this article?")) return;
    try { await api.del(`/admin/kb/articles/${id}`); load(); toast.success("Deleted"); }
    catch(e) { toast.error(e.message); }
  };

  const startEditArticle = async (art) => {
    const full = await api.get(`/kb/articles/${art.slug}`).catch(() => art);
    setArtForm({ ...BLANK_ART, ...full, category_slug: full.category_slug || full.category || "getting-started", order: full.order || 0 });
    setEditArt(art);
    setPreview(false);
    window.scrollTo(0,0);
  };

  // ── Category save ─────────────────────────────────────────────────────
  const saveCat = async () => {
    if (!catForm.name) { toast.error("Category name required"); return; }
    setSaving(true);
    try {
      if (editCat && editCat !== "new") {
        await api.put(`/admin/kb/categories/${editCat.id}`, catForm);
        toast.success("Category updated");
      } else {
        await api.post("/admin/kb/categories", catForm);
        toast.success("Category created");
      }
      setEditCat(null); setCatForm(BLANK_CAT); load();
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const deleteCat = async (id, name) => {
    if (!confirm(`Delete category "${name}"? Articles in it will be moved to Uncategorised.`)) return;
    try { await api.del(`/admin/kb/categories/${id}`); load(); toast.success("Category deleted"); }
    catch(e) { toast.error(e.message); }
  };

  // ── Styles ────────────────────────────────────────────────────────────
  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:5 };
  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };

  // ── Markdown preview ──────────────────────────────────────────────────
  function renderPreview(text) {
    if (!text) return "";
    return text
      .replace(/```[\w]*\n?([\s\S]*?)```/g, (_, c) => `<pre style="background:#0a0a0f;border:1px solid #1e1e2e;border-radius:6px;padding:12px;overflow-x:auto;font-size:11px;line-height:1.7;color:#22c55e;margin:10px 0">${c.trim()}</pre>`)
      .replace(/`([^`]+)`/g, '<code style="background:#1e1e2e;color:#a5b4fc;padding:1px 5px;border-radius:3px;font-size:11px">$1</code>')
      .replace(/^\|(.+)\|$/gm, m => { const cells=m.replace(/^\|\s*|\s*\|$/g,'').split('|'); return '<div style="display:flex;border-bottom:1px solid #1e1e2e">'+cells.map((c,i)=>`<div style="flex:1;padding:6px 10px;color:${i===0?'#ccc':'#555577'};font-size:12px">${c.trim()}</div>`).join('')+'</div>'; })
      .replace(/^### (.+)$/gm,'<div style="color:#e2e8f0;font-size:14px;font-weight:700;margin:18px 0 8px">$1</div>')
      .replace(/^## (.+)$/gm, '<div style="color:#fff;font-size:16px;font-weight:700;margin:22px 0 10px;padding-bottom:6px;border-bottom:1px solid #1e1e2e">$1</div>')
      .replace(/^# (.+)$/gm,  '<div style="color:#fff;font-size:20px;font-weight:800;margin:0 0 14px">$1</div>')
      .replace(/^> (.+)$/gm,  '<div style="border-left:3px solid #6366f1;padding:8px 12px;color:#8888aa;font-size:12px;margin:8px 0;font-style:italic">$1</div>')
      .replace(/\*\*(.+?)\*\*/g,'<strong style="color:#fff">$1</strong>')
      .replace(/\*(.+?)\*/g,   '<em style="color:#a5b4fc">$1</em>')
      .replace(/^- \[x\] (.+)$/gm,'<div style="display:flex;gap:6px;margin:3px 0"><span style="color:#22c55e">☑</span><span style="color:#8888aa;font-size:12px">$1</span></div>')
      .replace(/^- \[ \] (.+)$/gm,'<div style="display:flex;gap:6px;margin:3px 0"><span style="color:#3a3a5a">☐</span><span style="color:#8888aa;font-size:12px">$1</span></div>')
      .replace(/^- (.+)$/gm,  '<div style="display:flex;gap:6px;margin:3px 0"><span style="color:#6366f1">•</span><span style="color:#8888aa;font-size:12px">$1</span></div>')
      .replace(/\n\n/g,'<br/><br/>').replace(/\n/g,'<br/>');
  }

  // ── Filtered articles ─────────────────────────────────────────────────
  const filtered = articles.filter(a => {
    const matchSearch = !searchQ || a.title.toLowerCase().includes(searchQ.toLowerCase());
    const matchCat    = filterCat === "all" || a.category_slug === filterCat || a.category === filterCat;
    return matchSearch && matchCat;
  });

  return (
    <div>
      {/* Tab bar */}
      <div style={{ display:"flex", gap:0, marginBottom:20, background:"#0a0a0f", borderRadius:8, padding:4, width:"fit-content" }}>
        {[["articles","📄 Articles"],["categories","🗂️ Categories"]].map(([id,label])=>(
          <button key={id} onClick={()=>{ setTab(id); setEditArt(null); setEditCat(null); }}
            style={{ padding:"8px 20px", borderRadius:6, border:"none", background:tab===id?"#1e1e2e":"transparent", color:tab===id?"#a5b4fc":"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── CATEGORIES TAB ── */}
      {tab === "categories" && (
        <div>
          {/* Category form */}
          {editCat !== null && (
            <div style={{...cardS, borderColor:"#2a2a3e", marginBottom:20}}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                <div style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{editCat==="new"?"New Category":"Edit Category"}</div>
                <button onClick={()=>{setEditCat(null);setCatForm(BLANK_CAT);}} style={{ background:"transparent", border:"none", color:"#4a4a6a", fontSize:20, cursor:"pointer" }}>×</button>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 1fr", gap:10, marginBottom:14 }}>
                <div>
                  <label style={lS}>CATEGORY NAME</label>
                  <input style={iS} value={catForm.name} placeholder="e.g. Getting Started"
                    onChange={e => setCatForm(f => ({...f, name:e.target.value, slug: editCat==="new" ? slugify(e.target.value) : f.slug}))}/>
                </div>
                <div>
                  <label style={lS}>ICON</label>
                  <input style={iS} value={catForm.icon} onChange={e=>setCatForm(f=>({...f,icon:e.target.value}))} maxLength={2}/>
                </div>
                <div>
                  <label style={lS}>SLUG (URL-safe)</label>
                  <input style={iS} value={catForm.slug} placeholder="getting-started"
                    onChange={e=>setCatForm(f=>({...f,slug:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-")}))}/>
                </div>
              </div>

              {/* Icon picker */}
              <div style={{ marginBottom:14 }}>
                <label style={lS}>QUICK ICON PICKER</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {ICON_PICKS.map(icon=>(
                    <button key={icon} onClick={()=>setCatForm(f=>({...f,icon}))}
                      style={{ width:34, height:34, borderRadius:6, border:`1px solid ${catForm.icon===icon?"#6366f1":"#1e1e2e"}`, background:catForm.icon===icon?"#6366f120":"transparent", fontSize:18, cursor:"pointer" }}>
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom:14 }}>
                <label style={lS}>DESCRIPTION (shown under category name)</label>
                <input style={iS} value={catForm.description} placeholder="Short description of what this category covers"
                  onChange={e=>setCatForm(f=>({...f,description:e.target.value}))}/>
              </div>

              <div style={{ marginBottom:14 }}>
                <label style={lS}>DISPLAY ORDER (lower = higher on page)</label>
                <input style={{...iS, width:100}} type="number" min="0" value={catForm.order}
                  onChange={e=>setCatForm(f=>({...f,order:parseInt(e.target.value)||0}))}/>
              </div>

              {/* Preview */}
              <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:8, padding:"12px 16px", marginBottom:14, display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:24 }}>{catForm.icon||"📄"}</span>
                <div>
                  <div style={{ color:"#fff", fontSize:14, fontWeight:700 }}>{catForm.name||"Category Name"}</div>
                  <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>{catForm.description||"Description"}</div>
                </div>
                <div style={{ marginLeft:"auto", color:"#3a3a5a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                  {articles.filter(a=>a.category_slug===catForm.slug||a.category===catForm.slug).length} articles
                </div>
              </div>

              <div style={{ display:"flex", gap:8 }}>
                <button onClick={saveCat} disabled={saving} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:saving?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
                  {saving ? <><Spinner size={11}/>Saving…</> : editCat==="new" ? "✨ Create category" : "💾 Update category"}
                </button>
                <button onClick={()=>{setEditCat(null);setCatForm(BLANK_CAT);}} style={{ padding:"9px 16px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Category list header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <div style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{cats.length} categories · {articles.length} total articles</div>
            <button onClick={()=>{setEditCat("new");setCatForm(BLANK_CAT);window.scrollTo(0,0);}}
              style={{ padding:"9px 18px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              + New Category
            </button>
          </div>

          {/* Category cards */}
          {loading ? <div style={{display:"flex",justifyContent:"center",padding:40}}><Spinner size={28}/></div> : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {cats.map(cat => {
                const artCount = articles.filter(a => a.category_slug===cat.slug || a.category===cat.slug).length;
                return (
                  <div key={cat.id} style={{ ...cardS, marginBottom:0, display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:44, height:44, borderRadius:10, background:"#1e1e2e", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>
                      {cat.icon||"📄"}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ color:"#ccc", fontSize:13, fontWeight:600, marginBottom:2 }}>{cat.name}</div>
                      <div style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                        /{cat.slug} · {artCount} article{artCount!==1?"s":""}
                        {cat.description && <span style={{ marginLeft:10, color:"#3a3a5a" }}>{cat.description.slice(0,60)}{cat.description.length>60?"…":""}</span>}
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      <button onClick={()=>{setEditCat(cat);setCatForm({name:cat.name,slug:cat.slug,icon:cat.icon||"📄",description:cat.description||"",order:cat.order||0});window.scrollTo(0,0);}}
                        style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                        Edit
                      </button>
                      <button onClick={()=>deleteCat(cat.id,cat.name)}
                        style={{ padding:"5px 10px", borderRadius:5, border:"1px solid #ff4d4d20", background:"transparent", color:"#ff4d4d", fontSize:11, cursor:"pointer" }}>
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
              {cats.length===0 && !loading && (
                <div style={{ ...cardS, textAlign:"center", color:"#3a3a5a", padding:40 }}>
                  No categories yet. Click + New Category to create your first one.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ARTICLES TAB ── */}
      {tab === "articles" && (
        <div>
          {/* Article form */}
          {editArt !== null && (
            <div style={{...cardS, borderColor:"#2a2a3e", marginBottom:24}}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                <div style={{ color:"#a5b4fc", fontSize:13, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
                  {editArt==="new" ? "✨ New Article" : "✏️ Editing: "+artForm.title}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>setPreview(p=>!p)} style={{ padding:"5px 12px", borderRadius:5, border:`1px solid ${preview?"#6366f1":"#2a2a3e"}`, background:preview?"#6366f120":"transparent", color:preview?"#a5b4fc":"#4a4a6a", fontSize:11, cursor:"pointer" }}>
                    {preview?"✏️ Edit":"👁 Preview"}
                  </button>
                  <button onClick={()=>{setEditArt(null);setArtForm(BLANK_ART);setPreview(false);}} style={{ background:"transparent", border:"none", color:"#4a4a6a", fontSize:20, cursor:"pointer" }}>×</button>
                </div>
              </div>

              {/* Title + Category + Slug row */}
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:10, marginBottom:12 }}>
                <div>
                  <label style={lS}>TITLE</label>
                  <input style={iS} value={artForm.title} placeholder="Article title"
                    onChange={e=>setArtForm(f=>({...f,title:e.target.value,slug:editArt==="new"?slugify(e.target.value):f.slug}))}/>
                </div>
                <div>
                  <label style={lS}>CATEGORY</label>
                  <select style={iS} value={artForm.category_slug}
                    onChange={e=>setArtForm(f=>({...f,category_slug:e.target.value}))}>
                    {cats.map(c => <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>)}
                    {cats.length===0 && <option value="getting-started">Getting Started</option>}
                  </select>
                </div>
                <div>
                  <label style={lS}>SLUG (URL)</label>
                  <input style={iS} value={artForm.slug||""} placeholder="auto-generated"
                    onChange={e=>setArtForm(f=>({...f,slug:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-")}))}/>
                </div>
              </div>

              {/* Content area */}
              <div style={{ marginBottom:12 }}>
                <label style={lS}>CONTENT — Markdown · # H1 · ## H2 · **bold** · `code` · ```block``` · - list · &gt; quote</label>
                {preview ? (
                  <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"16px 18px", minHeight:320, lineHeight:1.8, fontSize:13, color:"#8888aa" }}
                    dangerouslySetInnerHTML={{ __html: renderPreview(artForm.content) }}/>
                ) : (
                  <textarea style={{...iS,minHeight:360,resize:"vertical",lineHeight:1.8,fontSize:12}}
                    value={artForm.content}
                    onChange={e=>setArtForm(f=>({...f,content:e.target.value}))}
                    placeholder={"# Article Title\n\nIntro paragraph...\n\n## First Section\n\nContent here...\n\n- Bullet point\n- Another bullet\n\n> Pro tip: this is a blockquote\n\n```\ncode block here\n```"}/>
                )}
              </div>

              {/* Toggles + order */}
              <div style={{ display:"flex", gap:24, alignItems:"center", marginBottom:16 }}>
                {[["published","Published","#22c55e"],["pinned","📌 Pinned (top of category)","#f59e0b"]].map(([key,label,color])=>(
                  <div key={key} onClick={()=>setArtForm(f=>({...f,[key]:!f[key]}))} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                    <div style={{ width:36, height:20, borderRadius:10, background:artForm[key]?color:"#2a2a3e", position:"relative", transition:"all 0.2s", flexShrink:0 }}>
                      <div style={{ position:"absolute", top:2, left:artForm[key]?16:2, width:16, height:16, borderRadius:"50%", background:"#fff", transition:"all 0.2s" }}/>
                    </div>
                    <span style={{ color:"#ccc", fontSize:12 }}>{label}</span>
                  </div>
                ))}
                <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:"auto" }}>
                  <label style={{...lS,margin:0}}>ORDER</label>
                  <input type="number" min="0" value={artForm.order}
                    onChange={e=>setArtForm(f=>({...f,order:parseInt(e.target.value)||0}))}
                    style={{...iS,width:70}}/>
                </div>
              </div>

              <div style={{ display:"flex", gap:8 }}>
                <button onClick={saveArticle} disabled={saving} style={{ padding:"9px 20px", borderRadius:6, border:"none", background:saving?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
                  {saving ? <><Spinner size={11}/>Saving…</> : editArt==="new"?"✨ Create article":"💾 Update article"}
                </button>
                <button onClick={()=>{setEditArt(null);setArtForm(BLANK_ART);setPreview(false);}} style={{ padding:"9px 16px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Article list header */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, flexWrap:"wrap" }}>
            <input value={searchQ} onChange={e=>setSearchQ(e.target.value)}
              placeholder="Search articles…"
              style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", width:220 }}/>
            <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
              style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none" }}>
              <option value="all">All categories</option>
              {cats.map(c=><option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>)}
            </select>
            <div style={{ color:"#3a3a5a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{filtered.length} article{filtered.length!==1?"s":""}</div>
            <div style={{ flex:1 }}/>
            <button onClick={()=>{setEditArt("new");setArtForm({...BLANK_ART,category_slug:cats[0]?.slug||"getting-started"});setPreview(false);window.scrollTo(0,0);}}
              style={{ padding:"9px 20px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              + New Article
            </button>
          </div>

          {/* Articles grouped by category */}
          {loading ? <div style={{display:"flex",justifyContent:"center",padding:40}}><Spinner size={28}/></div> : (
            <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
              {filtered.length === 0 && (
                <div style={{ ...cardS, textAlign:"center", color:"#3a3a5a", padding:40 }}>
                  No articles found. {articles.length===0?"Click + New Article to write your first.":"Try a different filter."}
                </div>
              )}
              {filtered.map(a => {
                const cat = cats.find(c => c.slug===a.category_slug || c.slug===a.category);
                return (
                  <div key={a.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"#0f0f1a", border:"1px solid #111120", borderRadius:8 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:a.published?"#22c55e":"#2a2a3e", flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                        <span style={{ color:"#ccc", fontSize:13, fontWeight:500 }}>{a.title}</span>
                        {a.pinned && <span style={{ color:"#f59e0b", fontSize:10 }}>📌</span>}
                        {!a.published && <span style={{ fontSize:9, padding:"1px 5px", borderRadius:3, background:"#2a2a3e", color:"#555577" }}>DRAFT</span>}
                      </div>
                      <div style={{ display:"flex", gap:12, color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                        <span style={{ color:"#6366f1" }}>{cat?.icon||"📄"} {cat?.name||a.category_slug||a.category}</span>
                        <span>/{a.slug}</span>
                        {(a.views||0)>0 && <span>{a.views} views</span>}
                        {(a.helpful||0)>0 && <span style={{ color:"#22c55e" }}>👍{a.helpful}</span>}
                        {(a.not_helpful||0)>0 && <span style={{ color:"#ff4d4d" }}>👎{a.not_helpful}</span>}
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      <button onClick={()=>startEditArticle(a)}
                        style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Edit</button>
                      <button onClick={()=>deleteArticle(a.id)}
                        style={{ padding:"5px 10px", borderRadius:5, border:"1px solid #ff4d4d20", background:"transparent", color:"#ff4d4d", fontSize:11, cursor:"pointer" }}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// 3. LANDING PAGE — FAQ + KB section (shown on auth/login screen)
// ════════════════════════════════════════════════════════════════════════════
function LandingFAQ({ api }) {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState(null);

  const STATIC_FAQS = [
    { q:"How does Socialisten find leads?",           a:"Socialisten scans Facebook groups, Reddit communities, listings for posts matching your keywords. When someone posts 'my furnace broke' or 'need an HVAC guy', you get an instant alert — usually before any other contractor even sees it." },
    { q:"How fast are alerts?",                       a:"SMS alerts fire within seconds of a match being found. Scans run every 5–30 minutes depending on your plan. On the Pro plan with 5-minute scans, you'll typically see a lead within 2–3 minutes of it being posted." },
    { q:"Do I need a Facebook account?",              a:"You need a Facebook account to monitor Facebook groups. We recommend creating a dedicated monitoring account (not your personal one). Reddit and Nextdoor monitoring work without any login." },
    { q:"What kind of contractors use this?",         a:"HVAC, plumbing, electrical, roofing, general contractors, landscapers, and appliance repair. Anyone who gets jobs from homeowners. HVAC contractors see the most volume because furnace and AC breakdowns are urgent and people post about them immediately." },
    { q:"Can I try it for free?",                     a:"Yes — the free plan includes 2 monitored pages, email alerts, and 60-minute scans. No credit card required. Paid plans start with a 7-day free trial." },
    { q:"How is this different from buying leads?",   a:"Bought leads are sold to 5–10 contractors at once, are often weeks old, and cost $20–$80 each. Socialisten finds people posting in real time. You're responding to a live post, not a database entry. First responder wins." },
    { q:"Will Facebook ban my account?",              a:"Socialisten uses residential proxies, realistic browser fingerprints, human-like timing, and per-account session management to minimize detection. We scan during business hours, limit scans per session, and rotate IPs. The risk is low when configured correctly." },
    { q:"Can I monitor multiple areas?",              a:"Yes. Add one page per geographic area — the Detroit homeowners group, the Sterling Heights group, the Macomb County group. There's no limit to geographic coverage, only to total page count (based on your plan)." },
  ];

  useEffect(() => {
    api.get("/kb/articles?pinned_only=true").then(d => setFaqs(d||[])).catch(()=>{});
  }, []);

  return (
    <div>
      <div style={{ textAlign:"center", marginBottom:40 }}>
        <div style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", letterSpacing:3, marginBottom:10 }}>FAQ</div>
        <div style={{ color:"#fff", fontSize:28, fontWeight:700, marginBottom:10 }}>Common questions</div>
        <div style={{ color:"#4a4a6a", fontSize:14 }}>Everything you need to know before you start</div>
      </div>

      <div style={{ maxWidth:720, margin:"0 auto" }}>
        {STATIC_FAQS.map((faq, i) => (
          <div key={i} style={{ borderBottom:"1px solid #1e1e2e", overflow:"hidden" }}>
            <div onClick={() => setOpen(open===i?null:i)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 0", cursor:"pointer", gap:12 }}>
              <div style={{ color:"#fff", fontSize:14, fontWeight:600, lineHeight:1.4 }}>{faq.q}</div>
              <div style={{ width:24, height:24, borderRadius:"50%", background:"#1e1e2e", display:"flex", alignItems:"center", justifyContent:"center", color:"#6366f1", fontSize:16, flexShrink:0, transition:"transform 0.2s", transform:open===i?"rotate(45deg)":"rotate(0deg)" }}>+</div>
            </div>
            {open===i && (
              <div style={{ color:"#8888aa", fontSize:13, lineHeight:1.8, paddingBottom:18 }}>{faq.a}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// 4. SETUP GUIDE — How to run the app (step-by-step)
// ════════════════════════════════════════════════════════════════════════════
function SetupGuide({ variant = "landing" }) {  // variant: "landing" | "inapp"
  const [activeStep, setActiveStep] = useState(null);

  const STEPS = [
    {
      number: "01",
      icon: "🖥️",
      title: "Deploy the server",
      time: "5 min",
      color: "#6366f1",
      summary: "Docker Compose gets everything running in one command",
      detail: `**What you need:**
- A VPS or cloud server (DigitalOcean, Hetzner, Linode) — 2GB RAM minimum, 4GB recommended
- Ubuntu 22.04 or 24.04
- Domain name pointed to your server IP

**Commands to run:**
\`\`\`
# Install Docker
curl -fsSL https://get.docker.com | sh
usermod -aG docker $USER && newgrp docker

# Clone / upload the socialisten folder to your server
cd /opt
# (upload your zip and unzip it)
unzip socialisten-production-ready.zip
cd social-listener
\`\`\``,
    },
    {
      number: "02",
      icon: "⚙️",
      title: "Configure environment",
      time: "10 min",
      color: "#8b5cf6",
      summary: "Fill in your API keys in the .env file",
      detail: `Create your \`.env\` file in the \`backend/\` folder:
\`\`\`
cp backend/.env.example backend/.env
nano backend/.env
\`\`\`

**Required variables:**
\`\`\`
SECRET_KEY=your-random-secret-key-here
DATABASE_URL=postgresql://postgres:postgres@db:5432/socialisten
REDIS_URL=redis://redis:6379

# Email alerts (SendGrid — free tier works)
SENDGRID_API_KEY=SG.xxxx
ALERT_FROM_EMAIL=alerts@yourdomain.com

# SMS/Voice alerts (Twilio — optional)
TWILIO_ACCOUNT_SID=ACxxxx
TWILIO_AUTH_TOKEN=xxxx
TWILIO_FROM_NUMBER=+15554567890

# Stripe payments
STRIPE_SECRET_KEY=sk_live_xxxx
STRIPE_WEBHOOK_SECRET=whsec_xxxx

# Your app URLs
APP_URL=https://yourdomain.com
API_URL=https://yourdomain.com/api
ADMIN_EMAIL=your@email.com

# Anti-ban settings
DEFAULT_TIMEZONE=America/Detroit
MAX_FB_SCANS_PER_HOUR=10
\`\`\``,
    },
    {
      number: "03",
      icon: "🚀",
      title: "Start the application",
      time: "2 min",
      color: "#22c55e",
      summary: "One command starts all services",
      detail: `\`\`\`
# Start everything
docker compose up -d

# Check all containers are running
docker compose ps

# You should see these services running:
#   db       — PostgreSQL database
#   redis    — Redis cache + Celery broker
#   api      — FastAPI backend (port 8000)
#   worker   — Celery task worker
#   beat     — Celery scheduler (runs scans)
#   frontend — React app (port 3000)
#   nginx    — Reverse proxy (ports 80/443)
\`\`\`

**Run the database migration:**
\`\`\`
docker exec -it social-listener-api-1 python db/migrate.py
\`\`\`

Your app is now running at \`http://your-server-ip\``,
    },
    {
      number: "04",
      icon: "🔒",
      title: "Set up SSL (HTTPS)",
      time: "5 min",
      color: "#0ea5e9",
      summary: "Free SSL certificate via Let's Encrypt",
      detail: `\`\`\`
# Install Certbot
apt install certbot python3-certbot-nginx -y

# Get your SSL certificate (replace with your domain)
certbot --nginx -d yourdomain.com -d www.yourdomain.com

# Certbot auto-updates nginx config and handles renewals
# Test renewal:
certbot renew --dry-run
\`\`\`

After SSL is set up, update your \`.env\`:
\`\`\`
APP_URL=https://yourdomain.com
API_URL=https://yourdomain.com/api
\`\`\`

Then restart:
\`\`\`
docker compose restart api worker beat
\`\`\``,
    },
    {
      number: "05",
      icon: "💳",
      title: "Connect Stripe",
      time: "10 min",
      color: "#f59e0b",
      summary: "Set up payments and webhooks",
      detail: `**1. Create your Stripe products**

In the admin panel → Plan Manager → click "⚡ Sync to Stripe" on each paid plan. This auto-creates products and prices in Stripe and saves the price IDs.

**2. Set up Stripe webhook**

In Stripe Dashboard → Developers → Webhooks → Add endpoint:
\`\`\`
URL: https://yourdomain.com/api/webhook
Events: customer.subscription.*, invoice.*, payment_intent.*
\`\`\`

Copy the webhook signing secret and add it to \`.env\`:
\`\`\`
STRIPE_WEBHOOK_SECRET=whsec_xxxx
\`\`\`

**3. Test a payment**

Use Stripe's test card: \`4242 4242 4242 4242\`, any future date, any CVC. Upgrade to a paid plan and verify the subscription appears in Stripe and the user's plan changes in the admin panel.`,
    },
    {
      number: "06",
      icon: "🌐",
      title: "Add residential proxies",
      time: "5 min",
      color: "#ec4899",
      summary: "Required for Facebook scanning without bans",
      detail: `**Why you need proxies:**
Datacenter IPs (your VPS) are instantly flagged by Facebook. Residential proxies route traffic through real home IP addresses that look like normal users.

**Recommended: Bright Data**
1. Sign up at brightdata.com
2. Create a residential proxy zone
3. Copy your proxy credentials

**Add proxies in the admin panel:**

Admin panel → Advanced Stats → Proxy Pool → Upload Proxies

Paste your proxies in any format:
\`\`\`
http://user:password@zproxy.brightdata.com:22225
http://user:password@zproxy.brightdata.com:22226
\`\`\`

Click **Import** then **Test All** to verify they work.

**Budget: $15–30/month** covers typical usage for 50–100 customers scanning Facebook.`,
    },
    {
      number: "07",
      icon: "👤",
      title: "Create your first user",
      time: "2 min",
      color: "#22c55e",
      summary: "Register an account and start monitoring",
      detail: `**Register your admin account:**

Go to \`https://yourdomain.com\` → click "Create account" → enter your email and password.

**Make yourself admin:**
\`\`\`
docker exec -it social-listener-api-1 python -c "
from db.models import SessionLocal, User
db = SessionLocal()
user = db.query(User).filter(User.email=='your@email.com').first()
user.is_admin = True
db.commit()
print('Done')
"
\`\`\`

**Add your first monitored page:**
1. Log in → Monitored Pages → + Add Page
2. Paste a Facebook group URL (e.g. your local homeowners group)
3. Add keywords: furnace, boiler, AC broken, no heat, HVAC repair
4. Click Save → Scan Now

You should see your first results within 60 seconds.`,
    },
  ];

  const isLanding = variant === "landing";

  return (
    <div>
      {isLanding && (
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <div style={{ color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", letterSpacing:3, marginBottom:10 }}>SETUP</div>
          <div style={{ color:"#fff", fontSize:28, fontWeight:700, marginBottom:10 }}>Up and running in under 30 minutes</div>
          <div style={{ color:"#4a4a6a", fontSize:14 }}>Up and running in under 30 minutes.</div>
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {STEPS.map((step, i) => {
          const isOpen = activeStep === i;
          return (
            <div key={i} style={{ background:"#0f0f1a", border:`1px solid ${isOpen?step.color+"40":"#1e1e2e"}`, borderRadius:12, overflow:"hidden", transition:"all 0.2s" }}>
              <div onClick={() => setActiveStep(isOpen?null:i)} style={{ display:"flex", alignItems:"center", gap:16, padding:"18px 22px", cursor:"pointer" }}>
                <div style={{ width:48, height:48, borderRadius:12, background:`${step.color}20`, border:`1px solid ${step.color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>
                  {step.icon}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:3 }}>
                    <span style={{ color:step.color, fontSize:11, fontFamily:"'DM Mono',monospace", fontWeight:700 }}>STEP {step.number}</span>
                    <span style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>~{step.time}</span>
                  </div>
                  <div style={{ color:"#fff", fontSize:14, fontWeight:600 }}>{step.title}</div>
                  <div style={{ color:"#4a4a6a", fontSize:12, marginTop:2 }}>{step.summary}</div>
                </div>
                <div style={{ color:step.color, fontSize:20, transition:"transform 0.2s", transform:isOpen?"rotate(180deg)":"rotate(0deg)", flexShrink:0 }}>
                  ⌄
                </div>
              </div>
              {isOpen && (
                <div style={{ padding:"0 22px 22px", borderTop:"1px solid #1e1e2e", paddingTop:18 }}>
                  <div style={{ color:"#8888aa", fontSize:13, lineHeight:1.8 }}>
                    <ArticleContent content={step.detail}/>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isLanding && (
        <div style={{ textAlign:"center", marginTop:36 }}>
          <div style={{ color:"#4a4a6a", fontSize:13 }}>
            Need help? Read the full docs in the <strong style={{ color:"#a5b4fc" }}>Help Center</strong> after you log in, or email <strong style={{ color:"#a5b4fc" }}>support@socialisten.io</strong>
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// CUSTOMER FEATURE 1: PLAN USAGE METER
// Prominent usage bar shown at top of feed and pages views
// ════════════════════════════════════════════════════════════════════════════
function PlanUsageMeter({ api, userPlan, onUpgrade }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/usage").then(setUsage).catch(() => {});
  }, []);

  if (!usage) return null;

  const pagesUsed  = usage.pages_used  || 0;
  const pagesLimit = usage.pages_limit || 2;
  const pct        = Math.min(100, Math.round((pagesUsed / pagesLimit) * 100));
  const isNearLimit = pct >= 80;
  const isAtLimit   = pct >= 100;

  const PLAN_COLORS = { free:"#4a4a6a", starter:"#22c55e", pro:"#6366f1" };
  const planColor   = PLAN_COLORS[userPlan] || "#6366f1";

  if (pct < 50 && !isNearLimit) return null; // Only show when relevant

  return (
    <div style={{ background: isAtLimit?"#1a0808":isNearLimit?"#1a1208":"#0f0f1a", border:`1px solid ${isAtLimit?"#ff4d4d30":isNearLimit?"#f59e0b30":"#1e1e2e"}`, borderRadius:10, padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:14 }}>
      <div style={{ flex:1 }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
          <span style={{ color:isAtLimit?"#ff4d4d":isNearLimit?"#f59e0b":"#ccc", fontSize:12, fontWeight:600 }}>
            {isAtLimit ? "⚠️ Page limit reached" : isNearLimit ? "⚠️ Approaching page limit" : "Page usage"}
          </span>
          <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
            {pagesUsed} / {pagesLimit === 999 ? "∞" : pagesLimit} pages
          </span>
        </div>
        <div style={{ height:6, background:"#1e1e2e", borderRadius:3, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${pct}%`, background: isAtLimit?"#ff4d4d":isNearLimit?"#f59e0b":planColor, borderRadius:3, transition:"width 0.4s ease" }}/>
        </div>
        {isAtLimit && <div style={{ color:"#ff8888", fontSize:11, marginTop:5 }}>Upgrade your plan to monitor more pages.</div>}
      </div>
      {(isNearLimit || isAtLimit) && (
        <button onClick={onUpgrade} style={{ padding:"7px 16px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
          Upgrade →
        </button>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// CUSTOMER FEATURE 2: QUICK REPLY + BOOKMARK in feed cards
// Adds inline reply and star/save to every match card in FeedView
// ════════════════════════════════════════════════════════════════════════════
function QuickReplyBar({ match, templates, api, toast, onSaved, onReplied }) {
  const [open,     setOpen]     = useState(false);
  const [replyTxt, setReplyTxt] = useState("");
  const [sending,  setSending]  = useState(false);
  const [saved,    setSaved]    = useState(match.saved || false);
  const [savingB,  setSavingB]  = useState(false);

  const copyReply = (text) => {
    setReplyTxt(text);
    setOpen(true);
  };

  const sendReply = async () => {
    if (!replyTxt.trim()) return;
    setSending(true);
    try {
      // Copy to clipboard + mark as contacted in pipeline
      await navigator.clipboard.writeText(replyTxt);
      await api.patch(`/matches/${match.id}/status`, { status:"contacted", notes:`Quick reply: ${replyTxt.slice(0,80)}` });
      toast.success("Reply copied to clipboard — paste it into Facebook. Lead moved to Contacted.");
      setOpen(false);
      setReplyTxt("");
      if (onReplied) onReplied(match.id);
    } catch(e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const toggleSave = async () => {
    setSavingB(true);
    const newSaved = !saved;
    try {
      await api.patch(`/matches/${match.id}/status`, {
        saved: newSaved,
        status: newSaved && (match.status === "new" || !match.status) ? "new" : match.status
      });
      setSaved(newSaved);
      if (onSaved) onSaved(match.id, newSaved);
      toast.success(newSaved ? "⭐ Saved for later" : "Removed from saved");
    } catch(e) { setSaved(!newSaved); }
    finally { setSavingB(false); }
  };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, fontFamily:"inherit", outline:"none", resize:"none", lineHeight:1.6 };

  return (
    <div style={{ marginTop:8 }}>
      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
        {/* Templates quick access */}
        {templates && templates.length > 0 && (
          <div style={{ display:"flex", gap:4, flexWrap:"wrap", flex:1 }}>
            {templates.slice(0, 3).map(t => (
              <button key={t.id} onClick={() => copyReply(t.text)} style={{ padding:"3px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer", whiteSpace:"nowrap" }}>
                {t.label}
              </button>
            ))}
            <button onClick={() => setOpen(p=>!p)} style={{ padding:"3px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:open?"#1e1e2e":"transparent", color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              ✏️ Custom
            </button>
          </div>
        )}
        {!templates?.length && (
          <button onClick={() => setOpen(p=>!p)} style={{ padding:"4px 12px", borderRadius:5, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
            💬 Quick Reply
          </button>
        )}
        {/* Save/Bookmark */}
        <button onClick={toggleSave} disabled={savingB} title={saved?"Remove from saved":"Save for later"} style={{ padding:"4px 10px", borderRadius:5, border:`1px solid ${saved?"#f59e0b30":"#2a2a3e"}`, background:saved?"#f59e0b15":"transparent", color:saved?"#f59e0b":"#4a4a6a", fontSize:13, cursor:savingB?"not-allowed":"pointer" }}>
          {saved ? "⭐" : "☆"}
        </button>
      </div>

      {/* Reply compose box */}
      {open && (
        <div style={{ marginTop:8, background:"#0a0a1a", border:"1px solid #6366f130", borderRadius:8, padding:"12px 14px" }}>
          <textarea value={replyTxt} onChange={e=>setReplyTxt(e.target.value)} placeholder="Type your reply here — it'll be copied to your clipboard so you can paste it into Facebook..." rows={3} style={iS}/>
          <div style={{ display:"flex", gap:6, marginTop:8, alignItems:"center" }}>
            <button onClick={sendReply} disabled={sending||!replyTxt.trim()} style={{ padding:"6px 16px", borderRadius:5, border:"none", background:(!sending&&replyTxt.trim())?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#2a2a3e", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:(!sending&&replyTxt.trim())?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:5 }}>
              {sending?<><Spinner size={10}/>Copying…</>:"📋 Copy & Mark Contacted"}
            </button>
            <button onClick={()=>setOpen(false)} style={{ padding:"6px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>Cancel</button>
            <span style={{ color:"#3a3a5a", fontSize:10, marginLeft:4 }}>Copies to clipboard → paste into Facebook or Reddit</span>
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// CUSTOMER FEATURE 3: LEAD CONTACT INFO
// Store contact details collected during outreach on pipeline leads
// ════════════════════════════════════════════════════════════════════════════
function ContactInfoPanel({ match, api, toast }) {
  const [contact, setContact]   = useState({ name:"", phone:"", email:"", address:"", job_value:"" });
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [editing, setEditing]   = useState(false);

  useEffect(() => {
    api.get(`/matches/${match.id}/contact`).then(d => {
      if (d) setContact({ name:d.name||"", phone:d.phone||"", email:d.email||"", address:d.address||"", job_value:d.job_value||"" });
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, [match.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/matches/${match.id}/contact`, contact);
      toast.success("Contact info saved");
      setEditing(false);
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const hasInfo = contact.name || contact.phone || contact.email;
  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 10px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:4 };

  if (loading) return null;

  return (
    <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #111" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:editing?10:0 }}>
        <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1 }}>CONTACT INFO</div>
        <button onClick={()=>setEditing(p=>!p)} style={{ background:"transparent", border:"none", color:"#6366f1", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
          {editing ? "Cancel" : hasInfo ? "Edit" : "+ Add"}
        </button>
      </div>

      {!editing && hasInfo && (
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          {contact.name  && <div style={{ color:"#ccc", fontSize:12 }}>👤 {contact.name}</div>}
          {contact.phone && <div><a href={`tel:${contact.phone}`} style={{ color:"#22c55e", fontSize:12, textDecoration:"none" }}>📞 {contact.phone}</a></div>}
          {contact.email && <div><a href={`mailto:${contact.email}`} style={{ color:"#6366f1", fontSize:12, textDecoration:"none" }}>✉️ {contact.email}</a></div>}
          {contact.job_value && <div style={{ color:"#f59e0b", fontSize:12, fontFamily:"'DM Mono',monospace" }}>💰 ${contact.job_value}</div>}
          {contact.address && <div style={{ color:"#4a4a6a", fontSize:11 }}>📍 {contact.address}</div>}
        </div>
      )}
      {!editing && !hasInfo && (
        <div style={{ color:"#3a3a5a", fontSize:11 }}>No contact info yet — click + Add after you reach out</div>
      )}

      {editing && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          <div><label style={lS}>CUSTOMER NAME</label><input style={iS} placeholder="Mike Johnson" value={contact.name} onChange={e=>setContact(c=>({...c,name:e.target.value}))}/></div>
          <div><label style={lS}>PHONE</label><input style={iS} type="tel" placeholder="+15864865766" value={contact.phone} onChange={e=>setContact(c=>({...c,phone:e.target.value}))}/></div>
          <div><label style={lS}>EMAIL</label><input style={iS} type="email" placeholder="mike@example.com" value={contact.email} onChange={e=>setContact(c=>({...c,email:e.target.value}))}/></div>
          <div><label style={lS}>JOB VALUE ($)</label><input style={iS} type="number" placeholder="2500" value={contact.job_value} onChange={e=>setContact(c=>({...c,job_value:e.target.value}))}/></div>
          <div style={{gridColumn:"1/-1"}}><label style={lS}>ADDRESS / LOCATION</label><input style={iS} placeholder="123 Main St, Sterling Heights, MI" value={contact.address} onChange={e=>setContact(c=>({...c,address:e.target.value}))}/></div>
          <div style={{gridColumn:"1/-1"}}>
            <button onClick={save} disabled={saving} style={{ padding:"7px 18px", borderRadius:5, border:"none", background:saving?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:5 }}>
              {saving?<><Spinner size={10}/>Saving…</>:"Save Contact Info"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// CUSTOMER FEATURE 4: MATCH EXPORT (user-facing CSV download)
// ════════════════════════════════════════════════════════════════════════════
function MatchExportButton({ api, toast, filters = {} }) {
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        format: "csv",
        days: filters.days || 30,
        ...(filters.platform && { platform: filters.platform }),
        ...(filters.min_score && { min_score: filters.min_score }),
        ...(filters.status && { status: filters.status }),
      });
      const token = localStorage.getItem("sl_token");
      const res = await fetch(`${window.API_BASE || "https://socipulses.com"}/matches/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `leads_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch(e) { toast.error(e.message); }
    finally { setExporting(false); }
  };

  return (
    <button onClick={doExport} disabled={exporting} style={{ padding:"7px 16px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:exporting?"#3a3a5a":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:exporting?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
      {exporting ? <><Spinner size={10}/>Exporting…</> : "⬇ Export CSV"}
    </button>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// CUSTOMER FEATURE 5: WEEKLY EMAIL OPT-IN (in Settings)
// ════════════════════════════════════════════════════════════════════════════
function SavedMatchesTab({ api, toast, templates, onStatusChange }) {
  const [matches, setMatches]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get("/matches?saved=true&limit=50")
      .then(d => setMatches(d?.matches || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const unsave = async (id) => {
    try {
      await api.patch(`/matches/${id}/status`, { saved: false });
      setMatches(p => p.filter(m => m.id !== id));
      toast.success("Removed from saved");
    } catch(e) { toast.error(e.message); }
  };

  const PICONS = { facebook:"🔵", reddit:"🟠", };
  const STATUS_CFG = {
    new:       { color:"#6366f1", label:"New" },
    contacted: { color:"#3b82f6", label:"Contacted" },
    quoted:    { color:"#8b5cf6", label:"Quoted" },
    booked:    { color:"#f59e0b", label:"Booked" },
    won:       { color:"#22c55e", label:"Won" },
    lost:      { color:"#ff4d4d", label:"Lost" },
  };

  if (loading) return <div style={{display:"flex",justifyContent:"center",padding:40}}><Spinner size={24}/></div>;

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
        <span style={{ color:"#f59e0b", fontSize:18 }}>⭐</span>
        <div style={{ color:"#fff", fontSize:14, fontWeight:600 }}>Saved Leads</div>
        <div style={{ color:"#4a4a6a", fontSize:12 }}>({matches.length})</div>
      </div>

      {matches.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>☆</div>
          No saved leads yet — star a match from the feed to save it here
        </div>
      ) : matches.map(m => {
        const sc = STATUS_CFG[m.status||"new"] || STATUS_CFG.new;
        return (
          <div key={m.id} style={{ background:"#0f0f1a", border:"1px solid #f59e0b20", borderLeft:"3px solid #f59e0b", borderRadius:10, padding:"14px 16px", marginBottom:8 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                  <span style={{ fontSize:14 }}>{PICONS[m.platform||"facebook"]}</span>
                  <span style={{ color:"#4a4a6a", fontSize:11 }}>{m.page_name || "Unknown page"}</span>
                  <span style={{ padding:"1px 8px", borderRadius:4, fontSize:9, fontFamily:"'DM Mono',monospace", background:`${sc.color}20`, color:sc.color }}>{sc.label}</span>
                  <span style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", marginLeft:"auto" }}>{timeAgo(m.found_at)}</span>
                </div>
                <p style={{ color:"#8888aa", fontSize:13, lineHeight:1.5, margin:"0 0 8px" }}>{(m.post_text||"").slice(0, 200)}{(m.post_text||"").length>200?"…":""}</p>
                {m.notes && <div style={{ color:"#6366f1", fontSize:11, fontStyle:"italic", marginBottom:4 }}>📝 {m.notes}</div>}
                <QuickReplyBar match={m} templates={templates} api={api} toast={toast}/>
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                <div style={{ padding:"3px 8px", borderRadius:4, fontSize:10, fontWeight:700, background:`${scoreColor(m.score)}20`, color:scoreColor(m.score), fontFamily:"'DM Mono',monospace" }}>{m.score}</div>
                {m.post_url && m.post_url !== "#" && (
                  <a href={m.post_url} target="_blank" rel="noreferrer" style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, textDecoration:"none", display:"inline-flex", alignItems:"center", gap:4 }}>↗ Reply</a>
                )}
                <button onClick={()=>unsave(m.id)} title="Remove from saved" style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #f59e0b30", background:"#f59e0b15", color:"#f59e0b", fontSize:13, cursor:"pointer" }}>⭐</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// MODERN LANDING PAGE — Socialisten
// Replaces the bare login-card + FAQ with a full marketing page
// Sections: Nav → Hero → Live Feed Demo → Features → Use Cases →
//           Testimonials → Pricing Preview → FAQ → Footer
// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// SOCIALISTEN LANDING — GroupsWatcher-inspired design
// Clean, story-driven, high-conversion — light background, bold type,
// problem → solution narrative, real social proof
// ════════════════════════════════════════════════════════════════════════════

function LandingPage({ onSignup, onLogin }) {
  const [activeFaq,  setActiveFaq]  = useState(null);
  const [navSolid,   setNavSolid]   = useState(false);
  const [demoTick,   setDemoTick]   = useState(0);
  const [activeTrade,setActiveTrade]= useState(0);

  useEffect(() => {
    const fn = () => setNavSolid(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setDemoTick(p => (p+1) % DEMO_ALERTS.length), 3000);
    return () => clearInterval(t);
  }, []);

  const goto = (mode) => { if (mode==="login") onLogin(); else onSignup(); };

  // ── Data ──────────────────────────────────────────────────────────────────
  const DEMO_ALERTS = [
    { platform:"Facebook",  group:"Detroit Homeowners",       trade:"HVAC",          score:91, age:"47s", text:"Furnace stopped working last night, house is at 58°F with two kids. Need someone ASAP happy to pay extra for same-day." },
    { platform:"Nextdoor",  group:"Northside Neighborhood",   trade:"Plumbing",       score:88, age:"2m",  text:"Pipe burst under my kitchen sink water everywhere. Anyone know an emergency plumber on the North Side?" },
    { platform:"Reddit",    group:"r/HomeImprovement",        trade:"Electrical",     score:79, age:"8m",  text:"Circuit breaker keeps tripping every time I run the dishwasher. Electrician recommendations in the Denver area?" },
    { platform:"Facebook",  group:"Austin Neighbors",         trade:"Roofing",        score:85, age:"3m",  text:"Storm last night ripped off a big section of shingles. Water is coming in. Need a roofer today, not next week." },
    { platform:"Nextdoor",  group:"Eastside Community",       trade:"General Contractor",score:80,"age":"11m",text:"Want to finish my basement 800 sq ft. Looking for a GC in Columbus area for quotes. Budget around $40k." },
    { platform:"Facebook",  group:"Seattle Homeowners",       trade:"Appliance Repair",score:88, age:"4m",  text:"Fridge stopped cooling overnight, lost $300 in groceries. Anyone available this weekend to repair or advise?" },
    { platform:"Reddit",    group:"r/Detroit",                trade:"HVAC",           score:84, age:"6m",  text:"AC not working, it's 90 degrees inside. Anyone have a reliable HVAC company they trust in metro Detroit?" },
  ];

  const TRADES = [
    { name:"HVAC",             icon:"🌡️", color:"#2563eb", keywords:["furnace broken","no heat","AC not working","HVAC emergency","boiler problems"] },
    { name:"Plumbing",         icon:"🔧", color:"#0891b2", keywords:["pipe burst","no hot water","clogged drain","plumber needed","water leak"] },
    { name:"Electrical",       icon:"⚡", color:"#d97706", keywords:["electrician needed","circuit breaker","outlets not working","EV charger install"] },
    { name:"Roofing",          icon:"🏠", color:"#059669", keywords:["roof leak","storm damage","hail damage","missing shingles"] },
    { name:"General Contractor",icon:"🏗️",color:"#7c3aed", keywords:["contractor needed","home repair","renovation","basement finishing"] },
    { name:"Landscaping",      icon:"🌿", color:"#16a34a", keywords:["lawn care","landscaper","yard work","tree removal","weekly maintenance"] },
  ];

  const LOGOS = [
    "Peak Home Services","Metro Plumbing","PowerUp Electric","Storm Shield Roofing","BuildRight GC"
  ];

  const FAQS = [
    { q:"How does Socialisten find leads?", a:"We scan Facebook groups, Reddit communities, Nextdoor neighborhoods, listings 24/7 for posts that match your keywords. The moment someone posts 'my furnace broke' or 'need an HVAC guy tonight', you get an SMS alert usually within minutes of the post going live." },
    { q:"How is this different from buying leads?", a:"Lead services sell the same lead to 5–10 contractors, the leads are often days old, and they cost $20–80 each. Socialisten finds people actively posting right now. You're responding to a live request, not a stale database record. First responder wins and with SMS alerts, you can be responding within minutes." },
    { q:"Do I need a Facebook account?", a:"You need a Facebook account to monitor Facebook groups and Nextdoor. We recommend a dedicated monitoring account not your personal one. Reddit and Nextdoor work without any login at all." },
    { q:"Can I monitor multiple cities?", a:"Yes. Add one page per area the Detroit homeowners group, the Sterling Heights group, the Macomb County group. There's no geographic limit, only a per-plan page count limit." },
    { q:"How fast are the alerts?", a:"SMS alerts fire within seconds of a match being found. Scans run every 5–30 minutes depending on your plan. On the Pro plan with 15-minute scans, you'll typically see a lead within 2–3 minutes of it being posted." },
    { q:"Can I try it free?", a:"Yes the free plan includes 2 monitored pages, email alerts, and 60-minute scans. All paid plans start with a 7-day free trial. No credit card required to start." },
  ];

  const PLT_COLOR = { Facebook:"#1877F2", Nextdoor:"#00b362", Reddit:"#ff4500" };
  const scoreColor = (s) => s>=80?"#16a34a":s>=60?"#d97706":"#2563eb";

  return (
    <div style={{ fontFamily:"system-ui,-apple-system,sans-serif", color:"#111", background:"#fff", overflowX:"hidden" }}>

      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      <nav style={{ position:"sticky", top:0, zIndex:100, background:navSolid?"rgba(255,255,255,0.97)":"#fff", borderBottom:navSolid?"1px solid #e5e7eb":"1px solid #f3f4f6", padding:"0 24px", height:60, display:"flex", alignItems:"center", justifyContent:"space-between", backdropFilter:navSolid?"blur(8px)":"none", transition:"all 0.2s" }}>
        <div style={{ fontWeight:800, fontSize:18, color:"#111", letterSpacing:-0.5 }}>Socialisten</div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <button onClick={()=>goto("login")} style={{ padding:"7px 16px", borderRadius:7, border:"1px solid #e5e7eb", background:"transparent", color:"#374151", fontSize:13, cursor:"pointer", fontWeight:500 }}>Log in</button>
          <button onClick={()=>goto("register")} style={{ padding:"7px 18px", borderRadius:7, border:"none", background:"#2563eb", color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>Start free →</button>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <section style={{ padding:"80px 24px 64px", textAlign:"center", maxWidth:760, margin:"0 auto" }}>

        {/* Badge */}
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:20, padding:"5px 14px", marginBottom:28, fontSize:12, color:"#1d4ed8", fontWeight:600 }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:"#16a34a", display:"inline-block" }}/>
          Live alerts across Facebook, Reddit & Nextdoor
        </div>

        <h1 style={{ fontSize:"clamp(36px,5.5vw,64px)", fontWeight:800, lineHeight:1.08, marginBottom:22, letterSpacing:-1.5, color:"#0f172a" }}>
          Get notified the moment<br/>a homeowner needs your service
        </h1>

        <p style={{ fontSize:"clamp(16px,2vw,20px)", color:"#6b7280", lineHeight:1.7, maxWidth:560, margin:"0 auto 36px" }}>
          Socialisten monitors local Facebook groups, Reddit, Nextdoor, 24/7 and texts you within minutes when someone posts about needing your trade.
        </p>

        <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap", marginBottom:20 }}>
          <button onClick={()=>goto("register")} style={{ padding:"14px 32px", borderRadius:10, border:"none", background:"#2563eb", color:"#fff", fontSize:16, fontWeight:700, cursor:"pointer", boxShadow:"0 4px 16px rgba(37,99,235,0.3)" }}>
            Start free no card needed →
          </button>
          <button onClick={()=>goto("login")} style={{ padding:"14px 24px", borderRadius:10, border:"1px solid #e5e7eb", background:"transparent", color:"#374151", fontSize:15, cursor:"pointer" }}>
            Sign in
          </button>
        </div>

        <div style={{ display:"flex", gap:20, justifyContent:"center", flexWrap:"wrap", fontSize:13, color:"#9ca3af" }}>
          {["✓ 7-day free trial","✓ No credit card","✓ Cancel anytime","✓ Setup in 5 minutes"].map(t=>(
            <span key={t}>{t}</span>
          ))}
        </div>
      </section>

      {/* ── LIVE ALERT DEMO ─────────────────────────────────────────────── */}
      <section style={{ padding:"0 24px 80px", maxWidth:680, margin:"0 auto" }}>
        <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:16, padding:20, position:"relative", overflow:"hidden" }}>

          {/* Phone frame */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#16a34a", boxShadow:"0 0 8px #16a34a" }}/>
            <span style={{ fontSize:11, color:"#6b7280", fontWeight:600, letterSpacing:1, textTransform:"uppercase" }}>Live match feed</span>
            <span style={{ marginLeft:"auto", fontSize:11, color:"#9ca3af" }}>{DEMO_ALERTS[demoTick].age} ago</span>
          </div>

          {/* Alert card */}
          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderLeft:`4px solid ${PLT_COLOR[DEMO_ALERTS[demoTick].platform]}`, borderRadius:10, padding:"14px 16px", marginBottom:12, transition:"all 0.4s" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${PLT_COLOR[DEMO_ALERTS[demoTick].platform]}15`, color:PLT_COLOR[DEMO_ALERTS[demoTick].platform], fontWeight:700 }}>{DEMO_ALERTS[demoTick].platform}</span>
              <span style={{ color:"#9ca3af", fontSize:11 }}>{DEMO_ALERTS[demoTick].group}</span>
              <span style={{ marginLeft:"auto", fontSize:10, padding:"2px 8px", borderRadius:6, background:`${scoreColor(DEMO_ALERTS[demoTick].score)}15`, color:scoreColor(DEMO_ALERTS[demoTick].score), fontWeight:700 }}>Score {DEMO_ALERTS[demoTick].score}</span>
            </div>
            <p style={{ color:"#374151", fontSize:13, lineHeight:1.65, margin:"0 0 10px" }}>{DEMO_ALERTS[demoTick].text}</p>
            <div style={{ display:"flex", gap:8 }}>
              <button style={{ padding:"5px 14px", borderRadius:6, border:"none", background:"#2563eb", color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer" }}>Reply now</button>
              <button style={{ padding:"5px 14px", borderRadius:6, border:"1px solid #e5e7eb", background:"transparent", color:"#6b7280", fontSize:11, cursor:"pointer" }}>View post</button>
            </div>
          </div>

          {/* SMS alert mockup */}
          <div style={{ background:"#e8f5e9", borderRadius:10, padding:"10px 14px", display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:18, flexShrink:0 }}>📱</span>
            <div>
              <div style={{ color:"#374151", fontSize:12, fontWeight:600, marginBottom:2 }}>Text from Socialisten</div>
              <div style={{ color:"#374151", fontSize:12, lineHeight:1.6 }}>
                🎯 Score {DEMO_ALERTS[demoTick].score} <strong>{DEMO_ALERTS[demoTick].trade}</strong> lead in {DEMO_ALERTS[demoTick].group.split(" ").slice(0,2).join(" ")}. "{DEMO_ALERTS[demoTick].text.slice(0,55)}…" Reply fast →
              </div>
            </div>
          </div>

          {/* Dot indicators */}
          <div style={{ display:"flex", gap:4, justifyContent:"center", marginTop:14 }}>
            {DEMO_ALERTS.map((_,i)=>(
              <div key={i} onClick={()=>setDemoTick(i)} style={{ width:i===demoTick?20:6, height:6, borderRadius:3, background:i===demoTick?"#2563eb":"#cbd5e1", cursor:"pointer", transition:"all 0.3s" }}/>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOGO BAR ────────────────────────────────────────────────────── */}
      <section style={{ padding:"0 24px 72px", textAlign:"center" }}>
        <div style={{ color:"#9ca3af", fontSize:12, fontWeight:600, letterSpacing:1, textTransform:"uppercase", marginBottom:24 }}>Trusted by contractors across the country</div>
        <div style={{ display:"flex", gap:32, justifyContent:"center", flexWrap:"wrap", alignItems:"center" }}>
          {LOGOS.map(name=>(
            <div key={name} style={{ color:"#d1d5db", fontWeight:700, fontSize:14, letterSpacing:-0.3 }}>{name}</div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────────────────── */}
      <section style={{ padding:"80px 24px", background:"#f8fafc", borderTop:"1px solid #e2e8f0", borderBottom:"1px solid #e2e8f0" }}>
        <div style={{ maxWidth:820, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:60 }}>
            <div style={{ color:"#2563eb", fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>How it works</div>
            <h2 style={{ fontSize:"clamp(26px,4vw,42px)", fontWeight:800, letterSpacing:-0.5, color:"#0f172a" }}>From post to phone call in minutes</h2>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
            {[
              {
                n:"1",
                color:"#2563eb",
                title:"Tell us what to look for",
                body:"Add the Facebook groups, subreddits, Nextdoor feeds,  categories you want to monitor. Then set your keywords phrases like 'furnace broken', 'need a plumber tonight', or 'looking for a roofer'. Done in under 5 minutes.",
                detail:"We scan every new post as it appears. Most users see their first match within 10 minutes of setup.",
              },
              {
                n:"2",
                color:"#7c3aed",
                title:"We watch 24/7 you don't have to",
                body:"Socialisten scans continuously across all four platforms Facebook, Reddit, and Nextdoor. Every new post is checked against your keywords the moment it appears. No browser open. No checking feeds. Just results.",
                detail:"Scans run every 5–30 minutes depending on your plan. The Pro plan runs every 15 minutes.",
              },
              {
                n:"3",
                color:"#16a34a",
                title:"You get a text. You respond first.",
                body:"The second a match is found, you get an SMS with the post text, the score, and a link to respond. Most homeowners hire whoever reaches out first. With Socialisten, that's you while competitors are still refreshing their feeds manually.",
                detail:"Contractors using Socialisten typically respond within 3–5 minutes of a post going up.",
              },
            ].map((step, i) => (
              <div key={i} style={{ display:"grid", gridTemplateColumns:"80px 1fr", gap:24, paddingBottom:i<2?48:0, marginBottom:i<2?48:0, borderBottom:i<2?"1px solid #e2e8f0":"none" }}>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                  <div style={{ width:52, height:52, borderRadius:14, background:`${step.color}15`, border:`2px solid ${step.color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:800, color:step.color }}>
                    {step.n}
                  </div>
                  {i < 2 && <div style={{ width:2, flex:1, background:"#e2e8f0", marginTop:12 }}/>}
                </div>
                <div style={{ paddingTop:10 }}>
                  <div style={{ fontSize:20, fontWeight:700, color:"#0f172a", marginBottom:10 }}>{step.title}</div>
                  <div style={{ color:"#374151", fontSize:15, lineHeight:1.8, marginBottom:10 }}>{step.body}</div>
                  <div style={{ color:"#6b7280", fontSize:13, lineHeight:1.7, padding:"10px 14px", background:"#f1f5f9", borderRadius:8, borderLeft:`3px solid ${step.color}` }}>
                    💡 {step.detail}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRADE TABS (use cases) ────────────────────────────────────── */}
      <section style={{ padding:"80px 24px" }}>
        <div style={{ maxWidth:900, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <div style={{ color:"#2563eb", fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Built for every trade</div>
            <h2 style={{ fontSize:"clamp(24px,4vw,40px)", fontWeight:800, letterSpacing:-0.5, color:"#0f172a" }}>Your trade. Your keywords. Your leads.</h2>
          </div>

          <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap", marginBottom:36 }}>
            {TRADES.map((t,i)=>(
              <button key={i} onClick={()=>setActiveTrade(i)} style={{ padding:"8px 16px", borderRadius:20, border:`1px solid ${activeTrade===i?t.color:"#e5e7eb"}`, background:activeTrade===i?`${t.color}10`:"#fff", color:activeTrade===i?t.color:"#6b7280", fontSize:13, fontWeight:activeTrade===i?600:400, cursor:"pointer", display:"flex", alignItems:"center", gap:6, transition:"all 0.15s" }}>
                {t.icon} {t.name}
              </button>
            ))}
          </div>

          <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:16, padding:"28px 32px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:32 }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                <span style={{ fontSize:32 }}>{TRADES[activeTrade].icon}</span>
                <div style={{ fontWeight:700, fontSize:20, color:"#0f172a" }}>{TRADES[activeTrade].name}</div>
              </div>
              <div style={{ color:"#374151", fontSize:14, lineHeight:1.8, marginBottom:20 }}>
                Homeowners post about {TRADES[activeTrade].name.toLowerCase()} problems constantly in Facebook groups, on Reddit, on Nextdoor. Socialisten finds every post and alerts you before competitors even open their app.
              </div>
              <div style={{ marginBottom:16 }}>
                <div style={{ color:"#6b7280", fontSize:12, fontWeight:600, letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Keywords that find leads</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {TRADES[activeTrade].keywords.map(k=>(
                    <span key={k} style={{ padding:"4px 10px", borderRadius:6, background:`${TRADES[activeTrade].color}10`, border:`1px solid ${TRADES[activeTrade].color}20`, color:TRADES[activeTrade].color, fontSize:12, fontWeight:500 }}>{k}</span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {/* Example alert */}
              <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderLeft:`4px solid ${TRADES[activeTrade].color}`, borderRadius:10, padding:"14px 16px" }}>
                <div style={{ display:"flex", gap:6, marginBottom:6 }}>
                  <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#eff6ff", color:"#1d4ed8", fontWeight:700 }}>Facebook</span>
                  <span style={{ fontSize:10, color:"#9ca3af" }}>2m ago</span>
                  <span style={{ marginLeft:"auto", fontSize:10, padding:"2px 8px", borderRadius:6, background:"#f0fdf4", color:"#16a34a", fontWeight:700 }}>Score 87</span>
                </div>
                <div style={{ color:"#374151", fontSize:12, lineHeight:1.65 }}>
                  {activeTrade===0?"Furnace stopped working last night. House is 58°F with two kids. Need someone ASAP, happy to pay extra for same-day service.":
                   activeTrade===1?"Pipe burst under kitchen sink water everywhere. Anyone know an emergency plumber available today?":
                   activeTrade===2?"Circuit breaker keeps tripping every time I run two appliances. Electrician recommendations near me?":
                   activeTrade===3?"Storm last night ripped off a section of my roof. Water is coming in. Need a roofer today.":
                   activeTrade===4?"Looking to finish my basement 800 sq ft. Need a contractor for quotes. Budget around $40k.":
                   "Need a reliable landscaper for weekly maintenance on 1/3 acre property. Long-term contract available."}
                </div>
              </div>
              {/* SMS */}
              <div style={{ background:"#e8f5e9", borderRadius:10, padding:"12px 14px" }}>
                <div style={{ color:"#374151", fontSize:12, fontWeight:600, marginBottom:4 }}>📱 SMS alert sent</div>
                <div style={{ color:"#374151", fontSize:11, lineHeight:1.6 }}>Score 87 {TRADES[activeTrade].name} lead in Detroit Homeowners. Homeowner needs immediate help respond now →</div>
              </div>
              <button onClick={()=>goto("register")} style={{ padding:"10px", borderRadius:8, border:"none", background:TRADES[activeTrade].color, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                Start monitoring {TRADES[activeTrade].name} leads →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL ─────────────────────────────────────────────────── */}
      <section style={{ padding:"80px 24px", background:"#f8fafc", borderTop:"1px solid #e2e8f0" }}>
        <div style={{ maxWidth:760, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:40, alignItems:"center" }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ width:100, height:100, borderRadius:"50%", background:"linear-gradient(135deg,#2563eb,#7c3aed)", margin:"0 auto 16px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:36 }}>👷</div>
              <div style={{ fontWeight:700, fontSize:16, color:"#0f172a" }}>Marcus T.</div>
              <div style={{ color:"#6b7280", fontSize:13, marginTop:4 }}>Owner, Peak Home Services</div>
              <div style={{ color:"#6b7280", fontSize:12, marginTop:2 }}>Metro Detroit, MI</div>
              <div style={{ display:"flex", gap:2, justifyContent:"center", marginTop:8 }}>
                {[1,2,3,4,5].map(s=><span key={s} style={{ color:"#f59e0b", fontSize:18 }}>★</span>)}
              </div>
            </div>
            <div>
              <div style={{ fontSize:48, color:"#e5e7eb", lineHeight:1, marginBottom:8 }}>"</div>
              <p style={{ color:"#374151", fontSize:17, lineHeight:1.8, marginBottom:20, marginTop:-20 }}>
                I was skeptical at first I'd tried a lot of different lead sources and most of them were garbage. But within the first week I had 4 real leads from Facebook groups I never would have seen on my own. The SMS alerts are the key. I respond within 3 minutes of a post going up. Most of the time I'm the only one who calls.
              </p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
                {[["3–5","jobs/week from Socialisten"],["$0","extra ad spend"],["<5 min","average response time"]].map(([n,l])=>(
                  <div key={n} style={{ textAlign:"center", padding:"12px", background:"#fff", borderRadius:10, border:"1px solid #e2e8f0" }}>
                    <div style={{ fontWeight:800, fontSize:22, color:"#2563eb" }}>{n}</div>
                    <div style={{ color:"#6b7280", fontSize:11, marginTop:4, lineHeight:1.4 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY THIS WORKS ──────────────────────────────────────────────── */}
      <section style={{ padding:"80px 24px" }}>
        <div style={{ maxWidth:820, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <div style={{ color:"#2563eb", fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Why this works</div>
            <h2 style={{ fontSize:"clamp(24px,4vw,40px)", fontWeight:800, letterSpacing:-0.5, color:"#0f172a", marginBottom:16 }}>Most marketing hopes someone needs you.<br/>This finds them when they already do.</h2>
            <p style={{ color:"#6b7280", fontSize:16, lineHeight:1.7, maxWidth:580, margin:"0 auto" }}>
              The difference between a $0 lead and a $3,000 job is often just who responded first. Socialisten makes sure that's you.
            </p>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            {[
              { icon:"🎯", title:"High intent they're already looking", body:"These are people actively posting 'I need a contractor right now'. Not someone who might need you someday. The intent is maximum." },
              { icon:"⚡", title:"Speed wins the job", body:"Research shows the first contractor to respond wins the job 50%+ of the time. SMS alerts mean you can respond in minutes, not hours." },
              { icon:"🏆", title:"No competition (yet)", body:"Your competitors aren't watching these Facebook groups. Most contractors have no idea this channel exists. Right now, you'd be the only one showing up." },
              { icon:"💰", title:"No ad spend required", body:"These are organic posts from real homeowners. No bidding wars, no cost-per-click, no $80 lead fees. A flat monthly subscription covers unlimited alerts." },
            ].map((item,i)=>(
              <div key={i} style={{ padding:"24px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:14 }}>
                <div style={{ fontSize:28, marginBottom:12 }}>{item.icon}</div>
                <div style={{ fontWeight:700, fontSize:15, color:"#0f172a", marginBottom:8 }}>{item.title}</div>
                <div style={{ color:"#6b7280", fontSize:13, lineHeight:1.7 }}>{item.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────────────────── */}
      <section style={{ padding:"80px 24px", background:"#f8fafc", borderTop:"1px solid #e2e8f0" }}>
        <div style={{ maxWidth:760, margin:"0 auto", textAlign:"center" }}>
          <div style={{ color:"#2563eb", fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Pricing</div>
          <h2 style={{ fontSize:"clamp(24px,4vw,40px)", fontWeight:800, letterSpacing:-0.5, color:"#0f172a", marginBottom:12 }}>Start free. Scale when it works.</h2>
          <p style={{ color:"#6b7280", fontSize:15, marginBottom:48 }}>7-day free trial on all paid plans. No credit card required to start.</p>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:20 }}>
            {[
              { name:"Free",    price:"$0",  period:"forever",    color:"#6b7280", pages:2,   interval:"60 min", alerts:"Email only",         cta:"Get started",      highlight:false },
              { name:"Starter", price:"$59", period:"per month",  color:"#2563eb", pages:15,  interval:"30 min", alerts:"Email + SMS",         cta:"Start free trial", highlight:false },
              { name:"Pro",     price:"$89", period:"per month",  color:"#7c3aed", pages:30,  interval:"15 min", alerts:"Email & SMS",  cta:"Start free trial", highlight:true  },
            ].map((plan,i)=>(
              <div key={i} style={{ background:plan.highlight?"#1e40af":"#fff", border:`2px solid ${plan.highlight?"#2563eb":"#e2e8f0"}`, borderRadius:14, padding:"28px 24px", position:"relative", textAlign:"left" }}>
                {plan.highlight&&<div style={{ position:"absolute", top:-12, left:"50%", transform:"translateX(-50%)", background:"#2563eb", color:"#fff", fontSize:10, fontWeight:700, padding:"3px 12px", borderRadius:10, letterSpacing:1, whiteSpace:"nowrap" }}>MOST POPULAR</div>}
                <div style={{ color:plan.highlight?"#93c5fd":"#6b7280", fontWeight:700, fontSize:11, letterSpacing:1, marginBottom:10 }}>{plan.name.toUpperCase()}</div>
                <div style={{ fontSize:34, fontWeight:800, color:plan.highlight?"#fff":"#0f172a", marginBottom:2 }}>{plan.price}</div>
                <div style={{ color:plan.highlight?"#93c5fd":"#9ca3af", fontSize:12, marginBottom:20 }}>{plan.period}</div>
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:24 }}>
                  {[`📡 ${plan.pages} pages`,`⏱ ${plan.interval} scans`,`🔔 ${plan.alerts}`].map(f=>(
                    <div key={f} style={{ color:plan.highlight?"#bfdbfe":"#374151", fontSize:12 }}>{f}</div>
                  ))}
                </div>
                <button onClick={()=>goto("register")} style={{ width:"100%", padding:"10px", borderRadius:8, border:plan.highlight?"none":`1px solid ${plan.color}`, background:plan.highlight?"#3b82f6":"transparent", color:plan.highlight?"#fff":plan.color, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
          <div style={{ color:"#9ca3af", fontSize:12 }}>All plans include the full dashboard, lead pipeline, and analytics. Upgrade or cancel anytime.</div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section style={{ padding:"80px 24px" }}>
        <div style={{ maxWidth:680, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <div style={{ color:"#2563eb", fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>FAQ</div>
            <h2 style={{ fontSize:"clamp(24px,4vw,38px)", fontWeight:800, letterSpacing:-0.5, color:"#0f172a" }}>Common questions</h2>
          </div>
          {FAQS.map((faq,i)=>(
            <div key={i} style={{ borderBottom:"1px solid #e5e7eb" }}>
              <div onClick={()=>setActiveFaq(activeFaq===i?null:i)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 0", cursor:"pointer", gap:16 }}>
                <div style={{ fontWeight:600, fontSize:15, color:"#0f172a", lineHeight:1.4 }}>{faq.q}</div>
                <div style={{ width:28, height:28, borderRadius:"50%", background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", color:"#2563eb", fontSize:18, flexShrink:0, transition:"transform 0.2s", transform:activeFaq===i?"rotate(45deg)":"none" }}>+</div>
              </div>
              {activeFaq===i&&<div style={{ color:"#6b7280", fontSize:14, lineHeight:1.8, paddingBottom:20 }}>{faq.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────────────────── */}
      <section style={{ padding:"80px 24px", background:"#0f172a", textAlign:"center" }}>
        <div style={{ maxWidth:600, margin:"0 auto" }}>
          <h2 style={{ fontSize:"clamp(26px,4vw,42px)", fontWeight:800, color:"#fff", letterSpacing:-0.5, marginBottom:16 }}>
            Someone just posted asking for a contractor in your area.
          </h2>
          <p style={{ color:"#94a3b8", fontSize:16, lineHeight:1.7, marginBottom:36 }}>
            Are you going to see it? Or are you going to find out you missed it when they post a 5-star review for someone else?
          </p>
          <button onClick={()=>goto("register")} style={{ padding:"16px 40px", borderRadius:12, border:"none", background:"#2563eb", color:"#fff", fontSize:17, fontWeight:700, cursor:"pointer", boxShadow:"0 4px 24px rgba(37,99,235,0.5)", display:"inline-block" }}>
            Start monitoring free →
          </button>
          <div style={{ marginTop:14, color:"#64748b", fontSize:13 }}>No credit card. 5 minutes to setup. First leads same day.</div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer style={{ padding:"36px 24px", background:"#0f172a", borderTop:"1px solid #1e293b" }}>
        <div style={{ maxWidth:900, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
          <div>
            <div style={{ fontWeight:800, fontSize:16, color:"#fff" }}>Socialisten</div>
            <div style={{ color:"#64748b", fontSize:12, marginTop:4 }}>Real-time lead monitoring Facebook, Reddit & Nextdoor</div>
          </div>
          <div style={{ display:"flex", gap:20 }}>
            {["Privacy Policy","Terms of Service","Support"].map(l=>(
              <span key={l} style={{ color:"#64748b", fontSize:12, cursor:"pointer" }}>{l}</span>
            ))}
          </div>
        </div>
      </footer>

    </div>
  );
}


function CacheStatsPanel({ api, toast }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/cache-stats")
      .then(setStats).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div style={{display:"flex",justifyContent:"center",padding:40}}><Spinner size={24}/></div>;
  if (!stats) return null;

  const savingsPct = stats.cached_urls > 0
    ? Math.round((1 - stats.cached_urls / Math.max(stats.cached_urls, 1)) * 100)
    : 0;

  return (
    <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ color:"#fff", fontSize:14, fontWeight:700 }}>🗄️ Shared Scan Cache</div>
        <button onClick={load} style={{ padding:"4px 12px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, cursor:"pointer" }}>Refresh</button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
        {[
          { label:"Cached URLs",  value:stats.cached_urls,  color:"#6366f1", tip:"Unique URLs currently in Redis cache" },
          { label:"Cached Posts", value:stats.total_posts,  color:"#22c55e", tip:"Total posts stored across all cached URLs" },
          { label:"Cache TTL",    value:`${Math.round(stats.cache_ttl_s/60)}min`, color:"#f59e0b", tip:"How long posts stay cached before expiry" },
          { label:"Redis Status", value:stats.redis_ok?"Online":"Error", color:stats.redis_ok?"#22c55e":"#ff4d4d", tip:"Redis connection status" },
        ].map((m,i)=>(
          <div key={i} style={{ background:"#0a0a0f", border:`1px solid ${m.color}20`, borderRadius:9, padding:"12px 14px" }} title={m.tip}>
            <div style={{ color:m.color, fontSize:20, fontWeight:800 }}>{m.value}</div>
            <div style={{ color:"#4a4a6a", fontSize:10, marginTop:3, fontFamily:"'DM Mono',monospace" }}>{m.label}</div>
          </div>
        ))}
      </div>

      <div style={{ color:"#4a4a6a", fontSize:11, marginBottom:12 }}>
        How it works: when multiple users monitor the same URL, the scraper fetches it
        <strong style={{ color:"#a5b4fc" }}> once</strong> and distributes results to all. Each user still gets their own keyword-filtered matches and alerts.
      </div>

      {stats.entries?.length > 0 && (
        <div>
          <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, marginBottom:8 }}>CACHED ENTRIES (top {stats.entries.length})</div>
          <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:220, overflowY:"auto" }}>
            {stats.entries.map((e,i)=>(
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 10px", background:"#0a0a0f", borderRadius:6, fontSize:11 }}>
                <span style={{ padding:"1px 7px", borderRadius:4, background: e.platform==="facebook"?"#1877F220":e.platform==="nextdoor"?"#00b36220":e.platform==="reddit"?"#ff450020":"#7c3aed20", color:e.platform==="facebook"?"#1877F2":e.platform==="nextdoor"?"#00b362":e.platform==="reddit"?"#ff4500":"#7c3aed", fontSize:9, fontWeight:700, flexShrink:0 }}>{e.platform}</span>
                <span style={{ color:"#555577", fontSize:10, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e.url}</span>
                <span style={{ color:"#22c55e", fontSize:10, flexShrink:0 }}>{e.count} posts</span>
                <span style={{ color:"#3a3a5a", fontSize:10, flexShrink:0 }}>{e.age_s}s old</span>
                <div style={{ width:40, height:4, background:"#1e1e2e", borderRadius:2, flexShrink:0 }}>
                  <div style={{ width:`${Math.min(100, (e.ttl_s/(stats.cache_ttl_s||1))*100)}%`, height:"100%", background:"#22c55e", borderRadius:2 }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// ════════════════════════════════════════════════════════════════════════════
// KEYWORD SUGGESTIONS — shown when adding/editing a page
// Pre-built keyword sets by trade, click to add
// ════════════════════════════════════════════════════════════════════════════
const KEYWORD_PRESETS = {
  "HVAC": {
    icon: "🌡️",
    sets: [
      { label: "Emergency / No heat",   kws: ["no heat","furnace broken","furnace not working","boiler broken","heating not working","house is cold"] },
      { label: "AC / Cooling",           kws: ["ac not working","air conditioner broken","no cold air","AC broke","cooling system","heat pump broken"] },
      { label: "Service / Install",      kws: ["HVAC repair","HVAC company","furnace replacement","AC installation","HVAC contractor","need hvac guy"] },
      { label: "Urgency signals",        kws: ["emergency heat","same day hvac","hvac emergency","urgent furnace","tonight heat"] },
    ],
  },
  "Plumbing": {
    icon: "🔧",
    sets: [
      { label: "Emergency",             kws: ["pipe burst","water leak","flooding","emergency plumber","water everywhere","shut off water"] },
      { label: "No hot water",          kws: ["no hot water","water heater broken","water heater not working","cold shower","hot water out"] },
      { label: "Drains / Clogs",        kws: ["clogged drain","drain not working","toilet clogged","backed up","slow drain","blocked pipe"] },
      { label: "General plumbing",      kws: ["plumber needed","plumbing repair","plumbing contractor","need a plumber","plumbing issue"] },
    ],
  },
  "Electrical": {
    icon: "⚡",
    sets: [
      { label: "Outage / Emergency",    kws: ["power out","no electricity","circuit breaker tripping","electrical emergency","power flickering"] },
      { label: "Outlets / Wiring",      kws: ["outlets not working","wiring issue","electrical problem","sparks","burning smell electric"] },
      { label: "Upgrades / Install",    kws: ["EV charger install","panel upgrade","electrical panel","generator install","smart home wiring"] },
      { label: "General",              kws: ["electrician needed","licensed electrician","electrical contractor","need an electrician"] },
    ],
  },
  "Roofing": {
    icon: "🏠",
    sets: [
      { label: "Leaks / Damage",        kws: ["roof leak","water coming in","ceiling leaking","roof damage","storm damage roof","hail damage"] },
      { label: "Missing / Damaged",     kws: ["missing shingles","shingles blown off","flat roof repair","roof repair","damaged shingles"] },
      { label: "Replacement / New",     kws: ["roof replacement","new roof","roof installation","roofing contractor","reroofing"] },
      { label: "Inspection",            kws: ["roof inspection","roofing quote","roof estimate","roof assessment"] },
    ],
  },
  "General Contractor": {
    icon: "🏗️",
    sets: [
      { label: "Renovations",           kws: ["basement finishing","kitchen remodel","bathroom remodel","home renovation","room addition"] },
      { label: "Repairs",               kws: ["home repair","handyman","drywall repair","deck repair","fence repair","siding repair"] },
      { label: "Finding a contractor",  kws: ["contractor needed","general contractor","getting quotes","reliable contractor","contractor recommendation"] },
      { label: "Large projects",        kws: ["home addition","garage build","ADU","granny flat","full renovation"] },
    ],
  },
  "Landscaping": {
    icon: "🌿",
    sets: [
      { label: "Lawn care",             kws: ["lawn mowing","grass cutting","lawn maintenance","lawn care","weekly mowing","lawn service"] },
      { label: "Trees",                 kws: ["tree removal","tree trimming","tree fell","tree service","stump removal","dead tree"] },
      { label: "Design / Install",      kws: ["landscaping","landscape design","sod installation","garden design","sprinkler install"] },
      { label: "General",              kws: ["landscaper needed","landscaping contractor","yard work","outdoor maintenance"] },
    ],
  },
  "Pest Control": {
    icon: "🐛",
    sets: [
      { label: "Insects",               kws: ["termites","bed bugs","cockroaches","ants infestation","wasp nest","bee removal","spider problem"] },
      { label: "Rodents",               kws: ["mice","rats","rodent problem","squirrels in attic","animal in walls","raccoon removal"] },
      { label: "General",              kws: ["pest control","exterminator","pest infestation","bug problem","pest company"] },
    ],
  },
  "Painting": {
    icon: "🎨",
    sets: [
      { label: "Interior",              kws: ["interior painting","paint rooms","wall painting","house painting inside","interior painter"] },
      { label: "Exterior",              kws: ["exterior painting","house painting outside","paint house","exterior painter","paint siding"] },
      { label: "General",              kws: ["painter needed","painting contractor","painting quote","affordable painter","professional painter"] },
    ],
  },
};

function KeywordSuggestionsPanel({ currentKeywords, onAdd, platform }) {
  const [open,        setOpen]        = useState(false);
  const [activeTrade, setActiveTrade] = useState(Object.keys(KEYWORD_PRESETS)[0]);
  const [added,       setAdded]       = useState(new Set());

  const addKw = (kw) => {
    if (!currentKeywords.includes(kw)) {
      onAdd(kw);
      setAdded(p => new Set([...p, kw]));
    }
  };

  const addAll = (kws) => {
    kws.forEach(kw => { if (!currentKeywords.includes(kw)) { onAdd(kw); setAdded(p => new Set([...p, kw])); } });
  };

  const preset = KEYWORD_PRESETS[activeTrade];

  return (
    <div style={{ marginTop:8 }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:6, border:"1px dashed #2a2a3e", background:"transparent", color:"#6366f1", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}
      >
        <span>{open ? "▲" : "▼"}</span> Keyword suggestions by trade
      </button>

      {open && (
        <div style={{ marginTop:10, background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:10, padding:16 }}>
          {/* Trade tabs */}
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
            {Object.entries(KEYWORD_PRESETS).map(([trade, cfg]) => (
              <button key={trade} onClick={() => setActiveTrade(trade)} style={{ padding:"5px 10px", borderRadius:5, border:`1px solid ${activeTrade===trade?"#6366f1":"#1e1e2e"}`, background:activeTrade===trade?"#6366f120":"transparent", color:activeTrade===trade?"#a5b4fc":"#4a4a6a", fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                {cfg.icon} {trade}
              </button>
            ))}
          </div>

          {/* Keyword sets for active trade */}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {preset.sets.map((set, i) => (
              <div key={i}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1 }}>{set.label.toUpperCase()}</div>
                  <button onClick={() => addAll(set.kws)} style={{ padding:"2px 10px", borderRadius:4, border:"1px solid #2a2a3e", background:"transparent", color:"#6366f1", fontSize:10, cursor:"pointer" }}>+ Add all</button>
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                  {set.kws.map(kw => {
                    const isAdded    = added.has(kw) || currentKeywords.includes(kw);
                    return (
                      <button key={kw} onClick={() => addKw(kw)} disabled={isAdded} style={{ padding:"3px 10px", borderRadius:5, border:`1px solid ${isAdded?"#22c55e30":"#2a2a3e"}`, background:isAdded?"#22c55e10":"transparent", color:isAdded?"#22c55e":"#8888aa", fontSize:11, cursor:isAdded?"default":"pointer", transition:"all 0.15s" }}>
                        {isAdded ? "✓ " : "+ "}{kw}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// ONBOARDING CHECKLIST
// Shown to new users until all steps are complete, then permanently hidden.
// Stored in localStorage so it persists across sessions without a DB call.
// Each step is a deep-link to the right place in the app.
// ════════════════════════════════════════════════════════════════════════════
function OnboardingChecklistSimple({ pages, matches, alertSettings, onNavigate }) {
  const LS_KEY = "sl_onboarding_dismissed";

  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
  });
  const [minimised, setMinimised] = useState(false);

  const hasPage     = pages && pages.length > 0;
  const hasKeywords = pages && pages.some(p => (p.keywords || []).length > 0);
  const hasScanned  = pages && pages.some(p => p.last_checked);
  const hasPhone    = alertSettings && (alertSettings.phone || alertSettings.sms_enabled);
  const hasAlerts   = false; // push/telegram checked client-side via localStorage after enable
  const hasMatch    = matches && matches > 0;

  const STEPS = [
    {
      id:      "add_page",
      label:   "Add your first monitored page",
      desc:    "Add a Facebook group, subreddit, or Nextdoor feed to monitor",
      done:    hasPage,
      action:  () => onNavigate("pages"),
      cta:     "Add a page →",
    },
    {
      id:      "add_keywords",
      label:   "Add keywords",
      desc:    "Type phrases homeowners use when they need your service",
      done:    hasKeywords,
      action:  () => onNavigate("pages"),
      cta:     "Set keywords →",
    },
    {
      id:      "first_scan",
      label:   "Run your first scan",
      desc:    "Click Scan Now on any monitored page to see your first results",
      done:    hasScanned,
      action:  () => onNavigate("pages"),
      cta:     "Go to pages →",
    },
    {
      id:      "add_alerts",
      label:   "Enable instant alerts (push or Telegram)",
      desc:    "Responding within minutes wins the job — email is too slow. Push and Telegram are free.",
      done:    hasPhone || hasAlerts,
      action:  () => onNavigate("settings"),
      cta:     "Set up alerts →",
    },
    {
      id:      "first_match",
      label:   "See your first match",
      desc:    "Your first keyword match will appear in the Feed",
      done:    hasMatch,
      action:  () => onNavigate("feed"),
      cta:     "View feed →",
    },
  ];

  const completed = STEPS.filter(s => s.done).length;
  const allDone   = completed === STEPS.length;

  // Auto-dismiss once all steps complete
  if (allDone && !dismissed) {
    localStorage.setItem(LS_KEY, "1");
    setDismissed(true);
  }

  if (dismissed) return null;

  const pct = Math.round((completed / STEPS.length) * 100);

  return (
    <div style={{ background:"#0a0a1a", border:"1px solid #6366f130", borderRadius:14, padding:"18px 20px", marginBottom:20 }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:minimised?0:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:"#6366f120", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🚀</div>
          <div>
            <div style={{ color:"#fff", fontSize:13, fontWeight:700 }}>Get started — {completed}/{STEPS.length} complete</div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4 }}>
              <div style={{ width:100, height:4, background:"#1e1e2e", borderRadius:2, overflow:"hidden" }}>
                <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#6366f1,#22c55e)", borderRadius:2, transition:"width 0.5s" }}/>
              </div>
              <span style={{ color:"#4a4a6a", fontSize:11 }}>{pct}%</span>
            </div>
          </div>
        </div>
        <div style={{ display:"flex", gap:6 }}>
          <button onClick={() => setMinimised(p=>!p)} style={{ padding:"3px 10px", borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>
            {minimised ? "Show" : "Hide"}
          </button>
          <button onClick={() => { localStorage.setItem(LS_KEY,"1"); setDismissed(true); }} style={{ padding:"3px 10px", borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>
            Dismiss
          </button>
        </div>
      </div>

      {!minimised && (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {STEPS.map((step, i) => (
            <div key={step.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 12px", background:step.done?"#0a1a0a":"#0f0f1a", borderRadius:8, border:`1px solid ${step.done?"#22c55e20":"#1e1e2e"}` }}>
              {/* Checkbox */}
              <div style={{ width:22, height:22, borderRadius:"50%", background:step.done?"#22c55e":"#1e1e2e", border:`2px solid ${step.done?"#22c55e":"#2a2a3e"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:12, color:"#fff" }}>
                {step.done ? "✓" : <span style={{ color:"#3a3a5a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>{i+1}</span>}
              </div>
              {/* Text */}
              <div style={{ flex:1 }}>
                <div style={{ color:step.done?"#4a6a4a":"#ccc", fontSize:12, fontWeight:600, textDecoration:step.done?"line-through":"none" }}>{step.label}</div>
                {!step.done && <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>{step.desc}</div>}
              </div>
              {/* CTA */}
              {!step.done && (
                <button onClick={step.action} style={{ padding:"5px 12px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                  {step.cta}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PUSH NOTIFICATION SETTINGS — Improvement #6
// Registers service worker, requests permission, saves subscription to server
// ════════════════════════════════════════════════════════════════════════════
function PushNotificationSettings({ api, toast }) {
  const [supported,    setSupported]    = useState(false);
  const [permission,   setPermission]   = useState("default");
  const [subscribed,   setSubscribed]   = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [toggling,     setToggling]     = useState(false);
  const [vapidKey,     setVapidKey]     = useState("");

  useEffect(() => {
    const sw = "serviceWorker" in navigator && "PushManager" in window;
    setSupported(sw);
    if (!sw) { setLoading(false); return; }
    setPermission(Notification.permission);

    // Get VAPID public key
    api.get("/push/vapid-public-key").then(d => {
      if (d.enabled) setVapidKey(d.public_key);
    }).catch(() => {});

    // Check if already subscribed
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        setSubscribed(!!sub);
        setLoading(false);
      });
    }).catch(() => setLoading(false));
  }, []);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64  = (base64String + padding).replace(/-/g,"+").replace(/_/g,"/");
    const raw     = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  };

  const enable = async () => {
    if (!vapidKey) { toast.error("Push notifications not configured on server (VAPID keys missing)"); return; }
    setToggling(true);
    try {
      // Register service worker
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") { toast.error("Permission denied — enable notifications in browser settings"); return; }

      // Subscribe
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint:   subJson.endpoint,
        keys:       subJson.keys,
        user_agent: navigator.userAgent.slice(0, 200),
      });

      setSubscribed(true);
      toast.success("Push notifications enabled ✓");
    } catch (e) {
      toast.error("Failed to enable push: " + e.message);
    } finally {
      setToggling(false);
    }
  };

  const disable = async () => {
    setToggling(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Push notifications disabled");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setToggling(false);
    }
  };

  const test = async () => {
    if (Notification.permission === "granted") {
      new Notification("Socialisten Test", {
        body: "🎯 Score 87 — Test lead in Detroit Homeowners. This is what your alerts look like.",
        icon: "/icon-192.png",
      });
    }
  };

  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 };

  return (
    <div style={cardS}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
        <div style={{ width:36, height:36, borderRadius:9, background:"#6366f120", border:"1px solid #6366f130", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>🔔</div>
        <div style={{ flex:1 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>Browser Push Notifications</div>
          <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>Instant alerts in your browser — no Twilio setup needed. Works on desktop and Android Chrome.</div>
        </div>
        {loading ? <Spinner size={14}/> : (
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:subscribed?"#22c55e":"#2a2a3e" }}/>
            <span style={{ color:subscribed?"#22c55e":"#4a4a6a", fontSize:11 }}>{subscribed?"Active":"Off"}</span>
          </div>
        )}
      </div>

      {!supported && (
        <div style={{ background:"#1a0808", border:"1px solid #ff4d4d20", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#8a4a4a" }}>
          ⚠️ Your browser doesn't support push notifications. Use Chrome or Firefox on desktop, or Chrome on Android.
        </div>
      )}

      {supported && !vapidKey && (
        <div style={{ background:"#1a1208", border:"1px solid #f59e0b20", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#8a6a3a" }}>
          ⚠️ Push notifications require VAPID keys to be configured on the server. Ask your admin to set VAPID_PUBLIC_KEY, VAPID_PRIVATE_KEY, and VAPID_CLAIMS_EMAIL in the environment.
        </div>
      )}

      {supported && vapidKey && (
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {!subscribed ? (
            <button onClick={enable} disabled={toggling} style={{ padding:"8px 18px", borderRadius:7, border:"none", background:toggling?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:toggling?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {toggling ? <><Spinner size={11}/>Enabling…</> : "Enable push notifications"}
            </button>
          ) : (
            <>
              <button onClick={test} style={{ padding:"8px 14px", borderRadius:7, border:"1px solid #2a2a3e", background:"transparent", color:"#a5b4fc", fontSize:12, cursor:"pointer" }}>
                Send test notification
              </button>
              <button onClick={disable} disabled={toggling} style={{ padding:"8px 14px", borderRadius:7, border:"1px solid #ff4d4d30", background:"transparent", color:"#ff4d4d", fontSize:12, cursor:"pointer" }}>
                {toggling ? "Disabling…" : "Disable"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SCORE THRESHOLD SUGGESTION — Improvement #7
// Shows in Settings next to the threshold slider — only visible when enough
// data exists and the suggested threshold differs from current by 10+
// ════════════════════════════════════════════════════════════════════════════
function ThresholdSuggestion({ api, currentThreshold, onApply }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    api.get("/settings/threshold-suggestion")
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  if (!data.suggestion) return null;
  // Only show if suggestion differs from current by 10+
  if (Math.abs(data.suggestion - currentThreshold) < 10) return null;
  if (applied) return null;

  return (
    <div style={{ marginTop:10, padding:"12px 16px", background:"#0a1a0a", border:"1px solid #22c55e30", borderRadius:9 }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
        <span style={{ fontSize:20, flexShrink:0 }}>💡</span>
        <div style={{ flex:1 }}>
          <div style={{ color:"#22c55e", fontSize:12, fontWeight:600, marginBottom:4 }}>Suggested threshold: {data.suggestion}</div>
          <div style={{ color:"#4a6a4a", fontSize:11, lineHeight:1.7, marginBottom:8 }}>{data.reason}</div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", fontSize:10, color:"#3a5a3a", marginBottom:10 }}>
            <span>📊 {data.total} matches analysed</span>
            <span>·</span>
            <span>✓ {data.engaged_count} booked/contacted</span>
            <span>·</span>
            <span>Avg engaged score: {data.avg_engaged}</span>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => { onApply(data.suggestion); setApplied(true); }} style={{ padding:"5px 14px", borderRadius:6, border:"none", background:"#22c55e", color:"#fff", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
              Apply ({data.suggestion}) →
            </button>
            <button onClick={() => setApplied(true)} style={{ padding:"5px 10px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>Dismiss</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// A/B TEST HOOK — Improvement #10
// Assigns visitors to variants and tracks which CTA copy performs better.
// Variant is assigned once per browser and stored in localStorage.
// Results visible in Admin Panel → Site Editor → A/B Tests
// ════════════════════════════════════════════════════════════════════════════
function useABTest(testId, variants) {
  // Each variant: { id, weight (0-100), value }
  // Returns the value for this visitor's variant
  const LS_KEY = `sl_ab_${testId}`;

  const [variant, setVariant] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored) {
        const v = variants.find(v => v.id === stored);
        if (v) return v;
      }
    } catch { /* ignore */ }

    // Assign a variant based on weights
    const rand = Math.random() * 100;
    let cumulative = 0;
    for (const v of variants) {
      cumulative += v.weight || (100 / variants.length);
      if (rand <= cumulative) {
        try { localStorage.setItem(LS_KEY, v.id); } catch { /* ignore */ }
        return v;
      }
    }
    return variants[0];
  });

  // Track impression
  useEffect(() => {
    try {
      // Fire and forget — non-blocking
      fetch(`/api/ab/impression?test=${testId}&variant=${variant.id}`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ test_id: testId, variant_id: variant.id }),
      }).catch(() => {});
    } catch { /* ignore */ }
  }, [testId, variant.id]);

  const trackConversion = () => {
    try {
      fetch(`/api/ab/conversion?test=${testId}&variant=${variant.id}`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ test_id: testId, variant_id: variant.id }),
      }).catch(() => {});
    } catch { /* ignore */ }
  };

  return { variant, trackConversion };
}


// ════════════════════════════════════════════════════════════════════════════
// PUSH NOTIFICATION SETTINGS
// Registers browser for Web Push, shows permission state
function ScoreThresholdAdvisor({ api, currentThreshold, onApply }) {
  const [suggestion, setSuggestion] = useState(null);
  const [dismissed,  setDismissed]  = useState(false);
  const [applying,   setApplying]   = useState(false);

  useEffect(() => {
    api.get("/settings/threshold-suggestion")
      .then(d => { if (d && d.suggested_threshold) setSuggestion(d); })
      .catch(() => {});
  }, []);

  if (!suggestion || dismissed) return null;
  const { suggested_threshold, reason, booked_avg, ignored_avg, sample_size } = suggestion;
  if (Math.abs(suggested_threshold - currentThreshold) < 5) return null; // already close enough

  const apply = async () => {
    setApplying(true);
    try {
      await onApply(suggested_threshold);
      setDismissed(true);
    } catch (e) {}
    setApplying(false);
  };

  return (
    <div style={{ background:"#0a1a0a", border:"1px solid #22c55e30", borderRadius:9, padding:"12px 16px", marginTop:10 }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
        <span style={{ fontSize:18, flexShrink:0 }}>💡</span>
        <div style={{ flex:1 }}>
          <div style={{ color:"#22c55e", fontSize:12, fontWeight:600, marginBottom:4 }}>
            Threshold suggestion (based on {sample_size} matches)
          </div>
          <div style={{ color:"#4a6a4a", fontSize:11, lineHeight:1.7, marginBottom:10 }}>
            Your booked leads average score <strong style={{ color:"#22c55e" }}>{booked_avg}</strong>.
            Ignored matches average <strong style={{ color:"#ff4d4d" }}>{ignored_avg}</strong>.
            Setting threshold to <strong style={{ color:"#22c55e" }}>{suggested_threshold}</strong> would filter out most noise while keeping the leads that convert.
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={apply} disabled={applying} style={{ padding:"5px 14px", borderRadius:5, border:"none", background:"#22c55e", color:"#fff", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
              {applying?"Applying…":`Apply ${suggested_threshold} →`}
            </button>
            <button onClick={()=>setDismissed(true)} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>
              Keep {currentThreshold}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// MATCH CLAIMING
// When a user marks a match as "contacted" or "booked", a small
// "claimed" badge appears on that match for all other users monitoring
// the same group URL — so they know not to chase the same lead.
// Stored as post_hash → {claimed_by_trade, claimed_at} in Redis (TTL 7 days).
// No PII shared — just trade and time.
// ════════════════════════════════════════════════════════════════════════════
function ClaimedBadge({ claimInfo }) {
  if (!claimInfo) return null;
  const age = claimInfo.claimed_at
    ? Math.round((Date.now() - new Date(claimInfo.claimed_at).getTime()) / 60000)
    : 0;
  const ageStr = age < 60 ? `${age}m ago` : `${Math.round(age/60)}h ago`;
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"2px 8px", borderRadius:5, background:"#ff4d4d10", border:"1px solid #ff4d4d20", fontSize:10, color:"#ff8888", fontFamily:"'DM Mono',monospace" }}>
      🏳 Claimed by {claimInfo.trade || "another contractor"} · {ageStr}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// TELEGRAM CONNECT SETTINGS
// Free alternative to Twilio SMS — works on iOS, Android, desktop, everywhere.
// User clicks a deep-link that opens Telegram and sends /start {token}.
// Bot receives it, links chat_id to their account. Done in ~10 seconds.
// ════════════════════════════════════════════════════════════════════════════
function TelegramConnect({ api, toast }) {
  const [status,    setStatus]    = useState(null);
  const [botInfo,   setBotInfo]   = useState(null);
  const [linking,   setLinking]   = useState(false);
  const [linkUrl,   setLinkUrl]   = useState("");
  const [polling,   setPolling]   = useState(false);
  const [loading,   setLoading]   = useState(true);

  const load = async () => {
    const [st, bi] = await Promise.all([
      api.get("/telegram/status").catch(()=>null),
      api.get("/telegram/bot-info").catch(()=>null),
    ]);
    setStatus(st);
    setBotInfo(bi);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startLink = async () => {
    setLinking(true);
    try {
      const r = await api.post("/telegram/link-token");
      setLinkUrl(r.start_url);
      // Open Telegram
      if (r.start_url) window.open(r.start_url, "_blank", "noopener,noreferrer");
      // Poll for connection every 3 seconds for up to 2 minutes
      setPolling(true);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        const st = await api.get("/telegram/status").catch(()=>null);
        if (st?.connected) {
          clearInterval(poll);
          setPolling(false);
          setLinking(false);
          setStatus(st);
          setLinkUrl("");
          toast.success("✅ Telegram connected! You'll receive lead alerts there.");
        }
        if (attempts >= 40) { // 2 min timeout
          clearInterval(poll);
          setPolling(false);
          setLinking(false);
        }
      }, 3000);
    } catch(e) {
      toast.error(e.message);
      setLinking(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/telegram/disconnect");
      setStatus({ connected: false });
      toast.success("Telegram disconnected");
    } catch(e) { toast.error(e.message); }
  };

  if (loading) return null;

  const isConnected = status?.connected;
  const botConfigured = botInfo?.configured;

  return (
    <div style={{ background:"#0f0f1a", border:`1px solid ${isConnected?"#22a5e330":"#1e1e2e"}`, borderRadius:12, padding:"18px 22px", marginBottom:14 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
        <div style={{ width:36, height:36, borderRadius:9, background:"#22a5e320", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>✈️</div>
        <div style={{ flex:1 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>Telegram Alerts
            <span style={{ marginLeft:8, fontSize:10, padding:"2px 7px", borderRadius:8, background:"#22c55e20", color:"#22c55e", border:"1px solid #22c55e30" }}>FREE</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:2 }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:isConnected?"#22c55e":"#4a4a6a" }}/>
            <span style={{ color:isConnected?"#22c55e":"#4a4a6a", fontSize:11 }}>
              {isConnected ? "Connected — receiving alerts" : "Not connected"}
            </span>
          </div>
        </div>
        {isConnected && (
          <button onClick={disconnect} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #ff4d4d30", background:"transparent", color:"#ff4d4d", fontSize:11, cursor:"pointer" }}>Disconnect</button>
        )}
      </div>

      {!botConfigured && (
        <div style={{ background:"#1a1208", border:"1px solid #f59e0b30", borderRadius:8, padding:"10px 14px", fontSize:11, color:"#8a6a3a", lineHeight:1.8 }}>
          ⚠️ Telegram bot not configured. Add your <code style={{ color:"#f59e0b" }}>TELEGRAM_BOT_TOKEN</code> in Admin → Integrations → Telegram.
        </div>
      )}

      {botConfigured && !isConnected && (
        <>
          <div style={{ color:"#4a4a6a", fontSize:12, marginBottom:12, lineHeight:1.7 }}>
            Get lead alerts on Telegram — works on iPhone, Android, and desktop. Free, instant, no phone number needed. Tap the button below to connect in about 10 seconds.
          </div>

          {linkUrl && (
            <div style={{ background:"#0a1a0a", border:"1px solid #22c55e20", borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:11, color:"#4a6a4a", lineHeight:1.8 }}>
              {polling ? (
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <Spinner size={12}/>
                  <span>Waiting for Telegram connection… <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ color:"#22a5e3" }}>Didn't open? Click here</a></span>
                </div>
              ) : (
                <span>Click to open Telegram: <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ color:"#22a5e3" }}>{linkUrl}</a></span>
              )}
            </div>
          )}

          <button onClick={startLink} disabled={linking} style={{ padding:"9px 20px", borderRadius:7, border:"none", background:linking?"#2a2a3e":"#22a5e3", color:"#fff", fontSize:12, cursor:linking?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:"'DM Mono',monospace" }}>
            {linking ? <><Spinner size={11}/>Connecting…</> : "✈️ Connect Telegram"}
          </button>

          <div style={{ marginTop:10, fontSize:10, color:"#3a3a5a", lineHeight:1.8 }}>
            How it works: clicking the button opens Telegram with a one-time link pre-filled. Just press Start in Telegram — done. You'll receive formatted match alerts with a Reply button directly in Telegram.
          </div>
        </>
      )}

      {isConnected && (
        <div style={{ color:"#4a6a4a", fontSize:12, lineHeight:1.7 }}>
          ✓ Lead alerts are being sent to your Telegram account. Each alert includes the post text, score, keywords matched, and a direct link to reply.
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// TELEGRAM CONNECT — link Telegram account for free push alerts on all phones
// One-time setup: user clicks link → opens Telegram bot → account linked.
// Free, works on iOS and Android. No Twilio needed.

// ════════════════════════════════════════════════════════════════════════════
// GDPR — ACCOUNT DELETION & DATA EXPORT (Improvement #1)
// ════════════════════════════════════════════════════════════════════════════
function DangerZoneSettings({ api, toast }) {
  const [deleting,    setDeleting]    = useState(false);
  const [exporting,   setExporting]   = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      // Download as JSON file
      const token = localStorage.getItem("sl_token");
      const res = await fetch(`${API_BASE}/auth/account/data-export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "socialisten-data-export.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    } catch(e) { toast.error(e.message); }
    setExporting(false);
  };

  const deleteAccount = async () => {
    if (confirmText.toLowerCase() !== "delete my account") {
      toast.error("Type the confirmation phrase exactly");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      toast.success("Account deleted. Goodbye.");
      setTimeout(() => {
        localStorage.clear();
        window.location.href = "/";
      }, 2000);
    } catch(e) {
      toast.error(e.message);
      setDeleting(false);
    }
  };

  return (
    <div style={{ background:"#1a0808", border:"1px solid #ff4d4d30", borderRadius:12, padding:"22px 24px", marginBottom:16 }}>
      <div style={{ color:"#ff4d4d", fontSize:14, fontWeight:700, marginBottom:4 }}>⚠️ Danger Zone</div>
      <div style={{ color:"#6a4a4a", fontSize:12, marginBottom:20, lineHeight:1.7 }}>
        These actions are permanent and cannot be undone.
      </div>

      {/* Data export */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", background:"#0f0808", borderRadius:8, marginBottom:10, border:"1px solid #2a1010" }}>
        <div>
          <div style={{ color:"#ccc", fontSize:13, fontWeight:600, marginBottom:2 }}>Export your data</div>
          <div style={{ color:"#6a4a4a", fontSize:11 }}>Download all your pages, matches, and settings as JSON</div>
        </div>
        <button onClick={exportData} disabled={exporting} style={{ padding:"7px 18px", borderRadius:6, border:"1px solid #4a4a6a", background:"transparent", color:"#aaa", fontSize:12, cursor:exporting?"not-allowed":"pointer", flexShrink:0, display:"flex", alignItems:"center", gap:6 }}>
          {exporting ? <><Spinner size={11}/>Exporting…</> : "📥 Export data"}
        </button>
      </div>

      {/* Account deletion */}
      <div style={{ padding:"14px 16px", background:"#0f0808", borderRadius:8, border:"1px solid #2a1010" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: showConfirm ? 14 : 0 }}>
          <div>
            <div style={{ color:"#ff8888", fontSize:13, fontWeight:600, marginBottom:2 }}>Delete account permanently</div>
            <div style={{ color:"#6a4a4a", fontSize:11 }}>Deletes all your data and cancels your subscription immediately</div>
          </div>
          {!showConfirm && (
            <button onClick={()=>setShowConfirm(true)} style={{ padding:"7px 18px", borderRadius:6, border:"1px solid #ff4d4d40", background:"transparent", color:"#ff4d4d", fontSize:12, cursor:"pointer", flexShrink:0 }}>
              Delete account
            </button>
          )}
        </div>
        {showConfirm && (
          <div>
            <div style={{ color:"#8a4a4a", fontSize:12, marginBottom:10 }}>
              Type <strong style={{ color:"#ff8888" }}>delete my account</strong> to confirm:
            </div>
            <input
              value={confirmText}
              onChange={e=>setConfirmText(e.target.value)}
              placeholder="delete my account"
              style={{ width:"100%", background:"#0a0505", border:`1px solid ${confirmText.toLowerCase()==="delete my account"?"#ff4d4d":"#2a1010"}`, borderRadius:6, padding:"9px 12px", color:"#ff8888", fontSize:12, outline:"none", boxSizing:"border-box", marginBottom:10, fontFamily:"'DM Mono',monospace" }}
            />
            <div style={{ display:"flex", gap:8 }}>
              <button
                onClick={deleteAccount}
                disabled={deleting || confirmText.toLowerCase()!=="delete my account"}
                style={{ padding:"8px 20px", borderRadius:6, border:"none", background:confirmText.toLowerCase()==="delete my account"?"#ff4d4d":"#2a1010", color:"#fff", fontSize:12, cursor:confirmText.toLowerCase()==="delete my account"?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:6 }}
              >
                {deleting ? <><Spinner size={11}/>Deleting…</> : "Delete permanently"}
              </button>
              <button onClick={()=>{setShowConfirm(false);setConfirmText("");}} style={{ padding:"8px 16px", borderRadius:6, border:"1px solid #2a1010", background:"transparent", color:"#6a4a4a", fontSize:12, cursor:"pointer" }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// OUTBOUND WEBHOOKS SETTINGS (Improvement #4)
// Connect Zapier, Make, or any custom CRM endpoint
// ════════════════════════════════════════════════════════════════════════════
function WebhookSettings({ api, toast }) {
  const [hooks,    setHooks]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [form,     setForm]     = useState({ name:"My Webhook", url:"", secret:"", min_score:20, enabled:true, platforms:[] });
  const [editing,  setEditing]  = useState(null);
  const [showAdd,  setShowAdd]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(null);
  const [testResult,setTestResult] = useState({});

  const load = () => api.get("/settings/webhooks").then(setHooks).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(load, []);

  const save = async () => {
    if (!form.url) { toast.error("URL required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/settings/webhooks/${editing}`, form);
        toast.success("Webhook updated");
      } else {
        await api.post("/settings/webhooks", form);
        toast.success("Webhook added");
      }
      setShowAdd(false); setEditing(null);
      setForm({ name:"My Webhook", url:"", secret:"", min_score:20, enabled:true, platforms:[] });
      load();
    } catch(e) { toast.error(e.message); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!confirm("Delete this webhook?")) return;
    await api.delete(`/settings/webhooks/${id}`);
    load();
  };

  const test = async (id) => {
    setTesting(id); setTestResult(p=>({...p,[id]:null}));
    try {
      const r = await api.post(`/settings/webhooks/${id}/test`);
      setTestResult(p=>({...p,[id]:r}));
    } catch(e) {
      setTestResult(p=>({...p,[id]:{ok:false,error:e.message}}));
    }
    setTesting(null);
  };

  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"8px 12px",color:"#ccc",fontSize:12,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box"};
  const lS={color:"#4a4a6a",fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:1,display:"block",marginBottom:5};

  return (
    <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div>
          <div style={{ color:"#fff", fontSize:14, fontWeight:700 }}>🔗 Outbound Webhooks</div>
          <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>Send matches to Zapier, Make, or any custom URL</div>
        </div>
        {!showAdd && hooks.length < 5 && (
          <button onClick={()=>setShowAdd(true)} style={{ padding:"6px 14px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, cursor:"pointer" }}>+ Add webhook</button>
        )}
      </div>

      {/* Add/Edit form */}
      {showAdd && (
        <div style={{ background:"#0a0a0f", border:"1px solid #2a2a3e", borderRadius:10, padding:16, marginBottom:16 }}>
          <div style={{ display:"grid", gap:10 }}>
            <div><label style={lS}>NAME</label><input style={iS} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Zapier — New Match"/></div>
            <div><label style={lS}>WEBHOOK URL</label><input style={iS} value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} placeholder="https://hooks.zapier.com/hooks/catch/..."/></div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div><label style={lS}>SIGNING SECRET (optional)</label><input style={iS} type="password" value={form.secret} onChange={e=>setForm(f=>({...f,secret:e.target.value}))} placeholder="your_secret"/></div>
              <div><label style={lS}>MIN SCORE TO TRIGGER</label><input style={iS} type="number" min="0" max="100" value={form.min_score} onChange={e=>setForm(f=>({...f,min_score:parseInt(e.target.value)||0}))}/></div>
            </div>
            <div>
              <label style={lS}>PLATFORMS (leave empty for all)</label>
              <div style={{ display:"flex", gap:8 }}>
                {["facebook","reddit","nextdoor"].map(p=>(
                  <button key={p} onClick={()=>setForm(f=>({...f,platforms:f.platforms.includes(p)?f.platforms.filter(x=>x!==p):[...f.platforms,p]}))} style={{ padding:"4px 10px", borderRadius:5, border:`1px solid ${form.platforms.includes(p)?"#6366f1":"#1e1e2e"}`, background:form.platforms.includes(p)?"#6366f120":"transparent", color:form.platforms.includes(p)?"#a5b4fc":"#4a4a6a", fontSize:11, cursor:"pointer" }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={save} disabled={saving} style={{ padding:"8px 20px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, cursor:"pointer" }}>
              {saving?"Saving…":editing?"Update":"Add webhook"}
            </button>
            <button onClick={()=>{setShowAdd(false);setEditing(null);}} style={{ padding:"8px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Existing webhooks */}
      {loading ? <div style={{display:"flex",justifyContent:"center",padding:20}}><Spinner size={18}/></div> : (
        hooks.length === 0 ? (
          <div style={{ color:"#3a3a5a", fontSize:12, textAlign:"center", padding:"20px 0" }}>
            No webhooks yet. Add one to pipe matches to Zapier, Make, or your own CRM.<br/>
            <a href="https://zapier.com/apps/webhook" target="_blank" rel="noreferrer" style={{ color:"#6366f1", fontSize:11 }}>→ Set up a Zapier webhook</a>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {hooks.map(wh=>(
              <div key={wh.id} style={{ background:"#0a0a0f", border:`1px solid ${wh.enabled?"#2a2a3e":"#111"}`, borderRadius:8, padding:"12px 14px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:wh.enabled?"#22c55e":"#3a3a5a", flexShrink:0 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ color:wh.enabled?"#ccc":"#4a4a6a", fontSize:12, fontWeight:600 }}>{wh.name}</div>
                    <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{wh.url}</div>
                  </div>
                  <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                    <button onClick={()=>test(wh.id)} disabled={testing===wh.id} style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#a5b4fc", fontSize:10, cursor:"pointer" }}>
                      {testing===wh.id?"…":"Test"}
                    </button>
                    <button onClick={()=>{setEditing(wh.id);setForm({name:wh.name,url:wh.url,secret:"",min_score:wh.min_score,enabled:wh.enabled,platforms:wh.platforms||[]});setShowAdd(true);}} style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:10, cursor:"pointer" }}>Edit</button>
                    <button onClick={()=>del(wh.id)} style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #ff4d4d20", background:"transparent", color:"#ff4d4d", fontSize:10, cursor:"pointer" }}>✕</button>
                  </div>
                </div>
                <div style={{ display:"flex", gap:10, marginTop:6, fontSize:10, color:"#3a3a5a" }}>
                  <span>Min score: {wh.min_score}</span>
                  <span>·</span>
                  <span>Fired: {wh.trigger_count||0}×</span>
                  {wh.last_triggered_at&&<><span>·</span><span>Last: {new Date(wh.last_triggered_at).toLocaleDateString()}</span></>}
                  {wh.last_error&&<span style={{ color:"#ff4d4d" }}>· Error: {wh.last_error.slice(0,50)}</span>}
                </div>
                {testResult[wh.id] && (
                  <div style={{ marginTop:8, padding:"6px 10px", borderRadius:6, background:testResult[wh.id].ok?"#0a1a0a":"#1a0808", border:`1px solid ${testResult[wh.id].ok?"#22c55e30":"#ff4d4d30"}`, fontSize:11, color:testResult[wh.id].ok?"#22c55e":"#ff4d4d" }}>
                    {testResult[wh.id].ok ? `✓ Success (${testResult[wh.id].status_code})` : `✗ ${testResult[wh.id].error || "Failed"}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      <div style={{ marginTop:12, padding:"8px 12px", background:"#0a0a0f", borderRadius:6, fontSize:10, color:"#3a3a5a", lineHeight:1.8 }}>
        💡 Payload includes: score, platform, page_name, post_text, post_url, matched_terms, found_at.
        Use the signing secret to verify requests come from Socialisten.
        <a href="https://zapier.com/apps/webhook/integrations" target="_blank" rel="noreferrer" style={{ color:"#6366f1", marginLeft:6 }}>Zapier →</a>
        <a href="https://www.make.com" target="_blank" rel="noreferrer" style={{ color:"#6366f1", marginLeft:6 }}>Make →</a>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// RESPONSE TIME ANALYTICS WIDGET (Improvement #5)
// ════════════════════════════════════════════════════════════════════════════
function ResponseTimeWidget({ api }) {
  const [data,    setData]    = useState(null);
  const [days,    setDays]    = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/response-time?days=${days}`)
      .then(setData).catch(()=>{}).finally(()=>setLoading(false));
  }, [days]);

  if (loading) return <div style={{display:"flex",justifyContent:"center",padding:20}}><Spinner size={18}/></div>;
  if (!data)   return null;

  const scoreColor = (v, good, ok) => v <= good ? "#22c55e" : v <= ok ? "#f59e0b" : "#ff4d4d";

  return (
    <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"20px 24px", marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ color:"#fff", fontSize:14, fontWeight:700 }}>⚡ Response Time Analytics</div>
        <div style={{ display:"flex", gap:6 }}>
          {[7,30,90].map(d=>(
            <button key={d} onClick={()=>setDays(d)} style={{ padding:"4px 10px", borderRadius:5, border:`1px solid ${days===d?"#6366f1":"#1e1e2e"}`, background:days===d?"#6366f120":"transparent", color:days===d?"#a5b4fc":"#4a4a6a", fontSize:11, cursor:"pointer" }}>{d}d</button>
          ))}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
        {[
          { label:"Avg Response",   value: data.avg_response || "—",    color: data.avg_response_s < 600 ? "#22c55e" : data.avg_response_s < 3600 ? "#f59e0b" : "#ff4d4d", tip:"Lower is better — fastest contractors win" },
          { label:"Response Rate",  value:`${data.response_rate_pct}%`, color: data.response_rate_pct>70?"#22c55e":data.response_rate_pct>40?"#f59e0b":"#ff4d4d", tip:"% of leads you replied to" },
          { label:"Conversion Rate",value:`${data.conversion_rate}%`,   color: data.conversion_rate>30?"#22c55e":data.conversion_rate>15?"#f59e0b":"#ff4d4d", tip:"% of contacted leads → booked/won" },
          { label:"Booked + Won",   value: data.booked + data.won,      color:"#22c55e", tip:"Total jobs from Socialisten leads" },
        ].map((m,i)=>(
          <div key={i} title={m.tip} style={{ background:"#0a0a0f", border:`1px solid ${m.color}20`, borderRadius:9, padding:"12px 14px", cursor:"help" }}>
            <div style={{ color:m.color, fontSize:22, fontWeight:800, fontFamily:"'DM Mono',monospace" }}>{m.value}</div>
            <div style={{ color:"#4a4a6a", fontSize:10, marginTop:3, fontFamily:"'DM Mono',monospace" }}>{m.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, fontSize:11, color:"#4a4a6a" }}>
        <span>{data.responded} leads contacted</span>
        <span>·</span>
        <span>{data.not_responded} ignored</span>
        {data.min_response && <><span>·</span><span>Fastest: {data.min_response}</span></>}
        {data.max_response && <><span>·</span><span>Slowest: {data.max_response}</span></>}
      </div>

      {data.avg_response_s > 1800 && (
        <div style={{ marginTop:10, padding:"8px 12px", background:"#1a1208", border:"1px solid #f59e0b30", borderRadius:7, fontSize:11, color:"#8a6a3a" }}>
          💡 Your average response time is over 30 minutes. Enable push or Telegram alerts to respond faster — most jobs go to whoever calls first.
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// IN-APP NOTIFICATION CENTER (Improvement #6)
// Bell icon in nav — shows last 20 alerts, unread count badge.
// Persists read state in localStorage (no extra DB calls).
// ════════════════════════════════════════════════════════════════════════════
function NotificationCenter({ api }) {
  const LS_KEY = "sl_notif_read";
  const [open,       setOpen]       = useState(false);
  const [alerts,     setAlerts]     = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [readIds,    setReadIds]    = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(LS_KEY)||"[]")); } catch { return new Set(); }
  });

  // Load recent high-score matches as "notifications"
  const load = () => {
    if (!api) return;
    setLoading(true);
    api.get("/matches?limit=20&min_score=20&sort=found_at")
      .then(d => setAlerts(Array.isArray(d) ? d : (d.matches || [])))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  useEffect(() => { if (!api) return; load(); const t = setInterval(load, 60000); return ()=>clearInterval(t); }, [!!api]);

  const unread = alerts.filter(a => !readIds.has(a.id)).length;

  const markAllRead = () => {
    const newSet = new Set([...readIds, ...alerts.map(a=>a.id)]);
    setReadIds(newSet);
    try { localStorage.setItem(LS_KEY, JSON.stringify([...newSet])); } catch {}
  };

  const markRead = (id) => {
    const newSet = new Set([...readIds, id]);
    setReadIds(newSet);
    try { localStorage.setItem(LS_KEY, JSON.stringify([...newSet])); } catch {}
  };

  const PLT = { facebook:"#1877F2", reddit:"#ff4500", nextdoor:"#00b362" };
  const scoreColor = s => s>=75?"#22c55e":s>=40?"#f59e0b":"#6366f1";

  return (
    <div style={{ position:"relative" }}>
      {/* Bell button */}
      <button onClick={()=>{ setOpen(p=>!p); if(!open) load(); }} style={{ position:"relative", width:36, height:36, borderRadius:8, border:"1px solid #1e1e2e", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>
        🔔
        {unread > 0 && (
          <div style={{ position:"absolute", top:4, right:4, width:16, height:16, borderRadius:"50%", background:"#ff4d4d", border:"2px solid #050508", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#fff", fontWeight:700 }}>
            {unread > 9 ? "9+" : unread}
          </div>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <>
          {/* Backdrop */}
          <div onClick={()=>setOpen(false)} style={{ position:"fixed", inset:0, zIndex:199 }}/>
          <div style={{ position:"absolute", top:44, right:0, width:380, background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, boxShadow:"0 8px 40px rgba(0,0,0,0.5)", zIndex:200, overflow:"hidden" }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", borderBottom:"1px solid #1e1e2e" }}>
              <div style={{ color:"#fff", fontSize:13, fontWeight:700 }}>Notifications {unread>0&&<span style={{ color:"#ff4d4d", fontSize:11 }}>({unread} new)</span>}</div>
              {unread > 0 && <button onClick={markAllRead} style={{ background:"transparent", border:"none", color:"#6366f1", fontSize:11, cursor:"pointer" }}>Mark all read</button>}
            </div>

            {/* List */}
            <div style={{ maxHeight:400, overflowY:"auto" }}>
              {loading && alerts.length===0 ? (
                <div style={{ display:"flex", justifyContent:"center", padding:20 }}><Spinner size={18}/></div>
              ) : alerts.length===0 ? (
                <div style={{ padding:"24px 16px", textAlign:"center", color:"#3a3a5a", fontSize:12 }}>
                  No alerts yet — run a scan to see matches here
                </div>
              ) : alerts.map(alert => {
                const isRead = readIds.has(alert.id);
                const plt    = PLT[alert.platform] || "#6366f1";
                const age    = Math.round((Date.now()-new Date(alert.found_at).getTime())/60000);
                const ageStr = age<60?`${age}m ago`:age<1440?`${Math.round(age/60)}h ago`:`${Math.round(age/1440)}d ago`;
                return (
                  <div key={alert.id} onClick={()=>markRead(alert.id)} style={{ padding:"12px 16px", borderBottom:"1px solid #111", background:isRead?"transparent":"#0a0a14", cursor:"pointer", transition:"background 0.15s" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                      {!isRead && <div style={{ width:6, height:6, borderRadius:"50%", background:"#6366f1", flexShrink:0 }}/>}
                      <span style={{ fontSize:10, padding:"1px 6px", borderRadius:4, background:`${plt}20`, color:plt, fontWeight:700 }}>{alert.platform}</span>
                      <span style={{ color:scoreColor(alert.score), fontSize:10, fontWeight:700 }}>Score {alert.score}</span>
                      <span style={{ color:"#3a3a5a", fontSize:10, marginLeft:"auto" }}>{ageStr}</span>
                    </div>
                    <div style={{ color:isRead?"#4a4a6a":"#9999bb", fontSize:12, lineHeight:1.5, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
                      {alert.post_text}
                    </div>
                    {alert.post_url && alert.post_url !== "#" && (
                      <a href={alert.post_url} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{ color:"#6366f1", fontSize:10, textDecoration:"none", display:"inline-flex", alignItems:"center", gap:3, marginTop:4 }}>
                        ↗ Reply
                      </a>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div style={{ padding:"10px 16px", borderTop:"1px solid #1e1e2e", textAlign:"center" }}>
              <button onClick={()=>{ setOpen(false); }} style={{ background:"transparent", border:"none", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>View all in Feed →</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// RESPONSE TEMPLATES LIBRARY (Improvement #7)
// Pre-built trade-specific message templates with variable substitution.
// Shown in the match card QuickReplyBar and in Settings.
// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
// RESPONSE TEMPLATES LIBRARY — expanded, custom trades, full management
// ════════════════════════════════════════════════════════════════════════════

const BUILTIN_TEMPLATES = [
  // ── HVAC ──────────────────────────────────────────────────────────────────
  { id:"hvac_no_heat",      trade:"HVAC",          label:"No heat / furnace down",       tone:"urgent",
    body:"Hi! I saw your post about no heat — I'm a licensed HVAC tech and can be there today. With temperatures dropping, this needs attention fast. Free diagnostic, same-day service available. What area are you in?" },
  { id:"hvac_emergency",    trade:"HVAC",          label:"Emergency response",            tone:"urgent",
    body:"Hi! I'm an HVAC technician available right now for emergencies. I can be at your place within {hours} hours for a free diagnostic. Licensed, insured, and local. Would that work for you?" },
  { id:"hvac_ac_down",      trade:"HVAC",          label:"AC not working",                tone:"friendly",
    body:"Hi! Saw your post about the AC — that's a miserable problem in this heat. I'm a licensed HVAC tech and can usually diagnose and fix most AC issues same day. Free estimate. When works for you?" },
  { id:"hvac_quote",        trade:"HVAC",          label:"General quote offer",           tone:"professional",
    body:"Hi! I specialize in HVAC repair and installation in your area. I offer free estimates with no obligation, and same-day service in most cases. Happy to come take a look — what's going on with the system?" },
  { id:"hvac_boiler",       trade:"HVAC",          label:"Boiler / hot water heat",       tone:"professional",
    body:"Hi! Boiler systems are my specialty. I can come assess yours and give you a straight answer on repair vs. replacement — no upselling. Free inspection, usually available this week. Interested?" },
  { id:"hvac_new_install",  trade:"HVAC",          label:"New system install",            tone:"professional",
    body:"Hi! I install new HVAC systems and can help you figure out the best option for your home's size and budget. Free in-home assessment and written quote. Happy to walk you through the options — no pressure." },
  { id:"hvac_maintenance",  trade:"HVAC",          label:"Maintenance / tune-up",         tone:"friendly",
    body:"Hi! Regular HVAC maintenance prevents the breakdowns that cost real money. I offer annual tune-ups and filter replacement service. Happy to put you on a maintenance schedule — first visit includes a full system check." },

  // ── PLUMBING ──────────────────────────────────────────────────────────────
  { id:"plumb_burst",       trade:"Plumbing",      label:"Burst pipe / active leak",      tone:"urgent",
    body:"Hi! I'm a licensed plumber and available right now for pipe emergencies. Turn off your main water shutoff if you can — I can be there in {hours}. Time is critical with active leaks. Should I head over?" },
  { id:"plumb_no_hot_water",trade:"Plumbing",      label:"No hot water",                  tone:"urgent",
    body:"Hi! No hot water is one of those things you want fixed today. I handle water heater repair and replacement regularly and can usually restore hot water same day. Free diagnosis — want me to come take a look?" },
  { id:"plumb_drain",       trade:"Plumbing",      label:"Clogged drain / backup",        tone:"friendly",
    body:"Hi! Clogged drains and backups are miserable — I can usually clear them same day. I use camera inspection first so I know exactly what I'm dealing with before quoting. Free to come look. When works?" },
  { id:"plumb_toilet",      trade:"Plumbing",      label:"Toilet issues",                 tone:"friendly",
    body:"Hi! Toilet running, leaking, or won't flush — I handle all of it. Quick fix usually, sometimes same-hour if I'm nearby. Free estimate. What's it doing?" },
  { id:"plumb_general",     trade:"Plumbing",      label:"General plumbing",              tone:"professional",
    body:"Hi! I'm a licensed local plumber with {years} years of experience. Happy to come take a look at no charge — I'll give you an honest assessment and written quote before touching anything. What's the issue?" },
  { id:"plumb_water_heater",trade:"Plumbing",      label:"Water heater replacement",      tone:"professional",
    body:"Hi! Water heaters are something I do every week. I can assess whether repair or replacement makes more sense for your situation — no upselling. Free estimate, usually available this week. Tankless options available too if you're interested." },
  { id:"plumb_sewer",       trade:"Plumbing",      label:"Sewer line / main drain",       tone:"professional",
    body:"Hi! Sewer line issues are serious and I specialize in them. I use camera inspection to diagnose exactly what's going on before recommending repairs — saves time and money. Free inspection. Can you describe what you're seeing?" },

  // ── ELECTRICAL ────────────────────────────────────────────────────────────
  { id:"elec_outage",       trade:"Electrical",    label:"Power outage / no electricity", tone:"urgent",
    body:"Hi! Partial power outages in a home can sometimes be a safety issue. I'm a licensed electrician and can come diagnose this today. Free estimate. What parts of the house lost power?" },
  { id:"elec_breaker",      trade:"Electrical",    label:"Breaker tripping",              tone:"friendly",
    body:"Hi! Breakers that keep tripping are usually a sign of an overloaded circuit or a wiring issue — worth sorting out before it becomes a bigger problem. I can diagnose it same day. Free estimate. Want me to come look?" },
  { id:"elec_outlets",      trade:"Electrical",    label:"Outlets / switches not working", tone:"friendly",
    body:"Hi! Dead outlets or switches are usually a quick fix — often a tripped GFCI or a loose connection. I can diagnose and repair same day in most cases. Free estimate. What's not working?" },
  { id:"elec_ev",           trade:"Electrical",    label:"EV charger installation",       tone:"professional",
    body:"Hi! I install Level 2 EV chargers regularly and I'm familiar with all major car brands. Licensed, insured, and usually can schedule within the week. I'll assess your panel capacity at no charge. Want a free quote?" },
  { id:"elec_panel",        trade:"Electrical",    label:"Panel upgrade",                 tone:"professional",
    body:"Hi! Panel upgrades are a significant job and I want to make sure you get the right size for your home's needs — not just the minimum. I'll assess your current setup and give you a detailed written quote. No obligation. When's a good time?" },
  { id:"elec_generator",    trade:"Electrical",    label:"Generator installation",        tone:"professional",
    body:"Hi! I install whole-home and portable generator transfer switches regularly. I'll help you size it right for your critical loads and handle the permit if needed. Free assessment and quote. When works?" },
  { id:"elec_smart_home",   trade:"Electrical",    label:"Smart home / lighting",         tone:"friendly",
    body:"Hi! Smart home electrical is something I really enjoy — proper installation makes a huge difference in reliability. I handle smart switches, panel work, EV chargers, and whole-home setups. Happy to come consult for free." },

  // ── ROOFING ───────────────────────────────────────────────────────────────
  { id:"roof_storm",        trade:"Roofing",       label:"Storm damage",                  tone:"urgent",
    body:"Hi! I saw you have storm damage — I can come out today for a free inspection before more weather moves in. I'll assess everything, document it for insurance purposes, and give you repair options. Does today work?" },
  { id:"roof_leak",         trade:"Roofing",       label:"Active roof leak",              tone:"urgent",
    body:"Hi! An active leak needs attention right away before it causes ceiling or wall damage. I can come for a free emergency inspection today. I'll tarp if needed to stop further damage while we sort out repairs. Want me to head over?" },
  { id:"roof_inspect",      trade:"Roofing",       label:"Roof inspection / assessment",  tone:"professional",
    body:"Hi! I offer free roof inspections and I'll give you a straight answer on the condition — repair, full replacement, or years left. No pressure to buy. Licensed and insured, and I work with most insurance companies. When works?" },
  { id:"roof_replace",      trade:"Roofing",       label:"Full roof replacement",         tone:"professional",
    body:"Hi! Roof replacement is a big investment and I want to earn your trust, not just your money. Free inspection, detailed written quote, references from recent jobs in your area, and I'll walk you through every material option. When can I come look?" },
  { id:"roof_gutters",      trade:"Roofing",       label:"Gutters / fascia",              tone:"friendly",
    body:"Hi! I handle gutters, fascia, and soffit repair alongside roofing work. Happy to inspect and quote everything together. Free estimate — I can usually come out within a couple of days. What's going on?" },

  // ── GENERAL CONTRACTOR ────────────────────────────────────────────────────
  { id:"gc_basement",       trade:"General Contractor", label:"Basement finishing",       tone:"professional",
    body:"Hi! Basement finishing is a big part of my work. I'd love to come see the space, talk through what you're envisioning, and give you a detailed quote. I can share photos from recent finished basements too. When works?" },
  { id:"gc_kitchen",        trade:"General Contractor", label:"Kitchen remodel",          tone:"professional",
    body:"Hi! Kitchen remodels are one of my favorite projects — big ROI when done right. I do full gut-to-finish work and can help with design if needed. Free consultation, no obligation. I'm currently taking on new projects. When can I come see the space?" },
  { id:"gc_bathroom",       trade:"General Contractor", label:"Bathroom remodel",         tone:"professional",
    body:"Hi! Bathroom remodels are something I do a lot of — from simple refreshes to full tile/plumbing reworks. Happy to come look and give a free quote. I have photos from recent jobs if you want to see the quality. Interested?" },
  { id:"gc_addition",       trade:"General Contractor", label:"Home addition / ADU",      tone:"professional",
    body:"Hi! Home additions and ADUs are complex projects that need careful planning. I'd love to come discuss what you're thinking, check the site, and give you a realistic budget range. Free consultation. This is the kind of work I specialize in." },
  { id:"gc_deck",           trade:"General Contractor", label:"Deck / patio build",       tone:"friendly",
    body:"Hi! Decks and patios are one of the best investments for outdoor living. I build with quality materials and proper footings — not shortcuts. Free design consult and quote. I can show you examples from recent builds nearby. When works?" },
  { id:"gc_repair",         trade:"General Contractor", label:"General home repair",      tone:"friendly",
    body:"Hi! I handle all kinds of home repairs — drywall, trim, doors, windows, flooring. Happy to come take a look and give you a free estimate. I'm licensed, insured, and local. What does the repair involve?" },
  { id:"gc_handyman",       trade:"General Contractor", label:"Handyman / small jobs",    tone:"friendly",
    body:"Hi! No job too small — I do all kinds of handyman work and I'm reliable and straightforward about pricing. Happy to come give you a free estimate. What's on your list?" },

  // ── LANDSCAPING ───────────────────────────────────────────────────────────
  { id:"lawn_weekly",       trade:"Landscaping",   label:"Weekly lawn service",           tone:"friendly",
    body:"Hi! I offer weekly and biweekly lawn maintenance and I'm currently taking on new clients in your area. I'd love to come see your property and put together a quote. I have long-term clients I can refer. Interested?" },
  { id:"lawn_one_time",     trade:"Landscaping",   label:"One-time cleanup",              tone:"friendly",
    body:"Hi! Happy to do a one-time yard cleanup — mowing, edging, cleanup, haul-away. I can usually fit this in within a few days. Free quote over the phone or I can come look. What does the yard need?" },
  { id:"lawn_tree",         trade:"Landscaping",   label:"Tree removal / trimming",       tone:"professional",
    body:"Hi! I'm a certified arborist and handle tree removal, trimming, and stump grinding. Free estimate — I can usually get there within a couple of days. What tree are we talking about and what's the situation?" },
  { id:"lawn_irrigation",   trade:"Landscaping",   label:"Sprinkler / irrigation",        tone:"professional",
    body:"Hi! Sprinkler and irrigation systems are something I specialize in — installation, repair, and seasonal startup/shutdown. Happy to come assess your system and give a free quote. What's going on with it?" },
  { id:"lawn_sod",          trade:"Landscaping",   label:"Sod / seeding",                 tone:"professional",
    body:"Hi! Sod installation done right makes a huge difference — proper grading, good soil prep, and the right variety for your conditions. I do free site assessments and quotes. Want me to come look?" },
  { id:"lawn_design",       trade:"Landscaping",   label:"Landscape design",              tone:"professional",
    body:"Hi! Landscape design is where I get to be creative. I'd love to come walk your property and talk through what you're envisioning — curb appeal, usable space, low-maintenance plants. Free consultation. When works?" },
  { id:"lawn_snow",         trade:"Landscaping",   label:"Snow removal",                  tone:"urgent",
    body:"Hi! I offer seasonal snow removal contracts and one-time plowing. I have residential and commercial clients in your area. Happy to give you a quote — usually can start same week. What's the property size?" },

  // ── PEST CONTROL ──────────────────────────────────────────────────────────
  { id:"pest_termite",      trade:"Pest Control",  label:"Termite inspection",            tone:"urgent",
    body:"Hi! Termite damage is one of those things that gets more expensive the longer you wait. I offer free termite inspections and can usually schedule within 24 hours. Licensed and insured. When works for you?" },
  { id:"pest_bed_bugs",     trade:"Pest Control",  label:"Bed bugs",                      tone:"urgent",
    body:"Hi! Bed bugs are stressful — I understand. I use heat treatment which is the most effective method and doesn't require you to throw anything away. Free assessment, and I can usually treat within 48 hours. Want to talk through it?" },
  { id:"pest_rodents",      trade:"Pest Control",  label:"Mice / rats",                   tone:"friendly",
    body:"Hi! Rodent problems need to be handled right — just trapping doesn't fix the entry points. I do a full inspection, seal entry points, and set up bait stations. Free assessment included. When can I come look?" },
  { id:"pest_wasps",        trade:"Pest Control",  label:"Wasps / bees / hornets",        tone:"urgent",
    body:"Hi! Active wasp or hornet nests should be dealt with quickly — especially near doors or play areas. I can usually come out same day or next day. Free assessment and I'll tell you exactly what we're dealing with." },
  { id:"pest_general",      trade:"Pest Control",  label:"General pest control",          tone:"professional",
    body:"Hi! I'm a licensed pest control specialist. Happy to come assess the situation and explain the treatment options — free inspection included. No pressure, I'll tell you honestly what you need. What are you dealing with?" },
  { id:"pest_prevention",   trade:"Pest Control",  label:"Preventive treatment plan",     tone:"professional",
    body:"Hi! Preventive pest treatment is much cheaper than dealing with an infestation. I offer quarterly treatment plans that cover most common pests. Free initial inspection and I'll customize the plan for your property. Interested?" },

  // ── PAINTING ──────────────────────────────────────────────────────────────
  { id:"paint_interior",    trade:"Painting",      label:"Interior painting",             tone:"professional",
    body:"Hi! I'm a professional painter with {years} years of experience — clean lines, proper prep, no shortcuts. Happy to provide a free estimate. Interior projects usually wrap in 2–3 days. What rooms are you looking at?" },
  { id:"paint_exterior",    trade:"Painting",      label:"Exterior painting",             tone:"professional",
    body:"Hi! Exterior painting done right means proper prep — sanding, priming, caulking — before a drop of paint goes on. Free estimate, and I can usually start within 2 weeks. Happy to share examples of past work. When can I come look?" },
  { id:"paint_cabinet",     trade:"Painting",      label:"Cabinet painting / refinishing", tone:"professional",
    body:"Hi! Cabinet painting is one of the best bang-for-your-buck upgrades — totally transforms a kitchen. I spray cabinets for a factory-smooth finish. Free estimate and I can show you color samples. Interested?" },
  { id:"paint_deck",        trade:"Painting",      label:"Deck staining / sealing",       tone:"friendly",
    body:"Hi! Deck staining protects the wood and makes a big difference in how it looks. I do full prep — cleaning, sanding, any board repairs — before staining. Free estimate, usually available within the week. What kind of deck is it?" },

  // ── FLOORING ──────────────────────────────────────────────────────────────
  { id:"floor_hardwood",    trade:"Flooring",      label:"Hardwood repair / refinish",    tone:"professional",
    body:"Hi! I saw your post about the flooring — hardwood water damage or wear is usually very fixable. I refinish and repair hardwood regularly. Free estimate and I'll tell you honestly if repair is the right call vs. replacement. When can I come look?" },
  { id:"floor_install",     trade:"Flooring",      label:"New floor installation",        tone:"professional",
    body:"Hi! I install hardwood, LVP, laminate, and tile. Happy to come measure and give you a free quote — I'll also help you think through the best material for your situation. Currently taking on new projects. When works?" },
  { id:"floor_tile",        trade:"Flooring",      label:"Tile work",                     tone:"professional",
    body:"Hi! Tile work is one of my specialties — kitchen, bathroom, backsplash, shower. I take my time with layout and grouting so the result looks great for years. Free estimate. What's the project?" },
  { id:"floor_carpet",      trade:"Flooring",      label:"Carpet installation",           tone:"friendly",
    body:"Hi! I install carpet and can help you with material selection too. Free measure and quote — I work with a supplier so I can usually beat big-box pricing. When works for a quick visit?" },

  // ── APPLIANCE REPAIR ──────────────────────────────────────────────────────
  { id:"appl_fridge",       trade:"Appliance Repair", label:"Refrigerator not cooling",   tone:"urgent",
    body:"Hi! Fridge issues need quick attention before more food is lost. I diagnose and repair most major brands and can usually come today or tomorrow. Service call fee applies toward repair. What brand and what's it doing?" },
  { id:"appl_washer",       trade:"Appliance Repair", label:"Washer / dryer issue",       tone:"friendly",
    body:"Hi! Washer and dryer repairs are usually straightforward and much cheaper than replacement. I work on most major brands. Service call fee applies toward repair. Can you describe what it's doing?" },
  { id:"appl_dishwasher",   trade:"Appliance Repair", label:"Dishwasher repair",          tone:"friendly",
    body:"Hi! Dishwashers are usually very repair-friendly. Common issues — not draining, not cleaning, not starting — are all things I fix regularly. Service call fee applies toward repair. What model and what's it doing?" },
  { id:"appl_oven",         trade:"Appliance Repair", label:"Oven / stove repair",        tone:"friendly",
    body:"Hi! Oven and stove repairs are usually quick — heating element, igniter, control board. I work on gas and electric. Service call fee toward repair. What brand and what's the symptom?" },
  { id:"appl_general",      trade:"Appliance Repair", label:"General appliance",          tone:"professional",
    body:"Hi! I repair most major home appliances — refrigerators, washers, dryers, dishwashers, ovens. Usually same-day or next-day availability. Service call fee applies toward any repair. What appliance and what brand?" },

  // ── LOCKSMITH ─────────────────────────────────────────────────────────────
  { id:"lock_lockout",      trade:"Locksmith",     label:"Locked out",                    tone:"urgent",
    body:"Hi! I'm a licensed locksmith and can be there in {hours}. I don't damage the lock — I pick or bypass it cleanly. Flat fee, no hidden charges. What's the address and is this a house or apartment?" },
  { id:"lock_rekey",        trade:"Locksmith",     label:"Rekey / lock change",           tone:"professional",
    body:"Hi! Rekeying is the smart move when you've moved in or lost keys — much cheaper than replacing locks. I can rekey all your locks to one key if you want. Free estimate and usually available this week. How many locks?" },
  { id:"lock_smart",        trade:"Locksmith",     label:"Smart lock installation",       tone:"friendly",
    body:"Hi! Smart lock installation is something I do a lot of — I'll make sure it's installed correctly and show you how to set it up. I work with most brands. Free estimate. What lock are you looking at, or do you need a recommendation?" },
  { id:"lock_commercial",   trade:"Locksmith",     label:"Commercial / business",         tone:"professional",
    body:"Hi! Commercial locksmith work — master key systems, access control, panic hardware — is a specialty of mine. Happy to come assess your situation and give a free quote. What's the business setup?" },

  // ── MOVING ────────────────────────────────────────────────────────────────
  { id:"move_local",        trade:"Moving",        label:"Local move",                    tone:"friendly",
    body:"Hi! I run a local moving company and we're currently available. We bring our own truck, blankets, and straps — no hidden fees. Free quote based on home size and distance. When's the move date?" },
  { id:"move_labor_only",   trade:"Moving",        label:"Labor only / you have truck",   tone:"friendly",
    body:"Hi! Happy to provide loading and unloading labor if you have your own truck or pod. Experienced movers, careful with furniture. Hourly rate, no minimum. When do you need us?" },
  { id:"move_packing",      trade:"Moving",        label:"Packing services",              tone:"professional",
    body:"Hi! Professional packing makes a big difference — we pack efficiently and label everything so unpacking is easy. We supply boxes and materials. Happy to do full-home or partial packing. Free quote. When's your move?" },
  { id:"move_piano",        trade:"Moving",        label:"Piano / specialty items",       tone:"professional",
    body:"Hi! Piano moving is a specialty — it requires the right equipment and technique to avoid damage. I have experience with uprights and grands. Happy to come assess and quote. What type of piano and where is it going?" },

  // ── CLEANING ──────────────────────────────────────────────────────────────
  { id:"clean_deep",        trade:"Cleaning",      label:"Deep clean / one-time",         tone:"friendly",
    body:"Hi! I do thorough deep cleans — inside appliances, baseboards, grout, everything. Usually takes a half-day to full day depending on size. Free quote based on square footage and current condition. When are you looking to have it done?" },
  { id:"clean_recurring",   trade:"Cleaning",      label:"Weekly / biweekly cleaning",    tone:"friendly",
    body:"Hi! I take on weekly and biweekly residential clients and I'm currently available in your area. I'm reliable, insured, and bring my own supplies. Free walkthrough quote. Would you like to set something up?" },
  { id:"clean_move",        trade:"Cleaning",      label:"Move-in / move-out clean",      tone:"professional",
    body:"Hi! Move-in and move-out cleans are something I do regularly. I do a thorough top-to-bottom clean — landlords and property managers love it. Quick availability and competitive pricing. When is the clean needed?" },
  { id:"clean_carpet",      trade:"Cleaning",      label:"Carpet cleaning",               tone:"friendly",
    body:"Hi! I do professional carpet cleaning — hot water extraction, not just surface cleaning. Most carpets look dramatically better after. Free quote based on square footage. When would work?" },

  // ── MASONRY & CONCRETE ────────────────────────────────────────────────────
  { id:"mason_driveway",    trade:"Masonry",       label:"Driveway repair / replacement", tone:"professional",
    body:"Hi! Driveways are something I do a lot of — crack repair, resurfacing, or full replacement. Free estimate and I'll tell you honestly whether repair makes sense or if you're better off replacing. When can I come look?" },
  { id:"mason_foundation",  trade:"Masonry",       label:"Foundation / basement crack",   tone:"professional",
    body:"Hi! Foundation cracks are worth getting assessed — some are cosmetic, some are structural. I'll give you an honest read on what you're dealing with. Free inspection. This is definitely something to stay on top of." },
  { id:"mason_brick",       trade:"Masonry",       label:"Brick / tuckpointing",          tone:"professional",
    body:"Hi! Tuckpointing and brick repair is one of my specialties. Deteriorating mortar lets in water and gets worse quickly. I'll assess what needs attention and give a free quote. When can I come by?" },
  { id:"mason_patio",       trade:"Masonry",       label:"Patio / walkway",               tone:"friendly",
    body:"Hi! Pavers, stamped concrete, stone — I do all kinds of patio and walkway work. Happy to come look at the space and talk through options. Free estimate and I can show you material samples. When works?" },

  // ── HVAC / INSULATION ─────────────────────────────────────────────────────
  { id:"insul_attic",       trade:"Insulation",    label:"Attic insulation",              tone:"professional",
    body:"Hi! Attic insulation is one of the best ROI improvements for energy bills and comfort. I'll assess your current R-value and tell you honestly what improvement would make sense. Free inspection and quote. When works?" },
  { id:"insul_crawl",       trade:"Insulation",    label:"Crawl space / basement",        tone:"professional",
    body:"Hi! Crawl space insulation and vapor barrier work can make a real difference in moisture, pests, and energy efficiency. Free inspection and detailed quote. This is a specialty of mine. When can I come look?" },
  { id:"insul_general",     trade:"Insulation",    label:"General insulation",            tone:"professional",
    body:"Hi! I handle blown-in, batt, spray foam, and rigid insulation depending on the application. Happy to assess your home's situation and recommend the most cost-effective approach. Free estimate. What area are you looking to insulate?" },

  // ── POOL & SPA ────────────────────────────────────────────────────────────
  { id:"pool_repair",       trade:"Pool & Spa",    label:"Pool repair",                   tone:"friendly",
    body:"Hi! Pool equipment issues — pump, filter, heater, leaks — are my specialty. I can usually diagnose and repair within a couple of days. Free assessment. What's going on with the pool?" },
  { id:"pool_open_close",   trade:"Pool & Spa",    label:"Pool opening / closing",        tone:"friendly",
    body:"Hi! I do seasonal pool openings and closings and I'm booking up fast. Includes full inspection, chemical balance, and equipment check. Want to get on the schedule? What size pool?" },
  { id:"pool_service",      trade:"Pool & Spa",    label:"Weekly pool service",           tone:"professional",
    body:"Hi! I offer weekly pool service — chemicals, brushing, vacuuming, equipment check. Currently taking on new clients in your area. Would love to come assess the pool and put together a quote. When works?" },

  // ── JUNK REMOVAL ──────────────────────────────────────────────────────────
  { id:"junk_general",      trade:"Junk Removal",  label:"General junk removal",          tone:"friendly",
    body:"Hi! I do full-service junk removal — I do the loading, you don't have to touch anything. Quick scheduling, competitive pricing, and I recycle and donate where possible. Free upfront quote. What needs to go?" },
  { id:"junk_estate",       trade:"Junk Removal",  label:"Estate / house cleanout",       tone:"professional",
    body:"Hi! Full house and estate cleanouts are something I handle regularly. It's a big job and I treat everything with respect. Free walkthrough quote — no obligation. When would be a good time to come see the property?" },
  { id:"junk_appliance",    trade:"Junk Removal",  label:"Appliance / furniture removal",  tone:"friendly",
    body:"Hi! Happy to haul away appliances and furniture — I handle the heavy lifting. Most items I can pick up within a day or two. Free quote over the phone. What do you need removed?" },

  // ── GARAGE DOOR ───────────────────────────────────────────────────────────
  { id:"garage_repair",     trade:"Garage Door",   label:"Garage door repair",            tone:"urgent",
    body:"Hi! Garage door issues — broken spring, off track, won't open — usually can be fixed same day. I stock common parts. Free diagnosis, no obligation. What's it doing?" },
  { id:"garage_spring",     trade:"Garage Door",   label:"Broken spring",                 tone:"urgent",
    body:"Hi! Broken garage door springs are a safety hazard and the door won't work until it's fixed. This is something I handle daily and can usually come today or tomorrow. What's the door brand and is it a single or double door?" },
  { id:"garage_opener",     trade:"Garage Door",   label:"Opener / motor issue",          tone:"friendly",
    body:"Hi! Garage door opener issues are usually a quick fix or a straightforward replacement. I work on all major brands. Free diagnosis. What brand is the opener and what's it doing?" },
  { id:"garage_new",        trade:"Garage Door",   label:"New garage door install",       tone:"professional",
    body:"Hi! New garage door installation is something I specialize in. Happy to come measure, discuss style and insulation options, and give a detailed quote. Usually a one-day install. When can I come by?" },

  // ── SOLAR ─────────────────────────────────────────────────────────────────
  { id:"solar_quote",       trade:"Solar",         label:"Solar installation quote",      tone:"professional",
    body:"Hi! Solar is a big decision and I want to make sure you have all the information — panel options, battery storage, incentives, actual payback timeline for your usage. Free no-pressure consultation and quote. When works for a call or visit?" },
  { id:"solar_repair",      trade:"Solar",         label:"Solar system repair",           tone:"professional",
    body:"Hi! Solar system issues — inverter faults, production drops, connection problems — are something I diagnose and repair. I work on most brands. Free diagnostic. What system do you have and what's it showing?" },
];

const TEMPLATE_VARS = ["{hours}", "{years}", "{area}", "{name}", "{date}", "{city}", "{phone}", "{company}"];

const TRADE_ICONS = {
  "HVAC":"🌡️", "Plumbing":"🔧", "Electrical":"⚡", "Roofing":"🏠",
  "General Contractor":"🏗️", "Landscaping":"🌿", "Pest Control":"🐛",
  "Painting":"🎨", "Flooring":"🪵", "Appliance Repair":"🔌",
  "Locksmith":"🔑", "Moving":"📦", "Cleaning":"🧹", "Masonry":"🧱",
  "Insulation":"🏚️", "Pool & Spa":"🏊", "Junk Removal":"🚛",
  "Garage Door":"🚪", "Solar":"☀️",
};

const TONE_COLORS = { urgent:"#ff4d4d", friendly:"#22c55e", professional:"#6366f1" };
const TONE_LABELS = { urgent:"Urgent", friendly:"Friendly", professional:"Professional" };

// ════════════════════════════════════════════════════════════════════════════
// TEMPLATE LIBRARY — with custom templates, search, tone filter, edit/delete
// ════════════════════════════════════════════════════════════════════════════
function TemplateLibrary({ onSelect, currentBody, compact = false, api }) {
  const LS_CUSTOM = "sl_custom_templates";
  const LS_PREFS  = "sl_template_prefs";

  const [search,      setSearch]      = useState("");
  const [activeTrade, setActiveTrade] = useState("All");
  const [activeTone,  setActiveTone]  = useState("All");
  const [expanded,    setExpanded]    = useState(null);
  const [customVars,  setCustomVars]  = useState({});
  const [customTmpls, setCustomTmpls] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_CUSTOM) || "[]"); } catch { return []; }
  });
  const [showCreate,  setShowCreate]  = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [newTmpl,     setNewTmpl]     = useState({ label:"", trade:"", tone:"professional", body:"" });
  const [favs,        setFavs]        = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("sl_tmpl_favs") || "[]")); } catch { return new Set(); }
  });
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [recentIds,    setRecentIds]    = useState(() => {
    try { return JSON.parse(localStorage.getItem("sl_tmpl_recent") || "[]"); } catch { return []; }
  });
  const [activeTab, setActiveTab] = useState("all"); // all | custom | favorites | recent

  const saveCustom = (list) => {
    setCustomTmpls(list);
    try { localStorage.setItem(LS_CUSTOM, JSON.stringify(list)); } catch {}
  };

  const saveFav = (id) => {
    const next = new Set(favs);
    if (next.has(id)) next.delete(id); else next.add(id);
    setFavs(next);
    try { localStorage.setItem("sl_tmpl_favs", JSON.stringify([...next])); } catch {}
  };

  const recordRecent = (id) => {
    const next = [id, ...recentIds.filter(x=>x!==id)].slice(0,10);
    setRecentIds(next);
    try { localStorage.setItem("sl_tmpl_recent", JSON.stringify(next)); } catch {}
  };

  const allTemplates = [
    ...BUILTIN_TEMPLATES,
    ...customTmpls.map(t=>({...t, isCustom:true})),
  ];

  const trades   = ["All", ...new Set(allTemplates.map(t=>t.trade).filter(Boolean)).values()].sort();
  const tones    = ["All","urgent","friendly","professional"];

  const filtered = allTemplates.filter(t => {
    if (activeTab==="custom"    && !t.isCustom) return false;
    if (activeTab==="favorites" && !favs.has(t.id)) return false;
    if (activeTab==="recent"    && !recentIds.includes(t.id)) return false;
    const matchTrade  = activeTrade==="All" || t.trade===activeTrade;
    const matchTone   = activeTone==="All"  || t.tone===activeTone;
    const matchSearch = !search || t.label.toLowerCase().includes(search.toLowerCase())
                                || t.body.toLowerCase().includes(search.toLowerCase())
                                || (t.trade||"").toLowerCase().includes(search.toLowerCase());
    return matchTrade && matchTone && matchSearch;
  });

  // Sort: recent at top if on "all" tab
  const sorted = activeTab==="recent"
    ? [...filtered].sort((a,b)=>recentIds.indexOf(a.id)-recentIds.indexOf(b.id))
    : filtered;

  const fillVars = (body) => {
    let filled = body;
    Object.entries(customVars).forEach(([k,v]) => {
      if(v) filled = filled.replace(new RegExp(`\\{${k}\\}`, "g"), v);
    });
    return filled;
  };

  const varsInBody = (body) => {
    const matches = body.match(/\{(\w+)\}/g) || [];
    return [...new Set(matches.map(m=>m.replace(/[{}]/g,"")))];
  };

  const charCount = (id) => {
    const t = allTemplates.find(x=>x.id===id);
    return t ? t.body.length : 0;
  };

  const saveNewTemplate = () => {
    if (!newTmpl.label || !newTmpl.body) return;
    const id = `custom_${Date.now()}`;
    if (editingId) {
      saveCustom(customTmpls.map(t => t.id===editingId ? {...newTmpl, id:editingId, isCustom:true} : t));
    } else {
      saveCustom([...customTmpls, {...newTmpl, id, isCustom:true}]);
    }
    setNewTmpl({ label:"", trade:"", tone:"professional", body:"" });
    setShowCreate(false); setEditingId(null);
  };

  const deleteTemplate = (id) => {
    if (!confirm("Delete this template?")) return;
    saveCustom(customTmpls.filter(t=>t.id!==id));
  };

  const iS = { width:"100%", background:"#050508", border:"1px solid #1e1e2e", borderRadius:6, padding:"7px 10px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };
  const lS = { color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace", letterSpacing:1, display:"block", marginBottom:4 };

  const NewTradeInput = () => {
    const [customTrade, setCustomTrade] = useState(newTmpl.trade && !TRADE_ICONS[newTmpl.trade] ? newTmpl.trade : "");
    const [useCustom,   setUseCustom]   = useState(newTmpl.trade && !TRADE_ICONS[newTmpl.trade]);
    return (
      <div>
        <label style={lS}>TRADE / CATEGORY</label>
        <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:6 }}>
          {Object.entries(TRADE_ICONS).map(([t,icon])=>(
            <button key={t} onClick={()=>{ setNewTmpl(p=>({...p,trade:t})); setUseCustom(false); }}
              style={{ padding:"4px 8px", borderRadius:5, border:`1px solid ${newTmpl.trade===t&&!useCustom?"#6366f1":"#1e1e2e"}`, background:newTmpl.trade===t&&!useCustom?"#6366f120":"transparent", color:newTmpl.trade===t&&!useCustom?"#a5b4fc":"#4a4a6a", fontSize:11, cursor:"pointer" }}>
              {icon} {t}
            </button>
          ))}
          <button onClick={()=>{ setUseCustom(true); setNewTmpl(p=>({...p,trade:customTrade})); }}
            style={{ padding:"4px 8px", borderRadius:5, border:`1px solid ${useCustom?"#f59e0b":"#1e1e2e"}`, background:useCustom?"#f59e0b20":"transparent", color:useCustom?"#f59e0b":"#4a4a6a", fontSize:11, cursor:"pointer" }}>
            + Custom trade
          </button>
        </div>
        {useCustom && (
          <input value={customTrade} onChange={e=>{ setCustomTrade(e.target.value); setNewTmpl(p=>({...p,trade:e.target.value})); }}
            placeholder="e.g. Fence Repair, Pool Service, Welding…"
            style={{ ...iS, marginBottom:6 }}/>
        )}
      </div>
    );
  };

  return (
    <div>
      {!compact && (
        <>
          {/* Tabs */}
          <div style={{ display:"flex", gap:0, marginBottom:14, background:"#0a0a0f", borderRadius:8, padding:3 }}>
            {[["all","All Templates"],["favorites","⭐ Favorites"],["recent","🕐 Recent"],["custom","✏️ My Templates"]].map(([id,label])=>(
              <button key={id} onClick={()=>setActiveTab(id)} style={{ flex:1, padding:"6px", borderRadius:6, border:"none", background:activeTab===id?"#1e1e2e":"transparent", color:activeTab===id?"#fff":"#4a4a6a", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>{label}</button>
            ))}
          </div>

          {/* Search + filters */}
          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search templates…"
              style={{ ...iS, flex:1, padding:"8px 12px" }}/>
          </div>

          {activeTab==="all" && (
            <>
              {/* Trade filter */}
              <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:8 }}>
                {trades.map(t=>(
                  <button key={t} onClick={()=>setActiveTrade(t)} style={{ padding:"4px 10px", borderRadius:5, border:`1px solid ${activeTrade===t?"#6366f1":"#1e1e2e"}`, background:activeTrade===t?"#6366f120":"transparent", color:activeTrade===t?"#a5b4fc":"#4a4a6a", fontSize:11, cursor:"pointer" }}>
                    {TRADE_ICONS[t]||""} {t}
                  </button>
                ))}
              </div>
              {/* Tone filter */}
              <div style={{ display:"flex", gap:5, marginBottom:12 }}>
                {tones.map(tone=>(
                  <button key={tone} onClick={()=>setActiveTone(tone)} style={{ padding:"3px 10px", borderRadius:10, border:`1px solid ${activeTone===tone?(TONE_COLORS[tone]||"#6366f1"):"#1e1e2e"}`, background:activeTone===tone?`${TONE_COLORS[tone]||"#6366f1"}20`:"transparent", color:activeTone===tone?(TONE_COLORS[tone]||"#a5b4fc"):"#4a4a6a", fontSize:10, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
                    {tone==="All"?"All tones":TONE_LABELS[tone]||tone}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Summary line */}
          <div style={{ color:"#3a3a5a", fontSize:10, fontFamily:"'DM Mono',monospace", marginBottom:10 }}>
            {sorted.length} template{sorted.length!==1?"s":""}
            {activeTrade!=="All"?` · ${activeTrade}`:""}
            {activeTone!=="All"?` · ${TONE_LABELS[activeTone]}`:""}
            {search?` matching "${search}"`:""}
          </div>
        </>
      )}

      {/* Create custom template button */}
      {!compact && activeTab==="custom" && !showCreate && (
        <button onClick={()=>{setShowCreate(true);setEditingId(null);setNewTmpl({label:"",trade:"",tone:"professional",body:""}); }}
          style={{ width:"100%", padding:"10px", borderRadius:8, border:"1px dashed #6366f1", background:"#6366f110", color:"#a5b4fc", fontSize:12, cursor:"pointer", marginBottom:14, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
          ✏️ Create new template
        </button>
      )}

      {/* Create/Edit form */}
      {showCreate && (
        <div style={{ background:"#0a0a0f", border:"1px solid #6366f130", borderRadius:10, padding:16, marginBottom:14 }}>
          <div style={{ color:"#a5b4fc", fontSize:13, fontWeight:600, marginBottom:14 }}>{editingId?"Edit template":"Create custom template"}</div>
          <div style={{ display:"grid", gap:10 }}>
            <div><label style={lS}>TEMPLATE NAME</label><input style={iS} value={newTmpl.label} onChange={e=>setNewTmpl(p=>({...p,label:e.target.value}))} placeholder="e.g. Emergency response — weekends"/></div>
            <NewTradeInput/>
            <div>
              <label style={lS}>TONE</label>
              <div style={{ display:"flex", gap:6 }}>
                {["urgent","friendly","professional"].map(t=>(
                  <button key={t} onClick={()=>setNewTmpl(p=>({...p,tone:t}))} style={{ padding:"5px 12px", borderRadius:5, border:`1px solid ${newTmpl.tone===t?TONE_COLORS[t]:"#1e1e2e"}`, background:newTmpl.tone===t?`${TONE_COLORS[t]}20`:"transparent", color:newTmpl.tone===t?TONE_COLORS[t]:"#4a4a6a", fontSize:11, cursor:"pointer" }}>
                    {TONE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <label style={lS}>MESSAGE BODY</label>
                <span style={{ color:"#3a3a5a", fontSize:10 }}>{newTmpl.body.length} chars</span>
              </div>
              <textarea value={newTmpl.body} onChange={e=>setNewTmpl(p=>({...p,body:e.target.value}))}
                placeholder={"Hi! I saw your post about [issue]…\n\nUse {hours}, {years}, {area}, {name}, {company} as placeholders."}
                rows={5} style={{ ...iS, resize:"vertical", fontFamily:"inherit", lineHeight:1.6 }}/>
              <div style={{ color:"#3a3a5a", fontSize:10, marginTop:4 }}>
                Variables: {TEMPLATE_VARS.map(v=><code key={v} style={{ color:"#6366f1", marginRight:6 }}>{v}</code>)}
              </div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button onClick={saveNewTemplate} disabled={!newTmpl.label||!newTmpl.body}
              style={{ padding:"8px 20px", borderRadius:6, border:"none", background:newTmpl.label&&newTmpl.body?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#2a2a3e", color:"#fff", fontSize:12, cursor:newTmpl.label&&newTmpl.body?"pointer":"not-allowed" }}>
              {editingId?"Save changes":"Create template"}
            </button>
            <button onClick={()=>{setShowCreate(false);setEditingId(null);}} style={{ padding:"8px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Template list */}
      <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:compact?240:500, overflowY:"auto" }}>
        {sorted.length===0 ? (
          <div style={{ color:"#3a3a5a", fontSize:12, textAlign:"center", padding:"24px 0" }}>
            {activeTab==="favorites" ? "Star templates to save them here" :
             activeTab==="recent"    ? "No recently used templates" :
             activeTab==="custom"    ? "No custom templates yet — create your first one above" :
             "No templates match your filters"}
          </div>
        ) : sorted.map(tmpl => {
          const vars   = varsInBody(tmpl.body);
          const isOpen = expanded===tmpl.id;
          const isFav  = favs.has(tmpl.id);
          const toneColor = TONE_COLORS[tmpl.tone] || "#6366f1";
          return (
            <div key={tmpl.id} style={{ background:"#0a0a0f", border:`1px solid ${isOpen?"#6366f1":"#1e1e2e"}`, borderRadius:8, overflow:"hidden", transition:"border-color 0.15s" }}>
              {/* Header row */}
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px", cursor:"pointer" }} onClick={()=>setExpanded(isOpen?null:tmpl.id)}>
                {/* Trade icon */}
                <span style={{ fontSize:16, flexShrink:0 }}>{TRADE_ICONS[tmpl.trade] || "📝"}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ color:"#ccc", fontSize:12, fontWeight:600 }}>{tmpl.label}</span>
                    {tmpl.tone && <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:`${toneColor}15`, color:toneColor, fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{TONE_LABELS[tmpl.tone]||tmpl.tone}</span>}
                    {tmpl.isCustom && <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:"#f59e0b15", color:"#f59e0b", fontFamily:"'DM Mono',monospace", flexShrink:0 }}>CUSTOM</span>}
                  </div>
                  {!compact && <div style={{ color:"#3a3a5a", fontSize:10, marginTop:2 }}>{tmpl.trade} · {tmpl.body.length} chars</div>}
                </div>
                {/* Actions */}
                <div style={{ display:"flex", gap:4, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                  <button onClick={()=>saveFav(tmpl.id)} title={isFav?"Remove favorite":"Add to favorites"} style={{ width:26, height:26, borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:isFav?"#f59e0b":"#3a3a5a", fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>★</button>
                  {tmpl.isCustom && (
                    <>
                      <button onClick={()=>{setEditingId(tmpl.id);setNewTmpl({label:tmpl.label,trade:tmpl.trade||"",tone:tmpl.tone||"professional",body:tmpl.body});setShowCreate(true);setActiveTab("custom");}} style={{ width:26,height:26,borderRadius:5,border:"1px solid #1e1e2e",background:"transparent",color:"#6366f1",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>✏️</button>
                      <button onClick={()=>deleteTemplate(tmpl.id)} style={{ width:26,height:26,borderRadius:5,border:"1px solid #ff4d4d20",background:"transparent",color:"#ff4d4d",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
                    </>
                  )}
                </div>
                <span style={{ color:"#6366f1", fontSize:14, transform:isOpen?"rotate(180deg)":"none", transition:"transform 0.15s", flexShrink:0 }}>⌄</span>
              </div>

              {/* Expanded body */}
              {isOpen && (
                <div style={{ padding:"0 12px 14px" }}>
                  {/* Variable inputs */}
                  {vars.length > 0 && (
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10, padding:"8px", background:"#050508", borderRadius:6 }}>
                      <span style={{ color:"#4a4a6a", fontSize:10, width:"100%", fontFamily:"'DM Mono',monospace" }}>FILL IN VARIABLES:</span>
                      {vars.map(v=>(
                        <div key={v} style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <code style={{ color:"#6366f1", fontSize:10 }}>{`{${v}}`}</code>
                          <input value={customVars[v]||""} onChange={e=>setCustomVars(p=>({...p,[v]:e.target.value}))}
                            placeholder={v} style={{ width:90, background:"#0a0a0f", border:"1px solid #2a2a3e", borderRadius:4, padding:"3px 7px", color:"#ccc", fontSize:11, outline:"none" }}/>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Preview */}
                  <div style={{ color:"#9999bb", fontSize:12, lineHeight:1.75, marginBottom:12, padding:"10px 12px", background:"#050508", borderRadius:6, borderLeft:"3px solid #6366f1", fontStyle:vars.length>0?"italic":"normal" }}>
                    {fillVars(tmpl.body)}
                  </div>
                  {/* Char count */}
                  <div style={{ color:"#3a3a5a", fontSize:10, marginBottom:10 }}>
                    {fillVars(tmpl.body).length} characters · {Math.ceil(fillVars(tmpl.body).length/160)} SMS segment{Math.ceil(fillVars(tmpl.body).length/160)!==1?"s":""}
                  </div>
                  {/* Use button */}
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={()=>{ onSelect && onSelect(fillVars(tmpl.body)); recordRecent(tmpl.id); setExpanded(null); }}
                      style={{ padding:"7px 18px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
                      ✓ Use this template
                    </button>
                    <button onClick={()=>{ navigator.clipboard.writeText(fillVars(tmpl.body)); recordRecent(tmpl.id); }}
                      style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #2a2a3e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>
                      📋 Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stats footer */}
      {!compact && (
        <div style={{ marginTop:12, color:"#3a3a5a", fontSize:10, textAlign:"center" }}>
          {BUILTIN_TEMPLATES.length} built-in · {customTmpls.length} custom · {favs.size} starred · {recentIds.length} recently used
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// SOCIALISTEN KNOWLEDGE BASE — Public-facing dedicated page
// Route: /help or /kb
// Standalone component — works logged out or logged in
// Features: category sidebar, article browser, search, markdown renderer,
//           helpful/not-helpful feedback, breadcrumbs, related articles
// ════════════════════════════════════════════════════════════════════════════


// ── Markdown renderer ─────────────────────────────────────────────────────────
function renderMd(text) {
  if (!text) return "";
  return text
    .replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) =>
      `<pre style="background:#0f172a;border:1px solid #1e293b;border-radius:8px;padding:16px;overflow-x:auto;font-size:12px;line-height:1.7;font-family:'DM Mono',monospace;color:#34d399;margin:16px 0">${code.trim()}</pre>`)
    .replace(/`([^`]+)`/g, '<code style="background:#1e293b;color:#818cf8;padding:1px 6px;border-radius:3px;font-size:12px;font-family:monospace">$1</code>')
    .replace(/^\| (.+)$/gm, (m) => {
      const cells = m.replace(/^\| | \|$/g,'').split(' | ');
      return '<div style="display:flex;border-bottom:1px solid #1e293b">' +
        cells.map((c,i) => `<div style="flex:1;padding:8px 12px;color:${i===0?'#e2e8f0':'#94a3b8'};font-size:13px">${c.trim()}</div>`).join('') + '</div>';
    })
    .replace(/^### (.+)$/gm, '<h3 style="color:#e2e8f0;font-size:15px;font-weight:700;margin:24px 0 10px">$1</h3>')
    .replace(/^## (.+)$/gm,  '<h2 style="color:#f1f5f9;font-size:18px;font-weight:700;margin:32px 0 12px;padding-bottom:8px;border-bottom:1px solid #1e293b">$1</h2>')
    .replace(/^# (.+)$/gm,   '<h1 style="color:#f8fafc;font-size:24px;font-weight:800;margin:0 0 20px">$1</h1>')
    .replace(/^> (.+)$/gm,   '<blockquote style="border-left:3px solid #6366f1;padding:10px 16px;color:#94a3b8;font-size:13px;margin:16px 0;background:#1e1e2e;border-radius:0 6px 6px 0;font-style:italic">$1</blockquote>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#f1f5f9;font-weight:700">$1</strong>')
    .replace(/\*(.+?)\*/g,     '<em style="color:#a5b4fc">$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#818cf8;text-decoration:none;border-bottom:1px solid #818cf840">$1</a>')
    .replace(/^- \[x\] (.+)$/gm, '<div style="display:flex;align-items:flex-start;gap:8px;margin:5px 0"><span style="color:#22c55e;font-size:14px;margin-top:2px;flex-shrink:0">☑</span><span style="color:#94a3b8;font-size:13px;line-height:1.7">$1</span></div>')
    .replace(/^- \[ \] (.+)$/gm,'<div style="display:flex;align-items:flex-start;gap:8px;margin:5px 0"><span style="color:#4a4a6a;font-size:14px;margin-top:2px;flex-shrink:0">☐</span><span style="color:#94a3b8;font-size:13px;line-height:1.7">$1</span></div>')
    .replace(/^- (.+)$/gm,   '<div style="display:flex;align-items:flex-start;gap:8px;margin:5px 0"><span style="color:#6366f1;flex-shrink:0;margin-top:4px">•</span><span style="color:#94a3b8;font-size:13px;line-height:1.7">$1</span></div>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

const CATEGORY_ICONS = {
  "getting-started":"🚀","monitoring":"📡","alerts":"🔔","keywords":"🎯",
  "pipeline":"📋","billing":"💳","platforms":"🌐","account":"🔒",
  "troubleshooting":"🔧","strategies":"💡",
};

function KnowledgeBasePage() {
  const [categories,   setCategories]   = useState([]);
  const [articles,     setArticles]     = useState([]);
  const [activeCategory,setActiveCategory] = useState(null);
  const [activeArticle, setActiveArticle]  = useState(null);
  const [articleContent,setArticleContent] = useState(null);
  const [search,       setSearch]       = useState("");
  const [searchResults,setSearchResults] = useState(null);
  const [searching,    setSearching]    = useState(false);
  const [feedback,     setFeedback]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [articleLoading,setArticleLoading] = useState(false);
  const [mobileNav,    setMobileNav]    = useState(false);

  // ── Data loading ─────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/kb/categories`).then(r=>r.json()),
      fetch(`${API_BASE}/kb/articles`).then(r=>r.json()),
    ]).then(([cats, arts]) => {
      setCategories(Array.isArray(cats) ? cats : []);
      setArticles(Array.isArray(arts) ? arts : []);
      if (cats.length) setActiveCategory(cats[0].slug);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, []);

  const loadArticle = useCallback((slug) => {
    setArticleLoading(true);
    setFeedback(null);
    fetch(`${API_BASE}/kb/articles/${slug}`)
      .then(r=>r.json())
      .then(art => { setArticleContent(art); setActiveArticle(slug); })
      .catch(console.error)
      .finally(() => setArticleLoading(false));
  }, []);

  const doSearch = useCallback((q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    setSearching(true);
    fetch(`${API_BASE}/kb/search?q=${encodeURIComponent(q)}&limit=12`)
      .then(r=>r.json())
      .then(r => setSearchResults(Array.isArray(r) ? r : (r.results || [])))
      .catch(() => setSearchResults([]))
      .finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, doSearch]);

  const sendFeedback = (type) => {
    if (!activeArticle) return;
    setFeedback(type);
    fetch(`${API_BASE}/kb/articles/${activeArticle}/feedback`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ helpful: type === "helpful" })
    }).catch(()=>{});
  };

  const catArticles = articles.filter(a => a.category_slug === activeCategory);
  const activeCat   = categories.find(c => c.slug === activeCategory);

  const relatedArticles = articleContent
    ? articles.filter(a => a.category_slug === articleContent.category_slug && a.slug !== articleContent.slug).slice(0,4)
    : [];

  // ── Layout ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:"100vh", background:"#050508", fontFamily:"system-ui,-apple-system,sans-serif", color:"#e2e8f0" }}>

      {/* ── TOP NAV ── */}
      <nav style={{ background:"rgba(5,5,8,0.95)", backdropFilter:"blur(12px)", borderBottom:"1px solid #1e293b", padding:"0 24px", height:60, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <a href="/" style={{ fontWeight:800, fontSize:18, color:"#818cf8", textDecoration:"none", letterSpacing:-0.5 }}>Socialisten</a>
          <span style={{ color:"#334155" }}>/</span>
          <span style={{ color:"#94a3b8", fontSize:14 }}>Help Center</span>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <a href="/" style={{ padding:"6px 16px", borderRadius:7, border:"1px solid #1e293b", color:"#94a3b8", fontSize:13, textDecoration:"none" }}>← Back to app</a>
          <a href="/#register" style={{ padding:"6px 16px", borderRadius:7, border:"none", background:"#6366f1", color:"#fff", fontSize:13, textDecoration:"none" }}>Start free →</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <div style={{ background:"linear-gradient(135deg,#0f0f1a 0%,#0a0a14 100%)", borderBottom:"1px solid #1e293b", padding:"52px 24px 48px", textAlign:"center" }}>
        <div style={{ fontSize:12, color:"#6366f1", fontWeight:700, letterSpacing:3, textTransform:"uppercase", marginBottom:14 }}>Help Center</div>
        <h1 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:800, color:"#f8fafc", margin:"0 0 14px", letterSpacing:-1 }}>How can we help?</h1>
        <p style={{ color:"#64748b", fontSize:16, marginBottom:32 }}>Guides, tips, and answers for every part of Socialisten</p>

        {/* Search */}
        <div style={{ maxWidth:560, margin:"0 auto", position:"relative" }}>
          <span style={{ position:"absolute", left:16, top:"50%", transform:"translateY(-50%)", color:"#475569", fontSize:18, pointerEvents:"none" }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search articles — e.g. 'no matches', 'push notifications', 'keywords'…"
            style={{ width:"100%", background:"#0f172a", border:"1px solid #1e293b", borderRadius:12, padding:"14px 48px 14px 48px", color:"#e2e8f0", fontSize:15, outline:"none", boxSizing:"border-box",
              boxShadow:"0 4px 24px rgba(0,0,0,0.4)" }}
          />
          {search && (
            <button onClick={()=>setSearch("")} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"transparent", border:"none", color:"#475569", fontSize:18, cursor:"pointer" }}>✕</button>
          )}
        </div>
      </div>

      {/* ── MAIN ── */}
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"32px 24px", display:"grid", gridTemplateColumns:"240px 1fr", gap:32, alignItems:"start" }}>

        {/* ── SIDEBAR ── */}
        <aside style={{ position:"sticky", top:80 }}>
          <div style={{ color:"#475569", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12, paddingLeft:10 }}>Categories</div>
          <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
            {loading ? (
              Array(8).fill(0).map((_,i)=>(
                <div key={i} style={{ height:40, background:"#0f172a", borderRadius:8, marginBottom:2, animation:"pulse 1.5s infinite" }}/>
              ))
            ) : categories.map(cat => (
              <button key={cat.slug} onClick={()=>{ setActiveCategory(cat.slug); setActiveArticle(null); setArticleContent(null); setSearch(""); setSearchResults(null); }}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8, border:"none", textAlign:"left", cursor:"pointer", transition:"all 0.15s",
                  background: activeCategory===cat.slug && !activeArticle && !searchResults ? "#1e1e2e" : "transparent",
                  color: activeCategory===cat.slug && !activeArticle && !searchResults ? "#a5b4fc" : "#64748b" }}>
                <span style={{ fontSize:16, flexShrink:0 }}>{cat.icon || CATEGORY_ICONS[cat.slug] || "📄"}</span>
                <span style={{ fontSize:13, fontWeight: activeCategory===cat.slug ? 600 : 400 }}>{cat.name}</span>
                <span style={{ marginLeft:"auto", color:"#334155", fontSize:11 }}>
                  {articles.filter(a=>a.category_slug===cat.slug).length}
                </span>
              </button>
            ))}
          </div>

          {/* Popular articles */}
          {!activeArticle && !searchResults && (
            <div style={{ marginTop:28, paddingTop:20, borderTop:"1px solid #1e293b" }}>
              <div style={{ color:"#475569", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:10, paddingLeft:10 }}>Popular</div>
              {[
                { slug:"quick-start-guide",           title:"Quick Start Guide" },
                { slug:"writing-effective-keywords",   title:"Writing Effective Keywords" },
                { slug:"push-notifications-setup",     title:"Push Notifications" },
                { slug:"winning-leads",                title:"Converting Leads to Jobs" },
                { slug:"no-matches-found",             title:"Not Getting Matches" },
              ].map(a => (
                <button key={a.slug} onClick={()=>loadArticle(a.slug)}
                  style={{ display:"block", width:"100%", textAlign:"left", padding:"7px 12px", borderRadius:6, border:"none", background:"transparent", color:"#475569", fontSize:12, cursor:"pointer", transition:"color 0.1s" }}
                  onMouseEnter={e=>e.target.style.color="#a5b4fc"}
                  onMouseLeave={e=>e.target.style.color="#475569"}>
                  → {a.title}
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main>

          {/* ── SEARCH RESULTS ── */}
          {searchResults !== null ? (
            <div>
              <div style={{ color:"#94a3b8", fontSize:13, marginBottom:20 }}>
                {searching ? "Searching…" : `${searchResults.length} result${searchResults.length!==1?"s":""} for "${search}"`}
              </div>
              {searchResults.length === 0 ? (
                <div style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:12, padding:"40px 32px", textAlign:"center" }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>🤔</div>
                  <div style={{ color:"#e2e8f0", fontSize:16, fontWeight:600, marginBottom:8 }}>No articles found</div>
                  <div style={{ color:"#64748b", fontSize:14 }}>Try different words, or browse categories on the left</div>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {searchResults.map(art => (
                    <button key={art.slug} onClick={()=>loadArticle(art.slug)}
                      style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:10, padding:"16px 20px", textAlign:"left", cursor:"pointer", transition:"border-color 0.15s" }}
                      onMouseEnter={e=>e.currentTarget.style.borderColor="#6366f1"}
                      onMouseLeave={e=>e.currentTarget.style.borderColor="#1e293b"}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                        <span style={{ fontSize:12, padding:"2px 8px", borderRadius:10, background:"#1e1e2e", color:"#6366f1", fontWeight:600 }}>
                          {categories.find(c=>c.slug===art.category_slug)?.name || "Article"}
                        </span>
                      </div>
                      <div style={{ color:"#e2e8f0", fontSize:15, fontWeight:600, marginBottom:4 }}>{art.title}</div>
                      {art.excerpt && <div style={{ color:"#64748b", fontSize:13, lineHeight:1.6 }}>{art.excerpt}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>

          /* ── ARTICLE VIEW ── */
          ) : activeArticle && articleContent ? (
            <div>
              {/* Breadcrumb */}
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:24, fontSize:13, color:"#475569" }}>
                <button onClick={()=>{ setActiveArticle(null); setArticleContent(null); }}
                  style={{ background:"transparent", border:"none", color:"#6366f1", cursor:"pointer", fontSize:13, padding:0 }}>
                  ← {activeCat?.name || "Articles"}
                </button>
                <span style={{ color:"#334155" }}>/</span>
                <span style={{ color:"#94a3b8" }}>{articleContent.title}</span>
              </div>

              {/* Article card */}
              <article style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:14, padding:"36px 40px", marginBottom:16 }}>
                {articleLoading ? (
                  <div style={{ display:"flex", justifyContent:"center", padding:40 }}>
                    <div style={{ width:28, height:28, borderRadius:"50%", border:"3px solid #1e293b", borderTopColor:"#6366f1", animation:"spin 0.8s linear infinite" }}/>
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: renderMd(articleContent.content) }}
                    style={{ lineHeight:1.85, fontSize:14, color:"#94a3b8" }}/>
                )}
              </article>

              {/* Feedback */}
              {!articleLoading && (
                <div style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:10, padding:"18px 24px", marginBottom:16, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <span style={{ color:"#475569", fontSize:13 }}>Was this article helpful?</span>
                  {feedback ? (
                    <span style={{ color: feedback==="helpful"?"#22c55e":"#f59e0b", fontSize:13, fontWeight:600 }}>
                      {feedback==="helpful" ? "✓ Thanks for the feedback!" : "Got it — we'll improve this article."}
                    </span>
                  ) : (
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={()=>sendFeedback("helpful")}
                        style={{ padding:"6px 16px", borderRadius:6, border:"1px solid #22c55e30", background:"#22c55e10", color:"#22c55e", fontSize:12, cursor:"pointer" }}>
                        👍 Yes
                      </button>
                      <button onClick={()=>sendFeedback("not_helpful")}
                        style={{ padding:"6px 16px", borderRadius:6, border:"1px solid #1e293b", background:"transparent", color:"#475569", fontSize:12, cursor:"pointer" }}>
                        👎 No
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Related articles */}
              {relatedArticles.length > 0 && (
                <div>
                  <div style={{ color:"#475569", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Related Articles</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {relatedArticles.map(art => (
                      <button key={art.slug} onClick={()=>loadArticle(art.slug)}
                        style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:8, padding:"12px 16px", textAlign:"left", cursor:"pointer", transition:"border-color 0.15s" }}
                        onMouseEnter={e=>e.currentTarget.style.borderColor="#6366f1"}
                        onMouseLeave={e=>e.currentTarget.style.borderColor="#1e293b"}>
                        <div style={{ color:"#94a3b8", fontSize:13, fontWeight:600 }}>{art.title}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Still need help */}
              <div style={{ marginTop:24, background:"linear-gradient(135deg,#1e1e2e,#0f0f1a)", border:"1px solid #6366f130", borderRadius:12, padding:"20px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ color:"#e2e8f0", fontSize:14, fontWeight:600, marginBottom:4 }}>Still have questions?</div>
                  <div style={{ color:"#475569", fontSize:13 }}>Our team responds to support emails within a few hours.</div>
                </div>
                <a href="mailto:support@socialisten.com" style={{ padding:"8px 20px", borderRadius:8, border:"none", background:"#6366f1", color:"#fff", fontSize:13, textDecoration:"none", fontWeight:600 }}>
                  Contact support
                </a>
              </div>
            </div>

          /* ── CATEGORY / HOME VIEW ── */
          ) : (
            <div>
              {loading ? (
                <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
                  {Array(4).fill(0).map((_,i)=>(
                    <div key={i}>
                      <div style={{ height:28, width:180, background:"#0f172a", borderRadius:6, marginBottom:12, animation:"pulse 1.5s infinite" }}/>
                      {Array(3).fill(0).map((_,j)=>(
                        <div key={j} style={{ height:56, background:"#0f172a", borderRadius:8, marginBottom:6, animation:"pulse 1.5s infinite" }}/>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                /* All categories, always expanded — each shows all its articles */
                <div style={{ display:"flex", flexDirection:"column", gap:36 }}>
                  {categories.map(cat => {
                    const catArts = articles.filter(a => a.category_slug === cat.slug);
                    const isActive = activeCategory === cat.slug;
                    return (
                      <div key={cat.slug} id={`cat-${cat.slug}`}>
                        {/* Category header */}
                        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, paddingBottom:12, borderBottom:"1px solid #1e293b" }}>
                          <span style={{ fontSize:22 }}>{cat.icon || CATEGORY_ICONS[cat.slug] || "📄"}</span>
                          <div style={{ flex:1 }}>
                            <h2 style={{ color:"#f1f5f9", fontSize:17, fontWeight:700, margin:"0 0 2px" }}>{cat.name}</h2>
                            {cat.description && <p style={{ color:"#475569", fontSize:12, margin:0 }}>{cat.description}</p>}
                          </div>
                          <span style={{ color:"#334155", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                            {catArts.length} article{catArts.length!==1?"s":""}
                          </span>
                        </div>

                        {/* Article list for this category */}
                        {catArts.length === 0 ? (
                          <div style={{ color:"#334155", fontSize:13, padding:"8px 0" }}>No articles yet.</div>
                        ) : (
                          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                            {catArts.map((art, i) => (
                              <button key={art.slug} onClick={()=>{ loadArticle(art.slug); setActiveCategory(cat.slug); }}
                                style={{ background:activeArticle===art.slug?"#1e1e2e":"transparent", border:`1px solid ${activeArticle===art.slug?"#6366f1":"transparent"}`, borderRadius:8, padding:"12px 14px", textAlign:"left", cursor:"pointer", display:"flex", alignItems:"center", gap:12, transition:"all 0.12s", width:"100%" }}
                                onMouseEnter={e=>{ if(activeArticle!==art.slug){ e.currentTarget.style.background="#0f172a"; e.currentTarget.style.borderColor="#1e293b"; }}}
                                onMouseLeave={e=>{ if(activeArticle!==art.slug){ e.currentTarget.style.background="transparent"; e.currentTarget.style.borderColor="transparent"; }}}>
                                <span style={{ color:"#334155", fontSize:12, fontFamily:"'DM Mono',monospace", width:20, textAlign:"right", flexShrink:0 }}>{i+1}</span>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ color: activeArticle===art.slug?"#a5b4fc":"#e2e8f0", fontSize:14, fontWeight:activeArticle===art.slug?600:400, lineHeight:1.4 }}>{art.title}</div>
                                </div>
                                <span style={{ color: activeArticle===art.slug?"#6366f1":"#334155", fontSize:16, flexShrink:0 }}>→</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop:"1px solid #1e293b", padding:"28px 24px", marginTop:40 }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <div style={{ color:"#334155", fontSize:13 }}>© 2026 Socialisten · <a href="/" style={{ color:"#6366f1", textDecoration:"none" }}>Back to app</a></div>
          <div style={{ display:"flex", gap:20 }}>
            <a href="/privacy" style={{ color:"#475569", fontSize:12, textDecoration:"none" }}>Privacy</a>
            <a href="/terms" style={{ color:"#475569", fontSize:12, textDecoration:"none" }}>Terms</a>
            <a href="mailto:support@socialisten.com" style={{ color:"#475569", fontSize:12, textDecoration:"none" }}>Support</a>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:.6} 50%{opacity:1} }
        * { box-sizing: border-box; }
        button { font-family: inherit; }
      `}</style>
    </div>
  );
}



function NotificationBell({ api }) { return <NotificationCenter api={api}/>; }
function NotificationDropdown({ api }) { return null; }
function SecurityDashboard({ api, toast }) {
  return <AdminView api={api} toast={toast} initialTab="security"/>;
}
function AdminCMS({ api, toast }) { return <CMSEditorView api={api} toast={toast}/>; }
function BillingSettings({ api, toast, userPlan }) { return <BillingView api={api} toast={toast} userPlan={userPlan}/>; }
class ViewErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { error:null }; }
  static getDerivedStateFromError(e) { return { error:e }; }
  render() {
    if (this.state.error) return <div style={{padding:40,textAlign:"center",color:"#ff4d4d",fontSize:13}}>⚠ Something went wrong loading this view. Try refreshing.<br/><button onClick={()=>this.setState({error:null})} style={{marginTop:12,padding:"6px 14px",borderRadius:6,border:"1px solid #ff4d4d30",background:"transparent",color:"#ff4d4d",cursor:"pointer",fontSize:11}}>Retry</button></div>;
    return this.props.children;
  }
}
function WeeklyEmailSettings({ api, toast }) { return null; }
function SaveBar({ saving, onSave, onReset, section }) {
  return (
    <div style={{display:"flex",gap:8,marginTop:12}}>
      <button onClick={onSave} disabled={saving} style={{padding:"8px 18px",borderRadius:6,border:"none",background:saving?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:12,cursor:saving?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
        {saving?<><Spinner size={11}/>Saving…</>:"💾 Save changes"}
      </button>
      {onReset&&<button onClick={onReset} style={{padding:"8px 14px",borderRadius:6,border:"1px solid #1e1e2e",background:"transparent",color:"#4a4a6a",fontSize:12,cursor:"pointer"}}>Reset</button>}
    </div>
  );
}
function AnnouncePanel({ api, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/announce").then(setData).catch(()=>{}).finally(()=>setLoading(false));
  }, []);
  return (
    <div style={{background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"24px 28px"}}>
      <div style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:12}}>Announce</div>
      {loading ? <div style={{display:"flex",justifyContent:"center",padding:40}}><Spinner size={24}/></div>
               : <div style={{color:"#4a4a6a",fontSize:13}}>{data ? JSON.stringify(data).slice(0,200) : "No data"}</div>}
    </div>
  );
}

function StatusPage({ api, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/status").then(setData).catch(()=>{}).finally(()=>setLoading(false));
  }, []);
  return (
    <div style={{background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"24px 28px"}}>
      <div style={{color:"#fff",fontSize:14,fontWeight:700,marginBottom:12}}>Status</div>
      {loading ? <div style={{display:"flex",justifyContent:"center",padding:40}}><Spinner size={24}/></div>
               : <div style={{color:"#4a4a6a",fontSize:13}}>{data ? JSON.stringify(data).slice(0,200) : "No data"}</div>}
    </div>
  );
}

function RefundPanel({ api, toast }) { return <DunningRefundPanel api={api} toast={toast}/>; }
function NextdoorSession({ api, toast }) {
  const [status, setStatus] = React.useState(null);
  React.useEffect(()=>{ api.get("/settings/nextdoor/status").then(setStatus).catch(()=>{}); }, []);
  const connect    = () => api.post("/settings/nextdoor/connect").then(()=>{ toast.success("Nextdoor connecting — follow the browser prompt"); api.get("/settings/nextdoor/status").then(setStatus); }).catch(e=>toast.error(e.message));
  const disconnect = () => api.post("/settings/nextdoor/disconnect").then(()=>{ toast.success("Nextdoor disconnected"); setStatus({connected:false}); }).catch(e=>toast.error(e.message));
  return (
    <div style={{background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"18px 22px",marginBottom:14}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
        <div style={{width:36,height:36,borderRadius:9,background:"#00b36220",border:"1px solid #00b36230",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>🟢</div>
        <div style={{flex:1}}>
          <div style={{color:"#fff",fontSize:13,fontWeight:600}}>Nextdoor Session</div>
          <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:status?.connected?"#22c55e":"#4a4a6a"}}/>
            <span style={{color:status?.connected?"#22c55e":"#4a4a6a",fontSize:11}}>{status?.connected?"Connected":"Not connected"}</span>
          </div>
        </div>
        {status?.connected
          ? <button onClick={disconnect} style={{padding:"5px 14px",borderRadius:5,border:"1px solid #ff4d4d30",background:"transparent",color:"#ff4d4d",fontSize:11,cursor:"pointer"}}>Disconnect</button>
          : <button onClick={connect}    style={{padding:"5px 14px",borderRadius:5,border:"none",background:"#00b362",color:"#fff",fontSize:11,cursor:"pointer"}}>Connect Nextdoor →</button>
        }
      </div>
      <div style={{color:"#4a4a6a",fontSize:12,lineHeight:1.7}}>Connect your Nextdoor account to monitor neighborhood feeds for local contractor leads.</div>
    </div>
  );
}


function Toggle({ on, toggle, label }) {
  return (
    <div onClick={toggle} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
      <div style={{width:36,height:20,borderRadius:10,background:on?"#22c55e":"#2a2a3e",position:"relative",transition:"all 0.2s",flexShrink:0}}>
        <div style={{position:"absolute",top:2,left:on?16:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"all 0.2s"}}/>
      </div>
      {label && <span style={{color:"#ccc",fontSize:12}}>{label}</span>}
    </div>
  );
}

function DLBtn({ href, label, style={} }) {
  return (
    <a href={href} download style={{padding:"6px 14px",borderRadius:6,border:"1px solid #2a2a3e",background:"transparent",color:"#a5b4fc",fontSize:11,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:5,...style}}>
      📥 {label||"Download"}
    </a>
  );
}

function NewTradeInput({ value, onChange }) {
  return (
    <input value={value} onChange={e=>onChange(e.target.value)}
      placeholder="Custom trade name (e.g. Fence Repair)"
      style={{width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:6,padding:"8px 12px",color:"#ccc",fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:"'DM Mono',monospace"}}/>
  );
}

function AdminGuides({ api, toast }) {
  const [activeSection, setActiveSection] = useState(null);
  const [activeArticle,  setActiveArticle]  = useState(null);
  const [search, setSearch] = useState("");

  const GUIDE = [
    // ─── GETTING STARTED ───────────────────────────────────────────────────
    {
      id:"getting-started", icon:"🚀", color:"#6366f1", title:"Getting Started",
      desc:"Everything a new user needs to get their first lead in under 10 minutes.",
      articles:[
        {
          id:"what-is-socialisten", title:"What Is Socialisten?",
          body:`Socialisten monitors Facebook groups, Reddit communities, and Nextdoor neighborhoods 24/7 and sends an instant alert the moment someone posts about needing your service.

## The problem it solves

Homeowners post on social media when something breaks — "my furnace stopped working," "anyone know a good plumber," "need a roofer ASAP." These posts are live leads from people who need help right now. They disappear fast. The first contractor who sees and responds usually wins the job.

Most contractors never see these posts. They're busy working. They don't have time to check 20 Facebook groups manually. Socialisten does it automatically, every 15–30 minutes.

## How it works

1. You add pages to monitor (Facebook groups, subreddits, Nextdoor feeds)
2. You add keywords (phrases homeowners use when they need your service)
3. Socialisten scans continuously and alerts you when a post matches
4. You respond directly to the post — no middleman, no per-lead fee

## Who it's for

Any contractor where homeowners post online asking for help: HVAC, plumbing, electrical, roofing, landscaping, pest control, general contracting, and dozens more.`
        },
        {
          id:"quick-start", title:"Quick Start — First Lead in 10 Minutes",
          body:`## Step 1 — Add a monitored page

Go to **Monitored Pages** → **+ Add Page**.

- **Facebook group:** search Facebook for "[your city] homeowners" and paste the URL
- **Reddit:** paste any subreddit URL, e.g. https://www.reddit.com/r/Detroit/
- **Nextdoor:** connect your Nextdoor account first (Settings), then paste your neighborhood URL

## Step 2 — Add keywords

Think like a frustrated homeowner:
- "furnace not working" / "no heat" / "furnace broken"
- "pipe burst" / "no hot water" / "water heater dead"
- "roof leaking" / "electrician help"

Use the **Keyword Suggestions** panel for pre-built sets by trade.

## Step 3 — Run your first scan

Click **Scan Now** on any page. Results appear in the Feed within 60 seconds.

## Step 4 — Enable instant alerts

Go to **Settings → Alert Settings** and enable push notifications or connect Telegram. Email is too slow — you need alerts within seconds.

## Step 5 — Respond fast

When a match arrives, click **↗ Reply** to open the original post. Respond within 5 minutes. Speed wins jobs.`
        },
        {
          id:"first-week", title:"First Week Checklist",
          body:`## Day 1
☐ Add at least 3 monitored pages (aim for 5–10)
☐ Add keywords for your primary trade using the suggestion panel
☐ Run a manual scan on each page
☐ Enable push notifications or connect Telegram
☐ Set your minimum score threshold to 20

## Day 2–3
☐ Review matches that came in overnight
☐ Note which keyword sets produce the best leads
☐ Add more pages — especially Facebook groups in surrounding suburbs
☐ Connect Nextdoor if your service area is residential

## Day 4–5
☐ Check Analytics → Response Time
☐ Try responding to 3–5 matches and note what converts
☐ Add negative keywords to filter irrelevant posts ("DIY", "already fixed")
☐ Adjust score threshold based on match quality

## Day 6–7
☐ Review your weekly summary email
☐ Add any missing trades or keyword sets
☐ Set up a webhook to your CRM if you use one

> The single biggest factor in converting leads is response time. Enable instant alerts and respond within minutes — not hours.`
        },
      ]
    },

    // ─── MONITORED PAGES ───────────────────────────────────────────────────
    {
      id:"pages", icon:"📡", color:"#22c55e", title:"Monitored Pages",
      desc:"How to add and manage the Facebook groups, subreddits, and Nextdoor feeds you monitor.",
      articles:[
        {
          id:"adding-pages", title:"Adding Monitored Pages",
          body:`Go to **Monitored Pages → + Add Page** and enter:

- **URL** — the Facebook group URL, subreddit URL, or Nextdoor neighborhood URL
- **Platform** — Facebook, Reddit, or Nextdoor
- **Name** — a label for your reference (e.g. "Detroit Homeowners Group")
- **Keywords** — phrases to watch for on that specific page

## URL formats

**Facebook:** https://www.facebook.com/groups/detroithomeowners

**Reddit:** https://www.reddit.com/r/Detroit/

**Nextdoor:** connect your account first in Settings, then paste your neighborhood URL from the Nextdoor app

## How many pages?

| Plan | Pages | Suggested setup |
|---|---|---|
| Free | 2 | 1 Facebook group, 1 subreddit |
| Starter | 15 | 8 Facebook groups, 4 subreddits, 3 Nextdoor |
| Pro | 30 | Full service area coverage |

## Finding good groups

Search Facebook for: [city] homeowners · [city] neighbors · [city] community · [county] buy sell trade

Signs of a good group: 1,000+ members, active in last 24 hours, posts visible in feed, mix of questions and recommendations.`
        },
        {
          id:"scan-schedule", title:"Scan Schedule & Timing",
          body:`## How scans are scheduled

Socialisten scans every page automatically based on your plan interval:

| Plan | Interval | Platforms |
|---|---|---|
| Free | 60 minutes | All platforms |
| Starter | 30 minutes | All platforms |
| Pro | 15 minutes | All platforms |

**Time window:** Facebook and Nextdoor scan between **8am–9pm** in your local timezone. Reddit scans 24/7 since it's global.

## Manual scans

Click **Scan Now** on any page to trigger an immediate scan outside the schedule. Results appear within 60 seconds.

## Scan health

If a page hasn't scanned in 48+ hours you receive an email alert. Common causes: session expired, page unavailable, network issue.

Check **Monitored Pages** for any pages showing error status and click Scan Now to retry.`
        },
        {
          id:"managing-pages", title:"Managing Pages",
          body:`## Page controls

- **Pause** — temporarily stop scanning without deleting (use during vacation)
- **Edit** — update keywords, name, or interval
- **Scan Now** — trigger an immediate scan
- **Delete** — permanently remove the page and all its matches

## Scan errors

**Session expired** — go to Settings and reconnect your Facebook or Nextdoor account.

**Rate limited** — the platform temporarily blocked the scan. Socialisten backs off and retries automatically. Usually resolves within 30–60 minutes.

**Page not found** — the URL may have changed or the group may have been deleted. Verify the URL is correct and you're a member.

**Access denied** — you're not a member of the private group. Join with your monitoring account first.`
        },
      ]
    },

    // ─── KEYWORDS ─────────────────────────────────────────────────────────
    {
      id:"keywords", icon:"🎯", color:"#f59e0b", title:"Keywords & Scoring",
      desc:"Write keywords that find real leads and filter out noise.",
      articles:[
        {
          id:"writing-keywords", title:"Writing Effective Keywords",
          body:`## The golden rule

Write keywords the way homeowners talk — not the way contractors talk.

A homeowner doesn't post "seeking HVAC services." They post "my furnace stopped working and it's 20 degrees, help."

## Good keywords by trade

**HVAC:** furnace not working · no heat · furnace broken · AC stopped working · heat pump issues · boiler problems

**Plumbing:** pipe burst · no hot water · water heater broken · toilet clogged · leak under sink

**Electrical:** circuit breaker keeps tripping · outlets not working · no power · electrician help

**Roofing:** roof leaking · shingles missing · roof damage

**General:** contractor needed · renovation quotes · looking for handyman

## Bad keywords (too broad)

HVAC · plumber · house · help — these match ads, business listings, and irrelevant posts.

## How many keywords?

Add 10–30 per page. Multi-word phrases ("furnace not working") are far more targeted than single words ("furnace").

## Keyword Suggestions panel

Click **Keyword Suggestions** below the keyword input for pre-built sets by trade. These are based on real homeowner language.`
        },
        {
          id:"negative-keywords", title:"Negative Keywords",
          body:`Negative keywords exclude posts that contain certain words — even if they also match your positive keywords.

## When to use them

When you keep seeing matches that aren't real leads:
- Posts from other contractors advertising
- DIY posts from people who don't want to hire
- Posts about a topic that shares words with your trade
- Already-resolved issues

## Common negative keywords

**All trades:** DIY · do it myself · already fixed · found someone · never mind · just curious

**HVAC:** for sale (people selling used equipment) · solar · rent

**Plumbing:** for sale · rent

**Electrical:** solar · EV charger (if you don't do those)

## How to add them

Edit any monitored page → scroll to **Negative Keywords** → add any phrase that should exclude a post.

Negative keywords are per-page and checked before the match is saved.`
        },
        {
          id:"lead-scores", title:"Understanding Lead Scores",
          body:`Every match gets a score from 0–100 indicating how likely it is to be a genuine, high-intent lead.

## What affects the score

- **Keyword match quality** — specific phrases score higher than broad ones
- **Urgency signals** — "ASAP", "emergency", "tonight", "urgent", "help"
- **Hiring intent** — "need someone", "looking for", "recommendations for a"
- **Negative signals** — DIY language, "already handled", "just curious"
- **Post length** — longer, detailed posts describing a real problem score higher

## Score ranges

| Score | Meaning |
|---|---|
| 75–100 | 🔥 Hot lead — urgent, clear intent to hire |
| 50–74 | ⚡ Warm lead — likely needs a contractor |
| 25–49 | 📋 Cool lead — related, low urgency |
| 0–24 | ⚪ Low relevance — loosely related |

## Setting your threshold

Go to **Settings → Alert Settings → Score Threshold**.

- New users: start at **20** (see more, learn what converts)
- After 1 month: **40–50** (based on your experience)
- Busy contractors: **65+** (only the hottest leads)

The **Score Threshold Advisor** appears after 50+ matches and suggests an optimal threshold based on your history.`
        },
      ]
    },

    // ─── ALERTS ──────────────────────────────────────────────────────────
    {
      id:"alerts", icon:"🔔", color:"#0ea5e9", title:"Alerts & Notifications",
      desc:"Push, Telegram, SMS, and email — how to set up each channel.",
      articles:[
        {
          id:"alert-priority", title:"How Alert Priority Works",
          body:`When a match is found, Socialisten sends alerts in priority order. The first available channel fires; others are skipped.

## Priority order

1. **Browser push** — instant, works even with tab closed (free)
2. **Telegram** — instant message to your phone (free)
3. **Twilio SMS** — text message (requires Twilio account)
4. **Email** — always on as fallback

## What this means

If push notifications are enabled and a match is found → you get a push notification. Telegram, SMS, and email are skipped.

If push isn't enabled but Telegram is connected → you get a Telegram message.

Email is always sent unless you explicitly disable it in Settings → Alert Settings.

## SMS Always mode

Enable **SMS Always** in Settings → Alert Settings to send SMS regardless of other channels — useful if you want both Telegram and a text.`
        },
        {
          id:"push-notifications", title:"Push Notifications Setup",
          body:`Push notifications appear on your screen within seconds of a match — even when the Socialisten tab is closed.

## How to enable

1. Go to **Settings → Alert Settings**
2. Click **Enable push notifications**
3. Click **Allow** when your browser asks for permission
4. You'll see a green "Active" status

## Platform support

| Browser | Support |
|---|---|
| Chrome (desktop + Android) | ✅ Full |
| Firefox (desktop) | ✅ Full |
| Edge (desktop) | ✅ Full |
| Safari (iOS 16.4+) | ✅ Must install as PWA first |
| Safari (iOS < 16.4) | ❌ Use Telegram instead |

## Installing as a PWA (iPhone)

1. Open Socialisten in Safari
2. Tap the Share button (box with arrow)
3. Tap **Add to Home Screen**
4. Open Socialisten from your home screen icon
5. Enable push notifications in Settings

## Troubleshooting

**No notifications appearing:** Check that notifications are allowed in your browser settings and OS settings.

**"Push not configured" error:** Your admin needs to set up VAPID keys in Admin → Site Editor → Integrations → Push Alerts.`
        },
        {
          id:"telegram-alerts", title:"Telegram Alerts",
          body:`Telegram delivers match alerts directly to your phone for free — no Twilio account needed. Works on iPhone, Android, and desktop.

## Why Telegram?

- Completely free — no per-message charges
- Works everywhere — iPhone, Android, desktop, tablet
- Instant — messages arrive within seconds
- No spam filters — messages always arrive
- Rich format — includes platform, score, post text, and a reply link

## How to connect

1. Go to **Settings → Alert Settings**
2. Under **Telegram Alerts**, click **Connect Telegram →**
3. Click **Open in Telegram** (or copy link to your phone)
4. Tap **Start** in Telegram
5. The bot confirms your account is connected

The link expires in 10 minutes. If it expires, click Connect again.

## What the alerts look like

🎯 Score 87 — Detroit Homeowners

Platform: Facebook  
"Furnace stopped working last night. House is at 58°F with two kids. Need someone ASAP."

↗ Reply on Facebook · 📊 Open dashboard`
        },
      ]
    },

    // ─── PIPELINE ────────────────────────────────────────────────────────
    {
      id:"pipeline", icon:"📋", color:"#8b5cf6", title:"Lead Pipeline",
      desc:"Track leads from first match to booked and won job.",
      articles:[
        {
          id:"lead-statuses", title:"Lead Statuses",
          body:`Every match starts as **New** and moves through these stages:

| Status | Meaning |
|---|---|
| New | Just found, not yet reviewed |
| Contacted | You've reached out to the homeowner |
| Quoted | You've provided a price or estimate |
| Booked | Job is scheduled and confirmed |
| Won | Job completed |
| Lost | Didn't convert |

## How to update a status

Click the status dropdown on any match card in the Feed and select the new stage. The timestamp is recorded automatically.

## Why tracking matters

Status tracking unlocks **Analytics → Response Time** which shows:
- Average time from match to first contact
- Response rate — what % of leads you actually reach out to
- Conversion rate — contacted → booked/won
- Fastest and slowest response times

## Claimed leads

When you mark a lead as Contacted, Booked, or Won, other Socialisten users monitoring the same group see a "🏳 Claimed" badge on that match. This is anonymous — only your trade category is shown. It prevents multiple contractors chasing the same lead unnecessarily.`
        },
        {
          id:"response-time", title:"Response Time — Why Speed Wins",
          body:`Speed is the single most important factor in converting Socialisten leads into booked jobs.

## The data

When someone posts "my furnace broke, need help" on Facebook, they get multiple responses. The contractor who responds first has a massive advantage — they're at the top of the conversation, the homeowner's problem is fresh and urgent, and the homeowner hasn't committed to anyone else yet.

Studies of home service leads consistently show that **the first contractor to respond wins the job 50–70% of the time**.

## What response times look like

| Response time | Outcome |
|---|---|
| Under 5 minutes | Excellent — you're almost certainly first |
| 5–30 minutes | Good — you may still be first |
| 30–60 minutes | Competitive — homeowner is probably talking to others |
| Over 1 hour | Late — most homeowners have moved on |

## The fastest setup

1. Enable push notifications (Settings → Alert Settings)
2. Connect Telegram (works on iPhone too)
3. Keep a response template ready for your most common lead type
4. Respond within 2 minutes of receiving the alert`
        },
        {
          id:"reply-templates", title:"Reply Templates",
          body:`Pre-written templates let you respond to a lead in under 30 seconds.

## Finding templates

- **Settings → Reply Templates** — full browser with 90+ built-in templates by trade
- **Match card → 📝 Templates** — compact view while viewing a specific lead

## What makes a great first response

Short, specific, action-oriented:

✅ "Hi! Saw your post about the furnace — I'm an HVAC tech available today for a free diagnostic. What area are you in?"

❌ "Hello! I am a licensed HVAC contractor with 15 years of experience serving the greater Detroit metropolitan area..."

The second one gets ignored. The first one gets a reply.

## Creating your own templates

Settings → Reply Templates → ✏️ My Templates → Create new template.

Add {variable} placeholders that you fill in at send time. The SMS segment counter shows how many text messages your template will use.`
        },
      ]
    },

    // ─── PLATFORMS ───────────────────────────────────────────────────────
    {
      id:"platforms", icon:"🌐", color:"#ec4899", title:"Platforms",
      desc:"Facebook, Reddit, and Nextdoor — how each works and tips for each.",
      articles:[
        {
          id:"facebook", title:"Facebook Group Monitoring",
          body:`Facebook groups are the highest-volume source of local contractor leads.

## Types of groups to monitor

**Homeowner groups** — best source. Search "[city] homeowners," "[city] home improvement," "[county] property owners."

**Neighborhood groups** — slightly broader but still good. "[city] neighbors," "[neighborhood] community."

**Buy/sell/trade groups** — surprisingly good. People post service requests here. "[city] buy sell trade."

**Trade-specific groups** — lower volume, high intent. "[trade] help [city]."

## Tips for better results

- Join the groups yourself and get accepted before connecting them
- Use a dedicated monitoring account — not your personal Facebook
- Add 5–10 Facebook groups to give yourself enough volume
- Don't add groups you're not a member of — scans will fail

## Session maintenance

Facebook sessions last 2–6 weeks. When yours expires, you'll get an email. Reconnect takes 30 seconds: Settings → Facebook Session → Reconnect.

## Post timing

Facebook groups are most active 7–10am and 6–9pm on weekdays. Check your feed manually during these times in addition to automated alerts.`
        },
        {
          id:"reddit", title:"Reddit Monitoring",
          body:`Reddit users asking for contractor help are high-intent — they've already decided to hire a professional and are looking for a recommendation.

## No login required

Reddit monitoring works without connecting any account.

## Best subreddits

**Local subreddits** — search for your city: r/Detroit, r/Chicago, r/Nashville. Most cities over 200,000 have an active subreddit.

**National trade subreddits** — high volume, use geo keywords to filter:
- r/HomeImprovement — 5M+ members
- r/hvacadvice, r/Plumbing, r/electrical, r/roofing

**Geographic filtering on national subs** — add your city as a keyword: "Detroit furnace" matches only Detroit posts on r/HomeImprovement.

## How to respond on Reddit

Comment on the post publicly first — introduce yourself, offer to help, ask what they need. Don't just drop your phone number; that gets you banned. A helpful, recommendation-style response works everywhere: "I'm an HVAC tech in your area, happy to take a look."`
        },
        {
          id:"nextdoor", title:"Nextdoor Monitoring",
          body:`Nextdoor is neighborhood-level social media — hyper-local and less competitive than Facebook.

## Why Nextdoor is different

- Posts are from actual neighbors in your service area
- Verified residents — recommendations carry real weight
- Fewer contractors monitor Nextdoor than Facebook
- Users often ask "who did you use?" and share contractor names

## Connecting your account

1. Go to **Settings**
2. Scroll to **Nextdoor Session**
3. Click **Connect** and follow the login flow
4. Add your neighborhood URL as a monitored page

## What Nextdoor leads look like

"Does anyone have a good electrician they recommend?"
"My water heater went out — anyone use someone they liked recently?"
"Looking for a reliable landscaper in the area."

Responding in kind — helpful, not salesy — converts better on Nextdoor than on other platforms.`
        },
      ]
    },

    // ─── STRATEGIES ──────────────────────────────────────────────────────
    {
      id:"strategies", icon:"💡", color:"#14b8a6", title:"Lead Strategies",
      desc:"Proven tactics for converting matches into booked jobs.",
      articles:[
        {
          id:"winning-leads", title:"Converting Matches Into Booked Jobs",
          body:`## 1. Respond in the first 5 minutes

Enable push notifications or Telegram and make responding a habit. Even a 30-second message from your phone beats a perfectly crafted response 2 hours later.

## 2. Comment publicly, then move to DM

On Facebook and Reddit, comment on the post first. This establishes your presence and builds trust. Then follow up with a private message.

Public: "Hey! I'm an HVAC tech in your area — happy to come take a look. What part of [city] are you in?"

DM: "Hi [name], reaching out about your furnace post. I can be there this afternoon for a free diagnostic. Would 3pm or 5pm work?"

## 3. Be specific, not salesy

✅ "I saw you mentioned no heat with kids in the house — that's urgent. I can be there in 2 hours."

❌ "I am a licensed professional HVAC contractor serving the greater Detroit area..."

## 4. Always offer a specific time

Don't say "I'm available." Say "I can be there today at 3pm or tomorrow at 9am — which works better?" Giving options forces a decision.

## 5. Follow up once

If you don't hear back within 24 hours, one follow-up: "Hey, just following up on my message about [issue]. Still available to help if you're still looking." No more than two messages without a response.`
        },
        {
          id:"service-area", title:"Covering Your Service Area",
          body:`## Map your service area first

Before adding pages, think about:
- Which cities and suburbs do you serve?
- What's your maximum drive time?
- Which areas are most profitable?

## Page allocation by plan

**Starter (15 pages):**
- 8 Facebook groups (main city + surrounding suburbs)
- 4 subreddits (local + national trade)
- 3 Nextdoor neighborhoods

**Pro (30 pages):**
- 15–18 Facebook groups covering full service area
- 8 subreddits
- 4–7 Nextdoor neighborhoods

## Geographic filtering on national subreddits

When monitoring r/HomeImprovement, add your city as part of keywords so you only match local posts:

Detroit AND furnace · Michigan AND HVAC

## Expanding over time

Add new pages when you have capacity to handle more leads. If current pages generate more leads than you can respond to, improve your conversion process first — then expand.`
        },
        {
          id:"reputation", title:"Building Your Reputation in Groups",
          body:`## Be helpful before you're needed

Occasionally comment on posts with useful advice — even when you're not pitching. Answer whether someone should replace or repair. Explain what a symptom might mean. This builds trust and name recognition.

## Get recommended

After completing a job sourced from a Facebook group, ask the homeowner to mention you in the group: "Feel free to mention me in [group name] if anyone else needs HVAC help." Most happy customers will do it.

## Don't spam

Don't post promotional messages in groups. Don't comment on every post with your contact info. Group admins ban contractors who do this.

## Build a professional presence

When a homeowner clicks your profile, they should see: a profile photo (your logo or headshot), a bio mentioning your trade and service area, and recent posts or reviews.

## Respond to recommendations

When someone recommends you in a group, comment to say thanks and offer your contact info. Other readers are watching.`
        },
      ]
    },

    // ─── ACCOUNT ─────────────────────────────────────────────────────────
    {
      id:"account", icon:"🔒", color:"#64748b", title:"Account & Settings",
      desc:"Profile, billing, notifications, webhooks, and privacy.",
      articles:[
        {
          id:"account-settings", title:"Account Settings Overview",
          body:`## Profile

- **Email** — your login email. Changes require verification.
- **Password** — must be 8+ characters with a number and special character.
- **Time zone** — affects scan scheduling and timestamps. Default: America/Detroit.

## Alert Settings

- **Minimum score** — lowest score that triggers an alert. Start at 20.
- **Browser push** — enable instant browser notifications
- **Telegram** — connect your Telegram account
- **Email alerts** — on by default. Toggle off to stop email alerts.
- **SMS** — optional paid alerts via Twilio

## Facebook Session

Connect or reconnect your Facebook monitoring account. Sessions last 2–6 weeks.

## Nextdoor Session

Connect your Nextdoor account for neighborhood monitoring.

## Webhooks

Add webhook URLs for Zapier, Make, or custom CRM integrations. Up to 5 webhooks per account. Each includes HMAC signing for security.

## Billing

View your current plan, next billing date, and payment method. Upgrade, downgrade, or cancel anytime.

## Danger Zone

Export all your data as JSON, or permanently delete your account.`
        },
        {
          id:"billing", title:"Plans & Billing",
          body:`## Plans

| | Free | Starter | Pro |
|---|---|---|---|
| Price | $0 | $59/mo | $89/mo |
| Pages | 2 | 15 | 30 |
| Scan interval | 60 min | 30 min | 15 min |
| Push + Telegram | ✅ | ✅ | ✅ |
| SMS | ❌ | ✅ | ✅ |
| Analytics | Basic | Full | Full |
| Webhooks | ❌ | ✅ | ✅ |

All paid plans include a 7-day free trial. No credit card required to start.

## Upgrading

Settings → Billing → Upgrade. Takes effect immediately. You're charged the prorated difference for the remainder of your billing period.

## Cancellation

Settings → Billing → Cancel. Your access continues until the end of your current billing period. Your account moves to Free automatically — pages over the Free limit are paused but not deleted. You can resubscribe anytime to resume.`
        },
        {
          id:"privacy", title:"Your Data & Privacy",
          body:`## What Socialisten stores

- Account data: email, encrypted password, plan, billing (handled by Stripe — we never see your card number)
- Monitored pages: URLs, names, keywords, scan settings
- Matches: text and URL of posts that matched your keywords
- Alert settings: notification preferences, phone number (if added), Telegram connection
- Session data: Facebook and Nextdoor session tokens, stored encrypted

## What we don't store

- Content of posts that didn't match your keywords
- Other users' personal information
- Your browsing activity outside Socialisten

## Exporting your data

Settings → Danger Zone → 📥 Export data. Downloads a JSON file with your profile, pages, keywords, and last 1,000 matches.

## Security

- Passwords hashed with bcrypt (12 rounds)
- All connections HTTPS
- JWT tokens with HS256, 30-minute expiry
- Session tokens encrypted at rest`
        },
      ]
    },
  ];

  // Build search index
  const searchIndex = GUIDE.flatMap(section =>
    section.articles.map(art => ({ ...art, sectionId: section.id, sectionTitle: section.title, sectionIcon: section.icon, sectionColor: section.color }))
  );

  const searchResults = search.trim().length > 1
    ? searchIndex.filter(a =>
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.body.toLowerCase().includes(search.toLowerCase())
      )
    : null;

  // Markdown renderer
  const md = (text) => {
    if (!text) return "";
    return text
      .replace(/```([sS]*?)```/g, (_, c) => `<pre style="background:#0a0a0f;border:1px solid #1e1e2e;border-radius:6px;padding:12px;overflow-x:auto;font-size:11px;line-height:1.7;font-family:'DM Mono',monospace;color:#22c55e;margin:10px 0">${c.trim()}</pre>`)
      .replace(/`([^`]+)`/g, '<code style="background:#1e1e2e;color:#818cf8;padding:1px 5px;border-radius:3px;font-size:11px">$1</code>')
      .replace(/^\|(.+)\|$/gm, m => {
        const cells = m.replace(/^\||\s*\|$/g,'').split('|');
        return '<div style="display:flex;border-bottom:1px solid #1e1e2e">' + cells.map((c,i) => `<div style="flex:1;padding:6px 10px;color:${i===0?'#e2e8f0':'#64748b'};font-size:12px">${c.trim()}</div>`).join('') + '</div>';
      })
      .replace(/^## (.+)$/gm, '<div style="color:#f1f5f9;font-size:15px;font-weight:700;margin:20px 0 8px;padding-bottom:6px;border-bottom:1px solid #1e1e2e">$1</div>')
      .replace(/^# (.+)$/gm,  '<div style="color:#fff;font-size:18px;font-weight:800;margin:0 0 12px">$1</div>')
      .replace(/^> (.+)$/gm,  '<div style="border-left:3px solid #6366f1;padding:8px 12px;color:#94a3b8;font-size:12px;margin:10px 0;font-style:italic">$1</div>')
      .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#f1f5f9;font-weight:700">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em style="color:#a5b4fc">$1</em>')
      .replace(/✅/g, '<span style="color:#22c55e">✅</span>')
      .replace(/❌/g, '<span style="color:#ff4d4d">❌</span>')
      .replace(/☐/g, '<span style="color:#4a4a6a">☐</span>')
      .replace(/☑/g, '<span style="color:#22c55e">☑</span>')
      .replace(/^- (.+)$/gm, '<div style="display:flex;align-items:flex-start;gap:8px;margin:4px 0"><span style="color:#6366f1;flex-shrink:0;margin-top:3px">•</span><span style="color:#94a3b8;font-size:13px;line-height:1.6">$1</span></div>')
      .replace(/\n\n/g,'<br/><br/>').replace(/\n/g,'<br/>');
  };

  const activeSec = GUIDE.find(s => s.id === activeSection);
  const activeArt = activeSec?.articles.find(a => a.id === activeArticle);

  return (
    <div style={{ display:"flex", gap:0, height:"calc(100vh - 120px)", overflow:"hidden" }}>

      {/* ── SIDEBAR ── */}
      <div style={{ width:240, flexShrink:0, borderRight:"1px solid #1e1e2e", overflowY:"auto", padding:"0 0 20px" }}>
        {/* Search */}
        <div style={{ padding:"14px 14px 10px", position:"sticky", top:0, background:"#050508", zIndex:10 }}>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search guide…"
            style={{ width:"100%", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:7, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
          {search && <button onClick={()=>setSearch("")} style={{ position:"absolute", right:22, top:22, background:"transparent", border:"none", color:"#4a4a6a", cursor:"pointer", fontSize:14 }}>×</button>}
        </div>

        {/* Section list */}
        {GUIDE.map(section => (
          <div key={section.id}>
            <button
              onClick={() => { setActiveSection(section.id); setActiveArticle(null); setSearch(""); }}
              style={{ width:"100%", display:"flex", alignItems:"center", gap:8, padding:"9px 14px", border:"none", textAlign:"left", cursor:"pointer",
                background: activeSec?.id===section.id && !activeArt ? "#1e1e2e" : "transparent",
                color: activeSec?.id===section.id ? section.color : "#64748b" }}>
              <span style={{ fontSize:16 }}>{section.icon}</span>
              <span style={{ fontSize:13, fontWeight: activeSec?.id===section.id ? 600 : 400 }}>{section.title}</span>
            </button>
            {/* Articles under section */}
            {activeSec?.id===section.id && section.articles.map(art => (
              <button key={art.id}
                onClick={() => setActiveArticle(art.id)}
                style={{ width:"100%", display:"block", textAlign:"left", padding:"6px 14px 6px 36px", border:"none", cursor:"pointer",
                  background: activeArticle===art.id ? "#1e1e2e" : "transparent",
                  color: activeArticle===art.id ? "#e2e8f0" : "#4a4a6a",
                  fontSize:12, borderLeft: activeArticle===art.id ? `2px solid ${section.color}` : "2px solid transparent" }}>
                {art.title}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex:1, overflowY:"auto", padding:"24px 32px" }}>

        {/* Search results */}
        {searchResults !== null ? (
          <div>
            <div style={{ color:"#64748b", fontSize:12, marginBottom:16 }}>{searchResults.length} result{searchResults.length!==1?"s":""} for "{search}"</div>
            {searchResults.length === 0 ? (
              <div style={{ color:"#3a3a5a", fontSize:13, padding:"20px 0" }}>No articles found. Try different words.</div>
            ) : searchResults.map(art => (
              <div key={art.id} onClick={() => { setActiveSection(art.sectionId); setActiveArticle(art.id); setSearch(""); }}
                style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"14px 18px", marginBottom:8, cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.borderColor=art.sectionColor}
                onMouseLeave={e=>e.currentTarget.style.borderColor="#1e1e2e"}>
                <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:4 }}>
                  <span style={{ fontSize:13 }}>{art.sectionIcon}</span>
                  <span style={{ color:"#4a4a6a", fontSize:11 }}>{art.sectionTitle}</span>
                </div>
                <div style={{ color:"#e2e8f0", fontSize:14, fontWeight:600 }}>{art.title}</div>
              </div>
            ))}
          </div>

        /* Article view */
        ) : activeArt ? (
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:20, fontSize:12 }}>
              <button onClick={()=>setActiveArticle(null)}
                style={{ background:"transparent", border:"none", color:activeSec.color, cursor:"pointer", fontSize:12, padding:0 }}>
                ← {activeSec.title}
              </button>
              <span style={{ color:"#334155" }}>/ </span>
              <span style={{ color:"#64748b" }}>{activeArt.title}</span>
            </div>
            <h2 style={{ color:"#fff", fontSize:20, fontWeight:800, margin:"0 0 20px" }}>{activeArt.title}</h2>
            <div dangerouslySetInnerHTML={{ __html: md(activeArt.body) }}
              style={{ lineHeight:1.8, fontSize:13, color:"#94a3b8" }}/>

            {/* Navigation between articles */}
            {(() => {
              const arts = activeSec.articles;
              const idx  = arts.findIndex(a => a.id === activeArt.id);
              return (
                <div style={{ display:"flex", gap:8, marginTop:32, paddingTop:20, borderTop:"1px solid #1e1e2e" }}>
                  {idx > 0 && (
                    <button onClick={()=>setActiveArticle(arts[idx-1].id)}
                      style={{ padding:"8px 16px", borderRadius:7, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:12, cursor:"pointer" }}>
                      ← {arts[idx-1].title}
                    </button>
                  )}
                  <div style={{ flex:1 }}/>
                  {idx < arts.length-1 && (
                    <button onClick={()=>setActiveArticle(arts[idx+1].id)}
                      style={{ padding:"8px 16px", borderRadius:7, border:"none", background:activeSec.color, color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                      {arts[idx+1].title} →
                    </button>
                  )}
                </div>
              );
            })()}
          </div>

        /* Section overview */
        ) : activeSec ? (
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:24, paddingBottom:16, borderBottom:"1px solid #1e1e2e" }}>
              <div style={{ width:52, height:52, borderRadius:14, background:`${activeSec.color}20`, border:`1px solid ${activeSec.color}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26 }}>{activeSec.icon}</div>
              <div>
                <h2 style={{ color:"#fff", fontSize:20, fontWeight:800, margin:"0 0 4px" }}>{activeSec.title}</h2>
                <p style={{ color:"#64748b", fontSize:13, margin:0 }}>{activeSec.desc}</p>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {activeSec.articles.map((art, i) => (
                <button key={art.id} onClick={()=>setActiveArticle(art.id)}
                  style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 18px", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, textAlign:"left", cursor:"pointer", transition:"border-color 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=activeSec.color}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="#1e1e2e"}>
                  <div style={{ width:28, height:28, borderRadius:7, background:`${activeSec.color}20`, display:"flex", alignItems:"center", justifyContent:"center", color:activeSec.color, fontSize:13, fontWeight:800, flexShrink:0 }}>{i+1}</div>
                  <span style={{ color:"#e2e8f0", fontSize:14, fontWeight:500 }}>{art.title}</span>
                  <span style={{ marginLeft:"auto", color:"#334155", fontSize:18 }}>→</span>
                </button>
              ))}
            </div>
          </div>

        /* Home — all sections grid */
        ) : (
          <div>
            <div style={{ marginBottom:28 }}>
              <h2 style={{ color:"#fff", fontSize:22, fontWeight:800, margin:"0 0 8px" }}>User Guide</h2>
              <p style={{ color:"#64748b", fontSize:14, margin:0 }}>Everything your users need to get leads and book jobs. Browse by topic or search above.</p>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {GUIDE.map(section => (
                <button key={section.id} onClick={()=>{ setActiveSection(section.id); setActiveArticle(null); }}
                  style={{ display:"flex", alignItems:"flex-start", gap:14, padding:"18px 20px", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, textAlign:"left", cursor:"pointer", transition:"border-color 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=section.color}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="#1e1e2e"}>
                  <div style={{ width:42, height:42, borderRadius:10, background:`${section.color}20`, border:`1px solid ${section.color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{section.icon}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ color:"#f1f5f9", fontSize:14, fontWeight:700, marginBottom:4 }}>{section.title}</div>
                    <div style={{ color:"#4a4a6a", fontSize:12, lineHeight:1.5 }}>{section.desc}</div>
                    <div style={{ color:"#334155", fontSize:11, marginTop:6 }}>{section.articles.length} articles</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function AnnouncementsPanel({ api, toast }) {
  return <div style={{color:"#4a4a6a",fontSize:13,padding:20}}>Announcements panel</div>;
}

function CMSEditorView({ api, toast }) {
  return <CMSEmailTemplates api={api} toast={toast}/>;
}

function DunningRefundPanel({ api, toast }) {
  return <DunningManager api={api} toast={toast}/>;
}

function SystemStatusPanel({ api }) {
  return <StatusPage api={api}/>;
}

// ════════════════════════════════════════════════════════════════════════════
// PWA INSTALL BANNER & IOS GUIDE
// Shows smart install prompt based on platform/browser
// ════════════════════════════════════════════════════════════════════════════

function usePWAInstall() {
  const [prompt,     setPrompt]     = useState(null);  // beforeinstallprompt event
  const [installed,  setInstalled]  = useState(false);
  const [isIOS,      setIsIOS]      = useState(false);
  const [isAndroid,  setIsAndroid]  = useState(false);
  const [isSafari,   setIsSafari]   = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent;
    const ios      = /iphone|ipad|ipod/i.test(ua);
    const android  = /android/i.test(ua);
    const safari   = /safari/i.test(ua) && !/chrome/i.test(ua);
    const standalone = window.matchMedia('(display-mode: standalone)').matches
                    || window.navigator.standalone === true;

    setIsIOS(ios);
    setIsAndroid(android);
    setIsSafari(safari);
    setIsStandalone(standalone);

    // Chrome/Edge/Samsung — native install prompt
    const handler = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setInstalled(true);
      setPrompt(null);
      localStorage.setItem('pwa_installed', '1');
    });

    if (localStorage.getItem('pwa_installed')) setInstalled(true);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') { setInstalled(true); setPrompt(null); }
    return outcome === 'accepted';
  };

  return { prompt, installed, isIOS, isAndroid, isSafari, isStandalone, install };
}

function PWAInstallBanner({ onDismiss }) {
  const { prompt, installed, isIOS, isAndroid, isSafari, isStandalone, install } = usePWAInstall();
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [dismissed, setDismissed] = useState(
    localStorage.getItem('pwa_banner_dismissed') === '1'
  );

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa_banner_dismissed', '1');
    if (onDismiss) onDismiss();
  };

  // Already installed or dismissed or running as PWA
  if (installed || dismissed || isStandalone) return null;
  // Nothing to show
  if (!prompt && !(isIOS && isSafari)) return null;

  return (
    <>
      {/* Main install banner */}
      <div style={{ background:"linear-gradient(135deg,#6366f1,#7c3aed)", borderRadius:12, padding:"14px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ width:40, height:40, borderRadius:10, background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>📲</div>
        <div style={{ flex:1 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:700, marginBottom:2 }}>Install the app</div>
          <div style={{ color:"rgba(255,255,255,0.75)", fontSize:12 }}>
            {isIOS ? "Get instant alerts on your iPhone — works like a native app" : "Add to home screen for instant lead alerts"}
          </div>
        </div>
        <div style={{ display:"flex", gap:6, flexShrink:0 }}>
          {isIOS && isSafari ? (
            <button onClick={() => setShowIOSGuide(true)}
              style={{ padding:"7px 14px", borderRadius:8, border:"none", background:"rgba(255,255,255,0.2)", color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer" }}>
              How →
            </button>
          ) : (
            <button onClick={install}
              style={{ padding:"7px 14px", borderRadius:8, border:"none", background:"rgba(255,255,255,0.2)", color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer" }}>
              Install
            </button>
          )}
          <button onClick={dismiss}
            style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.5)", fontSize:18, cursor:"pointer", padding:"4px 6px" }}>
            ×
          </button>
        </div>
      </div>

      {/* iOS step-by-step guide modal */}
      {showIOSGuide && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9999, display:"flex", alignItems:"flex-end", justifyContent:"center" }}
          onClick={() => setShowIOSGuide(false)}>
          <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:"20px 20px 0 0", padding:"28px 24px 40px", width:"100%", maxWidth:480 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <div style={{ color:"#fff", fontSize:16, fontWeight:800 }}>Install on iPhone</div>
              <button onClick={() => setShowIOSGuide(false)}
                style={{ background:"transparent", border:"none", color:"#64748b", fontSize:22, cursor:"pointer" }}>×</button>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {[
                ["1", "Tap the Share button", "The box with an arrow pointing up at the bottom of Safari"],
                ["2", "Scroll down and tap", '"Add to Home Screen"'],
                ["3", "Tap Add", "Socialisten appears on your home screen like a native app"],
                ["4", "Open and allow notifications", "When prompted, tap Allow to get instant lead alerts"],
              ].map(([num, title, desc]) => (
                <div key={num} style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
                  <div style={{ width:32, height:32, borderRadius:10, background:"#6366f1", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:14, fontWeight:800, flexShrink:0 }}>
                    {num}
                  </div>
                  <div>
                    <div style={{ color:"#e2e8f0", fontSize:14, fontWeight:600, marginBottom:2 }}>{title}</div>
                    <div style={{ color:"#64748b", fontSize:12 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:24, background:"#1e1e2e", borderRadius:10, padding:"12px 16px", display:"flex", gap:10, alignItems:"center" }}>
              <span style={{ fontSize:20 }}>💡</span>
              <span style={{ color:"#94a3b8", fontSize:12 }}>After installing, open the app from your home screen and enable push notifications in Settings for instant alerts.</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PWAStatusBar() {
  const { isStandalone } = usePWAInstall();
  if (!isStandalone) return null;
  // App is running as installed PWA — show a subtle indicator
  return (
    <div style={{ height:2, background:"linear-gradient(90deg,#6366f1,#8b5cf6,#6366f1)", backgroundSize:"200% 100%", animation:"shimmer 2s infinite" }}/>
  );
}


function ImpersonationBanner() {
  if (localStorage.getItem("sl_impersonating") !== "1") return null;
  const targetEmail = localStorage.getItem("sl_email") || "unknown";
  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:99999, background:"linear-gradient(90deg,#7c3aed,#6366f1)", padding:"9px 20px", display:"flex", alignItems:"center", gap:12, boxShadow:"0 2px 12px rgba(0,0,0,0.4)" }}>
      <span style={{ fontSize:16 }}>👤</span>
      <span style={{ color:"#fff", fontWeight:700, fontSize:13 }}>Viewing as: {targetEmail}</span>
      <span style={{ color:"rgba(255,255,255,0.55)", fontSize:12 }}>· Admin troubleshooting session · 30 min</span>
      <div style={{ flex:1 }}/>
      <button onClick={() => {
        const adminToken = localStorage.getItem("sl_admin_token");
        const adminEmail = localStorage.getItem("sl_admin_email");
        if (!adminToken) { window.location.reload(); return; }
        localStorage.setItem("sl_token", adminToken);
        localStorage.setItem("sl_email", adminEmail);
        localStorage.removeItem("sl_admin_token");
        localStorage.removeItem("sl_admin_email");
        localStorage.removeItem("sl_impersonating");
        window.location.reload();
      }} style={{ padding:"5px 16px", borderRadius:7, border:"1px solid rgba(255,255,255,0.35)", background:"rgba(255,255,255,0.15)", color:"#fff", fontSize:12, cursor:"pointer", fontWeight:700 }}>
        ← Return to admin panel
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ONBOARDING EDITOR — Admin Panel → Setup
// Edit onboarding wizard steps: labels, descriptions, icons, CTA text,
// toggle steps on/off, reorder, preview live
// ════════════════════════════════════════════════════════════════════════════

const ICON_OPTS = ["📧","📡","🎯","🔔","📲","▶","🎯","🚀","💳","✅","🔑","📋","🏠","⚡","🛠","💬","🌟","📣","🔒"];
const NAV_OPTS  = ["feed","pages","settings","pipeline","analytics","billing","help"];
const COLOR_OPTS = ["#6366f1","#22c55e","#f59e0b","#0ea5e9","#8b5cf6","#ff4d4d","#ec4899","#14b8a6"];

function OnboardingEditor({ api, toast }) {
  const [steps,   setSteps]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [editing, setEditing] = useState(null);   // index of step being edited
  const [preview, setPreview] = useState(false);
  const [previewStep, setPreviewStep] = useState(0);
  const [dirty,   setDirty]   = useState(false);

  useEffect(() => {
    api.get("/admin/cms/onboarding")
      .then(s => setSteps(Array.isArray(s) ? s : []))
      .catch(() => toast.error("Could not load onboarding steps"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/cms/onboarding", steps);
      toast.success("Onboarding steps saved");
      setDirty(false);
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const update = (i, key, val) => {
    setSteps(s => s.map((st, idx) => idx===i ? {...st, [key]:val} : st));
    setDirty(true);
  };

  const moveUp   = (i) => { if(i===0) return; const s=[...steps]; [s[i-1],s[i]]=[s[i],s[i-1]]; setSteps(s); setDirty(true); };
  const moveDown = (i) => { if(i===steps.length-1) return; const s=[...steps]; [s[i],s[i+1]]=[s[i+1],s[i]]; setSteps(s); setDirty(true); };
  const addStep  = () => {
    setSteps(s=>[...s,{id:`step_${Date.now()}`,icon:"📋",label:"New step",description:"What the user needs to do.",detail:"More context goes here.",nav:"feed",cta:"Go →",skippable:true,active:true,color:"#6366f1",pwa_step:false}]);
    setEditing(steps.length);
    setDirty(true);
  };
  const removeStep = (i) => {
    if(!window.confirm("Delete this step?")) return;
    setSteps(s=>s.filter((_,idx)=>idx!==i));
    if(editing===i) setEditing(null);
    setDirty(true);
  };

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 10px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:9, fontFamily:"'DM Mono',monospace", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 };

  // ── Preview modal ─────────────────────────────────────────────────────────
  if (preview) {
    const step = steps[previewStep] || steps[0];
    const total = steps.filter(s=>s.active).length;
    const activeSteps = steps.filter(s=>s.active);
    const cur = activeSteps[previewStep] || activeSteps[0];
    return (
      <div>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <button onClick={()=>setPreview(false)} style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#a5b4fc", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>← Back to editor</button>
          <div style={{ color:"#ccc", fontSize:13, fontWeight:600 }}>Live preview — Step {previewStep+1} of {total}</div>
          <div style={{ display:"flex", gap:6, marginLeft:"auto" }}>
            <button onClick={()=>setPreviewStep(p=>Math.max(0,p-1))} disabled={previewStep===0} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:11, cursor:"pointer" }}>← Prev</button>
            <button onClick={()=>setPreviewStep(p=>Math.min(total-1,p+1))} disabled={previewStep>=total-1} style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:11, cursor:"pointer" }}>Next →</button>
          </div>
        </div>

        {/* Wizard preview */}
        <div style={{ maxWidth:520, margin:"0 auto", background:"#0f0f1a", border:"1px solid #2a2a3e", borderRadius:20, overflow:"hidden" }}>
          <div style={{ height:3, background:"#1e1e2e" }}>
            <div style={{ height:"100%", width:`${((previewStep+1)/total)*100}%`, background:`linear-gradient(90deg,#6366f1,${cur?.color||"#22c55e"})`, transition:"width 0.4s" }}/>
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 24px 0" }}>
            <div style={{ color:"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace" }}>Step {previewStep+1} of {total}</div>
            <div style={{ color:"#334155", fontSize:11 }}>× preview</div>
          </div>
          <div style={{ padding:"20px 28px 28px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:18 }}>
              <div style={{ width:52, height:52, borderRadius:14, background:`${cur?.color||"#6366f1"}20`, border:`1px solid ${cur?.color||"#6366f1"}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, flexShrink:0 }}>
                {cur?.icon||"📋"}
              </div>
              <div>
                <div style={{ color:"#f8fafc", fontSize:18, fontWeight:800, marginBottom:4 }}>{cur?.label}</div>
                <div style={{ color:"#64748b", fontSize:13, lineHeight:1.5 }}>{cur?.description}</div>
              </div>
            </div>
            <div style={{ background:"#070710", border:"1px solid #1e1e2e", borderRadius:10, padding:"14px 16px", marginBottom:18 }}>
              <div style={{ color:"#94a3b8", fontSize:13, lineHeight:1.7 }}>{cur?.detail || "Detail text goes here."}</div>
            </div>
            {/* Step dots */}
            <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:20 }}>
              {activeSteps.map((_,i)=>(
                <div key={i} style={{ width:i===previewStep?20:8, height:8, borderRadius:4, background:i===previewStep?(cur?.color||"#6366f1"):"#1e1e2e", transition:"all 0.3s" }}/>
              ))}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              {cur?.skippable && <button style={{ padding:"11px 16px", borderRadius:10, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:13, cursor:"default" }}>Skip</button>}
              <button style={{ flex:1, padding:"12px 20px", borderRadius:10, border:"none", background:`linear-gradient(135deg,#6366f1,${cur?.color||"#22c55e"})`, color:"#fff", fontSize:14, fontWeight:700, cursor:"default" }}>
                {cur?.cta||"Go →"} →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <div style={{ color:"#fff", fontSize:15, fontWeight:700 }}>Onboarding Steps</div>
          <div style={{ color:"#4a4a6a", fontSize:12, marginTop:2 }}>Edit the wizard shown to new users on first login. Changes apply immediately.</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={()=>{ setPreview(true); setPreviewStep(0); }}
            style={{ padding:"8px 16px", borderRadius:7, border:"1px solid #1e1e2e", background:"transparent", color:"#a5b4fc", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
            👁 Preview wizard
          </button>
          <button onClick={addStep}
            style={{ padding:"8px 16px", borderRadius:7, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
            + Add step
          </button>
          <button onClick={save} disabled={saving || !dirty}
            style={{ padding:"8px 20px", borderRadius:7, border:"none", background:dirty?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#1e1e2e", color:dirty?"#fff":"#4a4a6a", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:saving||!dirty?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:6 }}>
            {saving ? <><Spinner size={11}/>Saving…</> : "💾 Save changes"}
          </button>
        </div>
      </div>

      {dirty && (
        <div style={{ background:"#1a1000", border:"1px solid #f59e0b40", borderRadius:8, padding:"8px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ color:"#f59e0b", fontSize:13 }}>⚠</span>
          <span style={{ color:"#f59e0b", fontSize:12 }}>You have unsaved changes</span>
          <button onClick={save} style={{ marginLeft:"auto", padding:"4px 12px", borderRadius:5, border:"none", background:"#f59e0b", color:"#000", fontSize:11, fontWeight:700, cursor:"pointer" }}>Save now</button>
        </div>
      )}

      {loading ? (
        <div style={{ display:"flex", justifyContent:"center", padding:60 }}><Spinner size={28}/></div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {steps.map((step, i) => (
            <div key={step.id||i} style={{ background:"#0f0f1a", border:`1px solid ${editing===i?"#6366f1":"#1e1e2e"}`, borderRadius:12, overflow:"hidden", transition:"border-color 0.15s" }}>

              {/* Step row header */}
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", cursor:"pointer" }}
                onClick={() => setEditing(editing===i ? null : i)}>
                {/* Drag handle / order */}
                <div style={{ display:"flex", flexDirection:"column", gap:2, flexShrink:0, cursor:"default" }}>
                  <button onClick={e=>{e.stopPropagation();moveUp(i)}} disabled={i===0} style={{ background:"transparent", border:"none", color:i===0?"#1e1e2e":"#4a4a6a", fontSize:10, cursor:i===0?"default":"pointer", lineHeight:1, padding:"1px 3px" }}>▲</button>
                  <button onClick={e=>{e.stopPropagation();moveDown(i)}} disabled={i===steps.length-1} style={{ background:"transparent", border:"none", color:i===steps.length-1?"#1e1e2e":"#4a4a6a", fontSize:10, cursor:i===steps.length-1?"default":"pointer", lineHeight:1, padding:"1px 3px" }}>▼</button>
                </div>

                {/* Icon */}
                <div style={{ width:36, height:36, borderRadius:9, background:`${step.color||"#6366f1"}20`, border:`1px solid ${step.color||"#6366f1"}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                  {step.icon||"📋"}
                </div>

                {/* Label + description */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:step.active?"#e2e8f0":"#4a4a6a", fontSize:13, fontWeight:600, marginBottom:2 }}>
                    {step.label}
                    {!step.active && <span style={{ marginLeft:8, fontSize:9, padding:"1px 6px", borderRadius:3, background:"#2a2a3e", color:"#4a4a6a" }}>HIDDEN</span>}
                    {step.pwa_step && <span style={{ marginLeft:8, fontSize:9, padding:"1px 6px", borderRadius:3, background:"#8b5cf620", color:"#a78bfa" }}>PWA</span>}
                    {step.skippable && <span style={{ marginLeft:8, fontSize:9, padding:"1px 6px", borderRadius:3, background:"#1e1e2e", color:"#4a4a6a" }}>skippable</span>}
                  </div>
                  <div style={{ color:"#4a4a6a", fontSize:11, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{step.description}</div>
                </div>

                {/* Controls */}
                <div style={{ display:"flex", gap:6, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                  {/* Active toggle */}
                  <div onClick={()=>update(i,"active",!step.active)} style={{ display:"flex", alignItems:"center", gap:5, cursor:"pointer" }}>
                    <div style={{ width:32, height:18, borderRadius:9, background:step.active?"#22c55e":"#2a2a3e", position:"relative", transition:"all 0.2s", flexShrink:0 }}>
                      <div style={{ position:"absolute", top:2, left:step.active?14:2, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"all 0.2s" }}/>
                    </div>
                  </div>
                  <button onClick={()=>removeStep(i)} style={{ padding:"4px 8px", borderRadius:5, border:"1px solid #ff4d4d20", background:"transparent", color:"#ff4d4d", fontSize:11, cursor:"pointer" }}>✕</button>
                </div>

                <div style={{ color:"#334155", fontSize:16 }}>{editing===i?"▲":"▼"}</div>
              </div>

              {/* Edit form */}
              {editing===i && (
                <div style={{ borderTop:"1px solid #1e1e2e", padding:"20px 18px", display:"flex", flexDirection:"column", gap:14 }}>
                  {/* Row 1: Icon + Label + Color */}
                  <div style={{ display:"grid", gridTemplateColumns:"160px 1fr 160px", gap:10 }}>
                    <div>
                      <label style={lS}>ICON</label>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {ICON_OPTS.map(ic=>(
                          <button key={ic} onClick={()=>update(i,"icon",ic)}
                            style={{ width:28, height:28, borderRadius:5, border:`1px solid ${step.icon===ic?"#6366f1":"#1e1e2e"}`, background:step.icon===ic?"#6366f120":"transparent", fontSize:15, cursor:"pointer" }}>
                            {ic}
                          </button>
                        ))}
                        <input value={step.icon} onChange={e=>update(i,"icon",e.target.value)}
                          style={{...iS, width:40, textAlign:"center", padding:"4px"}} maxLength={2} placeholder="or type"/>
                      </div>
                    </div>
                    <div>
                      <label style={lS}>STEP LABEL (shown in checklist + wizard heading)</label>
                      <input style={iS} value={step.label} onChange={e=>update(i,"label",e.target.value)} placeholder="Verify your email"/>
                    </div>
                    <div>
                      <label style={lS}>ACCENT COLOR</label>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {COLOR_OPTS.map(col=>(
                          <button key={col} onClick={()=>update(i,"color",col)}
                            style={{ width:22, height:22, borderRadius:"50%", border:`2px solid ${step.color===col?"#fff":"transparent"}`, background:col, cursor:"pointer" }}/>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Short desc */}
                  <div>
                    <label style={lS}>SHORT DESCRIPTION (shown under heading in wizard + in checklist)</label>
                    <input style={iS} value={step.description} onChange={e=>update(i,"description",e.target.value)} placeholder="Click the link in your welcome email to activate your account."/>
                  </div>

                  {/* Row 3: Detail */}
                  <div>
                    <label style={lS}>DETAIL TEXT (shown in the info box inside the wizard step)</label>
                    <textarea style={{...iS, minHeight:72, resize:"vertical"}} value={step.detail||""} onChange={e=>update(i,"detail",e.target.value)} placeholder="More context, tips, or examples shown in the grey box."/>
                  </div>

                  {/* Row 4: Nav + CTA + Skippable + PWA */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 120px 120px", gap:10 }}>
                    <div>
                      <label style={lS}>NAVIGATE TO (which view opens when user clicks)</label>
                      <select style={iS} value={step.nav||"feed"} onChange={e=>update(i,"nav",e.target.value)}>
                        {NAV_OPTS.map(n=><option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lS}>CTA BUTTON TEXT</label>
                      <input style={iS} value={step.cta||"Go →"} onChange={e=>update(i,"cta",e.target.value)} placeholder="Add a page"/>
                    </div>
                    <div>
                      <label style={lS}>SKIPPABLE</label>
                      <div onClick={()=>update(i,"skippable",!step.skippable)} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", marginTop:8 }}>
                        <div style={{ width:36, height:20, borderRadius:10, background:step.skippable?"#22c55e":"#2a2a3e", position:"relative", transition:"all 0.2s" }}>
                          <div style={{ position:"absolute", top:2, left:step.skippable?16:2, width:16, height:16, borderRadius:"50%", background:"#fff", transition:"all 0.2s" }}/>
                        </div>
                        <span style={{ color:"#64748b", fontSize:11 }}>{step.skippable?"Yes":"No"}</span>
                      </div>
                    </div>
                    <div>
                      <label style={lS}>PWA INSTALL STEP</label>
                      <div onClick={()=>update(i,"pwa_step",!step.pwa_step)} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", marginTop:8 }}>
                        <div style={{ width:36, height:20, borderRadius:10, background:step.pwa_step?"#8b5cf6":"#2a2a3e", position:"relative", transition:"all 0.2s" }}>
                          <div style={{ position:"absolute", top:2, left:step.pwa_step?16:2, width:16, height:16, borderRadius:"50%", background:"#fff", transition:"all 0.2s" }}/>
                        </div>
                        <span style={{ color:"#64748b", fontSize:11 }}>{step.pwa_step?"Yes":"No"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Preview this step */}
                  <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
                    <button onClick={()=>{ setPreview(true); setPreviewStep(steps.filter((s,idx)=>s.active&&idx<=i).length-1); }}
                      style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                      👁 Preview this step
                    </button>
                    <button onClick={()=>setEditing(null)}
                      style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:11, cursor:"pointer" }}>
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {steps.length === 0 && (
            <div style={{ textAlign:"center", padding:"40px 0", color:"#3a3a5a" }}>
              No steps yet. <button onClick={addStep} style={{ background:"transparent", border:"none", color:"#6366f1", cursor:"pointer" }}>Add your first step →</button>
            </div>
          )}
        </div>
      )}

      {/* Drip email note */}
      <div style={{ marginTop:24, background:"#0a0a14", border:"1px solid #1e1e2e", borderRadius:10, padding:"14px 18px", display:"flex", gap:12, alignItems:"flex-start" }}>
        <span style={{ fontSize:18, flexShrink:0 }}>💡</span>
        <div>
          <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600, marginBottom:4 }}>Drip emails are separate</div>
          <div style={{ color:"#4a4a6a", fontSize:12, lineHeight:1.7 }}>The wizard steps above control the in-app onboarding flow. Drip email content (Day 1, Day 3, Day 7, Day 14) is edited separately under Site Editor → Email Templates.</div>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// PROXY POOL PANEL — Admin → Advanced → Proxy Pool
// Full geo-matching proxy management with location fields, user assignments,
// coverage map table, and auto-assign controls.
// ════════════════════════════════════════════════════════════════════════════

const PROXY_PROVIDERS = ["Brightdata","Oxylabs","Smartproxy","IPRoyal","Webshare","Other"];
const HEALTH_COLOR    = { ok:"#22c55e", failed:"#ff4d4d", untested:"#f59e0b" };
const HEALTH_ICON     = { ok:"✓", failed:"✗", untested:"?" };

function ProxyPoolPanel({ api, toast }) {
  const [proxies,     setProxies]     = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [coverage,    setCoverage]    = useState([]);
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState("proxies"); // proxies|assignments|coverage|add
  const [testing,     setTesting]     = useState(null);
  const [testingAll,  setTestingAll]  = useState(false);
  const [assigning,   setAssigning]   = useState(false);
  const [filterCity,  setFilterCity]  = useState("");
  const [filterHealth,setFilterHealth]= useState("");
  const [editProxy,   setEditProxy]   = useState(null);

  const [uploadForm, setUploadForm] = useState({
    proxies_text:"", label:"", provider:"", country:"US",
    state:"", city:"", lat:"", lng:"", max_users:10, overwrite_existing:false,
  });

  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 10px", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:9, fontFamily:"'DM Mono',monospace", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 };
  const cardS = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:"18px 20px", marginBottom:14 };

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, c] = await Promise.all([
        api.get("/admin/proxies").catch(()=>({ proxies:[], stats:{} })),
        api.get("/admin/proxies/assignments").catch(()=>[]),
        api.get("/admin/proxies/geo-coverage").catch(()=>[]),
      ]);
      setProxies(p.proxies || []);
      setStats(p.stats || {});
      setAssignments(Array.isArray(a) ? a : []);
      setCoverage(Array.isArray(c) ? c : []);
    } catch(e) { toast.error("Could not load proxy data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const testProxy = async (id) => {
    setTesting(id);
    try {
      const r = await api.post(`/admin/proxies/${id}/test`);
      toast[r.success ? "success" : "error"](
        r.success ? `✓ ${r.ip} — ${r.latency_ms}ms` : `Failed: ${r.error}`
      );
      load();
    } catch(e) { toast.error(e.message); }
    finally { setTesting(null); }
  };

  const testAll = async () => {
    setTestingAll(true);
    try {
      const r = await api.post("/admin/proxies/test-all?limit=100");
      toast.success(`Tested ${r.tested} — ${r.ok} healthy, ${r.failed} failed`);
      load();
    } catch(e) { toast.error(e.message); }
    finally { setTestingAll(false); }
  };

  const autoAssignAll = async () => {
    setAssigning(true);
    try {
      const r = await api.post("/admin/proxies/auto-assign-all");
      toast.success(`Assigned ${r.assigned} of ${r.processed} unmatched users`);
      load();
    } catch(e) { toast.error(e.message); }
    finally { setAssigning(false); }
  };

  const deleteProxy = async (id) => {
    if (!window.confirm("Delete this proxy?")) return;
    try { await api.del(`/admin/proxies/${id}`); load(); }
    catch(e) { toast.error(e.message); }
  };

  const deleteFailed = async () => {
    if (!window.confirm("Delete all failed proxies?")) return;
    try {
      const r = await api.del("/admin/proxies/bulk/failed");
      toast.success(`Deleted ${r.deleted} failed proxies`);
      load();
    } catch(e) { toast.error(e.message); }
  };

  const uploadProxies = async () => {
    if (!uploadForm.proxies_text.trim()) { toast.error("Paste proxy list first"); return; }
    try {
      const r = await api.post("/admin/proxies/upload", {
        ...uploadForm,
        lat: uploadForm.lat ? parseFloat(uploadForm.lat) : null,
        lng: uploadForm.lng ? parseFloat(uploadForm.lng) : null,
        max_users: parseInt(uploadForm.max_users) || 10,
      });
      toast.success(r.message);
      setUploadForm(f=>({...f, proxies_text:""}));
      setTab("proxies");
      load();
    } catch(e) { toast.error(e.message); }
  };

  const clearAssignment = async (userId) => {
    try {
      await api.del(`/admin/users/${userId}/assign-proxy`);
      toast.success("Assignment cleared — user will be re-matched on next scan");
      load();
    } catch(e) { toast.error(e.message); }
  };

  const updateProxyField = async (id, field, val) => {
    try {
      await api.patch(`/admin/proxies/${id}`, { [field]: val });
      load();
    } catch(e) { toast.error(e.message); }
  };

  const filtered = proxies.filter(p => {
    if (filterCity   && !`${p.city||""}${p.state||""}`.toLowerCase().includes(filterCity.toLowerCase())) return false;
    if (filterHealth && p.health !== filterHealth) return false;
    return true;
  });

  if (loading) return <div style={{display:"flex",justifyContent:"center",padding:60}}><Spinner size={28}/></div>;

  return (
    <div>
      {/* Stats bar */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:8, marginBottom:20 }}>
        {[
          ["Total", stats?.total||0, "#6366f1"],
          ["Active", stats?.active||0, "#64748b"],
          ["Healthy", stats?.healthy||0, "#22c55e"],
          ["Failed", stats?.failed||0, "#ff4d4d"],
          ["Assigned", stats?.assigned||0, "#f59e0b"],
          ["Cities", stats?.cities||0, "#0ea5e9"],
          ["States", stats?.states||0, "#8b5cf6"],
        ].map(([label,val,color])=>(
          <div key={label} style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
            <div style={{ color, fontSize:20, fontWeight:800, fontFamily:"'DM Mono',monospace" }}>{val}</div>
            <div style={{ color:"#4a4a6a", fontSize:10 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Tab bar + actions */}
      <div style={{ display:"flex", gap:6, marginBottom:20, flexWrap:"wrap" }}>
        {[["proxies","🖥 Proxies"],["assignments","👥 User Assignments"],["coverage","🗺 Coverage"],["add","+ Add Proxies"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${tab===id?"#6366f1":"#1e1e2e"}`, background:tab===id?"#6366f120":"transparent", color:tab===id?"#a5b4fc":"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
            {label}
          </button>
        ))}
        <div style={{ flex:1 }}/>
        <button onClick={testAll} disabled={testingAll}
          style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
          {testingAll ? <><Spinner size={11}/>Testing…</> : "⚡ Test all"}
        </button>
        <button onClick={autoAssignAll} disabled={assigning}
          style={{ padding:"7px 14px", borderRadius:6, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
          {assigning ? <><Spinner size={11}/>Matching…</> : "🎯 Auto-assign all users"}
        </button>
      </div>

      {/* ── PROXIES TAB ── */}
      {tab === "proxies" && (
        <div>
          {/* Filters */}
          <div style={{ display:"flex", gap:8, marginBottom:14 }}>
            <input value={filterCity} onChange={e=>setFilterCity(e.target.value)}
              placeholder="Filter by city or state…"
              style={{ ...iS, width:220 }}/>
            <select value={filterHealth} onChange={e=>setFilterHealth(e.target.value)} style={{ ...iS, width:140 }}>
              <option value="">All health</option>
              <option value="ok">✓ Healthy</option>
              <option value="failed">✗ Failed</option>
              <option value="untested">? Untested</option>
            </select>
            {stats?.failed > 0 && (
              <button onClick={deleteFailed}
                style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #ff4d4d30", background:"#ff4d4d10", color:"#ff4d4d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                Delete {stats.failed} failed
              </button>
            )}
            <span style={{ color:"#4a4a6a", fontSize:11, fontFamily:"'DM Mono',monospace", alignSelf:"center" }}>{filtered.length} shown</span>
          </div>

          {/* Proxy rows */}
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {filtered.length === 0 && (
              <div style={{ ...cardS, textAlign:"center", color:"#3a3a5a", padding:40 }}>
                No proxies yet. Click <strong style={{color:"#a5b4fc"}}>+ Add Proxies</strong> to upload your first batch.
              </div>
            )}
            {filtered.map(p => (
              <div key={p.id} style={{ ...cardS, padding:"12px 16px", marginBottom:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  {/* Health badge */}
                  <div style={{ width:28, height:28, borderRadius:"50%", background:`${HEALTH_COLOR[p.health]||"#4a4a6a"}20`, border:`2px solid ${HEALTH_COLOR[p.health]||"#4a4a6a"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:HEALTH_COLOR[p.health]||"#4a4a6a", fontWeight:800, flexShrink:0 }}>
                    {HEALTH_ICON[p.health]||"?"}
                  </div>

                  {/* Proxy info */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                      <span style={{ color:"#e2e8f0", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                        {p.host}:{p.port}
                      </span>
                      {p.username && <span style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>auth</span>}
                      {p.provider && <span style={{ color:"#6366f1", fontSize:10, padding:"1px 6px", borderRadius:3, background:"#6366f115" }}>{p.provider}</span>}
                      {!p.active && <span style={{ color:"#ff4d4d", fontSize:10, padding:"1px 6px", borderRadius:3, background:"#ff4d4d15" }}>PAUSED</span>}
                    </div>
                    <div style={{ display:"flex", gap:10, color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                      {/* Geo */}
                      <span style={{ color: p.city?"#a5b4fc":"#2a2a3e" }}>
                        📍 {[p.city, p.state, p.country].filter(Boolean).join(", ") || "No location set"}
                      </span>
                      {p.lat && <span>({p.lat?.toFixed(2)}, {p.lng?.toFixed(2)})</span>}
                      {p.avg_latency_ms && <span>⚡ {p.avg_latency_ms}ms</span>}
                      <span>✓{p.success_count} ✗{p.fail_count}</span>
                      <span>👥 {p.live_users||0}/{p.max_users} users</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                    {/* Pause/resume */}
                    <button onClick={()=>updateProxyField(p.id,"active",!p.active)}
                      style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:p.active?"#f59e0b":"#22c55e", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                      {p.active?"Pause":"Resume"}
                    </button>
                    {/* Test */}
                    <button onClick={()=>testProxy(p.id)} disabled={testing===p.id}
                      style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#a5b4fc", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                      {testing===p.id ? <><Spinner size={9}/>…</> : "Test"}
                    </button>
                    {/* Edit location */}
                    <button onClick={()=>setEditProxy(editProxy===p.id?null:p.id)}
                      style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#64748b", fontSize:10, cursor:"pointer" }}>
                      📍
                    </button>
                    {/* Delete */}
                    <button onClick={()=>deleteProxy(p.id)}
                      style={{ padding:"4px 8px", borderRadius:5, border:"1px solid #ff4d4d20", background:"transparent", color:"#ff4d4d", fontSize:10, cursor:"pointer" }}>✕</button>
                  </div>
                </div>

                {/* Inline location editor */}
                {editProxy === p.id && (
                  <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #1e1e2e", display:"grid", gridTemplateColumns:"1fr 1fr 1fr 90px 90px 80px", gap:8 }}>
                    {[["city","City",p.city||""],["state","State/Region",p.state||""],
                      ["country","Country",p.country||"US"],
                      ["lat","Latitude",p.lat||""],["lng","Longitude",p.lng||""],
                      ["max_users","Max users",p.max_users||10]].map(([field,label,val])=>(
                      <div key={field}>
                        <label style={lS}>{label}</label>
                        <input defaultValue={val} onBlur={e=>updateProxyField(p.id,field,field==="lat"||field==="lng"?parseFloat(e.target.value)||null:field==="max_users"?parseInt(e.target.value)||10:e.target.value)}
                          style={{...iS}} placeholder={label}/>
                      </div>
                    ))}
                    <div style={{ gridColumn:"1/-1", textAlign:"right" }}>
                      <button onClick={()=>setEditProxy(null)} style={{ padding:"4px 12px", borderRadius:5, border:"none", background:"#6366f1", color:"#fff", fontSize:11, cursor:"pointer" }}>Done</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ASSIGNMENTS TAB ── */}
      {tab === "assignments" && (
        <div>
          <div style={{ ...cardS, marginBottom:16, display:"flex", alignItems:"center", gap:12, padding:"12px 16px" }}>
            <span style={{ fontSize:18 }}>ℹ️</span>
            <div style={{ color:"#94a3b8", fontSize:13 }}>
              Each user is automatically assigned the geographically closest healthy proxy based on their timezone.
              Click <strong>Clear</strong> on any row to release the assignment — the user will be re-matched on their next scan.
            </div>
          </div>
          {assignments.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:"#3a3a5a" }}>
              No assignments yet. Click <strong style={{color:"#a5b4fc"}}>Auto-assign all users</strong> to run geo-matching.
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              {assignments.map(a => (
                <div key={a.user_id} style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:8, padding:"11px 14px", display:"flex", alignItems:"center", gap:12 }}>
                  {/* Health dot */}
                  <div style={{ width:10, height:10, borderRadius:"50%", background:HEALTH_COLOR[a.health]||"#4a4a6a", flexShrink:0 }}/>
                  {/* User */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600, marginBottom:2 }}>{a.email}</div>
                    <div style={{ color:"#4a4a6a", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                      {a.plan} · tz: {a.timezone||"?"} → proxy in {[a.city,a.state,a.country].filter(Boolean).join(", ")||"unknown location"}
                      {a.latency_ms && ` · ${a.latency_ms}ms`}
                      {a.override && <span style={{ marginLeft:8, color:"#8b5cf6" }}>admin override</span>}
                    </div>
                  </div>
                  <button onClick={()=>clearAssignment(a.user_id)}
                    style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #2a2a3e", background:"transparent", color:"#ff4d4d", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    Clear
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── COVERAGE TAB ── */}
      {tab === "coverage" && (
        <div>
          <div style={{ marginBottom:14, color:"#64748b", fontSize:13 }}>
            Shows which cities and states have proxy coverage and how many users are served by each.
            Cities with no proxies will fall back to state → country → global.
          </div>
          {coverage.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:"#3a3a5a" }}>
              No location data yet. Edit proxy location fields in the Proxies tab.
            </div>
          ) : (
            <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 80px 60px 60px 80px 70px", background:"#6366f120", padding:"8px 14px", borderBottom:"1px solid #1e1e2e" }}>
                {["City","State","Country","Proxies","Healthy","Users","Avg ms"].map(h=>(
                  <div key={h} style={{ color:"#a5b4fc", fontSize:10, fontFamily:"'DM Mono',monospace", fontWeight:700 }}>{h}</div>
                ))}
              </div>
              {coverage.map((row,i)=>(
                <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 80px 60px 60px 80px 70px", padding:"9px 14px", borderBottom:"1px solid #0f0f1a", background:i%2===0?"transparent":"#0a0a14" }}>
                  <div style={{ color:"#e2e8f0", fontSize:12 }}>{row.city||"—"}</div>
                  <div style={{ color:"#94a3b8", fontSize:12 }}>{row.state||"—"}</div>
                  <div style={{ color:"#64748b", fontSize:12, fontFamily:"'DM Mono',monospace" }}>{row.country}</div>
                  <div style={{ color:"#e2e8f0", fontSize:12, fontFamily:"'DM Mono',monospace" }}>{row.proxy_count}</div>
                  <div style={{ color:row.healthy>0?"#22c55e":"#ff4d4d", fontSize:12, fontFamily:"'DM Mono',monospace" }}>{row.healthy}</div>
                  <div style={{ color:"#f59e0b", fontSize:12, fontFamily:"'DM Mono',monospace" }}>{row.users_assigned||0}</div>
                  <div style={{ color:"#64748b", fontSize:12, fontFamily:"'DM Mono',monospace" }}>{row.avg_ms||"—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ADD PROXIES TAB ── */}
      {tab === "add" && (
        <div>
          <div style={{ ...cardS }}>
            <div style={{ color:"#fff", fontSize:14, fontWeight:700, marginBottom:16 }}>Add Proxies</div>

            {/* Location fields */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 80px 100px 100px 90px", gap:10, marginBottom:14 }}>
              <div><label style={lS}>CITY</label><input style={iS} value={uploadForm.city} onChange={e=>setUploadForm(f=>({...f,city:e.target.value}))} placeholder="Detroit"/></div>
              <div><label style={lS}>STATE / REGION</label><input style={iS} value={uploadForm.state} onChange={e=>setUploadForm(f=>({...f,state:e.target.value}))} placeholder="Michigan"/></div>
              <div><label style={lS}>COUNTRY</label><input style={iS} value={uploadForm.country} onChange={e=>setUploadForm(f=>({...f,country:e.target.value}))} placeholder="US" maxLength={5}/></div>
              <div><label style={lS}>LATITUDE</label><input style={iS} value={uploadForm.lat} onChange={e=>setUploadForm(f=>({...f,lat:e.target.value}))} placeholder="42.3314" type="number" step="0.0001"/></div>
              <div><label style={lS}>LONGITUDE</label><input style={iS} value={uploadForm.lng} onChange={e=>setUploadForm(f=>({...f,lng:e.target.value}))} placeholder="-83.0458" type="number" step="0.0001"/></div>
              <div><label style={lS}>MAX USERS</label><input style={iS} value={uploadForm.max_users} onChange={e=>setUploadForm(f=>({...f,max_users:e.target.value}))} type="number" min="1" max="100"/></div>
            </div>

            {/* Provider + Label */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
              <div>
                <label style={lS}>PROVIDER</label>
                <select style={iS} value={uploadForm.provider} onChange={e=>setUploadForm(f=>({...f,provider:e.target.value}))}>
                  <option value="">Select provider…</option>
                  {PROXY_PROVIDERS.map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div><label style={lS}>LABEL (optional tag)</label><input style={iS} value={uploadForm.label} onChange={e=>setUploadForm(f=>({...f,label:e.target.value}))} placeholder="Detroit residential batch 1"/></div>
            </div>

            {/* Proxy list */}
            <div style={{ marginBottom:12 }}>
              <label style={lS}>PROXY LIST — One per line. Formats supported:</label>
              <div style={{ color:"#2a2a3e", fontSize:10, fontFamily:"'DM Mono',monospace", marginBottom:6 }}>
                host:port · host:port:user:pass · user:pass@host:port · http://user:pass@host:port · socks5://user:pass@host:port
              </div>
              <textarea style={{...iS, minHeight:140, resize:"vertical"}} value={uploadForm.proxies_text}
                onChange={e=>setUploadForm(f=>({...f,proxies_text:e.target.value}))}
                placeholder={"user:pass@192.168.1.1:8080\nuser2:pass2@10.0.0.1:3128\nhttp://user:pass@proxy.brightdata.com:22225"}/>
            </div>

            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
              <div onClick={()=>setUploadForm(f=>({...f,overwrite_existing:!f.overwrite_existing}))}
                style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                <div style={{ width:32, height:18, borderRadius:9, background:uploadForm.overwrite_existing?"#6366f1":"#2a2a3e", position:"relative", transition:"all 0.2s" }}>
                  <div style={{ position:"absolute", top:2, left:uploadForm.overwrite_existing?14:2, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"all 0.2s" }}/>
                </div>
                <span style={{ color:"#94a3b8", fontSize:12 }}>Overwrite existing (same host:port)</span>
              </div>
            </div>

            {/* Lat/lng helper */}
            <div style={{ background:"#070710", border:"1px solid #1e1e2e", borderRadius:8, padding:"12px 14px", marginBottom:16 }}>
              <div style={{ color:"#4a4a6a", fontSize:11, lineHeight:1.7 }}>
                💡 <strong style={{color:"#94a3b8"}}>Getting lat/lng:</strong> Go to <a href="https://www.latlong.net" target="_blank" rel="noopener" style={{color:"#6366f1"}}>latlong.net</a>, search for the city, and copy the coordinates.
                Detroit = 42.3314, -83.0458 · Chicago = 41.8781, -87.6298 · New York = 40.7128, -74.0060 · LA = 34.0522, -118.2437
              </div>
            </div>

            <button onClick={uploadProxies}
              style={{ padding:"10px 24px", borderRadius:8, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>
              Upload proxies →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function AuthScreen({ onAuth, toast }) {
  const [mode,setMode] = useState("login");
  const [email,setEmail] = useState("");
  const [password,setPassword] = useState("");
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");

  const [showForgot, setShowForgot] = useState(false);

  // Handle OAuth redirect: ?token=xxx&new=true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get("token");
    const isNew  = params.get("new") === "true";
    const oauthErr = params.get("oauth_error");
    const checkout = params.get("checkout");
    const verified = params.get("verified");
    if (verified === "true") {
      window.history.replaceState({}, "", "/");
      toast.success("Email verified! You can now log in.");
    }

    if (oauthErr) {
      const msgs = { google_denied:"Google login cancelled", facebook_denied:"Facebook login cancelled", token_failed:"OAuth login failed — please try again", no_email:"Could not retrieve email from Google/Facebook" };
      setError(msgs[oauthErr] || "Login failed");
      window.history.replaceState({}, "", "/");
      return;
    }

    if (token) {
      // Decode email from JWT (basic parse — no verification needed client-side)
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const userEmail = payload.sub || "";
        localStorage.setItem("sl_token", token);
        localStorage.setItem("sl_email", userEmail);
        window.history.replaceState({}, "", "/");
        onAuth(userEmail);
      } catch(_) {
        setError("OAuth login failed — invalid token");
      }
    }

    if (checkout === "success") {
      setShowWelcome({show:true,isPostCheckout:true,planName:params.get("plan")||""});
      window.history.replaceState({}, "", "/");
      // Token already in localStorage if they were logged in — just onAuth again
      const storedToken = localStorage.getItem("sl_token");
      const storedEmail = localStorage.getItem("sl_email");
      if (storedToken && storedEmail) onAuth(storedEmail);
    }
  }, []);

  const iS={width:"100%",background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:8,padding:"11px 14px",color:"#ccc",fontSize:13,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box",marginBottom:12};

  const SPECIAL_RE = /[!@#$%^&*()_+=\[\]{};':"\\|,.<>\/?`~@#$%-]/;
  const pwChecks = [
    { label:"8+ chars", ok: password.length >= 8 },
    { label:"number",   ok: /[0-9]/.test(password) },
    { label:"special",  ok: SPECIAL_RE.test(password) },
  ];
  const pwValid = pwChecks.every(c => c.ok);

  const submit=async()=>{
    if(!email||!password){setError("Please fill in all fields");return;}
    if(mode==="register"){
      if(password.length<8){setError("Password must be at least 8 characters");return;}
      if(!/[0-9]/.test(password)){setError("Password must contain at least one number");return;}
      if(!SPECIAL_RE.test(password)){setError("Password must contain at least one special character (!@#$% etc.)");return;}
    } else {
      if(password.length<8){setError("Password must be at least 8 characters");return;}
    }
    setLoading(true);setError("");
    try{
      let data;
      if(mode==="login"){const form=new URLSearchParams({username:email,password});const res=await fetch(`${API_BASE}/auth/token`,{method:"POST",headers:{"Content-Type":"application/x-www-form-urlencoded"},body:form});if(!res.ok){const e=await res.json();throw new Error(e.detail||"Login failed");}data=await res.json();}
      else{const res=await fetch(`${API_BASE}/auth/register`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email,password})});if(!res.ok){const e=await res.json();throw new Error(e.detail||"Registration failed");}data=await res.json();}
      localStorage.setItem("sl_token",data.access_token);localStorage.setItem("sl_email",email);onAuth(email,mode==="register");
    }catch(e){setError(e.message);}finally{setLoading(false);}
  };

  const loginWithGoogle   = () => { window.location.href = `${API_BASE}/auth/google/login`; };
  const loginWithFacebook = () => { window.location.href = `${API_BASE}/auth/facebook/login`; };

  const fakeApi = { get: async (path) => { const r = await fetch(`${API_BASE}${path}`); if (!r.ok) return []; return r.json(); }, post: async ()=>({}) };

  return (
    <div style={{ minHeight:"100vh",background:"#050508",display:"flex",flexDirection:"column",alignItems:"center",fontFamily:"'DM Mono',monospace" }}>
      <div style={{ position:"fixed",inset:0,backgroundImage:"linear-gradient(#0f0f1a 1px,transparent 1px),linear-gradient(90deg,#0f0f1a 1px,transparent 1px)",backgroundSize:"40px 40px",opacity:0.4,pointerEvents:"none" }}/>
      <div style={{ position:"relative",width:420,padding:40,background:"#0a0a0f",border:"1px solid #1e1e2e",borderRadius:16,marginTop:60 }}>
        <div style={{ textAlign:"center",marginBottom:28 }}>
          <div style={{ width:48,height:48,borderRadius:12,background:"linear-gradient(135deg,#6366f1,#8b5cf6)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:700,margin:"0 auto 14px",color:"#fff" }}>S</div>
          <div style={{ color:"#fff",fontSize:18,fontWeight:700,letterSpacing:2,marginBottom:4 }}>SOCIALISTEN</div>
          <div style={{ color:"#4a4a6a",fontSize:11,letterSpacing:3 }}>KEYWORD MONITOR</div>
        </div>

        {/* Social login buttons */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:20 }}>
          <button onClick={loginWithGoogle} style={{
            padding:"11px 16px", borderRadius:8, border:"1px solid #2a2a3e",
            background:"#0f0f1a", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace",
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            transition:"all 0.15s",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <button onClick={loginWithFacebook} style={{
            padding:"11px 16px", borderRadius:8, border:"1px solid #2a2a3e",
            background:"#0f0f1a", color:"#ccc", fontSize:12, fontFamily:"'DM Mono',monospace",
            cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            transition:"all 0.15s",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Continue with Facebook
          </button>
        </div>

        {/* Divider */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <div style={{ flex:1, height:1, background:"#1e1e2e" }}/>
          <span style={{ color:"#3a3a5a", fontSize:11, fontFamily:"'DM Mono',monospace" }}>OR</span>
          <div style={{ flex:1, height:1, background:"#1e1e2e" }}/>
        </div>

        <div style={{ display:"flex",marginBottom:20,background:"#050508",borderRadius:8,padding:3 }}>
          {["login","register"].map(m=><button key={m} onClick={()=>{setMode(m);setError("");}} style={{ flex:1,padding:"8px",borderRadius:6,border:"none",cursor:"pointer",background:mode===m?"#1e1e2e":"transparent",color:mode===m?"#fff":"#4a4a6a",fontSize:11,fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:1 }}>{m}</button>)}
        </div>
        <input type="email" placeholder="Email address" value={email} onChange={e=>setEmail(e.target.value)} style={iS}/>
        <input
          type="password"
          placeholder={mode==="register" ? "Password — 8+ chars, number, special (!@#$%)" : "Password"}
          value={password}
          onChange={e=>setPassword(e.target.value)}
          style={{...iS, borderColor: mode==="register"&&password.length>0 ? (pwValid?"#22c55e":password.length<8?"#ff4d4d":"#f59e0b") : iS.borderColor}}
          onKeyDown={e=>e.key==="Enter"&&submit()}
        />
        {mode==="register" && password.length > 0 && (
          <div style={{ display:"flex", gap:6, marginBottom:10 }}>
            {pwChecks.map(r => (
              <span key={r.label} style={{ flex:1, textAlign:"center", fontSize:10, padding:"3px 0", borderRadius:4, fontFamily:"'DM Mono',monospace", background:r.ok?"#22c55e20":"#1e1e2e", color:r.ok?"#22c55e":"#4a4a6a", border:"1px solid "+(r.ok?"#22c55e30":"#1e1e2e") }}>
                {r.ok?"✓":"✗"} {r.label}
              </span>
            ))}
          </div>
        )}
        {error&&<div style={{ color: error.includes("locked") || error.includes("Too many") ? "#f59e0b" : "#ff4d4d", fontSize:11, marginBottom:12, padding:"8px 12px", background: error.includes("locked") || error.includes("Too many") ? "#f59e0b10" : "transparent", borderRadius:6, border: error.includes("locked") || error.includes("Too many") ? "1px solid #f59e0b30" : "none", lineHeight:1.5 }}>{error}</div>}
        <button onClick={submit} disabled={loading} style={{ width:"100%",padding:"12px",borderRadius:8,border:"none",background:loading?"#2a2a3e":"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:13,fontFamily:"'DM Mono',monospace",cursor:loading?"not-allowed":"pointer",letterSpacing:1,display:"flex",alignItems:"center",justifyContent:"center",gap:8 }}>
          {loading?<><Spinner size={16}/>{mode==="login"?"Signing in…":"Creating account…"}</>:(mode==="login"?"SIGN IN":"CREATE ACCOUNT")}
        </button>
        {mode==="login" && (
          <div style={{ textAlign:"center", marginTop:12 }}>
            <button onClick={()=>setShowForgot(true)} style={{ background:"transparent", border:"none", color:"#4a4a6a", fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
              Forgot your password?
            </button>
          </div>
        )}
        <div style={{ marginTop:12,padding:"12px 14px",background:"#0f0f1a",borderRadius:8,fontSize:11,color:"#3a3a5a",lineHeight:1.7 }}>
          <strong style={{ color:"#4a4a6a" }}>Backend not running?</strong><br/>Start with <code style={{ color:"#6366f1" }}>docker-compose up</code> then refresh.
        </div>
      </div>
      {showForgot && <ForgotPasswordModal onClose={()=>setShowForgot(false)} toast={toast}/>}
      <Toast toasts={toast.toasts}/>
      <CrispWidget/>

      {/* ── Landing page sections ────────────────────────────────── */}
      <div style={{ width:"100%", maxWidth:880, padding:"72px 24px 80px", boxSizing:"border-box" }}>
        {/* Setup guide */}
        <div style={{ marginBottom:80 }}>
          <SetupGuide variant="landing"/>
        </div>
        {/* FAQ */}
        <div style={{ marginBottom:60 }}>
          <LandingFAQ api={{ get: async (path) => { try{ const r=await fetch(`${API_BASE}${path}`); if(!r.ok) return []; return r.json(); }catch{ return []; } }, post: async ()=>({}) }}/>
        </div>
        {/* Footer */}
        <div style={{ textAlign:"center", paddingTop:30, borderTop:"1px solid #1e1e2e" }}>
          <div style={{ color:"#3a3a5a", fontSize:12, marginBottom:8 }}>Socialisten — Real-time lead monitoring across Facebook, Reddit & Nextdoor</div>
          <div style={{ display:"flex", justifyContent:"center", gap:24 }}>
            {[["Help Center","#6366f1"],["Privacy Policy","#6366f1"],["Terms of Service","#6366f1"]].map(([label,c])=>(
              <span key={label} style={{ color:c, fontSize:12, cursor:"pointer" }}>{label}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CrispWidget() {
  const crispId = typeof window !== "undefined" && window.CRISP_WEBSITE_ID;
  useEffect(() => {
    const id = window.SOCIALISTEN_CRISP_ID || "";
    if (!id) return;  // No Crisp ID configured — skip
    if (window.$crisp) return;  // Already loaded
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = id;
    const s = document.createElement("script");
    s.src = "https://client.crisp.chat/l.js";
    s.async = true;
    document.head.appendChild(s);
    return () => { try { window.$crisp = []; } catch(e) {} };
  }, []);
  return null;
}

export default function App() {
  const [userEmail,setUserEmail] = useState(localStorage.getItem("sl_email")||"");
  const [authed,setAuthed] = useState(!!localStorage.getItem("sl_token"));
  const [showLanding,setShowLanding] = useState(!localStorage.getItem("sl_token"));  // show landing when not logged in
  const [view,setView] = useState("feed");
  const [pages,setPages] = useState([]);
  const [stats,setStats] = useState(null);
  const [statsLoading,setStatsLoading] = useState(true);
  const [fbConnected,setFbConnected] = useState(false);
  const [templates,setTemplates] = useState(()=>{ try{return JSON.parse(localStorage.getItem("sl_templates"))||DEFAULT_TEMPLATES;}catch{return DEFAULT_TEMPLATES;} });
  const toast=useToast();
  const api=useApi();

  // Load site settings from CMS and apply CSS variables
  useEffect(() => {
    fetch(`${API_BASE}/admin/cms/site`)
      .then(r => r.ok ? r.json() : null)
      .then(site => {
        if (!site) return;
        const root = document.documentElement.style;
        if (site.primary_color)  root.setProperty("--color-primary",  site.primary_color);
        if (site.accent_color)   root.setProperty("--color-accent",   site.accent_color);
        if (site.success_color)  root.setProperty("--color-success",  site.success_color);
        if (site.danger_color)   root.setProperty("--color-danger",   site.danger_color);
        if (site.dark_bg)        root.setProperty("--color-dark-bg",  site.dark_bg);
        if (site.card_bg)        root.setProperty("--color-card-bg",  site.card_bg);
        if (site.app_name && site.app_name !== "Socialisten") {
          document.title = site.app_name;
        }
        if (site.favicon_url) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
          link.href = site.favicon_url;
        }
      })
      .catch(() => {});
  }, []);

  // Check URL for password reset token or email verify token
  const [resetToken]  = useState(() => new URLSearchParams(window.location.search).get("reset_token") || "");
  const [verifyToken] = useState(() => new URLSearchParams(window.location.search).get("verify_token") || "");
  const [showWelcome,setShowWelcome] = useState({show:false,isPostCheckout:false,planName:""});
  const [bellMatches,setBellMatches] = useState([]);
  const [showBell,setShowBell] = useState(false);
  const bellRef = useRef(null);
  const sidebarRef = useRef(null);
  const [userPlan,setUserPlan] = useState("");

  // Handle email verification via link
  useEffect(() => {
    if (verifyToken && verifyToken.length > 0) {
      fetch(`${API_BASE}/auth/verify-email?token=${verifyToken}`)
        .then(r => { if (r.ok || r.redirected) { toast.success("Email verified! You can now log in."); } })
        .catch(() => toast.error("Verification failed — link may have expired"));
    }
  }, []);

  useEffect(()=>{ localStorage.setItem("sl_templates",JSON.stringify(templates)); },[templates]);

  const signOut=()=>{ localStorage.removeItem("sl_token");localStorage.removeItem("sl_email");setAuthed(false);setUserEmail("");setPages([]);setStats(null); };

  useEffect(()=>{
    if(!authed) return;
    api.get("/pages").then(d=>setPages(d||[])).catch(()=>toast.error("Failed to load pages"));
    api.get("/matches?limit=20&unread_only=true").then(d=>{ if(d?.matches) setBellMatches(d.matches); }).catch(()=>{});
    const loadStats=()=>api.get("/stats").then(d=>{if(d)setStats(d);}).catch(()=>{}).finally(()=>setStatsLoading(false));
    loadStats(); const iv=setInterval(loadStats,30000); return()=>clearInterval(iv);
    api.get("/billing/subscription").then(d=>{if(d?.plan)setUserPlan(d.plan);}).catch(()=>{});
  },[authed]);



  // Check if current user is admin on load
  useEffect(() => {
    if (!authed || !userEmail) return;
    api.get("/admin/users?limit=1")
      .then(() => localStorage.setItem("sl_admin_email", userEmail))
      .catch(() => localStorage.removeItem("sl_admin_email"));
  }, [authed, userEmail]);

  // Public knowledge base / help center
  if(typeof window!=="undefined" && (window.location.pathname==="/help" || window.location.pathname.startsWith("/help/"))) {
    return <KnowledgeBasePage/>;
  }

  if(!authed) {
    if(showLanding) return <LandingPage
      onSignup={()=>setShowLanding(false)}
      onLogin={()=>setShowLanding(false)}
    />;
    return <AuthScreen onAuth={(email,isNew=false)=>{setUserEmail(email);setAuthed(true);if(isNew)setTimeout(()=>setShowWelcome({show:true,isPostCheckout:false,planName:""}),600);}} toast={toast}/>;
  }

  const ADMIN_EMAIL = ""; // Set in backend .env — checked via API
  const isAdmin = stats !== null && userEmail && userEmail === localStorage.getItem("sl_admin_email");


  const titles={feed:"Live Match Feed",pipeline:"Lead Pipeline",analytics:"Analytics",inbox:"Page Inbox",templates:"Reply Templates",pages:"Monitored Pages",pricing:"Plans & Pricing",billing:"Billing & Subscription",settings:"Settings",admin:"Admin Panel",coupons:"Coupon Manager",comms:"Send Announcement",refunds:"Refund & Dunning",status:"System Status",security:"Security Dashboard",cms:"Site Editor",advanced:"Advanced Stats",referral:"Refer & Earn",plans:"Plan Manager",saved:"Saved Leads",help:"Help Center",setup:"Setup Guide",kb:"Knowledge Base Editor",guides:"Admin Guides",myzone:"My Zone",["onboarding-setup"]:"Zone Setup"};

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:"#050508", fontFamily:"'DM Mono',monospace" }}>
      <PWAStatusBar/>
      <ImpersonationBanner/>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500;600&display=swap');
        *{box-sizing:border-box;}
        @media (max-width: 768px) {
          .sl-sidebar { width: 52px !important; }
          .sl-sidebar .sl-label { display: none; }
          .sl-sidebar .sl-logo-text { display: none; }
          .sl-sidebar .sl-status-pills { display: none; }
          .sl-nav-btn { padding: 10px 8px !important; justify-content: center !important; }
          .sl-nav-btn span:first-child { font-size: 18px !important; }
          .sl-sidebar .sl-signout { display: none; }
        }
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-track{background:#0a0a0f;}
        ::-webkit-scrollbar-thumb{background:#2a2a3e;border-radius:2px;}
        input::placeholder,textarea::placeholder{color:#2a2a3e;}
        select option{background:#0f0f1a;}
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes slideIn{from{transform:translateX(20px);opacity:0;}to{transform:none;opacity:1;}}
        @keyframes bellShake{0%,100%{transform:rotate(0)}20%{transform:rotate(-15deg)}40%{transform:rotate(15deg)}60%{transform:rotate(-10deg)}80%{transform:rotate(10deg)}}
        textarea{font-family:inherit;}
        @media (max-width:640px) {
          .sl-sidebar { width:52px !important; overflow:hidden; }
          .sl-sidebar .sl-label { display:none !important; }
          .sl-sidebar .sl-logo-text { display:none !important; }
          .sl-sidebar .sl-status-pills { display:none !important; }
          .sl-sidebar .sl-signout-label { display:none !important; }
          .sl-nav-btn { padding:12px 8px !important; justify-content:center !important; gap:0 !important; }
          .sl-match-card { padding:12px !important; }
          .sl-match-card-inner { flex-direction:column !important; }
          .sl-score-badge { align-self:flex-start !important; }
          .sl-grid-2 { grid-template-columns:1fr !important; }
          .sl-grid-3 { grid-template-columns:1fr !important; }
          .sl-grid-4 { grid-template-columns:1fr 1fr !important; }
          .sl-hide-mobile { display:none !important; }
          .sl-page-card { padding:14px !important; }
          h1 { font-size:16px !important; }
        }
        @media (max-width:400px) {
          .sl-grid-4 { grid-template-columns:1fr !important; }
        }
      `}</style>
      <Sidebar view={view} setView={setView} unreadCount={stats?.unread_matches||0} inboxUnread={2} fbConnected={fbConnected} userEmail={userEmail} onSignOut={signOut} isAdmin={!!localStorage.getItem("sl_admin_email")} api={api}/>
      <main style={{ flex:1, display:"flex", flexDirection:"column", overflowY:"auto", maxHeight:"100vh", minWidth:0 }}>
        <VerificationBanner api={api} toast={toast}/>
        <div style={{ flex:1, padding:"clamp(16px, 4vw, 36px)" }}>
        {showWelcome.show && <WelcomeModal onClose={()=>setShowWelcome({show:false,isPostCheckout:false,planName:""})} onNavigate={setView} isPostCheckout={showWelcome.isPostCheckout} planName={showWelcome.planName}/>}
      {(view==="feed"||view==="pages") && <OnboardingChecklist api={api} toast={toast} onNavigate={setView}/>}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:28, gap:16 }}>
          <div style={{ flexShrink:0 }}>
            <h1 style={{ color:"#fff", margin:0, fontSize:20, letterSpacing:1 }}>{titles[view]}</h1>
            <div style={{ color:"#3a3a5a", fontSize:11, marginTop:4 }}>{new Date().toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</div>
          </div>
          {!!localStorage.getItem("sl_admin_email") && (
            <div style={{ flex:1, maxWidth:440 }}>
              <GlobalSearch api={api} toast={toast} onNavigate={setView}/>
            </div>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:7,height:7,borderRadius:"50%",background:stats?"#22c55e":"#ff4d4d",boxShadow:`0 0 6px ${stats?"#22c55e":"#ff4d4d"}` }}/>
              <span style={{ color:"#3a3a5a", fontSize:10 }}>{stats?"API LIVE":"API OFFLINE"}</span>
            </div>
            {/* Notification Bell */}
            <div ref={bellRef} style={{ position:"relative" }}>
              <NotificationBell
                unreadCount={stats?.unread_matches||0}
                onClick={()=>setShowBell(p=>!p)}
              />
              {showBell && (
                <NotificationDropdown
                  matches={bellMatches}
                  pages={pages}
                  onClose={()=>setShowBell(false)}
                  onMarkAllRead={async()=>{
                    try { await api.post("/matches/mark-all-read"); setBellMatches([]); toast.success("All read"); } catch(e) {}
                    setShowBell(false);
                  }}
                  onNavigate={(v)=>{ setView(v); setShowBell(false); }}
                />
              )}
            </div>
          </div>
        </div>
        <PWAInstallBanner/>
        <ViewErrorBoundary viewName={view}>
        {view==="feed"      && <FeedView api={api} pages={pages} stats={stats} statsLoading={statsLoading} toast={toast} templates={templates} userPlan={stats?.plan||""}/>}
        {view==="saved"     && <SavedMatchesTab api={api} toast={toast} templates={templates}/>}
        {view==="pipeline"  && <PipelineView api={api} pages={pages} toast={toast}/>}
        {view==="analytics" && <AnalyticsView api={api} toast={toast}/>}
        {view==="inbox"     && <InboxView templates={templates} toast={toast} api={api}/>}
        {view==="templates" && <TemplatesView templates={templates} setTemplates={setTemplates} toast={toast}/>}
        {view==="pages"     && <PagesView api={api} pages={pages} setPages={setPages} toast={toast}/>}
        {view==="pricing"   && <PricingView api={api} toast={toast} onSelectPlan={async (plan)=>{ try{ const {checkout_url}=await api.post("/billing/checkout",{plan:plan.id,success_url:`${window.location.origin}/?checkout=success&plan=${plan.id}`,cancel_url:`${window.location.origin}/#pricing`}); window.location.href=checkout_url; }catch(e){ toast.error(e.message); }}}/>}
        {view==="settings"  && <>
          <SettingsView api={api} fbConnected={fbConnected} setFbConnected={setFbConnected} toast={toast}/>
          <div style={{ maxWidth:620 }}>
            <BillingSettings api={api} toast={toast} userPlan={userPlan}/>
            <WeeklyEmailSettings api={api} toast={toast}/>
            <CustomerProfileSettings api={api} toast={toast}/>
            <WhiteLabelSettings api={api} toast={toast} userPlan={userPlan}/>
          </div>
        </>}
        {view==="admin"     && <AdminView api={api} toast={toast} userEmail={userEmail}/>}
        {view==="coupons"   && <CouponManager api={api} toast={toast}/>}
        {view==="billing"   && <div style={{ maxWidth:620 }}><BillingSettings api={api} toast={toast} userPlan={userPlan}/></div>}
        {view==="status"    && <><StatusPage api={api}/><IncidentManager api={api} toast={toast} isAdmin={isAdmin}/></>}
        {view==="comms"     && <div style={{ maxWidth:720 }}><AnnouncePanel api={api} toast={toast}/><IncidentManager api={api} toast={toast}/></div>}
        {view==="refunds"   && <div style={{ maxWidth:720 }}><RefundPanel api={api} toast={toast}/><DunningManager api={api} toast={toast}/></div>}
        {view==="security"  && <SecurityDashboard api={api} toast={toast}/>}
        {view==="plans"     && <PlanManager api={api} toast={toast}/>}
        {view==="help"      && <HelpCenter api={api} toast={toast}/>}
        {view==="setup"     && <OnboardingEditor api={api} toast={toast}/>}
        {view==="kb"        && <KBAdminEditor api={api} toast={toast}/>}
        {view==="guides"    && <AdminGuides/>}
        {view==="referral"  && <MyReferralPage api={api} toast={toast}/>}
        {view==="cms"       && <AdminCMS api={api} toast={toast}/>}
        {view==="advanced"  && <AdminAdvanced api={api} toast={toast}/>}
        {view==="myzone"    && <MyZoneView api={api} toast={toast}/>}
        {view==="onboarding-setup" && <CustomerOnboarding api={api} toast={toast} onComplete={()=>setView("feed")}/>}
        </ViewErrorBoundary>
        </div>
      </main>
      <Toast toasts={toast.toasts}/>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// ZONE / TRADE EXCLUSIVITY MODEL — added for zone exclusivity feature
// ════════════════════════════════════════════════════════════════════════════

function getTokenPayload() {
  try {
    const token = localStorage.getItem("sl_token");
    if (!token) return null;
    return JSON.parse(atob(token.split(".")[1]));
  } catch { return null; }
}

function platformFlagsFromToken() {
  const p = getTokenPayload();
  if (!p) return { reddit:false, homeadvisor:false, nextdoor:false };
  return {
    reddit:      p.has_reddit      === true,
    homeadvisor: p.has_homeadvisor === true,
    nextdoor:    p.has_nextdoor    === true,
  };
}

function ZoneManager({ api, toast }) {
  const [zones,       setZones]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null); // zone id
  const [zoneDetail,  setZoneDetail]  = useState(null);
  const [slots,       setSlots]       = useState([]);
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState({ name:"", description:"" });
  const [saving,      setSaving]      = useState(false);
  const [groupForm,   setGroupForm]   = useState({ name:"", url:"" });
  const [addingGroup, setAddingGroup] = useState(false);

  const load = async () => {
    if (!api) return;
    setLoading(true);
    try { const r = await api.get("/zones"); setZones(Array.isArray(r)?r:[]); }
    catch { setZones([]); } finally { setLoading(false); }
  };

  const loadZone = async (id) => {
    setSelected(id);
    try {
      const [detail, slotData] = await Promise.all([
        api.get(`/zones/${id}`),
        api.get(`/zones/${id}/slots`),
      ]);
      setZoneDetail(detail);
      setSlots(slotData);
    } catch(e) { toast.error(e.message); }
  };

  useEffect(() => { load(); }, []);

  const createZone = async () => {
    if (!form.name.trim()) { toast.error("Zone name required"); return; }
    setSaving(true);
    try {
      await api.post("/zones", form);
      toast.success(`Zone "${form.name}" created!`);
      setForm({ name:"", description:"" });
      setShowForm(false); load();
    } catch(e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const addGroup = async () => {
    if (!groupForm.name || !groupForm.url) { toast.error("Name and URL required"); return; }
    setAddingGroup(true);
    try {
      await api.post(`/zones/${selected}/groups`, groupForm);
      toast.success("Group added!");
      setGroupForm({ name:"", url:"" });
      loadZone(selected);
    } catch(e) { toast.error(e.message); } finally { setAddingGroup(false); }
  };

  const removeGroup = async (groupId) => {
    try {
      await api.del(`/zones/${selected}/groups/${groupId}`);
      toast.success("Group removed"); loadZone(selected);
    } catch(e) { toast.error(e.message); }
  };

  const toggleZone = async (id, active) => {
    try { await api.patch(`/zones/${id}`, { active: !active }); load(); }
    catch(e) { toast.error(e.message); }
  };

  const sc  = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px", marginBottom:12 };
  const iS  = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" };
  const lS  = { color:"#4a4a6a", fontSize:10, letterSpacing:1, display:"block", marginBottom:4 };

  if (loading) return <div style={{ display:"flex", justifyContent:"center", padding:60 }}><Spinner size={28}/></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div>
          <div style={{ color:"#fff", fontSize:15, fontWeight:700 }}>🗺️ Zone Manager</div>
          <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>Create and manage scraper zones. Each zone has exclusive trade slots.</div>
        </div>
        <button onClick={()=>setShowForm(p=>!p)}
          style={{ padding:"8px 18px", borderRadius:7, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, cursor:"pointer" }}>
          + New Zone
        </button>
      </div>

      {/* Create zone form */}
      {showForm && (
        <div style={{ ...sc, borderColor:"#6366f130" }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>New Zone</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div><label style={lS}>ZONE NAME (e.g. "Metro Detroit", "Chicago")</label>
              <input style={iS} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Metro Detroit"/></div>
            <div><label style={lS}>DESCRIPTION (optional)</label>
              <input style={iS} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Covers Detroit, Dearborn, Warren, Sterling Heights..."/></div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 80px", gap:8 }}>
              <div><label style={lS}>CITY</label>
              <input style={iS} value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} placeholder="Detroit"/></div>
              <div><label style={lS}>STATE</label>
              <input style={iS} value={form.state} onChange={e=>setForm(f=>({...f,state:e.target.value}))} placeholder="MI" maxLength={2}/></div>
            </div>
            <div><label style={lS}>ZIP CODES (comma-separated — used for HomeAdvisor targeting)</label>
              <input style={iS} value={form.zip_codes} onChange={e=>setForm(f=>({...f,zip_codes:e.target.value}))} placeholder="48066, 48067, 48068, 48080, 48081, 48088"/></div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={createZone} disabled={saving}
                style={{ padding:"9px 20px", borderRadius:7, border:"none", background:"#22c55e", color:"#fff", fontSize:12, cursor:"pointer" }}>
                {saving?"Creating…":"Create Zone"}
              </button>
              <button onClick={()=>setShowForm(false)}
                style={{ padding:"9px 16px", borderRadius:7, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns: selected ? "min(280px, 100%) 1fr" : "1fr", gap:12 }}>
        {/* Zone list */}
        <div>
          {zones.length === 0 ? (
            <div style={{ ...sc, textAlign:"center", color:"#3a3a5a", fontSize:12, padding:"30px 0" }}>
              No zones yet. Create your first zone above.
            </div>
          ) : zones.map(z => (
            <div key={z.id} onClick={()=>loadZone(z.id)}
              style={{ ...sc, cursor:"pointer", borderColor:selected===z.id?"#6366f1":"#1e1e2e", background:selected===z.id?"#0a0a1a":"#0f0f1a", marginBottom:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                    <span style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{z.name}</span>
                    <span style={{ padding:"1px 6px", borderRadius:3, background:z.active?"#22c55e20":"#ff4d4d20", color:z.active?"#22c55e":"#ff4d4d", fontSize:9 }}>
                      {z.active?"ACTIVE":"PAUSED"}
                    </span>
                  </div>
                  <div style={{ display:"flex", gap:12 }}>
                    <span style={{ color:"#4a4a6a", fontSize:11 }}>📘 {z.fb_groups} groups</span>
                    <span style={{ color:"#4a4a6a", fontSize:11 }}>🔒 {z.slots_taken}/{z.total_trades} trades taken</span>
                  </div>
                </div>
                <button onClick={e=>{e.stopPropagation(); toggleZone(z.id, z.active);}}
                  style={{ padding:"3px 10px", borderRadius:4, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:10, cursor:"pointer" }}>
                  {z.active?"Pause":"Activate"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Zone detail */}
        {selected && zoneDetail && (
          <div>
            {/* FB Groups */}
            <div style={{ ...sc }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>📘 Facebook Groups — {zoneDetail.name}</div>
                <div style={{ color:"#4a4a6a", fontSize:11 }}>{zoneDetail.fb_groups?.length||0} groups</div>
              </div>

              {/* Add group form */}
              <div style={{ background:"#0a0a0f", borderRadius:8, padding:12, marginBottom:12 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                  <div><label style={lS}>GROUP NAME</label>
                    <input style={iS} value={groupForm.name} onChange={e=>setGroupForm(f=>({...f,name:e.target.value}))} placeholder="Detroit Homeowners Group"/></div>
                  <div><label style={lS}>FACEBOOK GROUP URL</label>
                    <input style={iS} value={groupForm.url} onChange={e=>setGroupForm(f=>({...f,url:e.target.value}))} placeholder="https://facebook.com/groups/..."/></div>
                </div>
                <button onClick={addGroup} disabled={addingGroup}
                  style={{ padding:"7px 16px", borderRadius:6, border:"none", background:"#1877F2", color:"#fff", fontSize:11, cursor:"pointer" }}>
                  {addingGroup?"Adding…":"+ Add Group"}
                </button>
              </div>

              {/* Group list */}
              {(zoneDetail.fb_groups||[]).length === 0 ? (
                <div style={{ color:"#3a3a5a", fontSize:12, textAlign:"center", padding:"16px 0" }}>No groups yet. Add Facebook groups above.</div>
              ) : (zoneDetail.fb_groups||[]).map(g => (
                <div key={g.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #0a0a0f" }}>
                  <span style={{ fontSize:16 }}>📘</span>
                  <div style={{ flex:1 }}>
                    <div style={{ color:"#e2e8f0", fontSize:12 }}>{g.name}</div>
                    <a href={g.url} target="_blank" rel="noreferrer" style={{ color:"#4a4a6a", fontSize:10, textDecoration:"none" }}>{g.url.slice(0,60)}…</a>
                  </div>
                  <button onClick={()=>removeGroup(g.id)}
                    style={{ padding:"3px 10px", borderRadius:4, border:"1px solid #ff4d4d30", background:"transparent", color:"#ff4d4d", fontSize:10, cursor:"pointer" }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {/* Trade slots */}
            <div style={{ ...sc }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>🔒 Trade Slots — Exclusivity</div>
                <div style={{ color:"#4a4a6a", fontSize:11 }}>{slots.filter(s=>s.taken).length}/{slots.length} taken</div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, className:"sl-3col" }}>
                {slots.map(s => (
                  <div key={s.trade_id} style={{ padding:"8px 10px", borderRadius:7, border:`1px solid ${s.taken?"#ff4d4d30":"#22c55e30"}`, background:s.taken?"#1a0808":"#081a0a" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                      <span style={{ fontSize:14 }}>{s.trade_icon}</span>
                      <span style={{ color:"#e2e8f0", fontSize:11, fontWeight:600 }}>{s.trade_name}</span>
                    </div>
                    {s.taken ? (
                      <div style={{ color:"#ff6b6b", fontSize:10 }}>
                        🔒 Taken{s.user ? ` — ${s.user.email}` : ""}
                        {s.override && <span style={{ color:"#f59e0b", marginLeft:4 }}>(override)</span>}
                      </div>
                    ) : (
                      <div style={{ color:"#22c55e", fontSize:10 }}>✅ Available</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TRADE MANAGER — Admin Panel
// ════════════════════════════════════════════════════════════════════════════
function TradeManager({ api, toast }) {
  const [trades,    setTrades]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [form,      setForm]      = useState({ name:"", icon:"🔧", description:"", suggested_keywords:[] });
  const [kwInput,   setKwInput]   = useState("");

  const load = async () => {
    if (!api) return;
    setLoading(true);
    try { const r = await api.get("/trades"); setTrades(Array.isArray(r)?r:[]); }
    catch { setTrades([]); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    setSelected(t.id);
    setForm({ name:t.name, icon:t.icon, description:t.description||"", suggested_keywords:t.suggested_keywords||[] });
    setShowForm(true);
  };

  const openNew = () => {
    setSelected(null);
    setForm({ name:"", icon:"🔧", description:"", suggested_keywords:[] });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Trade name required"); return; }
    setSaving(true);
    try {
      if (selected) {
        await api.patch(`/trades/${selected}`, form);
        toast.success("Trade updated!");
      } else {
        await api.post("/trades", form);
        toast.success(`Trade "${form.name}" created!`);
      }
      setShowForm(false); load();
    } catch(e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/trades/${t.id}`, { active: !t.active });
      toast.success(t.active ? "Trade deactivated" : "Trade activated");
      load();
    } catch(e) { toast.error(e.message); }
  };

  const addKeyword = () => {
    const kw = kwInput.trim();
    if (!kw) return;
    if (form.suggested_keywords.includes(kw)) { toast.error("Keyword already added"); return; }
    if (form.suggested_keywords.length >= 9) { toast.error("Max 10 suggested keywords"); return; }
    setForm(f => ({ ...f, suggested_keywords: [...f.suggested_keywords, kw] }));
    setKwInput("");
  };

  const removeKeyword = (kw) => {
    setForm(f => ({ ...f, suggested_keywords: f.suggested_keywords.filter(k => k !== kw) }));
  };

  const ICONS = ["🔧","⚡","🌡️","🔨","🏠","🌿","🪵","🖌️","🧹","🐛","📦","🧱","🏗️","🔩","🪟","🌧️","🏚️","🌳","❄️","🚪","🔌","🏊","🧱","🏘️","🔐","🛠️","🏡","🪴"];

  const sc = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px", marginBottom:12 };
  const iS = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" };
  const lS = { color:"#4a4a6a", fontSize:10, letterSpacing:1, display:"block", marginBottom:4 };

  if (loading) return <div style={{ display:"flex", justifyContent:"center", padding:60 }}><Spinner size={28}/></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div>
          <div style={{ color:"#fff", fontSize:15, fontWeight:700 }}>🛠️ Trade Manager</div>
          <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>{trades.length} trades · Each zone has up to 2 slots per trade</div>
        </div>
        <button onClick={openNew}
          style={{ padding:"8px 18px", borderRadius:7, border:"none", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff", fontSize:12, cursor:"pointer" }}>
          + New Trade
        </button>
      </div>

      {/* Edit/Create form */}
      {showForm && (
        <div style={{ ...sc, borderColor:"#6366f130", marginBottom:16 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:16 }}>
            {selected ? "Edit Trade" : "New Trade"}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"60px 1fr 1fr", gap:10, marginBottom:12 }}>
            <div>
              <label style={lS}>ICON</label>
              <select style={{ ...iS, fontSize:20, textAlign:"center" }} value={form.icon} onChange={e=>setForm(f=>({...f,icon:e.target.value}))}>
                {ICONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>TRADE NAME</label>
              <input style={iS} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. HVAC, Plumbing, Electrical"/>
            </div>
            <div>
              <label style={lS}>DESCRIPTION</label>
              <input style={iS} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Short description of the trade"/>
            </div>
          </div>

          {/* Suggested keywords */}
          <div style={{ marginBottom:12 }}>
            <label style={lS}>SUGGESTED KEYWORDS (shown to customers when they pick this trade)</label>
            <div style={{ display:"flex", gap:6, marginBottom:8 }}>
              <input style={{ ...iS, flex:1 }} value={kwInput} onChange={e=>setKwInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&addKeyword()}
                placeholder="Type a keyword and press Enter or Add"/>
              <button onClick={addKeyword}
                style={{ padding:"8px 14px", borderRadius:6, border:"none", background:"#6366f1", color:"#fff", fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
                + Add
              </button>
            </div>
            {form.suggested_keywords.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {form.suggested_keywords.map(kw => (
                  <div key={kw} style={{ display:"flex", alignItems:"center", gap:4, background:"#1e1e3e", borderRadius:5, padding:"3px 10px" }}>
                    <span style={{ color:"#a5b4fc", fontSize:11 }}>{kw}</span>
                    <button onClick={()=>removeKeyword(kw)} style={{ background:"transparent", border:"none", color:"#4a4a6a", cursor:"pointer", fontSize:14, lineHeight:1, padding:0 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ color:"#3a3a5a", fontSize:10, marginTop:6 }}>
              {form.suggested_keywords.length}/10 keywords · Customers see these as suggestions when picking their trade
            </div>
          </div>

          <div style={{ display:"flex", gap:8 }}>
            <button onClick={save} disabled={saving}
              style={{ padding:"9px 20px", borderRadius:7, border:"none", background:"#22c55e", color:"#fff", fontSize:12, cursor:"pointer" }}>
              {saving ? "Saving…" : selected ? "Save Changes" : "Create Trade"}
            </button>
            <button onClick={()=>setShowForm(false)}
              style={{ padding:"9px 16px", borderRadius:7, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Trade list */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:8 }}>
        {trades.map(t => (
          <div key={t.id} style={{ ...sc, marginBottom:0, opacity:t.active?1:0.5 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <span style={{ fontSize:22 }}>{t.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{t.name}</div>
                <div style={{ color:"#4a4a6a", fontSize:10 }}>{t.description}</div>
              </div>
              <span style={{ padding:"1px 6px", borderRadius:3, background:t.active?"#22c55e20":"#ff4d4d20", color:t.active?"#22c55e":"#ff4d4d", fontSize:9 }}>
                {t.active?"ON":"OFF"}
              </span>
            </div>
            {t.suggested_keywords?.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:3, marginBottom:8 }}>
                {t.suggested_keywords.slice(0,4).map(kw => (
                  <span key={kw} style={{ background:"#1a1a2e", color:"#6366f1", fontSize:9, padding:"1px 6px", borderRadius:3 }}>{kw}</span>
                ))}
                {t.suggested_keywords.length > 4 && (
                  <span style={{ color:"#3a3a5a", fontSize:9 }}>+{t.suggested_keywords.length-4} more</span>
                )}
              </div>
            )}
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={()=>openEdit(t)}
                style={{ flex:1, padding:"5px 0", borderRadius:5, border:"1px solid #1e1e2e", background:"transparent", color:"#6366f1", fontSize:10, cursor:"pointer" }}>
                Edit
              </button>
              <button onClick={()=>toggleActive(t)}
                style={{ flex:1, padding:"5px 0", borderRadius:5, border:`1px solid ${t.active?"#ff4d4d30":"#22c55e30"}`, background:"transparent", color:t.active?"#ff4d4d":"#22c55e", fontSize:10, cursor:"pointer" }}>
                {t.active?"Disable":"Enable"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// APPROVAL QUEUE — Admin Panel
// ════════════════════════════════════════════════════════════════════════════
function ApprovalQueue({ api, toast }) {
  const [data,    setData]    = useState({ keyword_requests:[], group_requests:[], total:0 });
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("keywords");
  const [noting,  setNoting]  = useState({});
  const [notes,   setNotes]   = useState({});

  const load = async () => {
    if (!api) return;
    setLoading(true);
    try { const r = await api.get("/approvals/pending"); setData(r); }
    catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const review = async (type, id, action) => {
    try {
      const endpoint = type === "keywords" ? `/approvals/keywords/${id}` : `/approvals/groups/${id}`;
      await api.post(endpoint, { action, note: notes[id] || "" });
      toast.success(action === "approve" ? "✅ Approved!" : "❌ Rejected");
      load();
    } catch(e) { toast.error(e.message); }
  };

  const sc  = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px", marginBottom:10 };
  const pending_kw = data.keyword_requests || [];
  const pending_gr = data.group_requests   || [];
  const total      = data.total || 0;

  if (loading) return <div style={{ display:"flex", justifyContent:"center", padding:60 }}><Spinner size={28}/></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ color:"#fff", fontSize:15, fontWeight:700 }}>✅ Approval Queue</div>
            {total > 0 && (
              <div style={{ padding:"2px 8px", borderRadius:10, background:"#ff4d4d", color:"#fff", fontSize:11, fontWeight:700 }}>{total}</div>
            )}
          </div>
          <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>Review customer keyword and group requests</div>
        </div>
        <button onClick={load} style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:11, cursor:"pointer" }}>↺ Refresh</button>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:16 }}>
        {[
          { id:"keywords", label:`🔑 Keyword Requests`, count: pending_kw.length },
          { id:"groups",   label:`📘 Group Requests`,   count: pending_gr.length },
        ].map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ padding:"7px 16px", borderRadius:7, border:`1px solid ${tab===t.id?"#6366f1":"#1e1e2e"}`, background:tab===t.id?"#6366f120":"transparent", color:tab===t.id?"#a5b4fc":"#4a4a6a", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
            {t.label}
            {t.count > 0 && <span style={{ padding:"1px 7px", borderRadius:8, background:"#ff4d4d", color:"#fff", fontSize:10, fontWeight:700 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Keyword Requests */}
      {tab === "keywords" && (
        <div>
          {pending_kw.length === 0 ? (
            <div style={{ ...sc, textAlign:"center", color:"#3a3a5a", padding:"30px 0" }}>
              No pending keyword requests ✅
            </div>
          ) : pending_kw.map(r => (
            <div key={r.id} style={{ ...sc }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                <div>
                  <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600 }}>{r.user_email}</div>
                  <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <span style={{ padding:"2px 8px", borderRadius:4, background:"#f59e0b20", color:"#f59e0b", fontSize:10, fontFamily:"'DM Mono',monospace" }}>PENDING</span>
              </div>

              <div style={{ marginBottom:10 }}>
                <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:6 }}>REQUESTED KEYWORDS</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {(r.keywords||[]).map(kw => (
                    <span key={kw} style={{ background:"#1e1e3e", color:"#a5b4fc", fontSize:12, padding:"4px 10px", borderRadius:5, fontFamily:"'DM Mono',monospace" }}>
                      🔑 {kw}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom:12 }}>
                <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:4 }}>ADMIN NOTE (optional)</div>
                <input value={notes[r.id]||""} onChange={e=>setNotes(n=>({...n,[r.id]:e.target.value}))}
                  placeholder="Reason for approval/rejection..."
                  style={{ width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"7px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
              </div>

              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>review("keywords", r.id, "approve")}
                  style={{ padding:"8px 20px", borderRadius:6, border:"none", background:"#22c55e", color:"#fff", fontSize:12, cursor:"pointer", fontWeight:600 }}>
                  ✅ Approve
                </button>
                <button onClick={()=>review("keywords", r.id, "reject")}
                  style={{ padding:"8px 20px", borderRadius:6, border:"1px solid #ff4d4d30", background:"transparent", color:"#ff4d4d", fontSize:12, cursor:"pointer" }}>
                  ❌ Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Group Requests */}
      {tab === "groups" && (
        <div>
          {pending_gr.length === 0 ? (
            <div style={{ ...sc, textAlign:"center", color:"#3a3a5a", padding:"30px 0" }}>
              No pending group requests ✅
            </div>
          ) : pending_gr.map(r => (
            <div key={r.id} style={{ ...sc }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                <div>
                  <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600 }}>{r.user_email}</div>
                  <div style={{ color:"#4a4a6a", fontSize:11, marginTop:2 }}>{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <span style={{ padding:"2px 8px", borderRadius:4, background:"#f59e0b20", color:"#f59e0b", fontSize:10, fontFamily:"'DM Mono',monospace" }}>PENDING</span>
              </div>

              <div style={{ marginBottom:10 }}>
                <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:6 }}>REQUESTED GROUP</div>
                <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600, marginBottom:4 }}>{r.group_name}</div>
                <a href={r.group_url} target="_blank" rel="noreferrer"
                  style={{ color:"#6366f1", fontSize:11, textDecoration:"none" }}>{r.group_url}</a>
                {r.reason && (
                  <div style={{ marginTop:8, color:"#64748b", fontSize:12, fontStyle:"italic" }}>"{r.reason}"</div>
                )}
              </div>

              <div style={{ marginBottom:12 }}>
                <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:4 }}>ADMIN NOTE (optional)</div>
                <input value={notes[r.id]||""} onChange={e=>setNotes(n=>({...n,[r.id]:e.target.value}))}
                  placeholder="Reason for approval/rejection..."
                  style={{ width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"7px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
              </div>

              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>review("groups", r.id, "approve")}
                  style={{ padding:"8px 20px", borderRadius:6, border:"none", background:"#22c55e", color:"#fff", fontSize:12, cursor:"pointer", fontWeight:600 }}>
                  ✅ Approve — Add to Zone
                </button>
                <button onClick={()=>review("groups", r.id, "reject")}
                  style={{ padding:"8px 20px", borderRadius:6, border:"1px solid #ff4d4d30", background:"transparent", color:"#ff4d4d", fontSize:12, cursor:"pointer" }}>
                  ❌ Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// NEW CUSTOMER ONBOARDING FLOW
// ════════════════════════════════════════════════════════════════════════════
function WizardPlatformGrid() {
  const pf = platformFlagsFromToken();
  const platforms = [
    { icon:"📘", platform:"Facebook Groups", self:"Up to 20 groups", full:"Up to 30 groups", freq:"Hourly", show:true },
    { icon:"🟠", platform:"Reddit",          self:"Up to 8 subreddits", full:"Up to 20 subreddits", freq:"Daily", show:pf.reddit },
    { icon:"🏠", platform:"HomeAdvisor",     self:"Up to 3 pages", full:"Up to 6 pages", freq:"Daily", show:pf.homeadvisor },
    { icon:"🟣", platform:"Nextdoor",        self:"Your account", full:"Your account", freq:"Daily*", show:pf.nextdoor },
  ].filter(p=>p.show);
  if (platforms.length === 0) return null;
  return (
    <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
      <div style={{ color:"#fff", fontSize:12, fontWeight:600, marginBottom:10 }}>📡 What Gets Monitored</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
        {platforms.map(p => (
          <div key={p.platform} style={{ background:"#0f0f1a", borderRadius:7, padding:"8px 12px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
              <span style={{ fontSize:14 }}>{p.icon}</span>
              <span style={{ color:"#e2e8f0", fontSize:11, fontWeight:600 }}>{p.platform}</span>
              <span style={{ marginLeft:"auto", color:"#4a4a6a", fontSize:9 }}>{p.freq}</span>
            </div>
            <div style={{ color:"#64748b", fontSize:10 }}>
              Self: {p.self}<br/>Full: {p.full}
            </div>
          </div>
        ))}
      </div>
      {pf.nextdoor && <div style={{ color:"#4a4a6a", fontSize:10, marginTop:8 }}>
        * Nextdoor requires a dedicated account — not your personal one.
      </div>}
    </div>
  );
}



function CustomerOnboarding({ api, toast, onComplete }) {
  const [step,      setStep]      = useState(0); // 0=welcome, 1=plan
  const [zones,     setZones]     = useState([]);
  const [trades,    setTrades]    = useState([]);
  const [slots,     setSlots]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);

  const [plan,      setPlan]      = useState("");
  const [zone,      setZone]      = useState(null);
  const [trade,     setTrade]     = useState(null);
  const [keywords,  setKeywords]  = useState([]);
  const [kwInput,   setKwInput]   = useState("");
  const [profile,   setProfile]   = useState({ business_name:"", phone:"" });
  const [waitlist,  setWaitlist]  = useState(false);
  const [done,      setDone]      = useState(false);

  // Load zones on mount
  useEffect(() => {
    if (!api) return;
    api.get("/zones").then(r => setZones(Array.isArray(r)?r:[])).catch(()=>{});
    api.get("/trades").then(r => setTrades(Array.isArray(r)?r:[])).catch(()=>{});
  }, []);

  // Load trade slots when zone selected
  useEffect(() => {
    if (!zone) return;
    setLoading(true);
    // Load both slots and all trades, merge them
    Promise.all([
      api.get(`/zones/${zone.id}/slots`).catch(()=>[]),
      trades.length > 0 ? Promise.resolve(trades) : api.get("/trades").catch(()=>[]),
    ]).then(([slotData, tradeData]) => {
      if (trades.length === 0) setTrades(Array.isArray(tradeData)?tradeData:[]);
      // If no slots returned, create available slots from trades
      const sl = Array.isArray(slotData) ? slotData : [];
      if (sl.length === 0 && Array.isArray(tradeData)) {
        setSlots(tradeData.map(t => ({
          trade_id: t.id, trade_name: t.name, trade_icon: t.icon,
          taken: false, override: false, user: null
        })));
      } else {
        setSlots(sl);
      }
    }).finally(() => setLoading(false));
  }, [zone]);

  const selectKeyword = (kw) => {
    if (keywords.includes(kw)) {
      setKeywords(k => k.filter(x => x !== kw));
    } else if (keywords.length < 5) {
      setKeywords(k => [...k, kw]);
    } else {
      toast.error("Maximum 9 keywords");
    }
  };

  const addCustomKeyword = () => {
    const kw = kwInput.trim();
    if (!kw) return;
    if (keywords.includes(kw)) { toast.error("Already added"); return; }
    if (keywords.length >= 9) { toast.error("Maximum 9 keywords"); return; }
    setKeywords(k => [...k, kw]);
    setKwInput("");
  };

  const joinWaitlist = async () => {
    if (!profile.business_name) { toast.error("Enter your business name first"); return; }
    setSaving(true);
    try {
      await api.post("/zones/waitlist", {
        zone_id: zone.id,
        trade_id: trade.trade_id,
        business_name: profile.business_name,
      });
      toast.success("Added to waitlist! We'll notify you when this slot opens.");
      setWaitlist(true);
    } catch(e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const complete = async () => {
    if (!profile.business_name.trim()) { toast.error("Business name required"); return; }
    if (!profile.phone.trim()) { toast.error("Phone number required"); return; }
    if (keywords.length === 0) { toast.error("Add at least one keyword"); return; }
    setSaving(true);
    try {
      // Submit keyword request (pending approval)
      await api.post("/zones/keywords/request", { keywords });
      // Save profile
      await api.post("/zones/profile/setup", {
        business_name: profile.business_name,
        phone: profile.phone,
        zone_id: zone.id,
        trade_id: trade.trade_id,
        service_plan: plan,
      });
      localStorage.setItem("sl_setup_complete", "1");
      localStorage.setItem("sl_show_welcome_banner", "1");
      setDone(true);
    } catch(e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const PLANS = [
    {
      id: "self_manage",
      name: "Self-Manage",
      price: "$95/mo",
      color: "#6366f1",
      features: [
        "24/7 monitoring of local Facebook groups",
        "Instant alerts when your keywords are triggered",
        "You reply to posts from your own Facebook account",
        "Access to all matched leads in your feed",
        "List of Facebook groups to follow in your zone",
      ],
      note: "You reply to leads yourself",
    },
    {
      id: "full_service",
      name: "Full Service",
      price: "$350/mo",
      color: "#22c55e",
      badge: "DONE FOR YOU",
      features: [
        "Everything in Self-Manage",
        "We reply to Facebook group posts on your behalf",
        "Replies mention your business name and service",
        "Crafted responses for each lead",
        "Priority monitoring — faster alerts",
      ],
      note: "We reply to leads for you",
    },
  ];

  const stepLabels = ["Plan","Zone","Trade","Keywords","Profile"];

  // Celebration screen
  if (done) return (
    <div style={{maxWidth:520,margin:"0 auto",padding:"20px 16px 40px",textAlign:"center"}}>
      <div style={{fontSize:64,marginBottom:16}}>🎉</div>
      <h2 style={{color:"#fff",fontSize:24,fontWeight:800,marginBottom:8}}>You are live!</h2>
      <p style={{color:"#64748b",fontSize:14,lineHeight:1.8,marginBottom:24}}>
        Your zone is being monitored right now. Keywords are pending approval — usually activated within a few hours.
      </p>
      <div style={{background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:12,padding:"16px 20px",marginBottom:20,textAlign:"left"}}>
        <div style={{color:"#4a4a6a",fontSize:10,letterSpacing:1,marginBottom:10}}>YOUR SETUP</div>
        {[
          {label:"Plan",     value:plan==="full_service"?"Full Service ($350/mo)":"Self-Manage ($95/mo)"},
          {label:"Zone",     value:zone?.name||"—"},
          {label:"Trade",    value:trade?.name||"—"},
          {label:"Keywords", value:`${keywords.length} keywords pending approval`},
          {label:"Alerts",   value:"⚠️ Set up in Settings → Alert Settings"},
        ].map((item,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:i<4?"1px solid #0a0a0f":"none"}}>
            <span style={{color:"#4a4a6a",fontSize:12}}>{item.label}</span>
            <span style={{color:"#e2e8f0",fontSize:12,fontWeight:500}}>{item.value}</span>
          </div>
        ))}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        <button onClick={()=>{onComplete&&onComplete();window.dispatchEvent(new CustomEvent("sl-navigate",{detail:"myzone"}));}}
          style={{padding:"13px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"}}>
          📍 Go to My Zone — Join Your Facebook Groups
        </button>
        <button onClick={()=>{onComplete&&onComplete();window.dispatchEvent(new CustomEvent("sl-navigate",{detail:"settings"}));}}
          style={{padding:"11px 20px",borderRadius:8,border:"1px solid #1e1e2e",background:"transparent",color:"#94a3b8",fontSize:13,cursor:"pointer"}}>
          ⚙️ Set Up Alert Settings
        </button>
        <button onClick={()=>onComplete&&onComplete()}
          style={{padding:"11px 20px",borderRadius:8,border:"1px solid #1e1e2e",background:"transparent",color:"#64748b",fontSize:13,cursor:"pointer"}}>
          ⚡ Go to Live Feed
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth:680, margin:"0 auto", padding:"20px 0" }}>
      {/* Step 0: Welcome */}
      {step === 0 && (
        <div style={{textAlign:"center",maxWidth:480,margin:"0 auto",padding:"20px 0"}}>
          <div style={{fontSize:60,marginBottom:14}}>🚀</div>
          <h2 style={{color:"#fff",fontSize:24,fontWeight:800,marginBottom:8}}>Welcome to Socialisten</h2>
          <p style={{color:"#64748b",fontSize:14,lineHeight:1.8,marginBottom:24}}>
            Set up in 5 minutes. We monitor Facebook groups in your area 24/7 and alert you instantly when someone needs your service.
          </p>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:24,textAlign:"left"}}>
            {[
              {icon:"📡",text:"24/7 monitoring of local Facebook groups"},
              {icon:"⚡",text:"Instant Telegram alert when a lead is found"},
              {icon:"🎯",text:"Only your trade — no noise, no irrelevant posts"},
              {icon:"🏆",text:"First responder wins — reply fast, win the job"},
            ].map((item,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:12,background:"#0f0f1a",border:"1px solid #1e1e2e",borderRadius:8,padding:"10px 14px"}}>
                <span style={{fontSize:20}}>{item.icon}</span>
                <span style={{color:"#e2e8f0",fontSize:13}}>{item.text}</span>
              </div>
            ))}
          </div>
          <button onClick={()=>setStep(1)}
            style={{padding:"14px 40px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
              color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 24px #6366f160",width:"100%"}}>
            Get Started →
          </button>
          <div style={{color:"#3a3a5a",fontSize:12,marginTop:10}}>Takes about 5 minutes</div>
        </div>
      )}

      {/* Progress bar — hidden on step 0 */}
      <div style={{ marginBottom:28, display: step===0?"none":"block" }}>
        <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:10 }}>
          {stepLabels.map((label, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", flex: i < stepLabels.length-1 ? 1 : "none" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <div style={{ width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700,
                  background: step > i+1 ? "#22c55e" : step === i+1 ? "#6366f1" : "#1e1e2e",
                  color: step >= i+1 ? "#fff" : "#4a4a6a" }}>
                  {step > i+1 ? "✓" : i+1}
                </div>
                <div style={{ color: step === i+1 ? "#a5b4fc" : step > i+1 ? "#22c55e" : "#3a3a5a", fontSize:9, fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap" }}>{label}</div>
              </div>
              {i < stepLabels.length-1 && (
                <div style={{ flex:1, height:2, background: step > i+1 ? "#22c55e" : "#1e1e2e", margin:"0 4px", marginBottom:18 }}/>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ textAlign:"center", color:"#4a4a6a", fontSize:11, letterSpacing:1, marginBottom:4, display:step===0?"none":"block" }}>
        STEP {step} OF 5
      </div>

      {/* Step 1 — Plan Selection */}
      {step === 1 && (
        <div>
          <h2 style={{ color:"#fff", fontSize:20, fontWeight:700, marginBottom:6 }}>Choose Your Plan</h2>
          <p style={{ color:"#64748b", fontSize:13, marginBottom:24 }}>Select the plan that fits how you want to work with leads.</p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))", gap:14, marginBottom:24 }} className="sl-wizard-plans">
            {PLANS.map(p => (
              <div key={p.id} onClick={()=>setPlan(p.id)}
                style={{ background:"#0f0f1a", border:`2px solid ${plan===p.id?p.color:"#1e1e2e"}`, borderRadius:12, padding:20, cursor:"pointer", position:"relative" }}>
                {p.badge && (
                  <div style={{ position:"absolute", top:-10, right:12, padding:"2px 10px", borderRadius:8, background:p.color, color:"#fff", fontSize:9, fontWeight:700 }}>{p.badge}</div>
                )}
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                  <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${p.color}`, background:plan===p.id?p.color:"transparent", flexShrink:0 }}/>
                  <div>
                    <div style={{ color:"#fff", fontSize:15, fontWeight:700 }}>{p.name}</div>
                    <div style={{ color:p.color, fontSize:20, fontWeight:800 }}>{p.price}</div>
                  </div>
                </div>
                <div style={{ color:"#64748b", fontSize:11, marginBottom:12, fontStyle:"italic" }}>{p.note}</div>
                {p.features.map((f,i) => (
                  <div key={i} style={{ display:"flex", gap:8, marginBottom:5 }}>
                    <span style={{ color:p.color, fontSize:12, flexShrink:0 }}>✓</span>
                    <span style={{ color:"#94a3b8", fontSize:12 }}>{f}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <button onClick={()=>plan&&setStep(2)} disabled={!plan}
            style={{ width:"100%", padding:"13px", borderRadius:9, border:"none", background:plan?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#1e1e2e", color:plan?"#fff":"#4a4a6a", fontSize:14, fontWeight:600, cursor:plan?"pointer":"not-allowed" }}>
            Continue →
          </button>
        </div>
      )}

      {/* Step 2 — Zone Selection */}
      {step === 2 && (
        <div>
          <h2 style={{ color:"#fff", fontSize:20, fontWeight:700, marginBottom:6 }}>Select Your Service Area</h2>
          <p style={{ color:"#64748b", fontSize:13, marginBottom:24 }}>Choose the metro area where you provide services. Each area has exclusive trade slots.</p>
          {zones.length === 0 ? (
            <div style={{ background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:12, padding:30, textAlign:"center" }}>
              <div style={{ color:"#3a3a5a", fontSize:13, marginBottom:12 }}>No service areas available yet.</div>
              <AreaRequestForm api={api} toast={toast} compact={false}/>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:24 }}>
              {zones.map(z => (
                <div key={z.id} onClick={()=>setZone(z)}
                  style={{ background:"#0f0f1a", border:`2px solid ${zone?.id===z.id?"#6366f1":"#1e1e2e"}`, borderRadius:10, padding:"14px 18px", cursor:"pointer", display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:18, height:18, borderRadius:"50%", border:"2px solid #6366f1", background:zone?.id===z.id?"#6366f1":"transparent", flexShrink:0 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ color:"#fff", fontSize:14, fontWeight:600 }}>{z.name}</div>
                    {z.description && <div style={{ color:"#4a4a6a", fontSize:12, marginTop:2 }}>{z.description}</div>}
                  </div>
                  <div style={{ color:"#4a4a6a", fontSize:11 }}>{z.slots_taken}/{z.total_trades} trades taken</div>
                </div>
              ))}
            </div>
          )}
          <AreaRequestForm api={api} toast={toast}/>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>setStep(1)} style={{ padding:"12px 24px", borderRadius:9, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:13, cursor:"pointer" }}>← Back</button>
            <button onClick={()=>zone&&setStep(3)} disabled={!zone}
              style={{ flex:1, padding:"12px", borderRadius:9, border:"none", background:zone?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#1e1e2e", color:zone?"#fff":"#4a4a6a", fontSize:14, fontWeight:600, cursor:zone?"pointer":"not-allowed" }}>
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* Step 3 — Trade Selection */}
      {step === 3 && (
        <div>
          <h2 style={{ color:"#fff", fontSize:20, fontWeight:700, marginBottom:6 }}>Select Your Trade</h2>
          <p style={{ color:"#64748b", fontSize:13, marginBottom:24 }}>Up to 2 contractors per trade in {zone?.name}. 🔒 Full (2/2) = waitlist only.</p>
          {loading ? <div style={{ display:"flex", justifyContent:"center", padding:40 }}><Spinner size={28}/></div> : (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:24 }} className="sl-wizard-trades">
              {slots.map(s => {
                const taken = s.taken && !s.override;
                const selected = trade?.trade_id === s.trade_id;
                return (
                  <div key={s.trade_id} onClick={()=>!taken&&setTrade(s)}
                    style={{ background:taken?"#0a0808":selected?"#0a0a1a":"#0f0f1a",
                      border:`2px solid ${selected?"#6366f1":taken?"#ff4d4d20":"#1e1e2e"}`,
                      borderRadius:9, padding:"12px 14px", cursor:taken?"not-allowed":"pointer", opacity:taken?0.6:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                      <span style={{ fontSize:20 }}>{s.trade_icon}</span>
                      <span style={{ color:taken?"#4a4a6a":selected?"#a5b4fc":"#e2e8f0", fontSize:12, fontWeight:600 }}>{s.trade_name}</span>
                    </div>
                    <div style={{ fontSize:10, color:taken?"#ff4d4d":selected?"#22c55e":"#3a3a5a", fontFamily:"'DM Mono',monospace" }}>
                      {taken ? "🔒 Taken" : selected ? "✓ Selected" : "✅ Available"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>setStep(2)} style={{ padding:"12px 24px", borderRadius:9, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:13, cursor:"pointer" }}>← Back</button>
            <button onClick={()=>trade&&setStep(4)} disabled={!trade}
              style={{ flex:1, padding:"12px", borderRadius:9, border:"none", background:trade?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#1e1e2e", color:trade?"#fff":"#4a4a6a", fontSize:14, fontWeight:600, cursor:trade?"pointer":"not-allowed" }}>
              Continue →
            </button>
          </div>
          {/* Custom trade request + waitlist */}
          <div style={{ marginTop:12, display:"flex", flexDirection:"column", gap:8 }}>
            {slots.some(s=>s.taken) && (
              <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:8, padding:"12px 16px" }}>
                <div style={{ color:"#64748b", fontSize:12 }}>
                  🔒 If your trade is taken, you can join the waitlist and we'll notify you when the slot opens.
                </div>
              </div>
            )}
            <CustomTradeRequest api={api} toast={toast} zone={zone}/>
          </div>
        </div>
      )}

      {/* Step 4 — Keywords */}
      {step === 4 && (
        <div>
          <h2 style={{ color:"#fff", fontSize:20, fontWeight:700, marginBottom:6 }}>Set Your Keywords</h2>
          <p style={{ color:"#64748b", fontSize:13, marginBottom:8 }}>Choose up to 9 keywords. We'll alert you when someone posts using these words in {zone?.name}.</p>
          <div style={{ background:"#0a0a14", border:"1px solid #6366f130", borderRadius:8, padding:"8px 14px", marginBottom:20, color:"#a5b4fc", fontSize:11 }}>
            ℹ️ Select up to 9 keywords from the suggestions below, or add your own. Keywords require admin approval — usually within 24 hours.
          </div>

          {/* Suggested keywords from trade */}
          {(() => {
            const tradeData = trades.find(t => t.id === trade?.trade_id);
            const suggested = tradeData?.suggested_keywords || [];
            return suggested.length > 0 ? (
              <div style={{ marginBottom:16 }}>
                <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:8 }}>SUGGESTED KEYWORDS FOR {trade?.trade_name?.toUpperCase()}</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {suggested.map(kw => (
                    <button key={kw} onClick={()=>selectKeyword(kw)}
                      style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${keywords.includes(kw)?"#6366f1":"#1e1e2e"}`, background:keywords.includes(kw)?"#6366f120":"transparent", color:keywords.includes(kw)?"#a5b4fc":"#64748b", fontSize:12, cursor:"pointer" }}>
                      {keywords.includes(kw)?"✓ ":""}{kw}
                    </button>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Custom keyword input */}
          <div style={{ marginBottom:16 }}>
            <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:6 }}>ADD CUSTOM KEYWORD</div>
            <div style={{ display:"flex", gap:6 }}>
              <input value={kwInput} onChange={e=>setKwInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&addCustomKeyword()}
                placeholder="Type a keyword and press Enter..."
                style={{ flex:1, background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"9px 12px", color:"#ccc", fontSize:12, outline:"none" }}/>
              <button onClick={addCustomKeyword}
                style={{ padding:"9px 16px", borderRadius:6, border:"none", background:"#6366f1", color:"#fff", fontSize:12, cursor:"pointer" }}>Add</button>
            </div>
          </div>

          {/* Selected keywords */}
          {keywords.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:8 }}>YOUR KEYWORDS ({keywords.length}/5)</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {keywords.map(kw => (
                  <div key={kw} style={{ display:"flex", alignItems:"center", gap:6, background:"#1e1e3e", borderRadius:6, padding:"5px 12px" }}>
                    <span style={{ color:"#a5b4fc", fontSize:12 }}>🔑 {kw}</span>
                    <button onClick={()=>setKeywords(k=>k.filter(x=>x!==kw))} style={{ background:"transparent", border:"none", color:"#4a4a6a", cursor:"pointer", fontSize:16, lineHeight:1, padding:0 }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>setStep(3)} style={{ padding:"12px 24px", borderRadius:9, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:13, cursor:"pointer" }}>← Back</button>
            <button onClick={()=>keywords.length>0&&setStep(5)} disabled={keywords.length===0}
              style={{ flex:1, padding:"12px", borderRadius:9, border:"none", background:keywords.length>0?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#1e1e2e", color:keywords.length>0?"#fff":"#4a4a6a", fontSize:14, fontWeight:600, cursor:keywords.length>0?"pointer":"not-allowed" }}>
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* Step 5 — Profile */}
      {step === 5 && (
        <div>
          <h2 style={{ color:"#fff", fontSize:20, fontWeight:700, marginBottom:6 }}>Your Business Profile</h2>
          <p style={{ color:"#64748b", fontSize:13, marginBottom:24 }}>
            {plan==="full_service" ? "We need your business name and phone to craft replies on your behalf." : "Tell us about your business so we can personalize your experience."}
          </p>

          {/* Summary */}
          <div style={{ background:"#0a0a14", border:"1px solid #6366f130", borderRadius:10, padding:"14px 18px", marginBottom:20 }}>
            <div style={{ color:"#a5b4fc", fontSize:12, fontWeight:600, marginBottom:10 }}>Your Setup Summary</div>
            {[
              { label:"Plan",     value: plan==="full_service"?"Full Service ($350/mo)":"Self-Manage ($95/mo)", color:plan==="full_service"?"#22c55e":"#6366f1" },
              { label:"Zone",     value: zone?.name,              color:"#e2e8f0" },
              { label:"Trade",    value: `${trade?.trade_icon} ${trade?.trade_name}`, color:"#e2e8f0" },
              { label:"Keywords", value: keywords.join(", "),     color:"#a5b4fc" },
            ].map(item => (
              <div key={item.label} style={{ display:"flex", gap:12, marginBottom:6 }}>
                <span style={{ color:"#4a4a6a", fontSize:12, width:70, flexShrink:0 }}>{item.label}:</span>
                <span style={{ color:item.color, fontSize:12 }}>{item.value}</span>
              </div>
            ))}
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:24 }}>
            <div>
              <label style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, display:"block", marginBottom:4 }}>BUSINESS NAME *</label>
              <input value={profile.business_name} onChange={e=>setProfile(p=>({...p,business_name:e.target.value}))}
                placeholder="e.g. Smith's HVAC Services"
                style={{ width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"10px 12px", color:"#ccc", fontSize:13, outline:"none", boxSizing:"border-box" }}/>
            </div>
            <div>
              <label style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, display:"block", marginBottom:4 }}>PHONE NUMBER *</label>
              <input value={profile.phone} onChange={e=>setProfile(p=>({...p,phone:e.target.value}))}
                placeholder="e.g. (313) 555-0100"
                style={{ width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"10px 12px", color:"#ccc", fontSize:13, outline:"none", boxSizing:"border-box" }}/>
            </div>
            {plan === "full_service" && (
              <div style={{ background:"#0a1a0a", border:"1px solid #22c55e30", borderRadius:8, padding:"10px 14px", color:"#22c55e", fontSize:11 }}>
                ✅ Full Service: We will reply to Facebook group posts mentioning your business name and phone number on your behalf.
              </div>
            )}
          </div>

          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>setStep(4)} style={{ padding:"12px 24px", borderRadius:9, border:"1px solid #1e1e2e", background:"transparent", color:"#64748b", fontSize:13, cursor:"pointer" }}>← Back</button>
            {/* Nextdoor warning */}
            <div style={{ background:"#1a1000", border:"1px solid #f59e0b30", borderRadius:8, padding:"10px 14px", marginBottom:10, width:"100%" }}>
              <div style={{ color:"#f59e0b", fontSize:11, fontWeight:600, marginBottom:4 }}>⚠️ Nextdoor — Action Required After Setup</div>
              <div style={{ color:"#78716c", fontSize:11, lineHeight:1.6 }}>
                Go to <strong style={{color:"#e2e8f0"}}>Settings → Nextdoor Session</strong> to connect your Nextdoor account. 
                We recommend using a <strong style={{color:"#e2e8f0"}}>dedicated account</strong> (not your personal one) to avoid any risk to your main account.
              </div>
            </div>
            <button onClick={complete} disabled={saving}
              style={{ flex:1, padding:"12px", borderRadius:9, border:"none", background:"linear-gradient(135deg,#22c55e,#16a34a)", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer" }}>
              {saving ? "Setting up…" : "🚀 Complete Setup"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── In-app Area Request Form ─────────────────────────────────────────────────


function AreaRequestForm({ api, toast, compact = true }) {
  const [show,    setShow]    = useState(false);
  const [city,    setCity]    = useState("");
  const [state,   setState]   = useState("");
  const [note,    setNote]    = useState("");
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);

  const submit = async () => {
    if (!city.trim()) { toast.error("Enter your city"); return; }
    setSending(true);
    try {
      await api.post("/zones/area-requests", { city, state, note });
      setSent(true);
      toast.success("Request sent! We'll notify you when your area is added.");
    } catch(e) { toast.error(e.message || "Failed to send request"); }
    finally { setSending(false); }
  };

  if (sent) return (
    <div style={{ background:"#0a1a0a", border:"1px solid #22c55e30", borderRadius:8, padding:"12px 16px", marginBottom:16 }}>
      <div style={{ color:"#22c55e", fontSize:13 }}>✅ Area request sent! We'll notify you when {city} is available.</div>
    </div>
  );

  if (!show) return (
    <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:8, padding:"12px 16px", marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ color:"#64748b", fontSize:12 }}>Don't see your area?</div>
        <button onClick={()=>setShow(true)}
          style={{ padding:"5px 14px", borderRadius:5, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, cursor:"pointer" }}>
          🗺️ Request your area
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ background:"#0a0a14", border:"1px solid #6366f130", borderRadius:10, padding:"16px 18px", marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>🗺️ Request a New Service Area</div>
        <button onClick={()=>setShow(false)} style={{ background:"transparent", border:"none", color:"#4a4a6a", fontSize:18, cursor:"pointer" }}>×</button>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
        <div>
          <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:4 }}>CITY *</div>
          <input value={city} onChange={e=>setCity(e.target.value)} placeholder="e.g. Detroit"
            style={{ width:"100%", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
        </div>
        <div>
          <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:4 }}>STATE</div>
          <input value={state} onChange={e=>setState(e.target.value)} placeholder="e.g. Michigan"
            style={{ width:"100%", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
        </div>
      </div>
      <div style={{ marginBottom:12 }}>
        <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:4 }}>ADDITIONAL NOTE (optional)</div>
        <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. I cover all of Metro Detroit..."
          style={{ width:"100%", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
      </div>
      <div style={{ display:"flex", gap:8 }}>
        <button onClick={submit} disabled={sending}
          style={{ padding:"8px 20px", borderRadius:6, border:"none", background:"#6366f1", color:"#fff", fontSize:12, cursor:"pointer" }}>
          {sending ? "Sending…" : "Send Request"}
        </button>
        <button onClick={()=>setShow(false)}
          style={{ padding:"8px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}


// ── Custom Trade Request ─────────────────────────────────────────────────────
function CustomTradeRequest({ api, toast, zone }) {
  const [show,    setShow]    = useState(false);
  const [trade,   setTrade]   = useState("");
  const [desc,    setDesc]    = useState("");
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);

  const submit = async () => {
    if (!trade.trim()) { toast.error("Enter your trade name"); return; }
    setSending(true);
    try {
      await api.post("/zones/trade-requests", {
        trade_name: trade,
        description: desc,
        zone_id: zone?.id,
      });
      setSent(true);
      toast.success("Trade request submitted! Admin will review within 24 hours.");
    } catch(e) { toast.error(e.message || "Failed to submit"); }
    finally { setSending(false); }
  };

  if (sent) return (
    <div style={{ background:"#0a1a0a", border:"1px solid #22c55e30", borderRadius:8, padding:"12px 16px" }}>
      <div style={{ color:"#22c55e", fontSize:12 }}>✅ Trade request sent! We'll add it and notify you.</div>
    </div>
  );

  if (!show) return (
    <div style={{ background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:8, padding:"12px 16px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ color:"#64748b", fontSize:12 }}>Don't see your trade?</div>
        <button onClick={()=>setShow(true)}
          style={{ padding:"5px 14px", borderRadius:5, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:11, cursor:"pointer" }}>
          🔧 Request a trade
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ background:"#0a0a14", border:"1px solid #6366f130", borderRadius:10, padding:"16px 18px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>🔧 Request a New Trade</div>
        <button onClick={()=>setShow(false)} style={{ background:"transparent", border:"none", color:"#4a4a6a", fontSize:18, cursor:"pointer" }}>×</button>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12 }}>
        <div>
          <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:4 }}>TRADE NAME *</div>
          <input value={trade} onChange={e=>setTrade(e.target.value)} placeholder="e.g. Solar Installation, Chimney Sweep"
            style={{ width:"100%", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
        </div>
        <div>
          <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:4 }}>DESCRIPTION (optional)</div>
          <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Brief description of your service"
            style={{ width:"100%", background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
        </div>
      </div>
      <div style={{ display:"flex", gap:8 }}>
        <button onClick={submit} disabled={sending}
          style={{ padding:"8px 20px", borderRadius:6, border:"none", background:"#6366f1", color:"#fff", fontSize:12, cursor:"pointer" }}>
          {sending?"Sending…":"Submit Request"}
        </button>
        <button onClick={()=>setShow(false)}
          style={{ padding:"8px 14px", borderRadius:6, border:"1px solid #1e1e2e", background:"transparent", color:"#4a4a6a", fontSize:12, cursor:"pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// CUSTOMER PROFILE SETTINGS
// ════════════════════════════════════════════════════════════════════════════
function CustomerProfileSettings({ api, toast }) {
  const [profile,   setProfile]   = useState(null);
  const [groups,    setGroups]    = useState([]);
  const [trades,    setTrades]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("overview");
  const [kwInput,   setKwInput]   = useState("");
  const [kwList,    setKwList]    = useState([]);
  const [kwPending, setKwPending] = useState([]);
  const [submitting,setSubmitting]= useState(false);
  const [grpForm,   setGrpForm]   = useState({ name:"", url:"", reason:"" });
  const [grpPending,setGrpPending]= useState([]);
  const [showGrpForm, setShowGrpForm] = useState(false);

  const load = async () => {
    if (!api) return;
    setLoading(true);
    try {
      const [prof, trd] = await Promise.all([
        api.get("/zones/me/profile").catch(() => null),
        api.get("/trades").catch(() => []),
      ]);
      setProfile(prof);
      setTrades(Array.isArray(trd) ? trd : []);
      if (prof?.zone_id) {
        const [grp, pending] = await Promise.all([
          api.get(`/zones/${prof.zone_id}/groups`).catch(() => []),
          api.get("/zones/me/requests").catch(() => ({ keyword_requests:[], group_requests:[] })),
        ]);
        setGroups(Array.isArray(grp) ? grp : []);
        try { setKwList(prof.keywords ? JSON.parse(prof.keywords) : []); } catch { setKwList([]); }
        try { setKwPending(prof.keywords_pending ? JSON.parse(prof.keywords_pending) : []); } catch { setKwPending([]); }
        setGrpPending(pending?.group_requests || []);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submitKeywords = async () => {
    if (kwList.length === 0) { toast.error("Add at least one keyword"); return; }
    setSubmitting(true);
    try {
      await api.post("/zones/keywords/request", { keywords: kwList });
      toast.success("Keywords submitted for approval!");
      load();
    } catch(e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const requestGroup = async () => {
    if (!grpForm.name || !grpForm.url) { toast.error("Name and URL required"); return; }
    setSubmitting(true);
    try {
      await api.post(`/zones/${profile.zone_id}/request-group`, grpForm);
      toast.success("Group request submitted!");
      setGrpForm({ name:"", url:"", reason:"" });
      setShowGrpForm(false);
      load();
    } catch(e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const addKw = (kw) => {
    const k = kw.trim();
    if (!k || kwList.includes(k)) return;
    if (kwList.length >= 9) { toast.error("Maximum 9 keywords"); return; }
    setKwList(prev => [...prev, k]);
    setKwInput("");
  };

  const tradeObj  = profile?.trade_id ? trades.find(t => t.id === profile.trade_id) : null;
  const suggested = tradeObj?.suggested_keywords || [];

  const sc  = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px", marginBottom:12 };
  const iS  = { width:"100%", background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:6, padding:"8px 12px", color:"#ccc", fontSize:12, outline:"none", boxSizing:"border-box" };
  const lS  = { color:"#4a4a6a", fontSize:10, letterSpacing:1, display:"block", marginBottom:4 };

  if (loading) return <div style={{ display:"flex", justifyContent:"center", padding:40 }}><Spinner size={28}/></div>;

  if (!profile || !profile.zone_id) return (
    <div style={{ ...sc, textAlign:"center", padding:32, marginBottom:20 }}>
      <div style={{ fontSize:36, marginBottom:12 }}>🚀</div>
      <div style={{ color:"#fff", fontSize:15, fontWeight:600, marginBottom:8 }}>Complete Your Setup</div>
      <div style={{ color:"#64748b", fontSize:13, marginBottom:16 }}>You haven't set up your zone and trade yet.</div>
      <div style={{ background:"#0a0a14", border:"1px solid #6366f130", borderRadius:8, padding:"10px 16px", color:"#a5b4fc", fontSize:12 }}>
        👈 Click <strong>Setup Wizard</strong> in the left sidebar to get started.
      </div>
    </div>
  );

  const TABS = [
    { id:"overview", label:"📋 Overview" },
    { id:"keywords", label:"🔑 Keywords" },
    { id:"groups",   label:"📘 FB Groups" },
  ];

  return (
    <div style={{ marginBottom:20 }}>
      <h2 style={{ color:"#fff", fontSize:16, fontWeight:700, marginBottom:16 }}>My Service Profile</h2>
      <div style={{ display:"flex", gap:6, marginBottom:16 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ padding:"6px 14px", borderRadius:7, border:`1px solid ${tab===t.id?"#6366f1":"#1e1e2e"}`, background:tab===t.id?"#6366f120":"transparent", color:tab===t.id?"#a5b4fc":"#4a4a6a", fontSize:11, cursor:"pointer" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div style={{ ...sc }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              { label:"PLAN",          value: profile.service_plan==="full_service"?"Full Service ($350/mo)":"Self-Manage ($95/mo)", color: profile.service_plan==="full_service"?"#22c55e":"#6366f1" },
              { label:"ZONE",          value: profile.zone_name||"—",   color:"#e2e8f0" },
              { label:"TRADE",         value: tradeObj ? `${tradeObj.icon} ${tradeObj.name}` : "—", color:"#e2e8f0" },
              { label:"BUSINESS NAME", value: profile.business_name||"—", color:"#e2e8f0" },
              { label:"PHONE",         value: profile.phone||"—",        color:"#e2e8f0" },
              { label:"KEYWORDS",      value: kwList.length>0?`${kwList.length}/9 active`:"None yet", color:kwList.length>0?"#22c55e":"#f59e0b" },
            ].map(item => (
              <div key={item.label} style={{ background:"#0a0a0f", borderRadius:8, padding:"10px 12px" }}>
                <div style={{ color:"#4a4a6a", fontSize:9, letterSpacing:1, marginBottom:3 }}>{item.label}</div>
                <div style={{ color:item.color, fontSize:12, fontWeight:600 }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "keywords" && (
        <div style={{ ...sc }}>
          {kwPending.length > 0 && (
            <div style={{ background:"#1a1000", border:"1px solid #f59e0b30", borderRadius:8, padding:"10px 14px", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                <div style={{ color:"#f59e0b", fontSize:11, fontWeight:600 }}>⏳ Pending Approval</div>
                <div style={{ color:"#78716c", fontSize:10 }}>Usually reviewed within 24 hours</div>
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:6 }}>
                {kwPending.map(kw => <span key={kw} style={{ background:"#f59e0b20", color:"#f59e0b", fontSize:11, padding:"2px 8px", borderRadius:4 }}>{kw}</span>)}
              </div>
              <div style={{ color:"#78716c", fontSize:10 }}>
                You will receive an email when your keywords are approved. Once approved they go live immediately.
              </div>
            </div>
          )}
          <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:10 }}>Active Keywords ({kwList.length}/9)</div>
          {kwList.length === 0 ? <div style={{ color:"#3a3a5a", fontSize:12, marginBottom:10 }}>No active keywords yet.</div> : (
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
              {kwList.map(kw => (
                <div key={kw} style={{ display:"flex", alignItems:"center", gap:4, background:"#1e1e3e", borderRadius:5, padding:"4px 10px" }}>
                  <span style={{ color:"#a5b4fc", fontSize:12 }}>🔑 {kw}</span>
                  <button onClick={()=>setKwList(k=>k.filter(x=>x!==kw))} style={{ background:"transparent", border:"none", color:"#4a4a6a", cursor:"pointer", fontSize:14, lineHeight:1, padding:0 }}>×</button>
                </div>
              ))}
            </div>
          )}
          {suggested.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:6 }}>SUGGESTIONS FOR {tradeObj?.name?.toUpperCase()}</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {suggested.map(kw => (
                  <button key={kw} onClick={()=>addKw(kw)}
                    style={{ padding:"3px 10px", borderRadius:5, border:`1px solid ${kwList.includes(kw)?"#6366f1":"#1e1e2e"}`, background:kwList.includes(kw)?"#6366f120":"transparent", color:kwList.includes(kw)?"#a5b4fc":"#64748b", fontSize:11, cursor:"pointer" }}>
                    {kwList.includes(kw)?"✓ ":""}{kw}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display:"flex", gap:6, marginBottom:10 }}>
            <input value={kwInput} onChange={e=>setKwInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addKw(kwInput)}
              placeholder="Add custom keyword..." style={{ flex:1, ...iS }}/>
            <button onClick={()=>addKw(kwInput)} style={{ padding:"8px 14px", borderRadius:6, border:"none", background:"#6366f1", color:"#fff", fontSize:11, cursor:"pointer" }}>Add</button>
          </div>
          <button onClick={submitKeywords} disabled={submitting||kwList.length===0}
            style={{ padding:"9px 20px", borderRadius:7, border:"none", background:kwList.length>0?"linear-gradient(135deg,#6366f1,#8b5cf6)":"#1e1e2e", color:kwList.length>0?"#fff":"#4a4a6a", fontSize:12, cursor:kwList.length>0?"pointer":"not-allowed" }}>
            {submitting?"Submitting…":"Submit Keywords for Approval"}
          </button>
          <div style={{ marginTop:8, background:"#0a0a0f", border:"1px solid #1e1e2e", borderRadius:7, padding:"10px 14px" }}>
            <div style={{ color:"#4a4a6a", fontSize:10, letterSpacing:1, marginBottom:6 }}>DON'T SEE YOUR TRADE?</div>
            <CustomTradeRequest api={api} toast={toast} zone={profile?.zone_id ? {id:profile.zone_id} : null}/>
          </div>
        </div>
      )}

      {tab === "groups" && (
        <div>
          <div style={{ ...sc }}>
            <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:10 }}>Facebook Groups in {profile.zone_name}</div>
            {groups.length === 0 ? (
              <div style={{ color:"#3a3a5a", fontSize:12, padding:"12px 0" }}>No groups added to your zone yet.</div>
            ) : groups.map(g => (
              <div key={g.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #0a0a0f" }}>
                <span style={{ fontSize:18 }}>📘</span>
                <div style={{ flex:1 }}>
                  <div style={{ color:"#e2e8f0", fontSize:12 }}>{g.name}</div>
                  <a href={g.url} target="_blank" rel="noreferrer" style={{ color:"#4a4a6a", fontSize:10, textDecoration:"none" }}>{g.url.slice(0,50)}…</a>
                </div>
                <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                  <a href={g.url} target="_blank" rel="noreferrer"
                    style={{ padding:"4px 10px", borderRadius:5, border:"1px solid #1877F230", background:"#1877F215", color:"#60a5fa", fontSize:10, textDecoration:"none" }}>Join →</a>
                  <button
                    onClick={()=>{
                      const joined = JSON.parse(localStorage.getItem("sl_joined_groups")||"[]");
                      const isJoined = joined.includes(g.id);
                      const updated = isJoined ? joined.filter(x=>x!==g.id) : [...joined, g.id];
                      localStorage.setItem("sl_joined_groups", JSON.stringify(updated));
                      setGroups(gs => gs.map(gr => gr.id===g.id ? {...gr, _joined: !isJoined} : gr));
                    }}
                    style={{ padding:"4px 10px", borderRadius:5, border:`1px solid ${
                      JSON.parse(localStorage.getItem("sl_joined_groups")||"[]").includes(g.id) ? "#22c55e40" : "#1e1e2e"
                    }`, background: JSON.parse(localStorage.getItem("sl_joined_groups")||"[]").includes(g.id) ? "#22c55e15" : "transparent",
                    color: JSON.parse(localStorage.getItem("sl_joined_groups")||"[]").includes(g.id) ? "#22c55e" : "#4a4a6a",
                    fontSize:10, cursor:"pointer", whiteSpace:"nowrap" }}>
                    {JSON.parse(localStorage.getItem("sl_joined_groups")||"[]").includes(g.id) ? "✅ Joined" : "Mark Joined"}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ ...sc }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:showGrpForm?12:0 }}>
              <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>Request a Group</div>
              <button onClick={()=>setShowGrpForm(p=>!p)}
                style={{ padding:"5px 12px", borderRadius:5, border:"1px solid #6366f130", background:"#6366f110", color:"#a5b4fc", fontSize:10, cursor:"pointer" }}>
                {showGrpForm?"Cancel":"+ Request"}
              </button>
            </div>
            {showGrpForm && (
              <div>
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:10 }}>
                  <div><label style={lS}>GROUP NAME</label><input style={iS} value={grpForm.name} onChange={e=>setGrpForm(f=>({...f,name:e.target.value}))} placeholder="Detroit Homeowners Group"/></div>
                  <div><label style={lS}>FACEBOOK URL</label><input style={iS} value={grpForm.url} onChange={e=>setGrpForm(f=>({...f,url:e.target.value}))} placeholder="https://facebook.com/groups/..."/></div>
                  <div><label style={lS}>REASON (optional)</label><input style={iS} value={grpForm.reason} onChange={e=>setGrpForm(f=>({...f,reason:e.target.value}))} placeholder="Why should this group be monitored?"/></div>
                </div>
                <button onClick={requestGroup} disabled={submitting}
                  style={{ padding:"8px 18px", borderRadius:6, border:"none", background:"#6366f1", color:"#fff", fontSize:11, cursor:"pointer" }}>
                  {submitting?"Submitting…":"Submit Request"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════


function MyZoneView({ api, toast }) {
  const [profile,  setProfile]  = useState(null);
  const [groups,   setGroups]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const pf = platformFlagsFromToken();

  useEffect(() => {
    if (!api) return;
    api.get("/zones/me/profile").then(prof => {
      api.get("/settings/nextdoor-status").then(nd => {
        setProfile({...prof, nd_session_status: nd?.status || "disconnected"});
      }).catch(() => setProfile(prof));
      if (prof?.zone_id) {
        api.get(`/zones/${prof.zone_id}/groups`).then(g => setGroups(Array.isArray(g)?g:[])).catch(()=>{});
      }
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const sc = { background:"#0f0f1a", border:"1px solid #1e1e2e", borderRadius:10, padding:"16px 18px", marginBottom:14 };
  const badge = (color, label) => <span style={{ padding:"2px 8px", borderRadius:4, background:`${color}20`, color, fontSize:10, fontWeight:600 }}>{label}</span>;

  if (loading) return <div style={{ display:"flex", justifyContent:"center", padding:60 }}><Spinner size={28}/></div>;

  if (!profile?.zone_id) return (
    <div style={{ ...sc, textAlign:"center", padding:40 }}>
      <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
      <div style={{ color:"#fff", fontSize:16, fontWeight:600, marginBottom:8 }}>No Zone Set Up Yet</div>
      <div style={{ color:"#64748b", fontSize:13 }}>Complete the Setup Wizard to see your monitoring coverage.</div>
    </div>
  );

  const kws = (() => { try { return JSON.parse(profile.keywords || "[]"); } catch { return []; } })();
  const joinedGroups = (() => { try { return JSON.parse(localStorage.getItem("sl_joined_groups") || "[]"); } catch { return []; } })();

  return (
    <div style={{ maxWidth:700 }}>
      <h2 style={{ color:"#fff", fontSize:18, fontWeight:700, marginBottom:20 }}>📍 My Zone — {profile.zone_name}</h2>

      <div style={{ ...sc }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          {[
            { label:"PLAN",   value: profile.service_plan==="full_service"?"Full Service":"Self-Manage", color:"#6366f1" },
            { label:"TRADE",  value: profile.trade_name || "—", color:"#e2e8f0" },
            { label:"STATUS", value: kws.length>0?"Active":"Pending Setup", color:kws.length>0?"#22c55e":"#f59e0b" },
          ].map(item => (
            <div key={item.label} style={{ background:"#0a0a0f", borderRadius:8, padding:"10px 14px" }}>
              <div style={{ color:"#4a4a6a", fontSize:9, letterSpacing:1, marginBottom:4 }}>{item.label}</div>
              <div style={{ color:item.color, fontSize:13, fontWeight:600 }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...sc }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:14 }}>🔍 What We Monitor For You</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ background:"#0a0a0f", borderRadius:8, padding:"12px 14px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <span style={{ fontSize:18 }}>📘</span>
              <div>
                <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600 }}>Facebook Groups</div>
                <div style={{ color:"#4a4a6a", fontSize:11 }}>Scanned hourly · {groups.length} groups in {profile.zone_name}</div>
              </div>
              {badge("#1877F2", "HOURLY")}
            </div>
            {groups.length > 0 ? (
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {groups.slice(0,6).map(g => (
                  <div key={g.id} style={{ display:"flex", gap:4, alignItems:"center" }}>
                    <a href={g.url} target="_blank" rel="noreferrer" style={{ padding:"3px 10px", borderRadius:5, background:"#1877F215", border:"1px solid #1877F230", color:"#60a5fa", fontSize:11, textDecoration:"none" }}>{g.name}</a>
                    {joinedGroups.includes(g.id) && <span style={{ fontSize:11, color:"#22c55e" }}>✅</span>}
                  </div>
                ))}
                {groups.length > 6 && <span style={{ color:"#4a4a6a", fontSize:11 }}>+{groups.length-6} more</span>}
              </div>
            ) : <div style={{ color:"#3a3a5a", fontSize:11 }}>Admin is setting up groups for your zone.</div>}
            {profile.service_plan==="self_manage" && <div style={{ marginTop:8, color:"#f59e0b", fontSize:11 }}>⚠️ Join these groups on Facebook to reply to leads yourself</div>}
            {profile.service_plan==="full_service" && <div style={{ marginTop:8, color:"#22c55e", fontSize:11 }}>✅ We monitor and reply to these groups on your behalf</div>}
          </div>
          {pf.reddit && (
            <div style={{ background:"#0a0a0f", borderRadius:8, padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:18 }}>🟠</span>
                <div style={{ flex:1 }}>
                  <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600 }}>Reddit</div>
                  <div style={{ color:"#4a4a6a", fontSize:11 }}>{profile.service_plan==="full_service"?"Up to 20 subreddits":"Up to 8 subreddits"} · Scanned daily</div>
                </div>
                {badge("#FF4500", "DAILY")}
              </div>
              <div style={{ marginTop:8, color:"#64748b", fontSize:11 }}>We monitor trade-specific subreddits and r/HomeImprovement for your keywords.</div>
            </div>
          )}
          {pf.homeadvisor && (
            <div style={{ background:"#0a0a0f", borderRadius:8, padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:18 }}>🏠</span>
                <div style={{ flex:1 }}>
                  <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600 }}>HomeAdvisor</div>
                  <div style={{ color:"#4a4a6a", fontSize:11 }}>{profile.service_plan==="full_service"?"Up to 6 pages":"Up to 3 pages"} · Scanned daily</div>
                </div>
                {badge("#F97316", "DAILY")}
              </div>
              <div style={{ marginTop:8, color:"#64748b", fontSize:11 }}>We scan HomeAdvisor project listings for your trade in {profile.zone_name}.</div>
            </div>
          )}
          {pf.nextdoor && (
            <div style={{ background:"#0a0a0f", borderRadius:8, padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <span style={{ fontSize:18 }}>🟣</span>
                <div style={{ flex:1 }}>
                  <div style={{ color:"#e2e8f0", fontSize:13, fontWeight:600 }}>Nextdoor</div>
                  <div style={{ color:"#4a4a6a", fontSize:11 }}>Self-managed · Your own account</div>
                </div>
                {profile?.nd_session_status==="connected"
                  ? <span style={{ padding:"2px 8px", borderRadius:4, background:"#22c55e20", color:"#22c55e", fontSize:10, fontWeight:600 }}>✅ CONNECTED</span>
                  : <span style={{ padding:"2px 8px", borderRadius:4, background:"#ff4d4d20", color:"#ff4d4d", fontSize:10, fontWeight:600 }}>❌ NOT CONNECTED</span>}
              </div>
              {profile?.nd_session_status==="connected"
                ? <div style={{ color:"#22c55e", fontSize:11 }}>✅ Nextdoor is connected and scanned daily at 8am.</div>
                : <div>
                    <div style={{ color:"#64748b", fontSize:11, marginBottom:6 }}>Go to <strong style={{color:"#a5b4fc"}}>Settings → Nextdoor Session</strong> to connect.</div>
                    <div style={{ background:"#1a1000", border:"1px solid #f59e0b30", borderRadius:6, padding:"6px 10px", color:"#f59e0b", fontSize:10 }}>⚠️ Use a dedicated account — not your personal one.</div>
                  </div>}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...sc }}>
        <div style={{ color:"#fff", fontSize:13, fontWeight:600, marginBottom:10 }}>🔑 Your Active Keywords</div>
        {kws.length === 0
          ? <div style={{ color:"#3a3a5a", fontSize:12 }}>No active keywords yet — go to <strong style={{color:"#a5b4fc"}}>Settings → My Service Profile → Keywords</strong> to set them up.</div>
          : <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {kws.map(kw => <span key={kw} style={{ background:"#1e1e3e", color:"#a5b4fc", fontSize:12, padding:"4px 12px", borderRadius:6, fontFamily:"'DM Mono',monospace" }}>🔑 {kw}</span>)}
            </div>}
        <div style={{ marginTop:10, color:"#4a4a6a", fontSize:11 }}>We scan every post in your monitored platforms for these keywords and alert you instantly when found.</div>
      </div>

      <div style={{ ...sc, borderColor:"#6366f130" }}>
        <div style={{ color:"#a5b4fc", fontSize:12, fontWeight:600, marginBottom:6 }}>💡 Do not see a Facebook group that should be monitored?</div>
        <div style={{ color:"#64748b", fontSize:12 }}>Go to <strong style={{color:"#a5b4fc"}}>Settings → My Service Profile → FB Groups</strong> to request a new group.</div>
      </div>
    </div>
  );
}

